/**
 * The invoice's line items, edited in place.
 *
 * ## ✅ THIS GRID IS THE LIVE EDITING SURFACE, AND THE DEAD MODAL IS NOW DELETED
 *
 * ✅ **`InvoiceLineFormModal.tsx` was DELETED on 2026-09-19, on the owner's
 * decision**, together with its quote twin `QuoteLineFormModal.tsx`. It was
 * re-verified unimported repo-wide immediately before the deletion: the only
 * references anywhere were comments like this one.
 *
 * ⚠️ *SUPERSEDED, and kept because a grep for the name now finds this note
 * rather than the file: "`InvoiceLineFormModal.tsx` still exists and still
 * compiles, and **nothing imports it** — measured repo-wide, it has zero
 * references outside itself. `InvoiceDetailPage` mounts THIS component; the
 * modal is mounted nowhere, and its quote twin `QuoteLineFormModal.tsx` is dead
 * for the same reason."* **Do not read either name below as live code.**
 *
 * ⚠️ **The LESSON outlives the file, which is why the paragraph stays: a line
 * field added to an unmounted modal ships INERT**, and that is not
 * hypothetical. The per-line REGION multiselect was built there first and was
 * unreachable on quotes and invoices from the day it landed — `tsc` clean,
 * every test green, a backend accepting `regionIds` that no screen could ever
 * send. It is the shape this codebase catalogues as **a value nothing
 * renders**, and the modal being a plausible-looking home for the field is
 * exactly what hid it: the diff looked complete.
 *
 * **Add a line field HERE.** Deals are still the one document whose line modal
 * IS mounted (`components/deals/LineItemFormModal.tsx`), so a field belongs in
 * this grid, in the quote grid, and in that deal modal.
 *
 * ⚠️ *SUPERSEDED 2026-09-19 by the deletion of the two dead modals: "so the
 * same field added in all three places is reachable on one of them — which is
 * how three near-identical modules come to behave differently while every one
 * of them reads as finished."* **There are no longer three near-identical line
 * MODALS** — the quote and invoice ones are gone and only the deal one remains.
 * ⛔ **The hazard is NOT retired, it has MOVED**: the three near-identical
 * surfaces are now this grid, the quote grid and the deal modal, and a field
 * added to two of the three still behaves differently on the third while every
 * one of them reads as finished.
 *
 * ## ⚠️ THIS IS THE SIBLING OF `@/components/quotes/QuoteLineItemsTable`
 *
 * **The two must move together.** They are a deliberate duplication, not an
 * accident: the owner was offered one shared component across both documents,
 * was told the duplication risk in those words, and chose two files. So this
 * file exists on the understanding that **a behavioural change to either grid
 * is a change to both**, and that nothing here may quietly become a second
 * opinion about something the quote grid already decides.
 *
 * `QuoteLineItemsTable` is the ORIGINAL and the reference. Read its header
 * before editing this one — it argues, at length and with the incidents behind
 * them, every decision this file inherits: why Xero's ten columns became these
 * eight, why Amount and the actions column are pinned, why the tax rate is one
 * control and not two, why the money never reads `cities.length`, why the
 * Description cell types over a live derivation, why the inclusions gate is the
 * strict predicate, and why saving is N requests that report partial success.
 * Not one of those arguments is restated here; restating them is how the two
 * copies come to disagree about what they say.
 *
 * ## What is SHARED — imported, never re-implemented
 *
 * Everything below is common to both grids and lives in `src/lib`. If you find
 * yourself writing a function here that one of these already exports, stop and
 * import it.
 *
 * | module | what it owns |
 * | --- | --- |
 * | `@/lib/line-description` | **`composeLineDescription`** — the line's derived text. ⚠️ **A JOIN KEY, not formatting.** The backend composes the same string independently at push time whenever `description` is `null`, and nothing on either side reports a divergence. A second composer in this file is the exact hazard that module's header documents. ⚠️ It is handed this row's real cities, **regions** and inclusions — see {@link descriptionOf}. *SUPERSEDED: "It is called with `inclusions: []` here and with the row's real ones on the quote … do not 'fix' it."* The backend's invoice builder imports the quote builder's `inclusionsSegment` and `geographySegment`, so both document types compose one string. |
 * | `@/lib/line-project` | `lineRefusalOf` — classifying the seven project/city refusals a line write can collect |
 * | `@/lib/use-line-project` | `productOfProject` — the one copy of "the product a project SEEDS a blank line with", which is still the primary — and `projectSellsProduct`, the one copy of the picker's narrowing rule, which since migration 156 is over the project's whole product SET. ⚠️ *SUPERSEDED: "`productOfProject` — the one copy of 'a project sells exactly one product'."* Their per-line HOOK is deliberately not used: a grid resolves the project list once, not once per row |
 * | `@/lib/inclusions` | `renderInclusion` / `isValidInclusionQuantity` — campaign-inclusion wording. Nothing here pluralises; both forms are stored |
 * | `@/lib/product-inclusions` | which products offer inclusions, and which. Set per product by an administrator through the link table |
 * | `@/lib/decimal` | **`computeGeneratedLinePreview`** and `sumDecimalStrings` — money arithmetic. Never hand-roll a total: the database rounds per line and tax is charged on the already-rounded subtotal |
 * | `@/lib/xero-tax-rates` | the tenant's rate list, `sameTaxCode`, and which rates a sales line may carry |
 * | `@/lib/use-project-options` | `cityLabel` |
 * | `@/lib/format` | `formatDecimalString` |
 *
 * ## What is NOT shared, and is duplicated from the quote grid on purpose
 *
 * The row draft, the payload builders, the dirty test, the validation, the
 * controls and the whole render. They are duplicated because the owner chose
 * duplication. **They are not different**, and every difference below is one of
 * the four genuine contract deltas — anything else that drifts is a bug in
 * whichever file drifted.
 *
 * ## The four ways an invoice line differs from a quote line
 *
 * 1. **The endpoints.** `/invoices/{id}/products` rather than
 *    `/quotes/{id}/products`, and `InvoiceProduct` /
 *    `InvoiceProductCreateInput` / `InvoiceProductUpdateInput`. The two write
 *    payloads are field for field identical, which is what keeps `convert`
 *    auditable — see `InvoiceProduct.project` in `@/lib/types`.
 * 2. **There is no `invoiced` flag.** A quote line carries `invoiced` and
 *    `invoicedOn`, because a quote line can be billed. An invoice line is the
 *    bill, so the "Billed" badge and the delete dialog's provenance warning
 *    have no counterpart here and are absent rather than stubbed.
 * 3. **Draft mode was ported, not invented.** `InvoiceFormModal` collects
 *    lines before the invoice exists, using the quote grid's `mode: 'draft'`
 *    wholesale — see the section below. It is the quote grid's mode, not a
 *    second opinion about it.
 * 4. **The push.** See below — and it is the one behaviour with no quote-side
 *    counterpart at all.
 *
 * ## `mode: 'draft'` — lines collected before the invoice exists
 *
 * The New Invoice modal wants line items entered in the same action that
 * creates the invoice. **A line cannot be written before the invoice has an
 * id** — every edit in saved mode is a request against
 * `/invoices/{id}/products` — so `draft` mode holds the rows entirely in local
 * state, issues **no line request of any kind** (no POST, no PATCH, no DELETE,
 * no basket load) and reports the payloads upward through
 * {@link InvoiceLineItemsDraftProps.onDraftLinesChange}. The parent creates the
 * invoice and then writes them.
 *
 * Four things make that mode honest rather than a second, weaker grid — they
 * are the quote grid's four, and its header argues each one at length:
 *
 * - **One payload builder.** Draft rows go through `createPayload()`, the same
 *   function the saved POST uses, so the shape the parent writes cannot drift
 *   from the shape this table has always sent. That is also what keeps
 *   {@link PriceMode} honest across the modal: `inherit` omits `unitPrice` in
 *   draft mode for the same reason and by the same line of code.
 * - **The money is a PREVIEW and says so.** `lineSubtotal`, `lineTax` and
 *   `lineTotal` are generated columns; in draft there is no server value at
 *   all, so every figure on screen is `computeGeneratedLinePreview` — our
 *   arithmetic — and the footer is labelled `Preview total`.
 * - **A repeated product is NOTICED here, and refused nowhere.** ⚠️ *SUPERSEDED:
 *   "The duplicate the server would refuse is caught here … the only thing
 *   standing between the user and a 409 that arrives after the invoice has been
 *   created."* Migration 129 dropped the
 *   `(invoice_id, product_id, project_key)` index, so no 409 remains.
 *   `duplicatePairsOf()` survives as a NOTICE, on `RowProblems.notice`, which
 *   `blockedKeys` deliberately does not read.
 * - **Only rows the server would accept are emitted.** A row failing local
 *   validation is withheld and counted through `onDraftBlockedChange`, so the
 *   parent can refuse to submit rather than create an invoice and then collect
 *   a 422 for a line it can no longer show. ⚠️ An invoice cannot be un-created,
 *   so a bad row has to block the create — never be dropped after it.
 *
 * ⚠️ **The reference-data fetches still run in draft mode**, and must: the
 * products, campaign-inclusions, active-projects and Xero tax-rate lists are
 * what the pickers are made of, and a grid with empty dropdowns is not a
 * no-request grid, it is a broken one. "No requests" means no *line* requests —
 * nothing that writes, and nothing that reads a basket which does not exist.
 *
 * ⚠️ **Draft mode changes nothing about `inclusions`**, and what it changes
 * nothing about has been reversed. *SUPERSEDED: "The invoice payload builder
 * omits the bracketed inclusions segment … `composeLineDescription` is called
 * with `inclusions: []` here and with the row's real ones on the quote … Do not
 * 'harmonise' the two."* The backend's invoice builder **imports** the quote
 * builder's `inclusionsSegment` (`3cd2e1c`), so this grid passes the row's real
 * inclusions and the two document types agree by construction. A draft row goes
 * through the same composer as a saved one, which is the part that still holds.
 *
 * ## ⚠️ "All cities" is a FLAG, and the description never says so
 *
 * A line either names its cities explicitly (`cityIds`) or is flagged as ALL of
 * its project's (`allCities`, migration 083). The two are **mutually exclusive
 * on the wire — sending both is a 422** — and the flag re-resolves against the
 * project on every read where a list does not, so they diverge the moment the
 * project changes. {@link Draft.allCities} records what this grid holds,
 * {@link CitiesControl} how the two are told apart on screen and what the
 * toggle does about a set it is replacing, and {@link createPayload} /
 * {@link updatePayload} are the only two places that pick one key.
 *
 * ⚠️ **`composeLineDescription` gains nothing from any of it.** The printed
 * line lists the RESOLVED city names — `Campus Outreach — Bangalore, Mumbai,
 * Delhi` — and never the words "all cities": the customer is told where the
 * work happens, and a document from March still reads as what was agreed in
 * March. {@link descriptionOf} passes the resolved set exactly as it always
 * has; there is no "all" branch to add to any description, here or on the
 * backend's composer.
 *
 * ⚠️ **Never implement "all" by expanding it to every city id at save time.**
 * That is a snapshot, it looks identical on screen today, and it stops
 * following the project silently — which is the entire reason the flag exists
 * rather than a "select all" button.
 *
 * ## The Item cell is a STACK: item, then project, then cities
 *
 * Project and Cities had their own columns until 2026-08-28. They now render
 * one under the other **inside the Item cell**, in the order somebody fills
 * them in: the item, the project that item is sold through, then the "all
 * cities" flag and the city picker. The `<colgroup>` lost two entries and
 * `columnCount` went from ten to eight.
 *
 * ⚠️ **The project block is HIDDEN when the line's product is not sold through
 * projects, and hiding it changes NOTHING about what is sent.** "Sold through
 * projects" is derived server-side and is never a flag, so this client
 * reproduces the guard's own test — `product_id = ? AND is_active = 1`, which
 * is byte for byte what `GET /projects?productId=N&isActive=true` asks — and
 * therefore arrives one project late, knowingly. {@link RowProject.showProject}
 * is that decision and it **fails towards SHOWING**: a still-loading, failed or
 * truncated project list, and any line that already names a project, all keep
 * the block. A line with no project is NORMAL — `Meet Up`,
 * `Profile Evaluations` and `Miscellaneous` have no projects and never will —
 * so an absent block is never an error state and says nothing at all.
 *
 * ⚠️ **A hidden block must OMIT its keys, never send `[]`.** `cityIds: []`
 * CLEARS a stored set, with a 200 and nothing to restore from. So hiding is a
 * render branch that never touches the draft, and `offersProjects` is a
 * parameter with **no default** on {@link createPayload}, {@link updatePayload}
 * and {@link isDirty} — the shape `offersInclusions` already has here — so
 * `tsc` refuses a call site that forgets it.
 *
 * ⚠️ **Project and Cities are two controls and ONE patch.** Choosing a project
 * still emits `{ projectId, cityIds: [] }` in the same `onChange`, because the
 * server judges the POST-MERGE state. Moving them into one cell did not merge
 * them into one control, and must never split that patch.
 *
 * ## The Discount box takes an AMOUNT or a PERCENTAGE, and stores whichever
 *
 * ⚠️ **THERE ARE TWO DISCOUNT COLUMNS NOW, AND NOTHING CONVERTS BETWEEN
 * THEM.** `discount` is a string PERCENTAGE (`0..100`, `NOT NULL`) and
 * `discountAmount` is exact money (nullable). They are **mutually exclusive**:
 * sending both non-zero is a 422 naming `discountAmount`, so a write always
 * carries the PAIR — the live one, and the other said to be off.
 *
 * 🔴 *SUPERSEDED, in full: "There is no amount discount on the wire … The
 * toggle on that box is a client-side convenience over a percentage column: it
 * stores `amount / (quantity × unitPrice) × 100` rounded to two decimals … Do
 * not 'finish' this with a `discountAmount` field — there is no column for
 * one, and a line write would refuse the key."* There is a column behind it,
 * the conversion is DELETED, and `percentageForAmount` no longer exists in
 * `@/lib/decimal`.
 *
 * ⚠️ **DO NOT RE-DERIVE THE CONVERSION.** It was lossy by POUNDS, not pence:
 * £1,000 off an £18,000 line became `5.56%`, which takes £1,000.08 — so the
 * cell had to carry a sentence saying the figure typed was not the figure
 * stored, and four refusals for amounts no percentage could express. All of
 * that was the apology, and all of it is gone with the thing it apologised
 * for. **The quote grid says the same thing at the same place; the two grids
 * are deliberately near-identical here.**
 *
 * ## ⚠️ A line write REWRITES the invoice's four header totals, server-side
 *
 * `subTotal`, `discountTotal`, `taxTotal` and `grandTotal` are re-derived from
 * the lines in the same transaction as every line create, update and delete,
 * and **none of the three returns the new header**. So:
 *
 * - {@link InvoiceLineItemsTableProps.onLinesChanged} fires after any
 *   successful write and the parent MUST re-read the invoice. The server's
 *   figures are authoritative.
 * - Nothing here ever recomputes a header total. The footer's "total of saved
 *   lines" is `sumDecimalStrings` over the server's own `lineTotal` values and
 *   is labelled as a cross-check, exactly as it is on the quote grid — a
 *   locally-summed figure that disagrees with the header is how a wrong number
 *   reaches an invoice.
 *
 * ## ⚠️ THE XERO PUSH IS THE SERVER'S — CORRECTED 2026-08-30
 *
 * **This component builds no push of its own and must never grow one.** That
 * sentence is the only part of this section that survives intact; everything
 * around it described a mechanism that has been deleted. It read:
 *
 * > *"THE XERO PUSH IS THE PARENT'S, AND THERE IS ONLY ONE OF THEM.
 * > `InvoiceDetailPage` owns `queueXeroPush()` — the linked-only, `conflict`-
 * > and `archived`-excluded, two-second-debounced, one-in-flight-per-invoice
 * > path that every header field save already goes through … It reaches it
 * > through `onLinesChanged`, and the coalescing is structural rather than
 * > hoped for: `saveAll()` writes N rows one at a time and calls
 * > `onLinesChanged` **once** … `queueXeroPush()` then debounces that call by
 * > two seconds and folds any further save — a line, a header field, a delete —
 * > into the same window … One push per row would spend the office's budget on
 * > a single save."*
 *
 * `queueXeroPush()` no longer exists. **Every line write pushes on the SERVER,
 * inside its own request**, and answers `xeroPush` beside `data`.
 *
 * ⚠️ **So a run of eight rows IS eight pushes now, not one.** The debounce that
 * coalesced them belonged to the page and went with it. That is the accepted
 * cost of the push being a property of the system — an importer, a script and
 * any second client get it too — and the server takes a per-document lock, so
 * the saves serialise rather than race. The rate-limit warning above was real
 * and is now the server's to bear.
 *
 * ⚠️ **`onLinesChanged` still fires ONCE per run**, and that is unchanged: it
 * is the REFETCH signal (the header totals above), which has nothing to do with
 * pushing and never did.
 *
 * What this grid does now is REPORT. See
 * {@link InvoiceLineItemsSavedProps.onXeroPush} for the one rule that governs
 * a run of N reports — first failure or silence, never a tally.
 *
 * ## ⚠️ `unitPrice` ABSENT is what snapshots the catalogue price
 *
 * Three states, and collapsing any two of them puts a wrong figure on a
 * customer's invoice — this went wrong live, with `100` landing on a line whose
 * catalogue price was 17,500. See {@link PriceMode}. **Never send `''`, `null`
 * or `0` to mean "use the catalogue".** Omitting the key is the only way to say
 * it, `null` is the different fact "no price on this line", and `0` is a real
 * price of zero.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **THE SEED SHAPE IS THE MAPPER'S OWN TYPE, IMPORTED — NOT A SECOND ONE
 * DECLARED HERE.** `plannedProductLines()` in `@/lib/api` already decides the
 * single question a seed has to answer: whether a PLANNED quantity — a decimal
 * string — can be a LINE quantity, which is an integer of at least 1. Declaring
 * a look-alike interface in this file would be a second place for that decision
 * to live, and the two would agree only until one of them was edited.
 *
 * ⚠️ **`lineQuantity: null` IS NOT A LICENCE TO SUBSTITUTE 1**, and this grid is
 * where that rule is actually kept — see `seededRowOf` and `validateRow`. The
 * quote grid carries the identical pair; the two move together.
 */
import type { PlannedProductLine } from '@/lib/api';
import { cx } from '@/lib/cx';
/*
 * ⚠️ **ONE MODEL FOR TWO ROUTES, and this grid is the second reader of it.**
 * `POST /deals/{id}/convert` (→ quote) and `POST /deals/{id}/invoices`
 * (→ invoice) take the IDENTICAL body — `raiseDealInvoiceSchema` and
 * `convertDealSchema` are the same two optional keys, `lines` and `lineSet`,
 * with the same `refuseBothLineKeys` — so the entry builder, the change test and
 * above all `convertDiscountWrite` are imported rather than re-implemented.
 *
 * A second copy would agree the day it was written and drift on the first
 * correction, and what would drift is the DISCOUNT PAIR: `discount` and
 * `discountAmount` are mutually exclusive columns on a document a customer is
 * asked to pay, and a row stating both is refused by a CHECK constraint.
 */
import {
  convertLineNotesWrite,
  convertLineSetEntryIsCarryOver,
  convertLineSetEntryOf,
  convertLineShapesMatch,
  type ConvertLineShape,
  type DealConvertLineSetEntry,
} from '@/lib/deal-convert-lines';
import {
  computeGeneratedLinePreview,
  solveLineAmount,
  sumDecimalStrings,
  type GeneratedLineInput,
  type GeneratedLinePreview,
} from '@/lib/decimal';
import { proRataUnitPrice } from '@/lib/city-pro-rata';
/*
 * Project pricing (owner 2026-10-27): a project price entry per product ×
 * currency × tier, else the legacy catalogue pro-rata. Both rules live in the
 * two modules below and are never re-implemented here; this grid only decides
 * WHEN an untouched line follows them and what goes on the wire.
 */
import { computeLinePrice, tierOptionsFor, type ComputedLinePrice } from '@/lib/line-pricing';
import { useProjectPrices } from '@/lib/use-project-prices';
import type { ProjectPriceEntry } from '@/lib/project-pricing';
/*
 * ⚠️ **WORDING ONLY.** The mode's two labels live in one module so the three
 * grids cannot word this choice differently; nothing about the payload, the
 * refusals or the arithmetic comes from there. `discountWriteOf` below is still
 * what decides which of the two columns the wire carries, and this grid keeps
 * its own refusal set — which genuinely differs from the quote grid's.
 *
 * ⚠️ **`percentageModeIsChosen` NARROWS THAT SENTENCE WITHOUT BREAKING IT, and
 * the distinction is worth stating rather than letting the next reader decide
 * it was already broken.** It answers a question about the MODE — this module's
 * whole subject — and returns a boolean; it computes no money and builds no
 * `{discount, discountAmount}` pair. What it does do is pick which BRANCH of
 * `solveLineAmount` the Amount cell takes, so a wrong answer here is a wrong
 * column written. ⛔ **It is NOT `discountModeOf(...) === 'percentage'`** — that
 * reads true on every untouched line, because `discountAmount === null` is the
 * default rather than a choice. See {@link EditableAmount}.
 */
import {
  DISCOUNT_MODE_GROUP_LABEL,
  DISCOUNT_MODE_OPTIONS,
  discountModeOf,
  percentageModeIsChosen,
} from '@/lib/discount-mode';
import { formatDecimalString } from '@/lib/format';
import { isValidInclusionQuantity, renderInclusion } from '@/lib/inclusions';
/*
 * ⚠️ **The ONE copy of the join, and it is pinned for a reason nothing here can
 * detect.** The backend composes the SAME string independently when it pushes a
 * line to Xero — that is what `description: null` means — so a joiner of ours
 * that differed would make a line read one way in EMERGE and another on the
 * customer's Xero document, with nothing on either side reporting it. It bites
 * only while `description` is null, which is the common case. Do not
 * re-implement the join, and do not inline it "because it is one template
 * literal".
 */
import {
  composeLineDescription,
  effectiveInclusionQuantity,
  type LineDescriptionInclusion,
} from '@/lib/line-description';
/*
 * ⚠️ **THE DECISIONS, AND THEY ARE NOT RE-IMPLEMENTED HERE.** What a line starts
 * its discount at and which campaign inclusions a quantity presets are one copy
 * for the quote, invoice and deal grids — three copies would agree the day they
 * were written and drift on the first correction, and what would drift is a
 * figure a customer is billed against and a set printed on their document.
 *
 * ⛔ **Neither function fetches or writes; both answer "given what the product
 * recommends and what the reader has already done, what should change?" and
 * return `null` for CHANGE NOTHING.** This file owns the requests, the cache and
 * the draft; it must not own the rule. In particular `null` is never widened
 * into `''`, `'0.00'` or `[]` on the way out — see {@link seedLinePresets}.
 */
import {
  inclusionPresetSeed,
  recommendedDiscountSeed,
  type LineInclusionDraft,
} from '@/lib/line-presets';
import { lineRefusalOf, type LineRefusal } from '@/lib/line-project';
/*
 * ⚠️ `shouldShowInclusionsEditor` is deliberately NOT imported, here for the
 * same reason it is not imported by the quote grid: the owner chose the strict
 * predicate `productOffersInclusions` over the lenient one, accepting that an
 * inclusion on a non-offering line becomes read-only in this app.
 */
/*
 * ⛔ `inclusionsRequiredOnLine` and `INCLUSION_REQUIRED_MESSAGE` are GONE, not
 * merely unimported — the at-least-one rule was withdrawn on 11 Sep 2026 and
 * both were deleted from `@/lib/product-inclusions` with it. See `validateRow`.
 */
import {
  hasOrphanedInclusions,
  inclusionsDenialReason,
  offeredInclusionIds,
  productOffersInclusions,
  NO_PRODUCT_CHOSEN,
  type NoProductChosen,
  type ProductInclusionOffer,
} from '@/lib/product-inclusions';
/*
 * Products sold per unit in fixed multiples (owner, 2026-10-16). The rules are
 * one copy for the three grids; this file only decides WHEN to apply them.
 */
import {
  autoLineDescription,
  defaultQuantityFor,
  isAutoDescription,
  perUnitDiscountTotal,
  quantityStepError,
  type AutoLineDescriptionArgs,
  type ProductUnitFields,
} from '@/lib/product-units';
import { productOfProject, projectSellsProduct, useCycleScopeIds } from '@/lib/use-line-project';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { cityLabel } from '@/lib/use-project-options';
/*
 * The `/regions` master, for the LINE's own regions of focus. It already
 * queries `isActive: true`, so a retired region is never OFFERED — offering one
 * is offering a guaranteed 422 — while a region a line already HOLDS is pinned
 * back in by {@link regionOptionsFor} rather than silently dropped.
 */
import { useReferenceOptions } from '@/lib/use-reference-options';
import type { XeroPushReport } from '@/lib/xero';
import { useXero } from '@/lib/xero-context';
import { useAuth } from '@/lib/use-auth';
import { LineNoteField } from '@/components/documents/LineNoteField';
import {
  companyTaxRateReason,
  expenseRateCount,
  hasEffectiveRate,
  /*
   * The gate for whether this line's tax control may be edited at all —
   * migration 096. It reads the SERVER's answer per line and is never a
   * constant; see its doc comment for why "make it read-only" is a defect if
   * taken literally.
   */
  isCompanySourcedTaxRate,
  isSalesTaxRate,
  lineTaxRates,
  /*
   * The ONE definition of "these two tax codes name the same rate". A stored
   * code and a live tenant code can differ in case — the server normalises on
   * write — so `===` between them is a bug every time.
   */
  sameTaxCode,
  taxRateHint,
  unquantifiedSalesRateCount,
  useDocumentTaxCodes,
  useXeroTaxRates,
} from '@/lib/xero-tax-rates';
import type {
  CampaignInclusion,
  DealProduct,
  InvoiceProduct,
  InvoiceProductCreateInput,
  InvoiceProductUpdateInput,
  LineCity,
  LineInclusion,
  PriceTier,
  Project,
  ProjectCity,
  QuoteProduct,
  ResolvedInclusionPreset,
  XeroTaxRate,
} from '@/lib/types';
/*
 * ⚠️ **The quote conversion's override grammar is IMPORTED, never restated.**
 * `POST /quotes/{id}/convert` and the two deal conversions carry the same five
 * keys under different id names, and the half that must have exactly one
 * implementation is the DISCOUNT PAIR — two mutually exclusive columns on a
 * customer's invoice. See the header of `@/lib/quote-convert-lines`.
 */
import type { ConvertLineEdit } from '@/lib/quote-convert-lines';
import {
  quoteConvertLineEditOf,
  quoteConvertLineOverrides,
  type QuoteConvertLineOverride,
} from '@/lib/quote-convert-lines';
import { validateConvertLineEdit } from '@/lib/deal-convert-lines';
import { Notice } from '@/components/ui/Notice';
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Textarea,
  Tooltip,
} from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';

/* ── The server's own patterns, so a refusal is caught before it is sent ──── */

/**
 * ⛔ **ONE PATTERN BECAME TWO ON 2026-09-12, AND MERGING THEM AGAIN IS A
 * SURCHARGE.** A single `PRICE_PATTERN` served `unitPrice` AND `discountAmount`
 * — correct while both were non-negative, wrong the moment one stopped being.
 *
 * Migration 171 lets a line PRICE be negative (a DEDUCTION LINE: INV-0886
 * carries one at `-4150.00`), while `discountAmount` stays non-negative under
 * `ck_*_discount_amount_sign`. A negative in the first box is a credit; a
 * negative in the second is a **surcharge arriving through the discount
 * field**, which the database refuses only as errno 4025 — a 422 naming no
 * field at all, which nobody can bind to the control they typed in.
 *
 * The backend pins the pair behaviourally in
 * `tests/line-negative-unit-price.test.ts`. **They differ by one character and
 * must never converge.**
 */

/** `unitPrice`: `^-?\d{1,10}(\.\d{1,2})?$`, straight off the POST schema. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;

/** `discountAmount`: unsigned — a discount is not a surcharge. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
/** `discount` and `taxRate`: a PERCENTAGE 0..100, `^\d{1,3}(\.\d{1,2})?$`. */
const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * A tenant's Xero rate, as a string `taxRate` will actually accept.
 *
 * ⚠️ **The parameter is `number` and STAYS `number`.** `effectiveRate` is
 * `number | null`, `null` meaning Xero declined to state a percentage, and this
 * function has no honest answer for it — `?? 0` would write 0% tax onto a
 * customer's document off a figure that does not exist. The CALLER decides:
 * `hasEffectiveRate()` narrows and {@link lineTaxRates} withholds such rates
 * from the picker. The rounding to two decimals is disclosed in the option's
 * own hint rather than applied quietly. See the quote grid's copy of this
 * function for the live tenant rate (Canada's `14.975`) that forced it.
 */
function percentForDraft(value: number): string {
  const exact = String(value);
  if (PERCENT_PATTERN.test(exact)) return exact;
  return value.toFixed(2);
}

/** A longer array is a 422 on `inclusions`. */
const MAX_INCLUSIONS_PER_LINE = 50;
/** `/products` caps here; 28 rows exist today, so one page is the whole list. */
const PRODUCTS_PER_PAGE = 100;
/** `/campaign-inclusions` caps here. */
const INCLUSIONS_PER_PAGE = 200;
/**
 * How long the Quantity box must settle before its inclusion preset is asked
 * for — the house 250ms, the same figure every debounced search here uses.
 *
 * ⚠️ **The request is per `(product, quantity)`**, so an undebounced box issues
 * one read per KEYSTROKE against a per-IP budget shared across the
 * office, and caches every intermediate figure nobody meant to type. Typing
 * `125` would ask about 1, then 12, then 125.
 * ⚠️ *SUPERSEDED: "a 120/60s per-IP budget."* It is **12,000/60s** (corrected
 * 2026-09-17), so what the debounce is for is the two cached answers nobody
 * meant to ask for, not the ceiling.
 */
const PRESET_DEBOUNCE_MS = 250;
/*
 * ⚠️ **`PROJECTS_PER_PAGE` IS GONE — the grid PAGES now (2026-09-15).**
 * *SUPERSEDED: `const PROJECTS_PER_PAGE = 100`, sent as the `perPage` of one
 * read.* It was being hit — 118 active projects against it — and the same 18
 * rows fell off every time. `api.listAllProjects` owns the page size; it is not
 * a number this file may choose. See `useActiveProjects`.
 */
/**
 * `cityIds` caps here. A denial-of-service bound rather than a business rule —
 * the real limit is how many cities the line's project declares.
 */
const MAX_CITIES_PER_LINE = 200;
/**
 * `description` caps here — the same 16,000 the invoice's own `terms` takes.
 *
 * Checked before sending rather than enforced with a `maxLength` on the box: a
 * `maxLength` truncates a PASTE silently, and the tail it drops is wording a
 * customer was meant to read.
 */
const MAX_DESCRIPTION_LENGTH = 16000;

/**
 * The default explanation for a delete control an account cannot use.
 *
 * ⚠️ **DELETE on a line is `ADMIN_ROLES` while POST and PATCH are
 * `WRITE_ROLES`.** A `basic_user` can add a line and edit it and cannot remove
 * it again — an asymmetry nobody would predict, and one that has to be visible
 * before the row is built rather than discovered afterwards.
 */
// 2026-10-01: deletes follow the set. *SUPERSEDED: "restricted to administrators".*
const DEFAULT_DELETE_DENIAL =
  'Your permission set does not include this. An administrator can change that.';

/* ── The two pinned columns ───────────────────────────────────────────────── */

/*
 * ⚠️ **AMOUNT AND THE ACTIONS COLUMN ARE PINNED TO THE TRAILING EDGE.** The
 * `<colgroup>` sums to 1492 and the wrapper is around 1100, so without this the
 * figure the whole row exists to produce is the one thing never on screen.
 * Narrowing columns cannot fix it — the whole saving available is about 128px
 * against a 202px shortfall. The quote grid's copy of this block has the
 * measurements and the three details that make it work: the offset equals the
 * actions column's own width, the pinned cells must be OPAQUE, and the leading
 * hairline is an inset shadow because `border-collapse` would leave a real
 * border behind while the cell slid away from it.
 */
const PIN_AMOUNT = 'sticky right-[94px] z-10 shadow-[inset_1px_0_0_var(--hairline)]';
/** Hard against the trailing edge; the Amount column is offset by its width. */
const PIN_ACTIONS = 'sticky right-0 z-10';
/** An ordinary body row: the `Panel` surface the table sits on. */
const PIN_ROW_BG = 'bg-surface';
/**
 * A FAILED row's pinned cells. `bg-danger-soft/40` is translucent and a
 * translucent pinned cell is the defect the pins exist to fix, so the exact
 * composite is computed instead — alpha compositing is linear in sRGB, so this
 * is byte-for-byte what the row's own tint paints over `bg-surface`.
 */
const PIN_ROW_BG_FAILED = 'bg-[color-mix(in_srgb,var(--danger-soft)_40%,var(--surface))]';

/* ── Props ────────────────────────────────────────────────────────────────── */

/**
 * Whether an invoice exists behind this grid.
 *
 * - `saved` — the default and the shape this component was born with. Every
 *   edit is a request against `/invoices/{id}/products`.
 * - `draft` — no invoice exists yet. Rows are held locally, **no line request
 *   is issued**, and the payloads are reported through `onDraftLinesChange` for
 *   the parent to write once the invoice has an id.
 * - `convert` — a DEAL is about to be raised as an invoice. The rows start as
 *   the deal's own lines, **no line request is issued**, and what goes up is the
 *   `lineSet` array `POST /deals/{id}/invoices` takes.
 * - `quote-convert` — a QUOTE is about to be converted. **The one mode holding
 *   TWO KINDS OF ROW**: the quote's own lines, which the server copies and this
 *   grid may only RESPELL, and rows somebody adds here, which are written after
 *   the invoice exists exactly as `draft` rows are.
 *
 * ⚠️ **`draft` and `convert` BOTH issue no request and are NOT the same mode.**
 * A draft row becomes its own `POST /invoices/{id}/products` after the invoice
 * exists; a convert row is written by the SERVER inside the conversion's own
 * transaction. Mounting the draft grid on a conversion appends rows to lines the
 * server has already copied — an invoice demanding money for the same work
 * twice, from a control that looks exactly like the one on a blank form.
 *
 * ## ⚠️ `convert` AND `quote-convert` ARE DIFFERENT ENDPOINTS, NOT TWO NAMES FOR ONE
 *
 * They look interchangeable and are not. The difference is the request each can
 * express, and mirroring one onto the other TYPECHECKS every time:
 *
 * | | `convert` (deal) | `quote-convert` |
 * | --- | --- | --- |
 * | wire key | `lineSet` — the COMPLETE final list | `lines` — SPARSE respellings |
 * | keyed by | `dealLineId` | `quoteProductId` |
 * | may change item/project/cities/regions/inclusions | **yes** | **no — no such key exists** |
 * | may ADD a copied row | yes, as a `lineSet` entry | no — an added row is a `draft` row written afterwards |
 * | may REMOVE a row | yes, by omission | **no — omission means "unchanged"** |
 * | tax | ONE company rate for every row | **the QUOTE LINE's own rate, per row** |
 *
 * ⚠️ **The omission row is the dangerous one.** On `lineSet` a row left out
 * disappears from the document; on `lines` a row left out is copied unchanged.
 * Two arrays of the same shape with opposite consequences for the rows nobody
 * touched — which is why these are separate modes rather than one with a flag.
 */
export type InvoiceLineItemsMode = 'saved' | 'draft' | 'convert' | 'quote-convert';

interface InvoiceLineItemsCommonProps {
  /**
   * **The header project's whole city set** — `invoice.cities` on a saved
   * invoice, and **empty on a create**.
   *
   * ⚠️ **There is no header project picker.** An invoice is not connected to a
   * project as a whole, individual line items are, so `InvoiceFormModal` passes
   * a structurally-empty set on create, exactly as `QuoteFormModal` does. A
   * saved invoice still carries a header project and still derives cities
   * through it, which is why this prop is not simply removed.
   *
   * ⚠️ **It is NOT a line's option list, and it is not what the server
   * validates against.** Since migration 067 a line names its own project and
   * `cityIds` is checked against THAT project's cities. Offering the header's
   * set on a line whose project differs would offer guaranteed 422s.
   *
   * It is kept for one honest use: naming a city we hold no other record of.
   * Never pass `/cities` here — the master offers rows every line write refuses
   * wholesale.
   */
  cities: ProjectCity[];
  /** `invoice.currency`. Rendered beside every figure; never converted. */
  currency: string | null;
  /**
   * The COMPANY's own sales tax — **for DISPLAY and PREVIEW, never for a
   * payload.** See {@link CompanySalesTax}, which carries the whole rule.
   *
   * A created line already takes this figure server-side
   * (`input.taxRate ?? companyRate`), so the database is right today and the
   * SCREEN was not: the Tax rate control read "Xero default" and the Amount
   * cell previewed tax at 0.00 on every new row, because a blank `taxRate`
   * resolves to `'0'` in the arithmetic. This prop is what lets both say what
   * the save will actually do. Nothing here reaches `createPayload` or
   * `updatePayload`.
   *
   * ⚠️ **OPTIONAL, and `{ known: false }` when it is absent — which must change
   * nothing on screen.** A mount that does not pass it renders exactly as this
   * grid rendered before the prop existed.
   *
   * ⚠️ *SUPERSEDED 2026-09-27: "`InvoiceDetailPage` is such a mount and is
   * deliberately untouched: it has no company read of its own to source this
   * from, and inventing one would be a second request per page load for a
   * label."* Owner's decision: it is not a label — a new row on that page
   * previewed tax at 0.00 over a save taxed at the company rate. The page now
   * reads the company once and passes this, built as `InvoiceFormModal` builds
   * it.
   *
   * ⚠️ **{@link InvoiceLineItemsConvertProps} narrows it to REQUIRED**, because
   * there the Tax rate column is a readout of this and nothing else.
   */
  companySalesTax?: CompanySalesTax;
  /** `ability.canUpdate('invoices')`. False renders the table read-only. */
  canEdit: boolean;
  /** `ability.canDelete('invoices')` — admin tier, unlike create and update. */
  canDelete: boolean;
  /**
   * Why this account cannot delete, from `denialReason(ability, 'delete',
   * 'invoices')`. Shown on the disabled control rather than hiding it, because
   * a missing control does not explain the create/delete asymmetry.
   */
  deleteDenialReason?: string;
  /**
   * Rendered above the save report — for a parent that created the invoice in
   * the same action and needs to say so ("Invoice INV-0007 was created"), or
   * that needs to state, before the create, that the two halves are not atomic.
   * Someone who closes a form after a partial failure believing nothing was
   * created will make a second invoice.
   */
  contextNote?: ReactNode;
}

/**
 * ⚠️ **No `hasProject` flag, deliberately, and the quote grid's is dead.**
 *
 * `QuoteLineItemsTable` still declares one and does not destructure it — its
 * only reader was a paragraph describing what the Project column now plainly
 * shows. Adding the flag here to "match" would import a prop nothing reads.
 * What the header project is actually good for on this grid is `cities`, above,
 * and that is how both modes receive it.
 */
export interface InvoiceLineItemsSavedProps extends InvoiceLineItemsCommonProps {
  mode?: 'saved';
  invoiceId: number;
  /**
   * Called after any successful line write — create, update or delete, and
   * **once per save run rather than once per row**.
   *
   * ⚠️ **CORRECTED 2026-08-30 — it now carries ONE obligation, not two.** It
   * read: *"Two things depend on it and neither is optional. Every line write
   * rewrites the invoice's four header totals in the same transaction and
   * returns none of them, so the parent must re-fetch the invoice; and on a
   * LINKED invoice the parent's `queueXeroPush()` is what carries the change to
   * Xero. Firing once per run is what makes a batch of rows one push."*
   *
   * The REFETCH half is unchanged and is still not optional: every line write
   * rewrites the four header totals in the same transaction and returns none of
   * them, so the parent must re-read the invoice.
   *
   * The PUSH half is gone. `queueXeroPush()` was deleted when the push moved
   * onto the server, so this callback carries nothing to Xero and firing once
   * per run no longer decides how many pushes happen — the server pushes once
   * per WRITE. See the header section above, and {@link
   * InvoiceLineItemsSavedProps.onXeroPush} for what a run reports.
   */
  onLinesChanged?: () => void;
  /**
   * Hands the page the SERVER'S report of what a save run did to Xero.
   *
   * ⚠️ **Added 2026-08-30 with the move of the push onto the server.** Every
   * line write pushes the parent invoice inside the same request and answers
   * `xeroPush` beside `data` — see `SaveEnvelope` in `@/lib/types`. This grid
   * pushes nothing and never did; what changed is that it can now say what the
   * server did.
   *
   * ## ⚠️ WHAT A RUN OF N SAVES REPORTS, AND WHY IT IS NOT A SUMMARY
   *
   * `saveAll` writes N rows one at a time, so a run produces N reports. Exactly
   * one thing is handed up:
   *
   * - **the FIRST report reading `failed`, if any row's did.** A failure must be
   *   visible — that is the whole reason the key exists on the wire.
   * - **`undefined` otherwise**, which every reader turns into "say nothing".
   *   A `pushed` needs no announcement, and `skipped` is the ORDINARY answer
   *   for every invoice that is not in Xero yet, which is most of them:
   *   alarming on it would put a warning on nearly every line edit in the
   *   system.
   *
   * ⚠️ **The first failure, never a count and never a synthesised sentence.**
   * Two failures in one run are almost always one cause — the same company
   * unlinked, the same tax code refused — and *"3 problems"* is a claim nothing
   * verified. One real refusal, in the server's own words, is what somebody can
   * act on.
   *
   * ⚠️ **NOTHING HERE MAY BE WORDED AS A FAILED SAVE.** The line is stored
   * either way, and Xero does not enforce invoice-number uniqueness — somebody
   * who reads a failed push as a lost save and saves again can end up with two
   * live demands for money against one customer. The page owns that copy; this
   * hands up the report and no verdict.
   *
   * ⚠️ **`undefined` is an argument, not an omission.** It CLEARS whatever the
   * page is showing, which is right: a standing failure from an earlier save is
   * stale once a later save has pushed. Nothing may read an absent report as
   * *"nothing was sent"* — see `autoPushOutcomeOf`, where that is settled once.
   *
   * ⚠️ **Nothing is reported for a DELETE.**
   * `DELETE /invoices/{id}/products/{lineId}` answers `204`, and a `204` has no
   * body to carry a report — the delete DOES push on the server, it simply
   * cannot say so. See `confirmDelete`.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  /**
   * How many rows are unsaved, and how many of those cannot be sent.
   *
   * ⚠️ **THIS EXISTS BECAUSE A PARENT WITH ITS OWN SAVE BUTTON WAS LYING.**
   * `InvoiceFormModal` disables its footer Save on `!isDirty`, where dirty means
   * the HEADER form — so somebody who edited only line items met a greyed-out
   * "Save changes" while every row beside it read "Unsaved". Measured in the
   * audit log on 2026-09-01 against the quote twin of this grid:
   * `GET /quotes/392/products` twice and **not one write** — the person pressed
   * the control they could see and it issued nothing. The invoice modal mounts
   * this grid the same way and disables Save on the same header-only diff, so it
   * carries the same defect; it is fixed here rather than waited for.
   *
   * Reported for saved mode only. Draft, convert and quote-convert rows have
   * nowhere to be saved to and their parents already count them through their
   * own props.
   */
  onUnsavedChange?: (state: { dirty: number; blocked: number }) => void;
  /**
   * Hands the parent this grid's own save so one control can finish the job.
   *
   * Called with the function after every render — the closure must be the
   * current one — and with `null` on unmount. It resolves `true` when every
   * row the run targeted landed, so a parent can decide whether to close.
   *
   * 🔴 **PASSING IT HIDES THE GRID'S OWN SAVE CONTROL, AND THAT IS A REVERSAL.**
   * *SUPERSEDED: "The grid still renders its own Save and its own per-row
   * report. This is a second way to reach the same run, not a replacement for
   * it."* Owner's instruction, 10 September 2026: one button, reading "Save
   * changes", with no second save-looking control beside it.
   *
   * **The per-row report is UNCHANGED**, and the half of the old note that
   * argued for it still stands: the report is row-by-row and belongs beside the
   * rows, and a parent that swallowed it would be announcing an outcome it
   * cannot see. Only the BUTTON goes.
   *
   * ⚠️ **A parent that takes the save MUST call it.** The grid stops offering
   * any way to write a row the moment this prop is present, so a parent that
   * registers and never calls leaves line edits with nowhere to go — and
   * nothing on screen would say so.
   */
  registerSave?: (save: (() => Promise<boolean>) | null) => void;
  /**
   * **A save run WROTE at least one line; the document's line items are no
   * longer what a parent fetched.**
   *
   * ⚠️ **A NOTIFICATION, NOT A RESULT.** It carries nothing and promises
   * nothing about whether every targeted row landed — `saveAll`'s boolean
   * already answers that, for `registerSave`. It fires on a PARTIAL run too
   * (some rows written, some refused), because the parent's copy is stale
   * either way and a partial run is exactly when a stale list misleads most.
   *
   * ⚠️ **AND ON A DELETE.** Removing a line moves a document's derived
   * line-level fields — `lineProjectStatus` on the invoices list is computed
   * live from the lines — just as much as adding one does. A signal that
   * covered only writes would leave half the staleness live.
   *
   * ⚠️ **NOT fired on `saveAll`'s early returns** (no invoice id yet, or no
   * dirty targets). Nothing was written, so nothing is stale — and firing
   * there would make this mean *"somebody pressed a button"* rather than
   * *"the data moved"*, which is a different signal with the same name.
   *
   * ## Why this is not {@link InvoiceLineItemsSavedProps.onLinesChanged}
   *
   * `onLinesChanged` is the PARENT re-reading the invoice it is itself
   * editing, because a line write rewrites the four header totals and returns
   * none of them. This one is for whatever is BEHIND that parent — a list that
   * fetched this document before the modal opened and has no other way to
   * learn that its row is out of date. They fire at the same moments today and
   * are answered by different code at different levels; collapsing them would
   * tie a grandparent's refetch to a prop documented as the parent's own.
   */
  onRowsSaved?: () => void;
  onDraftLinesChange?: never;
  onDraftBlockedChange?: never;
  /** There is a real basket to read, so nothing may seed rows over it. */
  initialDraftRows?: never;
  dealLines?: never;
  dealLinesLoading?: never;
  dealLinesError?: never;
  convertLocked?: never;
  convertLineErrors?: never;
  onConvertLinesChange?: never;
  onConvertBlockedChange?: never;
  /*
   * ⚠️ **`companySalesTax` is deliberately NOT `?: never` here any more.** It
   * used to be a convert-only prop; it is now a COMMON one, because a blank
   * percentage on a row this mode is about to POST takes the company's rate
   * too. {@link InvoiceLineItemsConvertProps} still narrows it to REQUIRED,
   * which is the guarantee that mode needs and this one does not.
   */
  quoteLines?: never;
  quoteLinesLoading?: never;
  quoteLinesError?: never;
  quoteLineErrors?: never;
  onQuoteOverridesChange?: never;
}

/**
 * No invoice exists yet: hold lines locally, issue no line request, and report
 * them upward. The parent writes them after the invoice is created.
 */
export interface InvoiceLineItemsDraftProps extends InvoiceLineItemsCommonProps {
  mode: 'draft';
  /** There is no invoice to address, so there is no id to take. */
  invoiceId?: never;
  onLinesChanged?: never;
  /** No request is issued in draft mode, so there is no push and no report. */
  onXeroPush?: never;
  /**
   * Neither applies: there is no save run to register and nothing "unsaved" in
   * the sense the saved-mode pair reports — `onDraftLinesChange` and
   * `onDraftBlockedChange` already tell the parent everything about these rows.
   */
  onUnsavedChange?: never;
  registerSave?: never;
  /**
   * No line request is issued in draft mode, so no row is ever written from
   * here and there is nothing for a list behind this form to go stale against.
   * The parent writes these rows itself after the create and already knows.
   */
  onRowsSaved?: never;
  /**
   * The lines as they stand, in grid order, **exactly the body
   * `POST /invoices/{id}/products` takes** — built by the same `createPayload()`
   * the saved mode POSTs, so the two cannot diverge. Each carries an explicit
   * `sortOrder` (its index), because the parent may write them in any order and
   * `sort_order ASC` is the order the customer's document prints in.
   *
   * ⚠️ **Only rows that would be ACCEPTED are included.** A row with no product,
   * a bad quantity, an out-of-range percentage, a city its project does not
   * declare, or a `(product, project)` pair another row already claims is
   * withheld and counted by {@link onDraftBlockedChange} instead. Creating the
   * invoice and then collecting a 422 for a line the person can no longer see
   * is the failure this split prevents — and an invoice, once created, cannot
   * be un-created — so **read the blocked count before submitting**, never
   * `lines.length` alone.
   *
   * Fires on every change, and only on a change: the callback's own identity is
   * not a trigger, so an inline arrow in the parent is safe.
   */
  onDraftLinesChange: (lines: InvoiceProductCreateInput[]) => void;
  /**
   * How many rows are currently withheld from `onDraftLinesChange`. `0` means
   * every row on screen will be written.
   *
   * Optional, and the grid already refuses to hide the problem — it renders its
   * own blocking banner. This exists so the parent can disable its own "Create
   * invoice" button rather than letting someone submit and silently lose a row.
   */
  onDraftBlockedChange?: (blockedRows: number) => void;
  /**
   * Rows the grid OPENS WITH — a planned basket, seeded once at mount.
   *
   * ⚠️ **Read in the `useState` INITIALISER and never again.** An effect that
   * re-ran would re-seed rows the person had already edited or deleted, and
   * deleting a line and watching it come back is worse than no prefill at all.
   * So the array's identity is not a trigger and a parent may build it inline.
   *
   * ⚠️ **A SEED IS NOT A LINE.** These rows are ordinary draft rows the moment
   * they exist: editable, deletable, and written through the same
   * `onDraftLinesChange` payloads as a row somebody typed. Nothing here is
   * pinned and nothing is written twice.
   *
   * ⚠️ **`lineQuantity: null` SEEDS AN EMPTY QUANTITY BOX AND BLOCKS THE SAVE.**
   * A planned `"2.50"` is not a line quantity, and the two wrong answers are
   * both silent: rounding it to 1 puts a figure nobody chose on a customer's
   * document, and leaving the box empty writes NOTHING — which the server reads
   * as 1, arriving at the same wrong number by a longer route. See
   * `validateRow`, which refuses exactly this row, and {@link
   * onDraftBlockedChange}, which is how the parent hears about it. An invoice,
   * once created, cannot be un-created, so the refusal matters more here than
   * on a quote.
   */
  initialDraftRows?: readonly PlannedProductLine[];
  dealLines?: never;
  dealLinesLoading?: never;
  dealLinesError?: never;
  convertLocked?: never;
  convertLineErrors?: never;
  onConvertLinesChange?: never;
  onConvertBlockedChange?: never;
  /*
   * ⚠️ **`companySalesTax` is deliberately NOT `?: never` here any more.** It
   * used to be a convert-only prop; it is now a COMMON one, because a blank
   * percentage on a row this mode is about to POST takes the company's rate
   * too. {@link InvoiceLineItemsConvertProps} still narrows it to REQUIRED,
   * which is the guarantee that mode needs and this one does not.
   */
  quoteLines?: never;
  quoteLinesLoading?: never;
  quoteLinesError?: never;
  quoteLineErrors?: never;
  onQuoteOverridesChange?: never;
}

/**
 * A DEAL is about to be raised as an invoice, and these rows are what it will be
 * raised WITH.
 *
 * ⚠️ **NOTHING HERE ISSUES A REQUEST, and nothing here writes a DEAL line.**
 * `POST /deals/{id}/invoices` copies the deal's basket inside the transaction
 * that creates the invoice; this mode edits the `lineSet` that conversion is
 * given. The deal's LINES are never written. Its STAGE does move (owner
 * 2026-10-18): the server moves it to the won stage after the invoice is
 * raised, said on screen through `INVOICE_CONVERSION_MOVES_THE_DEAL`
 * (`@/lib/deal-conversion`). `CONVERSION_LEAVES_THE_DEAL_ALONE` is deleted.
 *
 * ⚠️ **THE DRAFT GRID MUST NEVER STAND IN FOR THIS ONE.** A draft row is written
 * as its own `POST /invoices/{id}/products` once the invoice exists, so on this
 * path it would be APPENDED to lines the server has already copied — an invoice
 * demanding money for the same work twice, from a control that looks exactly
 * like the one on a blank form.
 *
 * ⚠️ **THE ONE THING THIS MODE MAY NOT COLLECT IS TAX.** The body schema is
 * `.strict()` and carries no `taxRate` and no `xeroTaxType` on either request
 * key, and `f_deal_products` has no tax column to override in the first place —
 * so the Tax rate column is a READOUT of
 * {@link InvoiceLineItemsConvertProps.companySalesTax} rather than a disabled
 * dropdown. A live control there would take a choice and discard it with a 201
 * and nothing on screen to say so.
 */
export interface InvoiceLineItemsConvertProps extends InvoiceLineItemsCommonProps {
  mode: 'convert';
  /** There is no invoice to address, so there is no id to take. */
  invoiceId?: never;
  onLinesChanged?: never;
  /** No request is issued in convert mode, so there is no push and no report. */
  onXeroPush?: never;
  /** Nothing is saved from this mode; the conversion writes every row at once. */
  onUnsavedChange?: never;
  registerSave?: never;
  /** Same reason: no row is written from here, so nothing goes stale from here. */
  onRowsSaved?: never;
  onDraftLinesChange?: never;
  onDraftBlockedChange?: never;
  /** The deal's own lines are the seed on this path — see `dealLines`. */
  initialDraftRows?: never;
  quoteLines?: never;
  quoteLinesLoading?: never;
  quoteLinesError?: never;
  quoteLineErrors?: never;
  onQuoteOverridesChange?: never;
  /**
   * The deal's own lines, in the order they were read.
   *
   * ⚠️ **`null` is "not read", never "none".** A failed read must not be
   * described as an empty basket — every line still crosses either way, and the
   * three states are rendered apart for exactly that reason.
   *
   * ⚠️ **Its IDENTITY re-seeds every row.** The grid holds each row's boxes in
   * its own state and rebuilds them whenever this array changes, so a parent that
   * rebuilt it on every render would wipe what somebody is typing. Hold it in
   * state, as `InvoiceFormModal` does; do not map or filter it inline.
   */
  dealLines: DealProduct[] | null;
  /** The read is in flight. The grid renders its own skeletons. */
  dealLinesLoading: boolean;
  /** Why the read failed, or `null`. There is no retry here — the parent owns the fetch. */
  dealLinesError: string | null;
  /**
   * The conversion has LANDED and the boxes are no longer live.
   *
   * 🔴 **This is the one place an editable table could lie.** Once
   * `POST /deals/{id}/invoices` has succeeded the invoice exists and its lines
   * are already written from whatever `lineSet` was sent — a retried Save
   * re-sends the HEADER PATCH and deliberately never converts a second time. A
   * box left live there is a control somebody types a new price into, presses
   * Save, and watches succeed with the old price **on a document that demands
   * money**: no error, nothing in a log, and a customer invoice that disagrees
   * with what was on screen.
   */
  convertLocked: boolean;
  /**
   * The server's per-line refusals, **keyed by this grid's own ROW KEY**.
   *
   * ⚠️ **Not by `dealLineId`.** A 422 names `lineSet.2.productId`, and `2` is a
   * POSITION in the array that was sent — and a row ADDED on this screen has no
   * `dealLineId` to be addressed by at all. `convertLineSetFieldErrors` in
   * `@/lib/deal-convert-lines` is the one place that translation happens, and it
   * is the PARENT's to call, because the parent is what holds the arrays it
   * actually sent.
   */
  convertLineErrors: Readonly<Record<string, Record<string, string>>>;
  /**
   * The lines as they stand, in grid order — **exactly the `lineSet` array
   * `POST /deals/{id}/invoices` takes** — plus the two things only this grid can
   * say about it.
   *
   * ⚠️ **EVERY row appears**, because `lineSet` is the complete final list and an
   * omitted row is a line the invoice will not have. The OMISSION rule hangs off
   * `pristine`: a conversion nobody edited must send no `lineSet` key at all,
   * because an absent key is today's proven behaviour and an array restating
   * every line is a request shape nothing has exercised.
   *
   * ⚠️ **`entries` and `rowKeys` are the SAME LENGTH IN THE SAME ORDER, built in
   * one pass**, and they are handed over together for exactly that reason — the
   * parent must be able to turn `lineSet.3.productId` back into a row, and two
   * arrays produced separately would agree until the day they did not.
   *
   * ⚠️ **Blocked rows are NOT withheld**, unlike draft mode's. A refused row is
   * not dropped on the floor, it takes the whole conversion with it — no invoice
   * at all rather than an invoice missing a line — so the answer is to refuse to
   * submit, which is what
   * {@link InvoiceLineItemsConvertProps.onConvertBlockedChange} is for.
   * Withholding it instead would convert without a line somebody had added.
   *
   * ⚠️ **`null` while the deal's lines have not been read.** Never `[]`: an empty
   * `lineSet` is an invoice with NO LINES, so a grid that has not loaded yet must
   * be unable to say anything at all rather than able to say *none*.
   *
   * Fires on every change and only on a change, so an inline arrow is safe.
   */
  onConvertLinesChange: (
    next: {
      entries: DealConvertLineSetEntry[];
      rowKeys: string[];
      /** Every row is the deal's own, in its own order, untouched. */
      pristine: boolean;
    } | null,
  ) => void;
  /**
   * How many rows carry a refusal of OURS. `0` means the conversion can be sent.
   *
   * ⚠️ **Client refusals only.** A server error already sits beside the box that
   * caused it and the submit must stay pressable so the correction can be
   * re-sent; counting one here would disable the one control that clears it.
   */
  onConvertBlockedChange: (blockedRows: number) => void;
  /**
   * The COMPANY's sales tax, which is what every converted line will be given.
   *
   * ⚠️ **REQUIRED HERE, though it is optional on the common props.** A converted
   * line has no tax rate of its own — `f_deal_products` has no such column — so
   * this is the only figure the Tax rate column can honestly print, and an
   * optional prop would let a call site forget the loader and render the "not
   * read" wording for ever with nothing failing. Narrowing an inherited optional
   * to a required member is the one instrument here that catches a dead loader
   * at compile time, and it is why this line is restated rather than inherited.
   *
   * ⚠️ **Read from the company record the parent already fetches.** Do NOT look
   * the percentage up from `GET /xero/tax-rates`: the server derives and owns
   * this figure, and a client that computes it is a second implementation of a
   * number printed on an invoice.
   */
  companySalesTax: CompanySalesTax;
}

/**
 * `mode: 'quote-convert'` — ONE grid over a quote about to become an invoice.
 *
 * ⚠️ **IT HOLDS TWO POPULATIONS AND THEY ARE WRITTEN BY DIFFERENT THINGS.**
 * This is the whole design, and it is the thing to understand before editing:
 *
 * - a **COPIED** row (`row.quoteConvert !== null`) is one of the quote's own
 *   lines. The SERVER copies it — with its project, city set, region set and
 *   campaign inclusions — inside the conversion's transaction. This grid may
 *   only RESPELL its five figures, through the `lines` key. Its identity is
 *   therefore locked, and it has no trash can: `lines` has no key that could
 *   carry any of that, and an omission from `lines` means *unchanged*, not
 *   *removed*.
 * - an **ADDED** row is not a copy of anything. It is an ordinary
 *   `POST /invoices/{id}/products` issued after the invoice exists, exactly what
 *   the create path already does, and it needs nothing from the convert
 *   endpoint. Fully editable, removable, and reported through
 *   {@link InvoiceLineItemsQuoteConvertProps.onDraftLinesChange}.
 *
 * ⚠️ **The two are ONE grid on the owner's instruction (2026-08-31), replacing
 * two stacked sections.** *SUPERSEDED, the rule that arrangement was built on:
 * "**The two must stay visibly separate.** One grid holding locked rows and
 * editable rows together is how somebody types a price into a copied line and
 * believes it was sent. Two headings, two grids, two behaviours."* The premise
 * died when the endpoint gained `lines`: a price typed into a copied row IS
 * sent now, so the reason for the split went with it — and what the split
 * actually produced was an "Additional line items" grid whose empty state read
 * *"No line items yet"* directly beneath a table full of line items.
 *
 * ⚠️ **NOT `mode: 'convert'`, and it must never be folded into it.** See the
 * table on {@link InvoiceLineItemsMode}: that mode emits `lineSet`, whose
 * omissions DELETE and whose entries carry `productId`, `projectId`, `cityIds`,
 * `regionIds` and `inclusions`. `convertQuoteSchema` accepts none of those, so a
 * grid pointed at the wrong one would collect five kinds of edit and send none
 * of them — a 201, a correct-looking invoice, and every structural edit
 * discarded in silence.
 */
export interface InvoiceLineItemsQuoteConvertProps extends InvoiceLineItemsCommonProps {
  mode: 'quote-convert';
  /**
   * The QUOTE's lines, or `null` when they are not known.
   *
   * ⚠️ **`null` is NOT "no lines". It is "we could not read them"**, which is a
   * different fact: a quote whose lines failed to load still converts and still
   * carries every one of them across. Seeding `[]` from it would tell somebody
   * they are about to raise a blank invoice.
   *
   * ⚠️ **Held in state by the parent and handed down WHOLE.** The grid re-seeds
   * its copied rows whenever this array's identity changes, so an array rebuilt
   * inline on every render would wipe whatever somebody is typing, with no error
   * and nothing on screen to explain it.
   */
  quoteLines: QuoteProduct[] | null;
  quoteLinesLoading: boolean;
  quoteLinesError: string | null;
  invoiceId?: never;
  onLinesChanged?: never;
  onXeroPush?: never;
  /**
   * Neither applies. There is no invoice to save a line to until the conversion
   * lands, and once it has, `convertLocked` takes every box dead — so this mode
   * has no run to register and nothing "unsaved" in the saved-mode sense. Its
   * two populations report through `onQuoteOverridesChange` and
   * `onDraftLinesChange` instead.
   */
  onUnsavedChange?: never;
  registerSave?: never;
  /** Same reason: no row is written from here, so nothing goes stale from here. */
  onRowsSaved?: never;
  dealLines?: never;
  dealLinesLoading?: never;
  dealLinesError?: never;
  convertLineErrors?: never;
  onConvertLinesChange?: never;
  onConvertBlockedChange?: never;
  /*
   * ⚠️ **`companySalesTax` is deliberately NOT `?: never` here any more.** It
   * used to be a convert-only prop; it is now a COMMON one, because a blank
   * percentage on a row this mode is about to POST takes the company's rate
   * too. {@link InvoiceLineItemsConvertProps} still narrows it to REQUIRED,
   * which is the guarantee that mode needs and this one does not.
   */
  /**
   * Locked once the conversion has landed.
   *
   * ⚠️ **Not cosmetic.** From that moment the invoice exists, the copied lines
   * are written from whatever `lines` was sent, and a retried Save re-sends the
   * HEADER alone — so a live box is one somebody types a new price into, presses
   * Save, and watches succeed against the old price on a document that demands
   * money. The same argument the deal path's `convertLocked` carries.
   */
  convertLocked: boolean;
  /**
   * Server refusals for the COPIED rows, keyed by `f_quote_products.id`.
   *
   * ⚠️ **By the quote line's id, and NOT by this grid's row key** — the opposite
   * of the deal path, and the difference is real rather than stylistic. A 422
   * names `lines.<n>.<field>` where `n` indexes the SPARSE overrides array, and
   * `quoteConvertLineFieldErrors` in `@/lib/quote-convert-lines` is the one place
   * that turns an index back into a `quoteProductId`. Only copied rows can be
   * refused this way; an ADDED row is not in that request at all and reports on
   * itself later, through its own POST.
   */
  quoteLineErrors: Readonly<Record<number, Record<string, string>>>;
  /**
   * The COPIED rows' respellings — exactly the `lines` array, in the order the
   * quote's lines were read, and **empty when nothing was edited**.
   *
   * ⚠️ **`[]` means the caller OMITS the key**, and must. An absent `lines` is
   * byte-for-byte the conversion this endpoint has always performed; `lines: []`
   * is a different instruction. This is NOT the whole-set-replace shape
   * `cityIds` and `inclusions` use.
   *
   * ⚠️ **`null` until the quote's lines are read**, never `[]`, for the same
   * reason `quoteLines` is nullable.
   */
  onQuoteOverridesChange: (overrides: QuoteConvertLineOverride[] | null) => void;
  /**
   * The ADDED rows as POST bodies — the same shape and the same builder `draft`
   * mode emits, because they are written the same way.
   */
  onDraftLinesChange: (lines: InvoiceProductCreateInput[]) => void;
  /** Rows held back because they are not ready, across BOTH populations. */
  onDraftBlockedChange: (blockedRows: number) => void;
  /**
   * ⚠️ **`never`, though this mode DOES emit draft payloads.** The quote's own
   * lines are the seed here and they arrive through `quoteLines`; a second seed
   * would put a planned basket beside the lines the conversion is already
   * copying, which is the same work billed twice.
   */
  initialDraftRows?: never;
}

/**
 * The COMPANY's sales tax, as this grid is able to know it.
 *
 * Three states, and the third is the one a boolean would lose. `known: false` is
 * *the company record has not been read* — in flight, failed, or a form that has
 * since moved to another company — and is **not** the same fact as a company
 * that states no rate. Reporting an unread value as an absence is a claim about
 * somebody else's tax treatment made from a request that never landed.
 *
 * `rate` is the server's own DECIMAL string (`"20.00"`), never a number and
 * never parsed here: `null` means the company states none. **`null` is not
 * `"0.00"`** — that would be a company somebody deliberately zero-rated, which is
 * a different answer to the same question, and `NONE` and `ECZROUTPUTSERVICES`
 * are both genuinely 0%.
 *
 * `taxType` is the company's Xero CODE (`"OUTPUT2"`), which is what a blank
 * `xeroTaxType` on a line resolves to at push time. ⚠️ **It is only ever
 * NAMED, never turned into a percentage** — deriving the figure from the code
 * against the tenant's published `EffectiveRate` would be a second
 * implementation of a number the server owns. ⚠️ **And it is resolved with
 * {@link sameTaxCode} before it is shown**: casing is normalised server-side,
 * so a stored `output2` against a live `OUTPUT2` is the same rate.
 *
 * ⚠️ **THIS IS READ AND NEVER SENT, on every mount.** The server already seeds
 * a created line from the company (`input.taxRate ?? companyRate`), so a client
 * that put this figure in a payload would be a SECOND writer of it — and a
 * stale one, since a rate changed while the modal sat open would be written in
 * preference to the current one. Every use below is display or preview.
 *
 * ⚠️ **ON THE INVOICE ROUTE `rate: null` IS VERY NEARLY UNREACHABLE, AND THE CELL
 * STILL SAYS IT.** `POST /deals/{id}/invoices` answers
 * `422 COMPANY_HAS_NO_TAX_RATE` when the deal has lines and the company records
 * no rate, and that check runs BEFORE the transaction opens — so nothing is
 * written and no line is ever created at 0.00 here, which is exactly where the
 * QUOTE route deliberately differs. The state is kept because this grid renders
 * long before any conversion is attempted, which is the one moment somebody can
 * still go and set the rate rather than meeting the refusal.
 */
export type CompanySalesTax =
  | { known: false }
  | { known: true; rate: string | null; taxType: string | null };

/** The stable "not read" identity, so a mount that passes none makes no object. */
const COMPANY_SALES_TAX_UNREAD: CompanySalesTax = { known: false };

/**
 * The stable empty map, so the two other modes pass no new object per render.
 *
 * A fresh `{}` would be a new identity in every dependency list that reads it,
 * which on this grid means re-running on every keystroke in the parent's header.
 */
const EMPTY_LINE_ERRORS: Readonly<Record<string, Record<string, string>>> = {};

/** The same stable-identity trick, keyed by `f_quote_products.id`. */
const EMPTY_QUOTE_LINE_ERRORS: Readonly<Record<number, Record<string, string>>> = {};

export type InvoiceLineItemsTableProps =
  | InvoiceLineItemsSavedProps
  | InvoiceLineItemsDraftProps
  | InvoiceLineItemsConvertProps
  | InvoiceLineItemsQuoteConvertProps;

/* ── The draft a row is edited as ─────────────────────────────────────────── */

/**
 * What the Price box means.
 *
 * Three states, and two of them are easy to collapse into one wrongly:
 *
 * - `inherit` — the key is **omitted**. On a new row that snapshots the
 *   catalogue price; on an existing row it leaves the stored price alone.
 * - `value` — an explicit decimal string.
 * - `none` — an explicit `null`: **no price on this line**, which is not the
 *   same fact as a price of `"0.00"`, even though both make the three
 *   generated columns read `"0.00"`.
 *
 * ⚠️ Sending an explicit price where the user set none is what put `100` on a
 * line whose catalogue price was 17,500. `inherit` must never be spelled `''`,
 * `null` or `0`.
 */
type PriceMode = 'inherit' | 'value' | 'none';

interface InclusionDraft {
  inclusionId: number;
  /** Empty means "omit the key", which the server reads as 1. Zero is refused. */
  quantity: string;
}

interface Draft {
  productId: string;
  /**
   * What Xero prints for this line — **`null` means DERIVE, never "empty"**.
   *
   * While it is `null` the cell follows {@link composeLineDescription} over the
   * line's project, item and cities, and the backend composes the same string
   * again when it pushes. A string is the person's own wording: stored, sent
   * verbatim, and never added to. `''` is not a third state — it is folded into
   * `null` by {@link descriptionForWrite} before any payload is built.
   */
  description: string | null;
  /**
   * The LINE's own project. `''` is "no project", which is a real and legal
   * state — some products are not sold through projects at all.
   *
   * It is half of the line's identity: `(product, project)`.
   */
  projectId: string;
  /**
   * **The WHOLE SET of cities this line runs in.** `[]` is "not city-specific",
   * which is the ORDINARY case. Order is the server's own per-line `sortOrder`,
   * then the order the user added in. Never re-sorted, and never a source of
   * arithmetic: the money is on the line, not per city.
   */
  cityIds: number[];
  /**
   * **This line means EVERY city its project has**, resolved live rather than
   * listed out. Migration 083.
   *
   * ⚠️ **A FLAG, not a shorthand for a full list, and the two are not
   * interchangeable.** A flagged line re-resolves against its project on every
   * read, so a city added to the project next month is on the document
   * automatically; a line that merely lists every city today keeps the list it
   * was given. The two look identical on screen right now and diverge the
   * moment the project changes — which is why the control is a tick box beside
   * the picker rather than a "select all" inside it, and why **nothing here
   * ever expands the flag into ids at save time**.
   *
   * ⚠️ **Mutually exclusive with `cityIds` ON THE WIRE — sending both is a
   * 422** — but deliberately NOT mutually exclusive in this draft. While the
   * flag is on, whatever was in `cityIds` stays here unsent, so unticking hands
   * it back instead of leaving the row with nothing. {@link createPayload} and
   * {@link updatePayload} are the one place that picks a single key, which is
   * what keeps that 422 a backstop that never actually fires.
   */
  allCities: boolean;
  /**
   * **SEED's regions of focus for this line — the WHOLE SET.** Migration 112.
   *
   * ⚠️ **Nothing to do with the cities above, and it is offered in the
   * OPPOSITE case.** Cities are named THROUGH a project, so only a product sold
   * through projects has any; a product that is NOT — `EMBA`, `LAE`,
   * `Virtual Masterclass` — recorded no geography anywhere at all until this
   * existed. {@link showsRegionsControl} therefore shows this control exactly
   * where {@link RowProject.showProject} takes the project block away.
   *
   * ⚠️ **There is no `allRegions` flag and none may be invented.**
   * {@link Draft.allCities} is a live RULE, re-resolved against a project on
   * every read; a line's regions are always its own explicit set, so `[]` here
   * is simply “none” and means it.
   *
   * ⚠️ **Order is not stored.** The read hands these back in the master's own
   * order rather than in a per-line `sortOrder`, which is why
   * {@link regionsKey} is order-INSENSITIVE where {@link citiesKey} is not.
   */
  regionIds: number[];
  quantity: string;
  priceMode: PriceMode;
  price: string;
  /**
   * The discount as a PERCENTAGE, as typed. Live only while
   * {@link Draft.discountAmount} is `null`.
   */
  discount: string;
  /**
   * The discount as an EXACT MONEY AMOUNT, as typed — and the MODE SWITCH.
   *
   *  - `null` — the box is taking a PERCENTAGE, the ordinary case;
   *  - a string (`''` included) — the box is taking an AMOUNT.
   *
   * ⚠️ **A real `Draft` key, unlike the screen-state map it replaced**, for the
   * same reason `description` is one: there is a column behind it, so it has to
   * dirty the row, reach a payload, survive a reload and revert with the rest.
   *
   * ⚠️ **NOTHING CONVERTS BETWEEN THE TWO.** The old amount box wrote
   * `draft.discount` on every keystroke, losing pounds; the switch now moves
   * only itself and leaves both figures where they are.
   */
  discountAmount: string | null;
  /**
   * The line's PRICE TIER (`f_price_tiers`, migration 328). `null` is the
   * default tier (Standard) and is what a new line starts at. Chosen in the
   * "Price" picker under the project; decides which project price entry an
   * untyped line follows. Never decided by a tier NAME.
   */
  priceTierId: number | null;
  taxRate: string;
  /**
   * Xero's tax CODE. `''` is `null`, which means **"use the company's Xero
   * sales default"** and never "no tax".
   */
  xeroTaxType: string;
  /** Ordered — the index becomes each entry's `sortOrder`. */
  inclusions: InclusionDraft[];
  /**
   * The line's INTERNAL note (2026-10-18). Never sent to Xero, never printed.
   *
   * ⚠️ **Read by `createPayload` ONLY.** A STORED line's note is saved on its
   * own by {@link saveLineNote} as a `{ notes }`-only PATCH — the one write a
   * locked line still accepts — so `updatePayload` and `isDirty` never see it,
   * and a note can neither dirty a row nor ride along with a figure edit.
   */
  notes: string | null;
}

type RowState = 'idle' | 'saving' | 'saved' | 'failed';

interface Row {
  /** Stable across re-renders and independent of the server id, which new rows lack. */
  key: string;
  /** The last state the server confirmed. `null` on a row that has never been saved. */
  saved: InvoiceProduct | null;
  /**
   * The DEAL line this row was seeded from — **`mode: 'convert'` only**, and
   * `null` on every row in the other two modes.
   *
   * ⚠️ **A converted row is NOT a saved invoice line, and it must not pretend to
   * be one.** A synthetic `InvoiceProduct` was the obvious alternative and is
   * refused: `f_deal_products` has no `tax_rate`, no `line_subtotal`, no
   * `line_tax`, no `all_cities`, no `invoiced` flag and no invoice id, so half of
   * that object would be a value nobody measured, rendered by a cell that had no
   * idea. *A TYPE asserting what the runtime lacks* is a defect class this
   * codebase catalogues by name. Keeping `saved` at `null` and the deal line here
   * makes every one of those fabrications an explicit branch instead.
   *
   * ⚠️ REQUIRED on the interface, not optional. An optional member would let a
   * row be built without deciding, and `tsc` refusing a call site is the only
   * guard nobody can forget.
   */
  convert: DealProduct | null;
  /**
   * The QUOTE line this row was copied from — **`mode: 'quote-convert'` only**,
   * and `null` on every row in the other three modes AND on a row ADDED on that
   * screen.
   *
   * ⚠️ **It is the discriminator between the mode's two populations**, and every
   * behaviour that differs between them reads it: a copied row's identity is
   * locked, it has no trash can, its Tax cell is a readout of its own stored
   * rate, and it contributes to `lines` rather than to the draft POSTs. A row
   * added on that screen has this `null` and behaves exactly as a draft row.
   *
   * ⚠️ **`null` is NOT "no quote line" in the way `convert` is "no deal line".**
   * Reading `row.saved ?? row.convert ?? row.quoteConvert` as one thing is safe
   * for the NAME maps (see {@link sourceLineOf}) and wrong for anything that
   * decides what may be written — those must ask for this field by name.
   *
   * ⚠️ REQUIRED on the interface, not optional, for the reason `convert` is: an
   * optional member would let a row be built without deciding, and `tsc`
   * refusing a call site is the only guard nobody can forget.
   */
  quoteConvert: QuoteProduct | null;
  draft: Draft;
  state: RowState;
  /** Form-level failure text — a 409, a role refusal, a strict-schema rejection. */
  error: string | null;
  fieldErrors: Record<string, string>;
  /**
   * The project/city refusal the last save collected, classified. `null` on any
   * failure that was not one of the seven — `lineRefusalOf` returns `null`
   * rather than guessing.
   *
   * Kept beside `fieldErrors` because it answers two questions that map alone
   * cannot: WHICH chips are wrong, and whether the fix is even on this screen.
   */
  refusal: LineRefusal | null;
  /**
   * The PLANNED quantity this row was seeded from, when that figure **cannot be
   * a line quantity** — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a REFUSAL FLAG, not a display value, and it is the only thing
   * standing between a planned `"2.50"` and a line that silently bills 1.** An
   * empty quantity box is ordinarily fine: `createPayload` omits the key and
   * the server defaults it to 1, which is the right answer for a row somebody
   * typed and left alone. It is the WRONG answer for a row seeded from a figure
   * that was measured and could not be expressed — there, an omitted key is a
   * number nobody chose, arriving on a customer's invoice with a 201 and
   * nothing on either side disagreeing. `validateRow` reads this and refuses
   * the row while the box is empty, which is what puts it in `blockedKeys`,
   * withholds it from `onDraftLinesChange`, and disables the parent's Save.
   *
   * The string is the planned figure verbatim, so the message can name it.
   *
   * ⚠️ REQUIRED on the interface, not optional, for the reason `convert` and
   * `quoteConvert` are: an optional member would let a row be built without
   * deciding, and `tsc` refusing a call site is the only guard nobody can
   * forget.
   */
  plannedQuantity: string | null;
  /**
   * The plan's own flat amount for a seeded row whose seeded PRICE cannot
   * express it exactly — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a NOTICE flag, not a refusal, and that is the whole difference
   * from {@link Row.plannedQuantity} beside it.** An unstatable quantity has no
   * right answer and the row is refused until somebody supplies one. An
   * inexact price HAS an answer and it is right to the penny in every case that
   * divides: `10000.00` over 3 seeds `3333.33` and the line comes to `9999.99`,
   * which is a real figure a person may perfectly well accept. Blocking the
   * save over a cent would refuse the feature; saying nothing would put the
   * cent on an invoice a customer is billed against with nobody able to see it,
   * because **this is the only moment anybody can** — after the create there is
   * no plan beside the line to compare it to.
   *
   * ⚠️ **The two boxes are carried so the notice can STAND DOWN.** It describes
   * the SEEDED state, so the moment the person changes the price or the
   * quantity it stops being true of the row and goes — the same shape as
   * `plannedQuantity`, whose message clears as soon as the box is filled.
   * Without them the sentence would go on naming a total the row no longer has.
   *
   * ⚠️ REQUIRED on the interface, not optional, for the reason `convert`,
   * `quoteConvert` and `plannedQuantity` are: an optional member would let a row
   * be built without deciding, and `tsc` refusing a call site is the only guard
   * nobody can forget.
   *
   * ⚠️ **The quote grid carries this field word for word. The two move together.**
   */
  plannedShortfall: PlannedShortfall | null;
}

/**
 * A seeded row whose unit price does not divide the plan's amount evenly.
 *
 * Every figure here is a STRING taken from, or computed once against, the seed
 * — never re-derived at render. `lineAmount` comes from
 * `computeGeneratedLinePreview`, the one place money arithmetic lives, so the
 * figure the notice names is the same arithmetic the Amount column shows.
 */
interface PlannedShortfall {
  /** The plan's flat amount for the line, verbatim, so the sentence names it. */
  plannedAmount: string;
  /** The seeded unit price. Displayed, and the stand-down test. */
  unitPrice: string;
  /** The seeded quantity, as the box holds it. Displayed, and the stand-down test. */
  quantity: string;
  /** `quantity × unitPrice` — what the line comes to at the seeded figures. */
  lineAmount: string;
}

/* ── Pure helpers ─────────────────────────────────────────────────────────── */

function draftOf(line: InvoiceProduct): Draft {
  return {
    productId: String(line.product.id),
    /*
     * Straight through, both ways. `null` opens the cell derived-and-untouched
     * and a string opens it typed — so "has the user typed" is a fact about the
     * RECORD, not only about this session. `convert` copies the quote line's
     * value INCLUDING its null, so a derived quote line arrives here derived.
     */
    description: line.description,
    projectId: line.project === null ? '' : String(line.project.id),
    /*
     * ⚠️ **A FLAGGED line seeds NO city ids, even though `line.cities` is
     * full.** That array is the RESOLVED set — what the project has today — and
     * a flagged line stores no explicit rows underneath it. Seeding from it
     * would tell {@link updatePayload} an explicit set is stored, and the next
     * save would quietly rewrite the flag as a snapshot of today. This base
     * draft mirrors STORAGE; the resolved set is rendered from
     * {@link RowProject.allCitiesToday} instead, where it cannot be mistaken
     * for something the row will send.
     *
     * Otherwise: already ordered by the server's own per-line `sortOrder`, so
     * keep that order. Re-sorting it — by name, "to be tidy" — changes what the
     * customer reads.
     */
    cityIds: line.allCities ? [] : line.cities.map((city) => city.id),
    allCities: line.allCities,
    /*
     * Always present and `[]` when none — never `null` — so there is no
     * fallback to write, and `?? []` here would be inventing a case.
     *
     * ⚠️ **Not the `line.cities` trap above.** Regions are STORED rows, not a
     * set resolved live against a project, so seeding straight off the read
     * asserts nothing the record does not already hold.
     */
    regionIds: line.regions.map((region) => region.id),
    quantity: String(line.quantity),
    priceMode: line.unitPrice === null ? 'none' : 'value',
    price: line.unitPrice ?? '',
    discount: line.discount,
    // A stored amount opens the row in amount mode; `null` opens it on the
    // percentage. The server keeps the pair exclusive, so never both.
    discountAmount: line.discountAmount,
    // `undefined` is an older API, which stores no tier: the default.
    priceTierId: line.priceTierId ?? null,
    taxRate: line.taxRate,
    xeroTaxType: line.xeroTaxType ?? '',
    // Already ordered by the server; keep the order it arrived in.
    inclusions: line.inclusions.map((entry) => ({
      inclusionId: entry.id,
      quantity: String(entry.quantity),
    })),
    notes: line.notes ?? null,
  };
}

/**
 * A new row starts with no project and no cities.
 *
 * The header project is deliberately NOT seeded. A project sells exactly one
 * product and the row has not chosen one yet, so pre-filling it would put a
 * value in the box that the very next choice can make a refusal — and a
 * pre-filled control reads as a decision somebody made.
 */
/** The key of the blank row a new document opens with; Add row keys are `new-N`. */
const DEFAULT_BLANK_ROW_KEY = 'new-default';

/** True while a row still holds exactly what {@link blankDraft} gave it. */
function isUntouchedBlankDraft(draft: Draft): boolean {
  return JSON.stringify(draft) === JSON.stringify(blankDraft());
}

/**
 * A row invented on screen — by Add row, or the one blank row a new invoice
 * opens with. One builder for both, so the two cannot differ.
 */
function blankRowOf(key: string): Row {
  return {
    key,
    saved: null,
    /* Not a converted row: this one was invented on screen. */
    convert: null,
    /*
     * ⚠️ **`null` even in `quote-convert` mode, and that is the whole point
     * of the field.** A row added on the conversion screen is NOT a copy of
     * a quote line: it is an ordinary `POST /invoices/{id}/products` written
     * after the invoice exists. Seeding a quote line here would put it into
     * the `lines` array as a respelling of a line it is not, and take it out
     * of the POSTs that are the only thing that can actually create it.
     */
    quoteConvert: null,
    draft: blankDraft(),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /*
     * ⚠️ `null`, and it must stay `null`. This row was invented on screen
     * with `blankDraft()`'s quantity of '1' already in the box, so there is
     * no planned figure it failed to express — and marking it would refuse
     * an empty quantity on an ordinary row, where blank legitimately means
     * "let the server default it to 1".
     */
    plannedQuantity: null,
    /*
     * ⚠️ `null` for the same reason. This row came from no plan, so there
     * is no planned amount for its price to fall short of — and a notice
     * about a figure nobody planned would be a sentence about nothing.
     */
    plannedShortfall: null,
  };
}

function blankDraft(): Draft {
  return {
    productId: '',
    // `null` is deriving, which is where a new line starts and where almost
    // every line stays. It is not "blank".
    description: null,
    projectId: '',
    cityIds: [],
    // A new line lists its cities. "All of them" is a decision somebody makes,
    // and `false` is the server's own default for a row nobody has flagged.
    allCities: false,
    // A new line names its regions or names none, and there is no "all" to
    // start from — see {@link Draft.regionIds}.
    regionIds: [],
    quantity: '1',
    // ⚠️ Never 'value' with an empty price. `inherit` is the only spelling of
    // "snapshot the catalogue price".
    priceMode: 'inherit',
    price: '',
    discount: '',
    // Percentage mode, which is where nearly every line stays.
    discountAmount: null,
    // The default tier.
    priceTierId: null,
    taxRate: '',
    xeroTaxType: '',
    inclusions: [],
    notes: null,
  };
}

function rowOf(line: InvoiceProduct): Row {
  return {
    key: `line-${line.id}`,
    saved: line,
    convert: null,
    quoteConvert: null,
    draft: draftOf(line),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A stored line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And a stored price, which no plan is standing beside to disagree with. */
    plannedShortfall: null,
  };
}

/**
 * A PLANNED product, as a draft row of this grid — `mode: 'draft'` only, and
 * only at mount. See {@link InvoiceLineItemsDraftProps.initialDraftRows}.
 *
 * ⚠️ **THE QUOTE GRID CARRIES THIS FUNCTION WORD FOR WORD. THE TWO MOVE
 * TOGETHER.** A seed on one document and not the other is the shape this
 * codebase has already paid for twice — a control built into a component
 * nothing mounts, and a column added on one side of convert only.
 *
 * ⚠️ **THE QUANTITY IS THE WHOLE OF THIS FUNCTION'S JUDGEMENT, and both wrong
 * answers are silent.** `plannedProductLines()` has already decided whether the
 * planned figure can be a line quantity; what happens when it cannot is decided
 * here:
 *
 *  - **NOT rounded.** `"2.50"` → `1` is a figure nobody chose, printed on an
 *    invoice a customer is billed against, with nothing anywhere disagreeing.
 *  - **NOT merely left blank.** An empty box means "omit the key", and an
 *    omitted `quantity` is **1** on the server — the same wrong number by a
 *    longer route, and harder to see because no line of code states it.
 *
 * So the box opens empty AND the row carries {@link Row.plannedQuantity}, which
 * `validateRow` turns into a refusal. The person supplies the figure; the grid
 * refuses to let the document be created until they do.
 *
 * ⚠️ **PARTLY SUPERSEDED 2026-09-03 — the PRICE half only.** That paragraph
 * read:
 *
 * > *"**Everything else is a blank draft row on purpose.** No project, no
 * > cities, no regions, no price, no tax — a plan records what is to be sold and
 * > how much of it, and nothing else. Seeding a price would freeze a figure the
 * > catalogue is supposed to supply at the moment the line is written, which is
 * > why `PlannedProductLine` deliberately carries none."*
 *
 * A plan records **a flat amount for the line** now (owner's decision), so
 * `PlannedProductLine` does carry a price and a PRICED plan seeds it.
 *
 * 🔴 **AND THE PROJECT FOLLOWED ON 2026-09-11 — the quoted "no project" is now
 * wrong about the FIRST of the four things it lists.** *SUPERSEDED, within the
 * quotation above and nowhere else: "**No project**, no cities, no regions, no
 * price, no tax".* A seed may now name a project, because a caller can exist
 * that knows one: a sales-tracker pursuit is a `(project, company)` pair, and
 * without this the document it raises names no project on any line, the
 * server's `(company, project)` sync never matches, and **the card never
 * advances** — with the document itself perfectly correct and nothing failing.
 * **Cities, regions and tax are untouched and are still a blank draft row for
 * exactly the reason quoted.**
 *
 * ⚠️ **THE PROJECT AND THE CITIES ARE ONE PATCH HERE TOO, and the seed gets
 * that for free rather than by accident.** The project control emits
 * `{projectId, cityIds: []}` in a single `onChange` because the server judges
 * the pair POST-MERGE — split them and it re-validates the STORED cities and
 * refuses, naming cities nobody touched. A seeded row spreads over
 * `blankDraft()`, whose `cityIds` is already `[]` and whose `allCities` is
 * already `false`, so the row arrives in exactly the state a chosen project
 * leaves it in. ⛔ **Never seed a `cityIds` beside a project**: a city write is
 * a whole-set REPLACE and a seed cannot know which cities were meant.
 *
 * ⚠️ **AND THE QUOTED ARGUMENT STILL GOVERNS HALF THE CASES, WHICH IS WHY IT IS
 * KEPT RATHER THAN DELETED.** An UNPRICED plan seeds no price and never will:
 * the box stays empty, `createPayload` omits `unitPrice`, and the server
 * snapshots the catalogue figure at the moment the line is written — the
 * behaviour every unplanned line has always had, and the behaviour the
 * paragraph above was written to protect.
 *
 * ⛔ **`priceMode: 'none'` IS NEVER SEEDED.** It sends an explicit `null`,
 * which is "this line has no price at all" — a claim about the line, not the
 * absence of a claim. An unpriced plan says nothing about price; `'inherit'` is
 * the only spelling of that.
 *
 * ⚠️ **A price that does not divide the plan is SEEDED ANYWAY and reported.**
 * See {@link Row.plannedShortfall}: refusing to seed makes somebody retype a
 * figure that is right to the penny in every case that divides, and rounding
 * the plan away loses a real fact.
 */
function seededRowOf(planned: PlannedProductLine, index: number): Row {
  return {
    /*
     * `seed-` rather than `new-`: `addRow` counts from `new-0` on its own
     * sequence, so a shared prefix would collide on the first added row and two
     * rows sharing a key is a React reconciliation bug, not a display one.
     */
    key: `seed-${index}`,
    saved: null,
    convert: null,
    /* Not copied from a quote line: this row is its own POST after the create. */
    quoteConvert: null,
    draft: {
      ...blankDraft(),
      productId: String(planned.productId),
      /*
       * ✅ **THE SEEDED PROJECT — identical to the quote grid's, and it must
       * stay identical for the reason the description below is: the two grids
       * mount the same planned rows through the same mapper, and a project
       * that reached a raised quote and not a raised invoice would be a
       * difference nobody could explain from either screen.**
       *
       * ⚠️ **The absent branch leaves `blankDraft()`'s `''`, which is "no
       * project", and writes nothing.** `createPayload` tests `!== ''` before
       * it sends, so a seed that named no project omits the key entirely — it
       * does not send a `projectId` of `0`, an empty string or a `null`, each
       * of which is a different wrong instruction on a `.strict()` schema.
       *
       * ⛔ **NO CLIENT-SIDE CHECK THAT THIS PROJECT SELLS THIS PRODUCT.** That
       * is refusal 5 and it belongs to the server, which judges the pair
       * POST-MERGE; reproducing it here would refuse a pair the API accepts.
       */
      ...(planned.project === undefined ? {} : { projectId: String(planned.project.id) }),
      // `blankDraft()` opens at '1'; an unstatable plan must NOT inherit that.
      quantity: planned.lineQuantity === null ? '' : String(planned.lineQuantity),
      /*
       * ⚠️ **The `null` branch leaves `blankDraft()`'s `'inherit'` and empty box
       * exactly as they were.** It is not `'none'` — see the block above — and
       * it is not `'value'` with an empty string, which `validateRow` refuses
       * and which would open the form already broken.
       */
      ...(planned.unitPrice === null
        ? {}
        : { priceMode: 'value' as const, price: planned.unitPrice }),
      /*
       * ✅ **THE PLANNED LINE DESCRIPTION (migration 160)** — identical to the
       * quote grid's seeding, and it must stay identical: the two grids mount
       * the same planned rows through the same mapper, and a description that
       * appeared on a raised quote and not on a raised invoice would be a
       * difference nobody could explain from either screen.
       *
       * ⚠️ **A SEEDED VALUE MAKES THE CELL TYPED, PERMANENTLY.** Empty means the
       * cell IS the derivation (product, then cities) and re-derives as the row
       * changes; a value stops that for good, with `Reset to derived` the only
       * way back.
       *
       * ⛔ **THE `null` BRANCH LEAVES THE KEY ALONE AND MUST NOT WRITE `''`.**
       * An empty string is a typed-empty description: blank on the customer's
       * document AND frozen against every later change, which is the one
       * outcome neither state should ever produce.
       */
      ...(planned.description === null ? {} : { description: planned.description }),
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    plannedQuantity: planned.lineQuantity === null ? planned.quantity : null,
    plannedShortfall: plannedShortfallOf(planned),
  };
}

/**
 * The shortfall a seeded row carries, or `null` when there is nothing to say.
 *
 * ⚠️ **`unitPriceExact` is the AUTHORITY, and this must not second-guess it.**
 * `plannedProductLines()` did the division; whether it came out even is its
 * answer, not a comparison to be re-run here against two decimal strings whose
 * scales need not match (`"10000"` and `"10000.00"` are the same amount and
 * different strings, and a string compare would report a shortfall on a plan
 * that divides perfectly).
 *
 * The line figure goes through `computeGeneratedLinePreview` — the one place
 * money arithmetic lives — at a neutral discount and tax, because the question
 * is whether the PRICE divides the plan. A discount applied afterwards is a
 * second, deliberate decision and must not read as the seed having gone wrong.
 * A preview that answers `null` yields no shortfall: nothing is invented.
 *
 * ⚠️ **The quote grid carries this word for word. The two move together.**
 */
function plannedShortfallOf(planned: PlannedProductLine): PlannedShortfall | null {
  if (planned.unitPriceExact) return null;
  if (planned.unitPrice === null || planned.plannedAmount === null) return null;
  if (planned.lineQuantity === null) return null;
  const quantity = String(planned.lineQuantity);
  const lineAmount = computeGeneratedLinePreview({
    quantity,
    unitPrice: planned.unitPrice,
    discount: '',
    discountAmount: null,
    taxRate: '',
  })?.lineSubtotal;
  if (lineAmount === undefined) return null;
  return {
    plannedAmount: planned.plannedAmount,
    unitPrice: planned.unitPrice,
    quantity,
    lineAmount,
  };
}

/**
 * A QUOTE line, as a copied row of this grid — `mode: 'quote-convert'` only.
 *
 * ⚠️ **Every seeded box is the LINE's own value and never a default.** The
 * change test compares against a fresh {@link convertLineEditOf} of the SAME
 * quote line, so a box seeded any other way reports the row as edited and puts
 * a figure nobody touched into `lines` — where the server will happily write it
 * onto a document that demands money.
 *
 * ⚠️ **`taxRate` and `xeroTaxType` ARE seeded here, unlike `convertRowOf`'s
 * neutral placeholders, and the difference is real.** A quote line carries its
 * own rate and its own Xero code and `copyLinesToInvoice` carries BOTH across as
 * a pair; the deal path has no such column and takes one company-wide figure
 * instead. So this row can honestly print the rate the invoice line will hold,
 * which is what {@link QuoteConvertedTaxRateCell} reads. **Nothing sends either
 * of them** — there is no key for them on `lines`, by an explicit backend ruling.
 *
 * ⚠️ **`key` is namespaced `quote-`.** `f_quote_products.id`,
 * `f_deal_products.id` and `f_invoice_products.id` are unrelated id spaces, and
 * a shared prefix would let a stale `expanded` set from one mode open a row of
 * another.
 */
function quoteConvertRowOf(line: QuoteProduct): Row {
  return {
    key: `quote-${line.id}`,
    saved: null,
    convert: null,
    quoteConvert: line,
    draft: {
      productId: String(line.product.id),
      /* Straight through, both ways: `null` opens the cell deriving. */
      description: line.description,
      projectId: line.project === null ? '' : String(line.project.id),
      /*
       * ⚠️ **A FLAGGED line seeds NO city ids, exactly as {@link draftOf} does
       * and for the same reason.** `line.cities` on a flagged line is the
       * RESOLVED set — what the project declares today — not stored rows. The
       * flag is copied by the server as a flag; seeding ids from it would make
       * the row look like it holds an explicit list.
       */
      cityIds: line.allCities ? [] : line.cities.map((city) => city.id),
      allCities: line.allCities,
      regionIds: line.regions.map((region) => region.id),
      quantity: String(line.quantity),
      priceMode: line.unitPrice === null ? 'none' : 'value',
      price: line.unitPrice ?? '',
      discount: line.discount,
      /* A stored amount opens the row in amount mode; `null` opens it on the percentage. */
      discountAmount: line.discountAmount,
      // Copied by the server on convert; shown here, never sent on `lines`.
      priceTierId: line.priceTierId ?? null,
      taxRate: line.taxRate,
      xeroTaxType: line.xeroTaxType ?? '',
      inclusions: line.inclusions.map((entry) => ({
        inclusionId: entry.id,
        quantity: String(entry.quantity),
      })),
      /* Editable; sent in `lines` only when it differs (the server copies it otherwise). */
      notes: line.notes ?? null,
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A quote line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And its own price. A conversion is not a plan and has nothing to divide. */
    plannedShortfall: null,
  };
}

/**
 * One copied row's five editable figures, in the shape
 * `@/lib/quote-convert-lines` reads.
 *
 * ⚠️ **`priceMode` has no equivalent on the wire.** `lines` carries a price or
 * omits the key; there is no `unitPrice: null`, so "record no price at all" is a
 * state the request cannot express and the tri-state Price box loses its third
 * position on this path — the same restriction the deal conversion carries.
 * `'none'` therefore reads as an empty box, which the validator then judges
 * against whether the line HAD a price.
 */
function quoteConvertEditOf(draft: Draft): ConvertLineEdit {
  return {
    quantity: draft.quantity,
    unitPrice: draft.priceMode === 'value' ? draft.price : '',
    discount: draft.discount,
    discountAmount: draft.discountAmount,
    description: draft.description ?? '',
  };
}

/**
 * A DEAL line, as a row of this grid — `mode: 'convert'` only.
 *
 * ⚠️ **Every seeded box is the LINE's own figure and never a default.** The
 * change test compares against a fresh `convertShapeOf` of THIS SAME FUNCTION's
 * output for the same line, so a box seeded any other way reports every row as
 * edited and puts figures nobody touched on the wire. That is also why the seed
 * is built by calling `convertRowOf` again rather than by a second seeder: one
 * function decides both what a row starts as and what it is judged against.
 *
 * The keys a deal line does not carry — `allCities`, `taxRate`, `xeroTaxType` —
 * are the absent fact rather than an invention: `allCities` is `false` because
 * `f_deal_products` has no such flag, and the two tax keys are neutral values the
 * inert tax cell never reads. Nothing sends any of them.
 *
 * ⚠️ **`key` is namespaced `deal-`, not `line-`.** The two id spaces are
 * unrelated — `f_deal_products.id` and `f_invoice_products.id` — and a shared
 * prefix would let a stale `expanded` set from one mode open a row of the other.
 */
function convertRowOf(line: DealProduct): Row {
  return {
    key: `deal-${line.id}`,
    saved: null,
    convert: line,
    quoteConvert: null,
    draft: {
      productId: String(line.product.id),
      /* Straight through, both ways: `null` opens the cell deriving. */
      description: line.description,
      projectId: line.project === null ? '' : String(line.project.id),
      cityIds: line.cities.map((city) => city.id),
      /* A deal line has no `all_cities` column. `false` is the fact, not a default. */
      allCities: false,
      regionIds: line.regions.map((region) => region.id),
      quantity: String(line.quantity),
      priceMode: line.unitPrice === null ? 'none' : 'value',
      price: line.unitPrice ?? '',
      discount: line.discount,
      /* A stored amount opens the row in amount mode; `null` opens it on the percentage. */
      discountAmount: line.discountAmount,
      // Copied by the server on convert; shown here, never sent on `lineSet`.
      priceTierId: line.priceTierId ?? null,
      /*
       * ⚠️ **Neither is read anywhere on this path.** The Tax rate cell is an
       * inert readout in convert mode — the rate a converted line gets comes from
       * the COMPANY, server-side, and no key on either request contract can carry
       * one — so these are placeholders the payload never sees, not a claim that
       * the line is at 0% with no code.
       */
      taxRate: '',
      xeroTaxType: '',
      inclusions: line.inclusions.map((entry) => ({
        inclusionId: entry.id,
        quantity: String(entry.quantity),
      })),
      /* Editable; sent in `lineSet` only when it differs (the server copies it otherwise). */
      notes: line.notes ?? null,
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A deal line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And its own price. A conversion is not a plan and has nothing to divide. */
    plannedShortfall: null,
  };
}

/**
 * The stored line a row was seeded from, whichever document it came from.
 *
 * An `InvoiceProduct`, a `DealProduct` and a `QuoteProduct` agree field for
 * field on everything this grid reads a stored line FOR — the product, the
 * project, the cities, the regions, the inclusions and the four figures — so one
 * accessor serves the name maps, the picker pinning and the description
 * derivation without any of them having to know which mode they are in. Where
 * they DIVERGE (tax, the generated subtotal and tax columns, `allCities`,
 * `invoiced`) the reader must ask `row.saved`, `row.convert` or
 * `row.quoteConvert` explicitly, which is what keeps those reads honest.
 *
 * ⚠️ **THIS IS FOR NAMING, NEVER FOR DECIDING WHAT MAY BE WRITTEN.** The three
 * sources have completely different override contracts — see the table on
 * {@link InvoiceLineItemsMode} — so anything gating a control or building a
 * payload must ask for the specific field rather than collapsing them here.
 */
function sourceLineOf(row: Row): InvoiceProduct | DealProduct | QuoteProduct | null {
  return row.saved ?? row.convert ?? row.quoteConvert;
}

/**
 * One convert row's boxes AND its structure, in the shape
 * `@/lib/deal-convert-lines` reads.
 *
 * ⚠️ **`priceMode` has no equivalent on the wire**, which carries a price or
 * nothing. `inherit` and `none` both mean *this box is empty*, and there is no
 * `unitPrice: null` key on either request key — so a price that exists cannot be
 * cleared on this screen, which is why the "record no price" toggle is not
 * rendered in convert mode: it would offer a state the request cannot express.
 *
 * ⚠️ **`offersInclusions`, `offersProjects` and `offersRegions` are REQUIRED**,
 * and passing them is what keeps a HIDDEN control from writing. The seed is built
 * by this same function with the same three flags, so a hidden set produces the
 * identical value on both sides and no key is emitted at all — where substituting
 * an empty array on one side only would send a CLEAR of a set nobody can see.
 * Same rule, same reason, and the same THREE flags as `createPayload` and
 * `updatePayload` on this grid.
 *
 * ⚠️ **`offersProjects` gates BOTH `projectId` and `cityIds`, exactly as
 * `createPayload` gates them together.** They are one block on screen and one
 * decision: a hidden project block emitting `projectId: null` would CLEAR the
 * project the deal line carries, and `cityIds: []` beside it would clear its
 * geography — on a line whose product is simply not sold through projects, where
 * nobody can see either control to put them back.
 */
function convertShapeOf(
  draft: Draft,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
): ConvertLineShape {
  return {
    quantity: draft.quantity,
    unitPrice: draft.priceMode === 'value' ? draft.price : '',
    discount: draft.discount,
    discountAmount: draft.discountAmount,
    /* `null` is DERIVE on the draft and `''` is DERIVE on the wire. One state. */
    description: draft.description ?? '',
    productId: draft.productId === '' ? null : Number(draft.productId),
    projectId: offersProjects && draft.projectId !== '' ? Number(draft.projectId) : null,
    /*
     * ⚠️ **The EXPLICIT set, always.** `allCities` has no key on `lineSet` and the
     * flag is locked on this path — see `allCitiesLocked` where {@link CitiesControl}
     * is mounted — so there is no flagged branch to honour here and no parked set
     * to suppress.
     */
    cityIds: offersProjects ? draft.cityIds : [],
    regionIds: offersRegions ? draft.regionIds : [],
    inclusions: offersInclusions ? inclusionsPayload(draft.inclusions) : [],
  };
}

/** An order-sensitive fingerprint, because the index IS the stored `sortOrder`. */
function inclusionsKey(entries: InclusionDraft[]): string {
  return entries.map((entry) => `${entry.inclusionId}:${entry.quantity || '1'}`).join('|');
}

/**
 * An ORDER-SENSITIVE fingerprint of the city set.
 *
 * The stored `sortOrder` is this array's order, and reordering the cities on a
 * line changes the string Xero prints — "Bangalore, Mumbai" and "Mumbai,
 * Bangalore" are the same set and two different customer documents. So a
 * reorder is a change and must be saved as one.
 */
function citiesKey(ids: number[]): string {
  return ids.join(',');
}

/**
 * Whether two decimal strings are the same amount — `"7000"` and `"7000.00"`
 * are. Exact, through `sumDecimalStrings`; `false` when either is not a decimal.
 */
function decimalStringsEqual(a: string, b: string): boolean {
  const pattern = /^-?\d+(\.\d+)?$/;
  const left = a.trim();
  const right = b.trim();
  if (!pattern.test(left) || !pattern.test(right)) return false;
  const negated = right.startsWith('-') ? right.slice(1) : `-${right}`;
  const difference = sumDecimalStrings([left, negated]);
  return difference !== null && /^-?0+(\.0+)?$/.test(difference);
}

/**
 * An ORDER-INSENSITIVE fingerprint of the region set.
 *
 * ⚠️ **Deliberately NOT {@link citiesKey}, and copying that one here would be
 * the bug.** A line's cities carry a stored per-line `sortOrder`, and their
 * order is printed on the customer's document, so reordering them is a real
 * change that has to be saved. Regions carry no such order — the read hands
 * them back in the master's own order — so treating a reorder as a change would
 * send a full REPLACE that stores nothing new, on a key whose `[]` form CLEARS
 * the set.
 *
 * Deduplicated for the same reason: the same region named twice is the same
 * set, and the server stores it once either way.
 */
function regionsKey(ids: number[]): string {
  return [...new Set(ids)].sort((a, b) => a - b).join(',');
}

/**
 * The draft's description as the WRITE means it — `null` for "derive".
 *
 * ⚠️ **An emptied box is a Reset, and it is folded into one HERE rather than
 * left to the server.** `{ description: "" }` is normalised to `null` on their
 * side, so relying on that would work; it would also mean our payload said
 * something we did not mean, and the two ways of asking for the derivation back
 * would travel as two different bodies that happened to agree today.
 *
 * The emptiness test TRIMS and the value sent does not: a box holding three
 * spaces is emptied by any reading a person would give it, but a description
 * that survives the test goes to Xero **verbatim**.
 */
function descriptionForWrite(value: string | null): string | null {
  if (value === null || value.trim() === '') return null;
  return value;
}

/**
 * The server's own uniqueness key: **(product, project)**.
 *
 * `UNIQUE (invoice_id, product_id, project_key)`, where `project_key` is a
 * generated `COALESCE(project_id, 0)` rather than `project_id` itself: MySQL
 * treats NULLs as distinct in a unique index, so keying on the raw column would
 * have lost duplicate protection for every project-less line.
 *
 * ⚠️ **The CITY is not part of this.** A line runs in a set of cities, so the
 * set can never be an identity.
 */
function pairKey(productId: string, projectId: string): string {
  return `${productId}:${projectId === '' ? '0' : projectId}`;
}

/**
 * The value the inclusions rule is asked about, for one row: **this row's
 * product's link-table offer**.
 *
 * ⚠️ **TWO distinguishable "nothing" results, and merging them is a real bug.**
 *
 * - {@link NO_PRODUCT_CHOSEN} — the item box is empty. A fact this row holds
 *   with certainty. Answers `'not-chosen'`, so nothing at all renders where the
 *   block would be.
 * - `null` — an item IS chosen and its offer is unread: in flight, or the read
 *   failed. Answers `'unknown'` and **still shows the block**, on purpose —
 *   hiding a feature on a load failure is unrecoverable from the user's side.
 * - A {@link ProductInclusionOffer} — the read succeeded. `inclusionIds: []` is
 *   a real answer meaning *an admin has linked none*, and is today's answer for
 *   every product. It is **not** the same value as `null`.
 */
function inclusionsOfferOf(
  row: Row,
  offers: ReadonlyMap<number, ProductInclusionOffer>,
): ProductInclusionOffer | NoProductChosen | null {
  if (row.draft.productId === '') return NO_PRODUCT_CHOSEN;
  return offers.get(Number(row.draft.productId)) ?? null;
}

/** One row's answer from `@/lib/product-inclusions`, resolved once. */
interface RowInclusions {
  offers: boolean;
  offerUnread: boolean;
  /** ⚠️ `null` means DO NOT NARROW — an unread offer, not "none linked". */
  offeredIds: ReadonlySet<number> | null;
  denialReason: string | null;
  orphaned: boolean;
}

/**
 * The fallback for a key the map has not caught up with: show, do not narrow.
 *
 * *SUPERSEDED 11 Sep 2026: a fourth field, `required: false`, resolved the same
 * uncertainty in the opposite direction — towards demanding less — because a
 * `true` would have turned an unresolved row into a line that could not be
 * written. The at-least-one rule is gone, so there is nothing left to demand.*
 */
const INCLUSIONS_SHOWN: RowInclusions = {
  offers: true,
  offerUnread: true,
  offeredIds: null,
  denialReason: null,
  orphaned: false,
};

function inclusionsPayload(entries: InclusionDraft[]) {
  return entries.map((entry, index) => {
    const quantity = Number(entry.quantity);
    return {
      inclusionId: entry.inclusionId,
      ...(entry.quantity !== '' && isValidInclusionQuantity(quantity) ? { quantity } : {}),
      sortOrder: index,
    };
  });
}

/* ── What a line STARTS WITH once it names a product ──────────────────────── */

/**
 * What {@link seedLinePresets} last put on one row, so the re-apply rule can
 * tell its own figure from a person's.
 *
 * ⚠️ **KEPT IN A REF, DELIBERATELY NOT IN {@link Draft}.** A `Draft` key is a
 * payload key: it dirties the row, reaches `createPayload`/`updatePayload`,
 * feeds `isDirty` and has to survive a reload. This is none of those — it is
 * bookkeeping about a seed, it names no column, and adding it to the draft
 * would make a row read as changed because a preset remembered itself.
 *
 * ⚠️ **`null` on either member means THIS MODULE HAS SEEDED NOTHING THERE**, and
 * it is not the same as having seeded an empty set: a line holding exactly what
 * was seeded is untouched, a line holding something else is a person's. That is
 * the whole of the owner's *re-apply only if untouched* rule.
 */
interface RowPresetSeed {
  discountAmount: string | null;
  inclusions: LineInclusionDraft[] | null;
}

/** The stable "nothing seeded here yet" identity, so a lookup miss makes no object. */
const NO_ROW_PRESET_SEED: RowPresetSeed = { discountAmount: null, inclusions: null };

/**
 * One product at one quantity on one project (`null` = no project) — what a
 * preset is resolved for, and cached by. A project ladder for (project, product)
 * wins over the product's own (2026-10-18), so the project is part of the key.
 */
interface PresetPair {
  productId: number;
  quantity: number;
  projectId: number | null;
}

/**
 * `(product, quantity, project)` as one cache key; the project segment is empty
 * for no project. Every segment is an integer or empty, so this cannot collide.
 */
function presetPairKeyOf(productId: number, quantity: number, projectId: number | null): string {
  return `${productId}:${quantity}:${projectId === null ? '' : projectId}`;
}

/** The line's project as a preset-key segment: `''` in the draft is no project. */
function presetProjectIdOf(projectId: string): number | null {
  return projectId === '' ? null : Number(projectId);
}

/**
 * The quantity a preset may be resolved for, or `null` for "do not ask".
 *
 * ⛔ **AN EMPTY BOX RESOLVES NOTHING, AND MUST NOT RESOLVE 1.** An omitted
 * `quantity` is 1 on the server, which is why `createPayload` may leave it out —
 * but a PRESET is a set of inclusions printed on the customer's document, and
 * presetting them against a figure nobody typed is the `seededRowOf` mistake one
 * column over. It matters most on exactly the row that opens empty: a seeded row
 * whose planned quantity could not be a line quantity, which `validateRow`
 * already refuses until somebody supplies one.
 *
 * The test is `validateRow`'s own, to the character — a whole number of 1 or
 * more — so this can never ask about a quantity the row itself would refuse.
 */
function presetQuantityOf(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const quantity = Number(trimmed);
  if (!Number.isSafeInteger(quantity) || quantity < 1) return null;
  return quantity;
}

/**
 * A row's inclusions in the shape the rule compares, `InclusionDraft[]` →
 * `LineInclusionDraft[]`.
 *
 * ⚠️ **`''` becomes `0`, and it is NOT resolved to 1.** `effectiveInclusionQuantity`
 * reads an empty box as the server's default of 1, which is right for the
 * DESCRIPTION it composes and wrong here: this array is one half of a
 * has-anybody-touched-this comparison, and folding a blanked box onto a seeded
 * `1` would report a real edit as untouched and overwrite it on the next
 * quantity change.
 */
function lineInclusionDraftsOf(entries: readonly InclusionDraft[]): LineInclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: Number(entry.quantity),
  }));
}

/**
 * The rule's answer as a row's draft holds it.
 *
 * ⚠️ **ORDER IS PRESERVED AND IS NOT COSMETIC** — the index becomes each entry's
 * `sortOrder` in {@link inclusionsPayload}, so the band's own order is what the
 * customer reads. Re-sorting it "to be tidy" changes the document.
 */
function inclusionDraftsOf(entries: readonly LineInclusionDraft[]): InclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: String(entry.quantity),
  }));
}

/**
 * Whether this row may be seeded at all — **only a row this grid is INVENTING.**
 *
 * ⛔ **A COPIED QUOTE LINE (`quoteConvert`) NEVER SEEDS, AND THAT IS THE SHARPEST
 * CASE.** It already carries wording, a price, a discount and an inclusion set
 * copied from a quote the customer was sent, and the rule in `@/lib/line-presets`
 * cannot protect it: its untouched test reads an EMPTY inclusion set as
 * untouched, so a quote that deliberately agreed no inclusions would have a
 * band's worth added to the invoice it converts into. The discount is worse — a
 * copied line at `discountAmount: null` with a `0.00` percentage passes both of
 * the rule's guards, so the recommendation would land, flip the line into AMOUNT
 * mode, and silently replace what the quote agreed. **Nothing on screen would
 * say the invoice had stopped matching the quote.** The rule is right and its
 * inputs would be wrong: a copy is not an untouched line, it is a line somebody
 * already settled somewhere else.
 *
 * ⛔ **A SAVED LINE (`saved`) AND A CONVERTED DEAL LINE (`convert`) ARE OUT FOR
 * THE SAME REASON, plus one that is visible immediately**: both populations are
 * seeded at MOUNT, so a seed would mark rows dirty the moment a grid loaded —
 * every saved line on `/invoices/:id` showing Unsaved against a discount nobody
 * had touched, and every deal line arriving at the conversion screen already
 * overridden.
 *
 * ✅ **A row ADDED on the quote-conversion screen DOES seed**, and must: it
 * carries `quoteConvert: null` because it is an ordinary
 * `POST /invoices/{id}/products` written after the conversion lands, and it is
 * exactly the new line the recommendation is for.
 *
 * ⚠️ **The three fields are asked for BY NAME rather than through
 * {@link sourceLineOf}.** That helper's own header says reading the three as one
 * thing is safe for the NAME maps and wrong for anything deciding what may be
 * written — and this decides whether a customer's figures are replaced.
 */
function seedsPresets(row: Row): boolean {
  return row.saved === null && row.convert === null && row.quoteConvert === null;
}

/**
 * What one draft's discount MEANS on the wire.
 *
 * ⚠️ **The two columns are mutually exclusive: sending both non-zero is a 422
 * naming `discountAmount`.** So the pair is resolved once here and both payload
 * builders emit it as a pair — whichever half is being set says the other is
 * off in the same request, because a request that sets one and leaves the other
 * stored IS the refusal.
 *
 * A blank `discount` stays blank rather than becoming `'0'`: on a create that
 * means "take the server's default" and on a patch it means "leave the stored
 * value alone", and only the callers can tell those apart.
 *
 * ⚠️ **An emptied AMOUNT box is a real zero, not a blank.** The percentage
 * control is not on screen in that mode, so "no amount" can only mean "no
 * discount on this line" — leaving a stored percentage standing would show an
 * empty discount box over a line that is still discounted.
 *
 * Field for field the quote grid's `discountWriteOf`. The two grids write the
 * same two columns, and a divergence here is two different documents from one
 * sale.
 */
interface DiscountWrite {
  /** A percentage, as typed. `''` means "say nothing about it". */
  discount: string;
  /** An exact amount, as typed, or `null` for "not discounted by amount". */
  discountAmount: string | null;
}

function discountWriteOf(draft: Draft): DiscountWrite {
  if (draft.discountAmount === null) {
    return { discount: draft.discount.trim(), discountAmount: null };
  }
  const amount = draft.discountAmount.trim();
  return { discount: '0', discountAmount: amount === '' ? null : amount };
}

/**
 * A price this grid COMPUTED for an UNTYPED line and therefore sends
 * explicitly (owner 2026-10-27: the frontend computes, the server resolves only
 * a create that omits `unitPrice`). Built by `pricedWriteFor` in the grid and
 * nowhere else; `null` means "nothing computed to send".
 *
 * - On a NEW row: only for a PROJECT price. A catalogue (pro-rata) result is
 *   still omitted, which lets the server resolve the same rule — and resolve a
 *   project price this grid has not read yet.
 * - On a SAVED row: only when the line still follows its rule, its inputs
 *   moved, and the figure differs from the stored one.
 *
 * `discountAmount` is a quantity override that does not divide into whole
 * cents; it is written only onto a line whose discount is still blank, so a
 * typed or seeded discount is never overwritten.
 */
interface PricedLineWrite {
  unitPrice: string;
  discountAmount: string | null;
  priceTierId: number | null;
}

/** What the grid's `linePriceFor` prices: a line's selection, tier, quantity and currency. */
interface LinePriceArgs {
  productId: string;
  projectId: string;
  cityCount: number;
  allCities: boolean;
  priceTierId: number | null;
  /** As the box holds it; `''` is the server default of 1. */
  quantity: string;
  currency: string | null;
}

function linePriceArgsOf(draft: Draft, currency: string | null, quantityFallback = ''): LinePriceArgs {
  return {
    productId: draft.productId,
    projectId: draft.projectId,
    cityCount: draft.allCities ? 0 : draft.cityIds.length,
    allCities: draft.allCities,
    priceTierId: draft.priceTierId,
    quantity: draft.quantity.trim() === '' ? quantityFallback : draft.quantity.trim(),
    currency,
  };
}

/** No discount of either kind on the draft: amount mode off and the percentage blank or zero. */
function discountIsBlank(draft: Draft): boolean {
  return draft.discountAmount === null && /^0*\.?0*$/.test(draft.discount.trim());
}

/**
 * The POST body for a new line.
 *
 * ⚠️ **`unitPrice` is OMITTED under `inherit`, and that omission is the whole
 * point of {@link PriceMode}** — it is the only way to say "snapshot the
 * catalogue price". An explicit `''`, `null` or `0` here all say something
 * else, and one of them put a wrong price on a live line.
 *
 * ⚠️ **`inclusions` is omitted outright when the product does not offer them.**
 * An inclusions write REPLACES the whole set, so emitting `[]` on a hidden
 * editor would silently clear a line's campaign inclusions with a 200. The
 * verdict is a parameter with no default so `tsc` refuses a call site that
 * forgets it.
 *
 * ⚠️ **`offersProjects` is the same shape, for the same reason.** When the
 * project block is not rendered, all three of `projectId`, `cityIds` and
 * `allCities` are omitted outright — `cityIds: []` CLEARS a stored set, and a
 * hidden control that clears what it was hiding is the defect the inclusions
 * parameter already exists to prevent.
 */
function createPayload(
  draft: Draft,
  sortOrder: number | undefined,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  /** See {@link PricedLineWrite}. Required, so no call site can forget it. */
  priced: PricedLineWrite | null,
): InvoiceProductCreateInput {
  const body: InvoiceProductCreateInput = { productId: Number(draft.productId) };
  if (sortOrder !== undefined) body.sortOrder = sortOrder;
  const description = descriptionForWrite(draft.description);
  if (description !== null) body.description = description;
  if (offersProjects && draft.projectId !== '') body.projectId = Number(draft.projectId);
  /*
   * ⚠️ **EXACTLY ONE of the two city keys, never both — both together is a
   * 422.** A flagged row sends the flag and no `cityIds` at all, even though
   * the draft may still be holding a set aside (see {@link Draft.allCities});
   * an unflagged row sends `cityIds` exactly as it always has.
   *
   * `allCities: false` is OMITTED rather than sent: a new line is unflagged
   * server-side already, and this builder omits every key it is not changing.
   *
   * ⚠️ This is also the DRAFT-MODE path — `draftLines` builds its create
   * bodies with this same function, so a row flagged in the New Invoice modal
   * reaches the server flagged rather than as an empty city set.
   */
  if (offersProjects) {
    if (draft.allCities) body.allCities = true;
    else if (draft.cityIds.length > 0) body.cityIds = [...draft.cityIds];
  }
  /*
   * ⚠️ **OMITTED when the control is not on screen**, exactly as `inclusions`
   * is at the foot of this function and for the neighbouring reason: a row can
   * hold regions chosen while its product was projectless and then have its
   * item changed to one that IS sold through projects, at which point the block
   * is gone and the person can no longer see or undo the set. Sending it would
   * store a geography nobody on this screen can look at.
   *
   * `[]` is not sent on a POST — nothing is stored yet for it to clear, and
   * this builder omits everything the user did not choose. On the PATCH beside
   * this one `[]` and an omitted key are OPPOSITE instructions, which is why
   * the rule is stated in both places rather than being true here by accident.
   *
   * ⚠️ This is also the DRAFT-MODE path — `draftLines` builds its create
   * bodies with this same function — so a row given regions in the New Invoice
   * modal reaches the server carrying them.
   */
  if (offersRegions && draft.regionIds.length > 0) body.regionIds = [...draft.regionIds];
  if (draft.quantity !== '') body.quantity = Number(draft.quantity);
  if (draft.priceMode === 'none') body.unitPrice = null;
  else if (draft.priceMode === 'value') body.unitPrice = draft.price;
  // `inherit` with a computed project price: sent, so the stored figure is the
  // one this grid showed. Without one the key stays omitted (see above).
  const inheritPriced = draft.priceMode === 'inherit' ? priced : null;
  if (inheritPriced !== null) body.unitPrice = inheritPriced.unitPrice;
  const money = discountWriteOf(draft);
  if (money.discountAmount !== null) {
    // The pair, always. `discount: 0` is what makes the amount the only live
    // discount on the line rather than a second one.
    body.discountAmount = money.discountAmount;
    body.discount = 0;
  } else if (money.discount !== '') {
    body.discount = money.discount;
  }
  if (
    inheritPriced !== null &&
    inheritPriced.discountAmount !== null &&
    body.discountAmount === undefined &&
    discountIsBlank(draft)
  ) {
    body.discountAmount = inheritPriced.discountAmount;
    body.discount = 0;
  }
  const tier = inheritPriced !== null ? inheritPriced.priceTierId : draft.priceTierId;
  if (tier !== null) body.priceTierId = tier;
  if (draft.taxRate !== '') body.taxRate = draft.taxRate;
  if (draft.xeroTaxType !== '') body.xeroTaxType = draft.xeroTaxType;
  if (offersInclusions && draft.inclusions.length > 0) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  /* Omitted when empty: nothing is stored yet for a `null` to clear. */
  if (draft.notes !== null && draft.notes.trim() !== '') body.notes = draft.notes;
  return body;
}

/**
 * The PATCH body for an existing line: only what actually moved.
 *
 * ⚠️ **`projectId` and `cityIds` go together whenever either changes.** The
 * guard runs on the post-merge state, so a `projectId`-only PATCH re-validates
 * the STORED cities and can be refused naming cities the caller never sent.
 *
 * ⚠️ **`offersProjects: false` omits the WHOLE block, all three keys.** The
 * project controls are not on screen for this row, so there is nothing anybody
 * changed and nothing to send — and the alternative spelling, `cityIds: []`,
 * would destroy a line's stored cities with a 200. A row that already names a
 * project never reaches this branch ({@link RowProject.showProject} keeps its
 * block), so this is a structural backstop rather than a live path.
 */
function updatePayload(
  draft: Draft,
  saved: InvoiceProduct,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  /** See {@link PricedLineWrite}. Non-null only for an untyped line being repriced. */
  priced: PricedLineWrite | null,
): InvoiceProductUpdateInput {
  const base = draftOf(saved);
  const body: InvoiceProductUpdateInput = {};
  if (draft.productId !== base.productId) body.productId = Number(draft.productId);
  const description = descriptionForWrite(draft.description);
  if (description !== base.description) body.description = description;
  const projectMoved = draft.projectId !== base.projectId;
  const flagMoved = draft.allCities !== base.allCities;
  /*
   * ⚠️ A flagged row's `cityIds` is the set held aside and is NOT sent, so
   * moving it cannot be a change. Counting it would mark a row dirty over a
   * value this body does not carry, and {@link isDirty} is defined as "the
   * PATCH would carry something".
   */
  const citiesMoved = !draft.allCities && citiesKey(draft.cityIds) !== citiesKey(base.cityIds);
  if (offersProjects && (projectMoved || citiesMoved || flagMoved)) {
    body.projectId = draft.projectId === '' ? null : Number(draft.projectId);
    if (draft.allCities) {
      /*
       * ⚠️ The flag ALONE — no `cityIds` key whatsoever, whatever the draft
       * is holding. The two together are a 422, and `allCities: true` is itself
       * what clears any explicit set the line has stored.
       */
      body.allCities = true;
    } else {
      body.cityIds = [...draft.cityIds];
      /*
       * Only when the row WAS flagged. Sending `cityIds` already clears the
       * flag server-side, so on a row that was never flagged this key would
       * state something nothing changed — and this builder sends only what
       * moved.
       */
      if (base.allCities) body.allCities = false;
    }
  }
  /*
   * ⚠️ **Sent ONLY when the person actually moved it, and the ABSENT form is
   * the load-bearing one.** `regionIds` is a full REPLACE: the key present
   * replaces the whole set, `[]` CLEARS it, and omitting it leaves the stored
   * set exactly as it is. So an unconditional send wipes a set another surface
   * populated, on a save that only changed the price — the `ContactPersons: []`
   * shape this codebase has paid for repeatedly.
   *
   * ⚠️ **It is deliberately NOT coupled to `projectId` the way `cityIds` is.**
   * Cities travel with the project because the server re-validates them against
   * the POST-MERGE project and can refuse naming a control nobody touched.
   * Regions are validated against the `/regions` master alone, so a project move
   * says nothing about them and re-sending them would be a REPLACE nobody asked
   * for.
   *
   * `offersRegions` is the same gate `offersProjects` and `offersInclusions`
   * are. It cannot fire on its own — hiding never touches the draft, so a hidden
   * set stays byte-identical to the stored one and `regionsKey` already finds no
   * change — which is precisely why it is written into the payload rather than
   * left true by accident, one edit away from somebody normalising a hidden set
   * to `[]`.
   */
  if (offersRegions && regionsKey(draft.regionIds) !== regionsKey(base.regionIds)) {
    body.regionIds = [...draft.regionIds];
  }
  if (draft.quantity !== '' && draft.quantity !== base.quantity) {
    body.quantity = Number(draft.quantity);
  }
  // `inherit` emits nothing at all: on an existing line it means "leave the
  // stored price alone", which an omitted key already says exactly.
  if (draft.priceMode === 'none' && base.priceMode !== 'none') body.unitPrice = null;
  if (draft.priceMode === 'value' && draft.price !== base.price) body.unitPrice = draft.price;
  // An untyped line whose rule moved: the repriced figure, sent explicitly.
  if (priced !== null) body.unitPrice = priced.unitPrice;
  /*
   * ⚠️ **The discount travels as a PAIR whenever either half moves**, because
   * the server refuses two live discounts — setting one while the other stays
   * stored is exactly the 422. A blank percentage box still says nothing at
   * all, which is what keeps "leave the stored value alone" available.
   */
  const money = discountWriteOf(draft);
  const baseMoney = discountWriteOf(base);
  const amountMoved = money.discountAmount !== baseMoney.discountAmount;
  const percentMoved = money.discount !== '' && money.discount !== baseMoney.discount;
  if (amountMoved || percentMoved) {
    body.discountAmount = money.discountAmount;
    body.discount = money.discount === '' ? 0 : money.discount;
  }
  if (
    priced !== null &&
    priced.discountAmount !== null &&
    (body.discountAmount === undefined || body.discountAmount === null) &&
    discountIsBlank(draft)
  ) {
    body.discountAmount = priced.discountAmount;
    body.discount = 0;
  }
  // The tier: the one the repriced figure used, else the draft's own choice.
  const tier = priced !== null ? priced.priceTierId : draft.priceTierId;
  if (tier !== base.priceTierId) body.priceTierId = tier;
  if (draft.taxRate !== '' && draft.taxRate !== base.taxRate) body.taxRate = draft.taxRate;
  if (draft.xeroTaxType !== base.xeroTaxType) {
    body.xeroTaxType = draft.xeroTaxType === '' ? null : draft.xeroTaxType;
  }
  /*
   * ⚠️ **The inclusions travel with a project MOVE as well** — migration 096.
   *
   * The post-merge trap that already couples `projectId` to `cityIds` now has a
   * second axis: a product may offer an inclusion only in certain REGIONS, and a
   * line's region comes from its project. So a `projectId`-only PATCH
   * re-validates the inclusions the line already holds and can answer
   * `422 INCLUSION_NOT_OFFERED_IN_REGION` naming a key nobody sent. Re-sending
   * the draft makes the refusal name what is on screen.
   *
   * ⚠️ **`offersInclusions: false` still omits the key, project move or not.** An
   * inclusions write REPLACES the whole set, so `[]` would destroy the line's
   * stored rows with a 200. On a row whose editor is hidden the refusal cannot
   * be fixed here anyway, and a destroyed set cannot be restored at all.
   */
  const inclusionsMoved = inclusionsKey(draft.inclusions) !== inclusionsKey(base.inclusions);
  if (offersInclusions && (inclusionsMoved || (offersProjects && projectMoved))) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  return body;
}

/** Dirty IS "the PATCH would carry something", so the two can never disagree. */
function isDirty(
  row: Row,
  offersInclusions: boolean,
  offersProjects: boolean,
  offersRegions: boolean,
  priced: PricedLineWrite | null,
): boolean {
  if (row.saved === null) return true;
  return (
    Object.keys(
      updatePayload(row.draft, row.saved, offersInclusions, offersProjects, offersRegions, priced),
    ).length > 0
  );
}

/**
 * Whether the LINE's own regions block is on screen for this row.
 *
 * ⚠️ **It is the COMPLEMENT of the project block, not a sibling of the city
 * one.** Cities are named through a project; regions exist for the products
 * that have no projects at all — `EMBA`, `LAE`, `Virtual Masterclass` — which
 * carried no geography anywhere before migration 112. So this reads the very
 * verdict {@link RowProject.showProject} already carries and answers the other
 * way, rather than asking “is this sold through projects” a second time and
 * drifting from it the first day either is edited.
 *
 * ⚠️ **`showProject` fails towards SHOWING, so this fails towards HIDING.** A
 * loading, failed or truncated project list keeps the project block up and
 * leaves this one down. That is the right direction: a region set offered on a
 * row that turns out to be project-backed invites somebody to record a geography
 * the push does not read there, where a region set briefly absent costs one
 * re-render.
 *
 * ⚠️ **A row that already HOLDS regions keeps its block whatever else is
 * true** — the same never-hide-data rule the project block follows. Hiding a
 * stored value makes real data invisible and uneditable, and this set is what
 * gives a projectless line its Xero tracking Location.
 *
 * ⚠️ **`projectHasNoCities` is the SECOND case, and it is the owner's, 2026-08-31.**
 * A line whose project declares no cities got an empty city picker and nothing
 * else — which is refusal 6, `project-has-no-cities`, whose own documented fix
 * is **on the project and not on this line**. So the person was shown a control
 * they could not act on and no way to record the line's geography at all. It is
 * built to fail towards hiding exactly as `showProject` fails towards showing:
 * `false` for every merely-UNRESOLVED state, true only where a chosen project
 * came back and declared nothing. See {@link RowProject.projectHasNoCities}.
 *
 * Hiding is a render branch and nothing else. It never touches
 * `draft.regionIds`: a regions write REPLACES the set, so a block that tidied
 * its own draft on the way to being hidden would clear a line's stored regions
 * on the next unrelated save, with a `200` and nothing to restore from.
 */
function showsRegionsControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  /*
   * 🔴 **A ROW WITH NO ITEM OPENS NOTHING — added 2026-09-19 WITH the change
   * that hides the project block on a productless row, and it is not optional
   * tidying.** `!showsProject` used to mean one thing, "this product is not sold
   * through projects", and now means two. Left as it was, the empty row that just
   * lost its project picker would have GAINED a regions picker in the same render
   * — one control swapped for another on a line that has not said what it sells.
   *
   * That is the direction this function's own doc calls wrong: a region set
   * offered on a row that turns out to be project-backed invites somebody to
   * record a geography the push does not read there. A row with no item is the
   * purest case of "turns out to be" — nothing is known yet.
   *
   * ⚠️ It reads `draft.productId` directly rather than taking a fourth boolean:
   * "has an item been chosen" is a one-field read off the row this function
   * already has, not a second derivation of "is this sold through projects",
   * which must keep coming from `showsProject` alone.
   *
   * The never-hide-data rule still outranks it — a row already holding regions
   * keeps them, or a stored set becomes invisible and unremovable.
   */
  if (row.draft.productId === '') {
    return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
  }
  if (!showsProject) return true;
  if (projectHasNoCities) return true;
  return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
}

/**
 * Whether the CITY controls — the "All cities" flag AND the picker beside it —
 * belong on this row.
 *
 * Owner's instruction, asked four times: on a line whose product is not
 * city-specific (`Virtual Masterclasses`, `LAE`, `EMBA`) the city controls must
 * not appear, leaving only the region ones that mean anything there. Until now
 * they were gated on {@link RowProject.showProject} alone, so a project
 * declaring no cities still rendered a tick box and a permanently empty
 * "Not city-specific" dropdown above the controls that matter.
 *
 * ⚠️ **THE FACT WAS ALREADY COMPUTED AND THIS RULE SIMPLY NEVER RECEIVED IT.**
 * {@link showsRegionsControl} has taken `projectHasNoCities` all along; this is
 * the same argument reaching the other half of the same decision.
 *
 * ⚠️ **HIDING NEVER CLEARS, AND THE NEVER-HIDE-DATA BRANCH IS ABOVE THE TEST.**
 * A row already holding cities, or carrying the all-cities flag, keeps its
 * controls whatever its project declares today — a project's city list can be
 * edited after the line was written, and a control that vanishes over stored
 * cities leaves them unreadable and unremovable. Note this is belt-and-braces
 * rather than the only guard: {@link createPayload} and {@link updatePayload}
 * decide whether to write cities from `showProject`, which this does NOT change,
 * so a hidden picker cannot drop a city from a payload in any case.
 *
 * ⚠️ **"Declares no cities" is not "not loaded yet", and the difference is the
 * whole safety of this.** `projectHasNoCities` is documented on
 * {@link RowProject} as definite — true only when a project is chosen AND was
 * resolved AND its own list is empty — with loading, failed, truncated, retired
 * and no-project-yet all `false`. **Unknown therefore keeps the control
 * showing**, which is the safe direction and the same one `showProject` fails
 * in.
 */
function showsCitiesControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  if (!showsProject) return false;
  if (row.draft.allCities || row.draft.cityIds.length > 0) return true;
  if ((row.saved?.cities.length ?? 0) > 0) return true;
  return !projectHasNoCities;
}

/**
 * The input to `computeGeneratedLinePreview`, with the blanks resolved.
 *
 * A blank box means two different things by row, so it is resolved before the
 * call rather than inside it: on a NEW line a blank percentage takes the
 * COMPANY's rate and a blank price snapshots the catalogue; on an EXISTING line
 * a blank leaves the stored value alone.
 *
 * ⚠️ *SUPERSEDED: "on a NEW line a blank percentage takes the server's default
 * of 0".* **It never did.** `invoice-products.service.ts` resolves a created
 * line's rate as `input.taxRate ?? companyRate ?? undefined`, so the column
 * default of 0 is reached only when the COMPANY states no rate — which, over
 * 100 live companies measured on 11 September 2026, is none of them. That
 * sentence is where the owner's "Tax 0.00" came from: it was believed, so the
 * blank was left to reach `@/lib/decimal`, which reads `''` as `'0'`.
 *
 * ⚠️ **RESOLVED HERE, NEVER WRITTEN INTO THE DRAFT.** The draft stays blank, so
 * `createPayload` still omits `taxRate` and the SERVER still decides. Seeding
 * the box instead would make this app a second writer of the figure and would
 * send a rate captured when the modal opened rather than the one in force when
 * Save is pressed.
 */
function previewInputOf(
  row: Row,
  catalogPrice: string | null,
  companySalesTax: CompanySalesTax,
  /**
   * City pro-rata, 2026-10-18: the price the SERVER will store on a saved line
   * whose untyped price follows its cities — see `repricedSavedPriceFor`.
   * `undefined` is "not repriced"; the row's own price then stands.
   */
  repriced?: string | null,
  /**
   * The rounding discount a computed project price carries (see
   * {@link PricedLineWrite}), previewed only where the save would write it.
   */
  pricedDiscount?: string | null,
): GeneratedLineInput {
  const { draft, saved } = row;
  const stored = (typed: string, value: string | undefined) =>
    typed !== '' ? typed : (value ?? '');
  const money = discountWriteOf(draft);
  const roundingDiscount =
    pricedDiscount !== undefined &&
    pricedDiscount !== null &&
    discountIsBlank(draft) &&
    (saved === null || saved.discountAmount === null)
      ? pricedDiscount
      : null;
  /*
   * ⚠️ **ROWS THAT WILL BE CREATED BY A POST, AND ONLY THOSE.** Three
   * populations reach this function and they resolve a blank differently:
   *
   * - `saved !== null` — an EXISTING line. A blank means *omit the key*, which
   *   leaves the stored rate alone, so the company's figure is not what
   *   happens and previewing it would show a change no PATCH will make.
   * - `quoteConvert !== null` — a COPIED quote line. `lines` has no tax key at
   *   all by an explicit backend ruling, so the quote's own rate is copied
   *   whatever this grid does. Its cell is `QuoteConvertedTaxRateCell`, not the
   *   live control, and it must not be told the company's figure applies.
   * - everything else — a draft row, a row ADDED on the quote-convert path, or
   *   a deal-convert row. All three are given the company's rate server-side,
   *   which is exactly what this fallback previews.
   *
   * ⚠️ **`''` — not `'0'` — when the company states none or has not been read.**
   * That is the old behaviour byte for byte, which is what keeps a mount passing
   * no `companySalesTax` rendering exactly as it did.
   */
  const companyRate =
    saved === null &&
    row.quoteConvert === null &&
    companySalesTax.known &&
    companySalesTax.rate !== null &&
    companySalesTax.rate.trim() !== ''
      ? companySalesTax.rate.trim()
      : '';
  return {
    quantity: stored(draft.quantity, saved === null ? undefined : String(saved.quantity)),
    unitPrice:
      repriced !== undefined
        ? repriced
        : draft.priceMode === 'none'
        ? // An explicit null: no price on this line, which is not a price of zero.
          null
        : draft.priceMode === 'value'
          ? draft.price
          : // `inherit` — the stored price on an existing line, the catalogue
            // price on a new one, because that is what the write will snapshot.
            saved === null
            ? catalogPrice
            : saved.unitPrice,
    /*
     * ⚠️ Read off the RESOLVED pair, so the figure previewed is the one the
     * payload will carry: in amount mode the percentage is written as ZERO, so
     * falling back to the stored one here would take BOTH discounts off the
     * line and preview a total the database will never produce.
     */
    discount:
      roundingDiscount !== null
        ? '0'
        : money.discountAmount === null
          ? stored(draft.discount, saved?.discount)
          : '0',
    discountAmount: roundingDiscount ?? money.discountAmount,
    taxRate: stored(draft.taxRate, saved === null ? companyRate : saved.taxRate),
  };
}

/* ── The Disc cell: two columns, and nothing converts between them ───────── */

/*
 * ⚠️ **THE AMOUNT↔PERCENTAGE CONVERSION IS DELETED. DO NOT BRING IT BACK.**
 *
 * `f_invoice_products` carries `discount_amount` beside `discount`, mutually
 * exclusive, so an amount is stored as the amount. What stood here —
 * `MONEY_SCALE`, `scaledMoney`, `grossOf`, `discountMoneyOf`,
 * `discountAmountAsPercent`, and a `DiscountAmount` carrying the money the
 * stored percentage really took off — existed only because there was ONE
 * column and it was a percentage.
 *
 * The four refusals were not validation, they were the cases the conversion
 * could not express: an amount larger than the line, a line worth `0.00`, a
 * line with no price, and a line whose value could not be worked out yet. The
 * server owns those now, and the only thing left worth checking here is that
 * the amount is a well-formed money string.
 *
 * The quote grid deleted the identical block for the identical reason; the two
 * grids are meant to read the same here.
 */

/**
 * ⛔ **`DISCOUNT_AMOUNT_PATTERN`, NOT `PRICE_PATTERN` — and this function used
 * to read the other one.** Its doc line said *"an amount is money, so it is a
 * price"*, which was true until 2026-09-12 and is now precisely the confusion
 * to avoid: a price may be negative, a discount may not. See the block at both
 * declarations.
 */
function isWellFormedAmount(value: string): boolean {
  return DISCOUNT_AMOUNT_PATTERN.test(value.trim());
}

/**
 * ⚠️ **Lifted to a constant only so `validateRow` can assemble the row's
 * notices in a LIST.** The wording is unchanged and its history is recorded at
 * the return site; nothing else about the message moved.
 */
const DUPLICATE_PRODUCT_NOTICE =
  'This product is on more than one line of this invoice. That is allowed — check it is deliberate.';

interface RowProblems {
  fieldErrors: Record<string, string>;
  error: string | null;
  /**
   * Something true about the row that is **not** a reason to refuse it.
   *
   * ⚠️ **Only `error` blocks a save; this never does.** That is why the repeated
   * -product message moved to its own field rather than being softened in place
   * — reworded on `error` it would have gone on disabling Save however gently it
   * read. Migration 129 dropped the (invoice_id, product_id, project_key) unique
   * key, so one invoice may bill a product on as many lines as it likes; 162
   * groups across 1,105 real Xero documents do exactly that, and none of them is
   * a true duplicate. The grid still points it out, because billing one thing
   * twice by accident is worth catching.
   */
  notice: string | null;
}

/**
 * Whether this row's save will actually carry `cityIds` and trigger the guard.
 *
 * ⚠️ **A flagged row carries `allCities` INSTEAD and never carries
 * `cityIds`**, so the "not declared on this project" guard cannot fire on it.
 * The set it is holding aside is not sent, and must not be judged as though it
 * were.
 */
function writesCities(row: Row): boolean {
  if (row.draft.allCities) return false;
  if (row.saved === null) return row.draft.cityIds.length > 0;
  const base = draftOf(row.saved);
  return (
    row.draft.projectId !== base.projectId ||
    // Unflagging is itself a `cityIds` write: the whole set goes over.
    row.draft.allCities !== base.allCities ||
    citiesKey(row.draft.cityIds) !== citiesKey(base.cityIds)
  );
}

/**
 * *SUPERSEDED 11 Sep 2026: a sixth parameter, `inclusionsRequired`, carried the
 * owner's at-least-one rule as a second and strictly narrower inclusions gate.
 * The rule was withdrawn, so the parameter went with it rather than being left
 * accepted and ignored — an argument nothing reads is the shape this repo has
 * already been bitten by.*
 */
function validateRow(
  row: Row,
  duplicatePairs: Set<string>,
  retiredIds: Set<number>,
  declaredCityIds: Set<number> | null,
  offersInclusions: boolean,
): RowProblems {
  const fieldErrors: Record<string, string> = {};
  const draft = row.draft;
  if (draft.productId === '') fieldErrors.productId = 'Choose a product.';
  const description = descriptionForWrite(draft.description);
  if (description !== null && description.length > MAX_DESCRIPTION_LENGTH) {
    fieldErrors.description = `At most ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters — this is ${description.length.toLocaleString()}.`;
  }
  /*
   * ⚠️ **The three city checks below are statements about an EXPLICIT SET,
   * and a flagged line does not have one.** The cap bounds the `cityIds` array,
   * "choose a project before naming cities" is about naming them, and the stray
   * check asks whether ids the caller sent are declared — none of which is a
   * question about a flag that sends no ids at all. Running them anyway would
   * refuse a row over the set it is holding aside and deliberately not sending.
   *
   * ⚠️ **A PROJECT is still required, though.** "All cities" is meaningless
   * without one: the server resolves the flag against the LINE's project, and
   * with no project there is nothing to resolve and nothing the line could
   * mean. Worded for the flag rather than for a list — telling somebody to
   * "name cities" is the opposite of what they have just asked for.
   */
  if (draft.allCities) {
    if (draft.projectId === '') {
      fieldErrors.cityIds =
        'Choose this line’s project first.';
    }
  } else if (draft.cityIds.length > MAX_CITIES_PER_LINE) {
    fieldErrors.cityIds = `At most ${MAX_CITIES_PER_LINE} cities on one line.`;
  } else if (draft.cityIds.length > 0 && draft.projectId === '') {
    fieldErrors.cityIds = 'Choose this line’s project before naming cities.';
  } else if (declaredCityIds !== null && writesCities(row)) {
    const stray = draft.cityIds.filter((id) => !declaredCityIds.has(id));
    if (stray.length > 0) {
      fieldErrors.cityIds = `${stray.length} of ${draft.cityIds.length} cities aren’t on this line’s project.`;
    }
  }
  /*
   * ⚠️ **AN EMPTY BOX IS FINE ON AN ORDINARY ROW AND A REFUSAL ON A SEEDED ONE,
   * AND THE ASYMMETRY IS THE POINT.**
   *
   * `createPayload` omits `quantity` when the box is empty, and an omitted
   * `quantity` is **1** on the server. On a row somebody typed that is a
   * convenience nobody has to think about. On a row seeded from a PLANNED
   * figure that could not be expressed — `"2.50"`, `"0.5"` — it is the exact
   * silent substitution `plannedProductLines()` refuses to make on the way in,
   * arriving anyway by the back door: a 201, a well-formed invoice, and a
   * quantity nobody chose printed on it.
   *
   * So the seeded row is refused while the box is empty. That puts it in
   * `blockedKeys`, withholds it from `onDraftLinesChange`, raises the grid's own
   * blocking banner, and disables the parent's Save — which is a person being
   * asked one question, rather than a wrong number nobody is ever asked about.
   *
   * ⚠️ The quote grid carries this branch verbatim. The two move together.
   */
  if (draft.quantity === '') {
    if (row.plannedQuantity !== null) {
      // The planned figure is named, because "enter a quantity" over a box that
      // opened empty on a row the person did not create explains nothing.
      fieldErrors.quantity = `Planned ${row.plannedQuantity} — enter a whole number of 1 or more.`;
    }
  } else {
    const quantity = Number(draft.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      fieldErrors.quantity = 'A whole number of 1 or more.';
    }
  }
  if (draft.priceMode === 'value' && !PRICE_PATTERN.test(draft.price.trim())) {
    /*
     * ✅ **THE NEGATIVE BRANCH IS GONE — a line price MAY be negative since
     * migration 171 (owner's instruction, 2026-09-12).** This read
     * *"A line price cannot be negative."*, justified as *"nothing on a LINE may
     * be negative, and the server refuses one too"*. **Both halves are now
     * false**: a DEDUCTION LINE is ordinary business here — INV-0886, this very
     * document type, carries one reading "Deduction for Cape Town Participation
     * Fee" at `-4150.00` — and the API's `UNIT_PRICE_PATTERN` gained a `-?` in
     * the same change.
     *
     * ⚠️ **The seeded-row case that made this branch reachable is UNCHANGED and
     * is now simply correct.** A planned line amount could always be negative
     * (`splitAmountOverQuantity` divides a signed amount deliberately, for a
     * credit note), so a seeded row could open holding `-3333.33` and be
     * refused by the grid it had just been seeded into. It now saves.
     *
     * ⚠️ The quote grid carries this branch verbatim. The two move together.
     */
    const typed = draft.price.trim();
    fieldErrors.unitPrice =
      typed === ''
        ? 'Enter a price, or record the line as having none.'
        : 'Up to two decimal places, and no currency symbol.';
  }
  /*
   * ⚠️ **The DISCOUNT is checked in exactly one of its two shapes, because only
   * one is on screen.** The mode decides which control the person can see, and
   * reporting the other's format would name a box that is not there.
   */
  if (draft.discountAmount === null) {
    const trimmed = draft.discount.trim();
    // `Number()` here compares a bound, never money. The value itself is sent
    // as the string the user typed, untouched by a float.
    if (trimmed !== '' && (!PERCENT_PATTERN.test(trimmed) || Number(trimmed) > 100)) {
      fieldErrors.discount = 'A percentage between 0 and 100.';
    }
  } else {
    const amount = draft.discountAmount.trim();
    if (amount !== '' && !isWellFormedAmount(amount)) {
      /*
       * ⚠️ **The negative branch became the likely mistake on 2026-09-12**, when
       * a line PRICE gained a sign (migration 171). Typing `-500` here is the
       * natural way to say "take this off", and the generic sentence answers it
       * by naming decimal places — a rule the figure already keeps.
       *
       * ⛔ A discount may never be negative; the database reports that only as
       * errno 4025, a 422 naming no field, so this sentence is the only place a
       * person learns what to use instead. Worded identically in
       * `deal-convert-lines.ts` and the other two grids.
       */
      fieldErrors.discountAmount = amount.startsWith('-')
        ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
        : 'Up to two decimal places, and no currency symbol.';
    }
  }

  const taxRate = draft.taxRate.trim();
  if (taxRate !== '' && (!PERCENT_PATTERN.test(taxRate) || Number(taxRate) > 100)) {
    fieldErrors.taxRate = 'A percentage between 0 and 100.';
  }
  /*
   * ⚠️ **Skipped wholesale when the block is hidden**, the same condition the two
   * payload builders use and for the same reason. Nothing below can be true of a
   * key that is not sent, and an error marking a control the person cannot see
   * would leave the row permanently unsaveable over a field they never touched.
   *
   * *SUPERSEDED 11 Sep 2026: this nesting was described as the guard on the
   * at-least-one refusal, which was the one inclusions check that demanded a
   * selection rather than objecting to one. That refusal is gone, so the nesting
   * now protects only the three checks below.*
   */
  if (offersInclusions) {
    /*
     * ⛔ AN EMPTY SET IS NOT REFUSED, AND THAT IS THE RULE RATHER THAN A GAP.
     *
     * *SUPERSEDED, owner's decision 11 Sep 2026: a line whose product offered
     * inclusions had to name one, and an empty set rendered
     * `INCLUSION_REQUIRED_MESSAGE`.* That rule predated per-inclusion
     * requiredness: with no way to say WHICH inclusion mattered, "at least one"
     * was the only thing the client could ask for. Migration 159 lets an admin
     * mark each offer required, and the owner chose PRE-TICK, STILL REMOVABLE —
     * so a refusal here would contradict the removability that was the whole
     * point of the choice.
     *
     * ⚠️ **Nothing on the server ever backed this up.** A line with no
     * inclusions is a clean `2xx` on all three endpoints, and the backend
     * declined enforcement explicitly, because this app hides the inclusions
     * block on lines whose product is not gated for it and an enforced refusal
     * could then name a control the person cannot see — the shape
     * `PROJECT_HAS_NO_CITIES` already has. **Do not reinstate this as a
     * client-side rule; it would be a convention dressed as a contract.**
     *
     * The three checks below all object to something PRESENT in the set, so
     * every one of them is unreachable on an empty set and none needs the
     * removed branch ahead of it.
     */
    if (draft.inclusions.length > MAX_INCLUSIONS_PER_LINE) {
      fieldErrors.inclusions = `At most ${MAX_INCLUSIONS_PER_LINE} inclusions on one line.`;
    } else if (
      draft.inclusions.some(
        (entry) => entry.quantity !== '' && !isValidInclusionQuantity(Number(entry.quantity)),
      )
    ) {
      fieldErrors.inclusions = 'Every count must be a whole number of 1 or more.';
    } else {
      const writingSet =
        row.saved === null
          ? draft.inclusions.length > 0
          : inclusionsKey(draft.inclusions) !== inclusionsKey(draftOf(row.saved).inclusions);
      if (writingSet && draft.inclusions.some((entry) => retiredIds.has(entry.inclusionId))) {
        fieldErrors.inclusions =
          'One of these inclusions has been retired. Remove it.';
      }
    }
  }
  const duplicate =
    draft.productId !== '' && duplicatePairs.has(pairKey(draft.productId, draft.projectId));
  /*
   * ⚠️ **A NOTICE AND NEVER A FIELD ERROR, because the seeded price is not
   * wrong.** `10000.00` over 3 is `3333.33` and the line comes to `9999.99`;
   * that is the best a per-unit price can do and a person may accept it. A
   * `fieldErrors.unitPrice` here would land the row in `blockedKeys` and
   * disable the parent's Save over a penny — a button wrongly disabled is a
   * false statement with nobody watching. So it says what is true and refuses
   * nothing.
   *
   * ⚠️ **It stands down the moment either box moves.** The sentence is about
   * the SEEDED figures; once the price or the quantity changes it no longer
   * describes the row, and a stale total naming an amount the line no longer
   * has is worse than no sentence at all.
   *
   * ⚠️ The quote grid carries this branch verbatim. The two move together.
   */
  const shortfall = row.plannedShortfall;
  const shortfallNotice =
    shortfall !== null &&
    draft.priceMode === 'value' &&
    draft.price === shortfall.unitPrice &&
    draft.quantity === shortfall.quantity
      ? `Planned ${shortfall.plannedAmount} — ${shortfall.unitPrice} × ${shortfall.quantity} comes to ${shortfall.lineAmount}. The planned amount does not divide evenly.`
      : null;
  /*
   * ⚠️ **BOTH notices render, joined — never one `??` the other.** A repeated
   * product and a plan that will not divide are two different true things about
   * one row, and coalescing would drop whichever came second exactly when the
   * row is under the most scrutiny.
   */
  const notices = [shortfallNotice, duplicate ? DUPLICATE_PRODUCT_NOTICE : null].filter(
    (line): line is string => line !== null,
  );
  return {
    fieldErrors,
    /*
     * ⚠️ **A REPEATED PRODUCT IS NO LONGER AN ERROR.** *SUPERSEDED: an `error`
     * reading "Already on this invoice under that project." and, for the
     * project-less case, "Give one of them a project."*
     *
     * The second told the reader to change something to make the save possible,
     * and after migration 129 there is nothing to change — the save succeeds
     * either way. Advice left beside a relaxed gate reads as a requirement.
     */
    error: null,
    notice: notices.length === 0 ? null : notices.join(' '),
  };
}

/**
 * The `(product, project)` pairs claimed by more than one row.
 *
 * ⚠️ **This feeds `RowProblems.notice` and refuses nothing** — migration 129
 * dropped the unique key it used to anticipate. A repeated product is still
 * pointed out, because billing one thing twice by accident is a real mistake,
 * but the save proceeds.
 *
 * Counted across rows rather than by excluding taken products from the picker:
 * the project picker is filtered by the row's own product, so an exclusion
 * deadlocks the second-project case. That still holds, and there is now nothing
 * to exclude anyway.
 */
function duplicatePairsOf(rows: Row[]): Set<string> {
  const seen = new Map<string, number>();
  for (const row of rows) {
    if (row.draft.productId === '') continue;
    const key = pairKey(row.draft.productId, row.draft.projectId);
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [key, count] of seen) if (count > 1) duplicates.add(key);
  return duplicates;
}

/**
 * Every path a line write can report a field error at. `priceMode` and `price`
 * are ours, not the server's; the wire name is `unitPrice`.
 */
const ROW_WRITE_KEYS: readonly string[] = [
  ...Object.keys(blankDraft()).filter((key) => key !== 'priceMode' && key !== 'price'),
  'unitPrice',
];

function describeFailure(error: unknown): {
  message: string;
  fieldErrors: Record<string, string>;
  refusal: LineRefusal | null;
} {
  if (error instanceof ApiError) {
    // `inclusions.0.quantity` and friends all belong on the one editor.
    const inclusionPaths = Object.keys(error.fieldErrors).filter(
      (path) => path === 'inclusions' || path.startsWith('inclusions.'),
    );
    const { fields, unroutable } = error.routeFieldErrors([...ROW_WRITE_KEYS, ...inclusionPaths]);
    const inclusionMessages: string[] = [];
    for (const path of inclusionPaths) {
      const message = fields[path];
      if (message === undefined) continue;
      inclusionMessages.push(message);
      delete fields[path];
    }
    if (inclusionMessages.length > 0) fields.inclusions = inclusionMessages.join(' ');
    /*
     * A quantity that is not a multiple of the product's step belongs on the
     * row's Qty box, whether or not the server named the path.
     */
    if (error.code === 'QUANTITY_NOT_MULTIPLE' && fields.quantity === undefined) {
      fields.quantity = error.message;
    }
    return {
      message: unroutable.length > 0 ? unroutable.join(' ') : error.message,
      fieldErrors: fields,
      refusal: lineRefusalOf(error),
    };
  }
  return { message: 'The request did not reach the server.', fieldErrors: {}, refusal: null };
}

/* ── Reference data ───────────────────────────────────────────────────────── */

interface ProductRow {
  id: number;
  productName: string;
  productCode: string | null;
  unitPrice: string | null;
  /**
   * What a line selling this product should START its discount at — migration
   * 172. Carried on the list this grid already reads, rather than fetched a
   * second time per row.
   *
   * ⚠️ **`null` is NO RECOMMENDATION and `"0.00"` is a recommendation of nothing
   * off** — the same distinction `unitPrice` draws between unpriced and free,
   * and the same `?? 0` that would destroy it. `recommendedDiscountSeed` reads
   * `null` as "change nothing" and `"0.00"` as a figure to seed, so collapsing
   * the two here would start writing a zero discount onto every line.
   */
  recommendedDiscountAmount: string | null;
  /** See `Product.quantityStep`. `null` for "no step" AND for an older API. */
  quantityStep: number | null;
  /** See `Product.unitLabel`. Singular. `null` for none AND for an older API. */
  unitLabel: string | null;
}

/**
 * Whether a product is sold PER UNIT: it names a unit or a step. Its
 * recommended discount is then per unit and is scaled by the quantity before
 * it is seeded as the line's discount amount.
 */
function isPerUnitProduct(product: ProductUnitFields | undefined): boolean {
  if (product === undefined) return false;
  return (
    (product.quantityStep ?? null) !== null || (product.unitLabel ?? '').trim() !== ''
  );
}

/** The product's unit label when it has a usable one, else `null`. */
function unitLabelOf(product: ProductUnitFields | undefined): string | null {
  const label = product?.unitLabel?.trim() ?? '';
  return label === '' ? null : label;
}

/**
 * The source line a row started from — saved, deal or quote — as the two facts
 * the step check compares against. `null` for a row invented on screen.
 */
function unitBaselineOf(row: Row): { productId: string; quantity: string } | null {
  const source = row.saved ?? row.convert ?? row.quoteConvert;
  if (source === null) return null;
  return { productId: String(source.product.id), quantity: String(source.quantity) };
}

/**
 * ⚠️ **Only a quantity somebody edited, or a product somebody picked, is
 * judged.** A legacy line selling a stepped product at quantity 1 keeps
 * working until either moves; the server is the real boundary.
 */
function withQuantityStepCheck(
  row: Row,
  problems: RowProblems,
  units: ReadonlyMap<number, ProductUnitFields>,
): RowProblems {
  if (row.draft.productId === '' || problems.fieldErrors.quantity !== undefined) return problems;
  const baseline = unitBaselineOf(row);
  if (
    baseline !== null &&
    baseline.productId === row.draft.productId &&
    baseline.quantity === row.draft.quantity
  ) {
    return problems;
  }
  const message = quantityStepError(units.get(Number(row.draft.productId)), row.draft.quantity);
  if (message === null) return problems;
  return { ...problems, fieldErrors: { ...problems.fieldErrors, quantity: message } };
}

function useProducts() {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts({ perPage: PRODUCTS_PER_PAGE, active: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(
          result.data.map((row) => ({
            id: row.id,
            productName: row.productName,
            productCode: row.productCode,
            unitPrice: row.unitPrice,
            recommendedDiscountAmount: row.recommendedDiscountAmount,
            quantityStep: row.quantityStep ?? null,
            unitLabel: row.unitLabel ?? null,
          })),
        );
        setTruncated(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);
  return { rows, truncated, failed };
}

function useCampaignInclusions() {
  const [rows, setRows] = useState<CampaignInclusion[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCampaignInclusions({ isActive: true, perPage: INCLUSIONS_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);
  return { rows, failed };
}

/**
 * Each chosen product's campaign-inclusion offer, read once per product id and
 * cached for the life of the grid.
 *
 * A failed read is left ABSENT rather than recorded as an empty offer: absent
 * resolves to `'unknown'`, which shows the editor, and an empty offer is an
 * administrator's real decision. Folding one into the other promotes a load
 * failure into a business rule.
 */
function useProductInclusionOffers(
  productIds: readonly number[],
): ReadonlyMap<number, ProductInclusionOffer> {
  const [offers, setOffers] = useState<ReadonlyMap<number, ProductInclusionOffer>>(
    () => new Map(),
  );
  const requested = useRef<Set<number>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();
  useEffect(() => {
    /*
     * ⚠️ **RE-ARM after a torn-down mount.** One controller per COMPONENT is
     * right — a per-effect one would cancel reads whose ids `requested` has
     * already recorded, and they would never be asked for again — but the
     * cleanup below also runs when the component is NOT gone: in development
     * React invokes setup, cleanup, then setup again, with no render in
     * between, so the `??=` above cannot replace the aborted controller.
     * Without this the offers map stays EMPTY for the life of the grid and
     * every row answers `'unknown'`: editor shown, picker never narrowed to
     * what an administrator linked, at-least-one rule never applied — a
     * working-looking screen that disagrees with production about a set that
     * prints on the customer's document. `QuoteLineItemsTable` carries the
     * full account.
     *
     * ⚠️ Clearing `requested` here does not retry a FAILED read: a rejected
     * fetch never aborts this controller.
     */
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    // Through the ref, not a captured copy — the branch above may have swapped it.
    return () => controller.current?.abort();
  }, []);
  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const productId of productIds) {
      if (requested.current.has(productId)) continue;
      requested.current.add(productId);
      api
        .productInclusions(productId, signal)
        .then((result) => {
          if (signal.aborted) return;
          const inclusionIds = result.data.map((inclusion) => inclusion.id);
          setOffers((previous) => new Map(previous).set(productId, { inclusionIds }));
        })
        .catch(() => {
          if (signal.aborted) return;
        });
    }
  }, [productIds]);
  return offers;
}

/**
 * Which campaign inclusions each chosen `(product, quantity)` presets, read once
 * per pair and cached for the life of the grid.
 *
 * ⚠️ **CACHED BY THE PAIR, NOT BY THE PRODUCT.** The ladder is bands of
 * quantity, so one product legitimately answers differently at 4 and at 5;
 * caching by product alone would preset the first quantity somebody typed for
 * ever, silently, on every later change.
 *
 * ⚠️ **DEBOUNCED, because the quantity box is a text input.** `pairs` is
 * recomputed from a keystroke-stable key by the caller and then held for
 * {@link PRESET_DEBOUNCE_MS} here, so an unsettled figure never reaches the
 * wire. Choosing a PRODUCT waits the same 250ms; one timer for both is what
 * keeps the settled state a single answer rather than two that can disagree.
 *
 * A failed read is left ABSENT rather than recorded as an empty preset, the same
 * arrangement {@link useProductInclusionOffers} makes and for a stronger reason:
 * `inclusionPresetSeed` reads `null` as "change nothing" and a band with an
 * empty list as "an administrator said this range presets nothing", which would
 * EMPTY the line. Folding a load failure into the second is a clear dressed as a
 * decision.
 */
function useInclusionPresets(
  pairs: readonly PresetPair[],
): ReadonlyMap<string, ResolvedInclusionPreset> {
  const [resolved, setResolved] = useState<ReadonlyMap<string, ResolvedInclusionPreset>>(
    () => new Map(),
  );
  const [settled, setSettled] = useState<readonly PresetPair[]>(pairs);
  const requested = useRef<Set<string>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();
  useEffect(() => {
    /*
     * ⚠️ **RE-ARM after a torn-down mount**, exactly as
     * {@link useProductInclusionOffers} does and for the identical reason: one
     * controller per COMPONENT is right, but React's development double-invoke
     * runs setup, cleanup, setup with no render between, so the `??=` above
     * cannot replace the aborted controller. Without this the map stays EMPTY
     * for the life of the grid, every pair reads `null`, and NOTHING is ever
     * preset — a working-looking screen that quietly drops a feature.
     *
     * ⚠️ Clearing `requested` here does not retry a FAILED read: a rejected
     * fetch never aborts this controller.
     */
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    // Through the ref, not a captured copy — the branch above may have swapped it.
    return () => controller.current?.abort();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(pairs), PRESET_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pairs]);
  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const pair of settled) {
      const key = presetPairKeyOf(pair.productId, pair.quantity, pair.projectId);
      if (requested.current.has(key)) continue;
      requested.current.add(key);
      api
        .resolveProductInclusionPreset(pair.productId, pair.quantity, signal, pair.projectId)
        .then((result) => {
          if (signal.aborted) return;
          setResolved((previous) => new Map(previous).set(key, result.data));
        })
        .catch(() => {
          if (signal.aborted) return;
          /*
           * Deliberately nothing. An absent entry is "not read", which
           * `inclusionPresetSeed` turns into "change nothing" — and there is no
           * loading flag to clear, which is why there is no `.finally` to guard.
           */
        });
    }
  }, [settled]);
  return resolved;
}

interface ProjectsState {
  rows: Project[];
  loading: boolean;
  failed: boolean;
  truncated: boolean;
}

/**
 * The active project list, resolved ONCE for the whole grid.
 *
 * ⚠️ The per-line hook in `@/lib/use-line-project` is deliberately not used: it
 * fetches per row, and a grid of twelve rows would issue twelve reads of the
 * same list. Its pure helper `productOfProject()` IS used — it is a function
 * over a list, so it carries no fetch, and it is the one copy of "a project
 * implies its product".
 *
 * ## 🔴 IT PAGES, AND THE CAP IT PAGES PAST WAS BEING HIT IN PRODUCTION
 *
 * ⚠️ *SUPERSEDED: one `listProjects` read of `perPage: 100`, reporting
 * `truncated` when `meta.total` exceeded what arrived.* `/projects` caps
 * `perPage` at **100, the server's own ceiling**, and production held **118
 * active projects on 2026-09-15**. One page arrived 18 short, and because the
 * order is `name` ascending it was **the same 18 every time** — the tail of the
 * alphabet, which reads as a naming convention rather than as a truncation.
 *
 * **All eight projects selling `Virtual Masterclasses` sat at ranks 106-113**,
 * so `projectSellsProduct` below matched nothing and that product's picker was
 * empty on every row of every grid, with nothing on screen saying why.
 *
 * ⛔ **The fix is paging, NEVER a bigger `perPage`** — 101 is a 422 and there is
 * no larger value to reach for. {@link api.listAllProjects} owns the loop, and
 * **throws rather than resolving short**.
 *
 * ⚠️ **`sort` is still sent, and under paging it is load-bearing rather than
 * cosmetic**: an unstable order across pages repeats rows and skips others, so
 * the list comes out right in COUNT and wrong in CONTENT.
 *
 * ⚠️ **`truncated` can no longer be true**, and the callers' handling of it is
 * kept: an incomplete list now arrives as `failed`, and the `true` branch is
 * still the correct treatment of one. **Not evidence that truncation is
 * reachable here.**
 */
function useActiveProjects(): ProjectsState {
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((all) => {
        if (controller.signal.aborted) return;
        setRows(all);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⛔ A partial list is DISCARDED, never rendered. A project list that
         * stops early looks identical to a complete one and this grid asserts
         * over it, so a flaky second page would become a confident falsehood.
         * The cost: the control locks and, since the failed-read footnote was
         * removed on 2026-09-05, nothing says why. That is the owner's call.
         */
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  /* Never true: see the doc above. A literal, so it reads as settled. */
  return { rows, loading, failed, truncated: false };
}

/* ── Presentational bits ──────────────────────────────────────────────────── */

function ErrorBlock({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      {children}
    </Notice>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </svg>
  );
}

/** The Price cell's "no price at all" toggle — a struck-through circle. */
function NoPriceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="m6.5 6.5 11 11" />
    </svg>
  );
}

function LinesIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M4 12h16M4 17h10" />
    </svg>
  );
}

function StatusBadge({ row, dirty }: { row: Row; dirty: boolean }) {
  if (row.state === 'saving') return <Badge tone="info">Saving…</Badge>;
  if (row.state === 'failed') return <Badge tone="danger">Not saved</Badge>;
  if (row.state === 'saved' && !dirty) return <Badge tone="success">Saved</Badge>;
  if (dirty) return <Badge tone="warning">{row.saved === null ? 'New' : 'Unsaved'}</Badge>;
  return <span className="text-xs text-fg-subtle">—</span>;
}

interface ControlProps {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  /**
   * The desktop grid. Labels are hidden and hints move into `placeholder` or
   * `title` — Xero's row is one control per cell with no sentence in it. The
   * stacked card below `md` is not a grid, so it keeps both.
   */
  compact: boolean;
  onChange: (patch: Partial<Draft>) => void;
}

function ProductControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  // `footnote` was a required prop. Its only sources were the convert-lock,
  // load-failure and truncation strings removed on 2026-09-05.
}: ControlProps & { options: ComboboxOption[] }) {
  const selectedLabel = options.find((option) => option.value === row.draft.productId)?.label;
  const control = (
    <Field label="Item" hideLabel={compact} error={problems.fieldErrors.productId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.productId}
          onChange={(value) => onChange({ productId: value })}
          options={options}
          placeholder="Choose a product"
          searchPlaceholder="Search products"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.productId)}
        />
      )}
    </Field>
  );
  if (!compact || selectedLabel === undefined) return control;
  return <div title={selectedLabel}>{control}</div>;
}

/**
 * The Description cell, which types over a LIVE derivation.
 *
 * While `description` is `null` the box IS `composeLineDescription`'s answer and
 * re-derives on every render — choosing a product, moving the project or adding
 * a city rewrites it in front of the person. The moment somebody types, it stops
 * following anything, and `Reset to derived` is the only way back.
 *
 * ⚠️ Typed text is never clobbered by a re-derivation. That is a deliberate
 * override, not an `a ?? b` fallback.
 */
function DescriptionControl({
  disabled,
  compact,
  derived,
  description,
  saved,
  error,
  onTyped,
  onReset,
  onRestore,
}: {
  disabled: boolean;
  compact: boolean;
  derived: string | null;
  description: string | null;
  /**
   * The wording this line is SAVED with — `row.saved?.description ?? null`.
   *
   * ⚠️ **A different question from `description` above, which is the DRAFT.**
   * The gap between the two is exactly what {@link onRestore} puts back.
   * `null` means the saved line derives, so there is no original to return to.
   */
  saved: string | null;
  error: string | undefined;
  onTyped: (value: string) => void;
  onReset: () => void;
  /** Put the SAVED wording back into the draft. */
  onRestore: () => void;
}) {
  const edited = description !== null;
  const value = edited ? description : (derived ?? '');
  /*
   * ⛔ **THE UNDO FOR `Reset to derived`, ADDED 2026-09-12 — AND THIS IS THE
   * GRID IT HAPPENED ON.**
   *
   * `Reset to derived` sends an explicit `null`, which DESTROYS the stored
   * wording, and nothing sat beside it to put that back. On **INV-0770** it was
   * pressed on lines carrying wording that had come from Xero — text nobody had
   * a second copy of — and the only sign was the cell quietly re-deriving and
   * the row going Unsaved like any other edit.
   *
   * ⚠️ **IT REACHES BACK ONLY AS FAR AS THE LAST SAVE, AND THAT LIMIT IS THE
   * POINT.** Once a `description: null` is SAVED, `row.saved.description` IS
   * null and there is nothing here to restore from. This stops a mis-click
   * becoming permanent; it does not undo one that already has.
   *
   * ⚠️ **The recovery after a save is the CUSTOMER'S OWN DOCUMENT.** The line's
   * wording is still in Xero, because a push to an approved or paid invoice
   * comes back `skipped` — which is what protected INV-0770. **A "restore from
   * Xero" control would close that half and does not exist**; recovering those
   * two lines was a hand repair against the Xero read.
   *
   * The test is `description !== saved`, not `edited` — after `Reset to
   * derived` the draft is `null` and the saved value is a string, which is
   * precisely the state this exists for and the one `edited` would miss.
   */
  const restorable = saved !== null && description !== saved;
  /*
   * 🔴 **THE STATE NOTE IS GONE — owner's stricter pass, 2026-09-05.** It read
   * "Your wording, sent to Xero as typed. It no longer follows the cities."
   * when edited, and "Derives from the item and its cities. Typing replaces the
   * whole string." otherwise. Same removal on the quote grid's copy.
   *
   * ⚠️ `edited` still decides the behaviour and `Reset to derived` is still the
   * only way back; what is gone is the reader being told which state they are
   * in, and that this string is what reaches Xero as typed.
   */
  return (
    <Field label="Description" hideLabel={compact} error={error}>
      {(fieldProps) => (
        <div className="grid gap-1">
          <Textarea
            {...fieldProps}
            autoGrow={compact ? { minRows: 2, maxRows: 6 } : { minRows: 3, maxRows: 10 }}
            value={value}
            onChange={(event) => onTyped(event.target.value)}
            placeholder={derived ?? 'Choose an item'}
            disabled={disabled}
            invalid={Boolean(error)}
          />
          {/*
            🔴 The sub-`md` card's "Fixed wording — adding a city will not
            update it." went with the hint in the owner's stricter pass,
            2026-09-05. Same removal on the quote grid.
          */}
          {(edited || restorable) && !disabled && (
            /* Two ways back, two destinations — one row, so a cell that gains
               the undo does not get taller than its neighbours. */
            <div className="flex flex-wrap items-center gap-1">
              {edited && (
                <Button type="button" variant="ghost" size="sm" onClick={onReset}>
                  Reset to derived
                </Button>
              )}
              {restorable && (
                /* `title` carries the text itself so it can be recognised
                   before it is restored — a value the cell is not currently
                   showing, which is the documented exception rather than
                   advice. */
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRestore}
                  title={`Put back: ${saved}`}
                >
                  Reset to original
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

/** Everything one row's project and city pickers need, resolved once. */
interface RowProject {
  /**
   * One option list per Current / Previous pill, the row's stored project pinned
   * into **both** — see `projectOptionsByScope`, which is where that invariant
   * lives. The pill is per row and its state sits in {@link ProjectControl}; the
   * `/cycles` read behind the split is once per grid.
   */
  options: Record<ProjectCycleScope, ComboboxOption[]>;
  cityOptions: MultiComboboxOption[];
  /** `null` means "cannot say" — never narrow or refuse on it. */
  declaredCityIds: Set<number> | null;
  /**
   * What a FLAGGED line means RIGHT NOW — the line's own project's cities, so
   * the flag can be shown together with the set it resolves to. `null` is
   * "cannot say", never "none".
   *
   * `name` is the bare city name the description composer joins; `label` adds
   * the country and is for the screen. They are carried together rather than
   * looked up twice, because the two are used for different jobs and only one
   * of them may reach {@link composeLineDescription}.
   *
   * ⚠️ **A DISPLAY value and the seed for unticking — never expanded into
   * `cityIds` on a save.** Writing "all cities" as a list of ids is exactly the
   * snapshot the flag exists to avoid.
   */
  allCitiesToday: { id: number; name: string; label: string }[] | null;
  /**
   * The regions the LINE's project declares. **`null` means the project could
   * not be resolved** — the list failed, was truncated at 100, or the line holds
   * a retired project an active-only fetch never returns — and is NOT the same
   * as a project that declares none, which is `[]`.
   *
   * ⚠️ **This is what the region picker OFFERS on a project-backed row**, on the
   * owner's decision: a line stays inside the geography its project actually
   * covers rather than being offered all of SEED's regions. A projectless row
   * has no project to take a set from and keeps the `/regions` master.
   *
   * ⚠️ **It never NARROWS what the line already holds.** Regions are validated
   * against the `/regions` master alone server-side, so a region absent from
   * this list is not a refusal — it is pinned back into the options and
   * labelled, exactly as a stray city is.
   */
  projectRegions: { id: number; name: string }[] | null;
  /**
   * ⚠️ **A DEFINITE "this project declares no cities", and nothing weaker.**
   *
   * True only when a project is chosen AND was resolved AND its own city list is
   * empty. Every unresolved state — no project picked yet, the list still
   * loading, failed, truncated, or a retired project — is `false`, because this
   * gates {@link showsRegionsControl} and that gate must fail towards hiding.
   *
   * ⚠️ Read off `selected.cities`, the project's own declaration, and **never
   * off {@link cityOptions}**, which also carries the row's stored cities the
   * project no longer declares. Those are real entries on the line and say
   * nothing about what the project offers today.
   */
  projectHasNoCities: boolean;
  projectHint: string | undefined;
  /** Per pill: "No projects in Current. 5 in Previous." or nothing at all. */
  projectFootnotes: Record<ProjectCycleScope, string | undefined>;
  cityHint: string | undefined;
  citiesUnavailable: boolean;
  /**
   * Whether this row shows the project block at all — the project picker, the
   * "all cities" flag and the city picker, which stand or fall together
   * because a city is only ever chosen through a project.
   *
   * ⚠️ **This reproduces a SERVER-side derivation and is therefore always one
   * project late.** "Sold through projects" is not a flag on a product; the
   * guard tests `product_id = ? AND is_active = 1`, which is byte for byte what
   * `GET /projects?productId=N&isActive=true` asks, so the answer here is only
   * as fresh as {@link useActiveProjects}. A project created a minute ago and
   * not yet re-read leaves the block hidden on a product that now has one.
   *
   * ⚠️ **So it fails towards SHOWING, on every UNRESOLVED state.** Loading,
   * failed, truncated, and any line that already holds a project all answer
   * `true` — hiding a control on a load failure is unrecoverable from the
   * user's side, and hiding one on a line that HAS a project would make that
   * project invisible and unremovable.
   *
   * 🔴 **THERE ARE NOW TWO WAYS TO GET `false`, AND ONLY ONE OF THEM IS A
   * VERDICT ABOUT THE PRODUCT.** ⚠️ *SUPERSEDED 2026-09-19: "`false` is only
   * ever returned when the list has settled and definitely names no project for
   * this product."* It is also returned for **a row with no product chosen**,
   * on the owner's decision of that date, which retires the project-first entry
   * path. The two are not interchangeable: one says *this item is not sold that
   * way*, the other says *nothing has been asked yet*. Anything reading this
   * field as the first meaning alone — `showsRegionsControl` did — has to test
   * `draft.productId` for itself.
   *
   * ⚠️ **`false` is not a fault and must never be reported as one.** `Meet Up`,
   * `Profile Evaluations` and `Miscellaneous` have no projects and never will;
   * a line with no project is the ordinary case. The block is simply absent,
   * with nothing said about it.
   *
   * ⚠️ Hiding is a RENDER branch. It never touches the draft, and the payload
   * builders take it as an explicit parameter — see {@link createPayload}.
   */
  showProject: boolean;
}

/**
 * The fallback for a row the resolver has not caught up with.
 *
 * Everything narrowing is at its widest-or-unknown setting: no options, no
 * declared cities (`null` — "cannot say", which never refuses), and
 * `showProject: true`, because an unresolved row is not a row that has been
 * shown to have no projects. The one thing that must never happen here is a
 * default that HIDES a control or NARROWS a set on the strength of not knowing.
 */
/** Every pill empty. Written as a `Record` so a third scope fails the build here. */
const NO_PROJECT_OPTIONS: Record<ProjectCycleScope, ComboboxOption[]> = {
  current: [],
  previous: [],
};

/** Nothing to say under any pill — see `projectScopeFootnote`. */
const NO_PROJECT_FOOTNOTES: Record<ProjectCycleScope, string | undefined> = {
  current: undefined,
  previous: undefined,
};

const ROW_PROJECT_UNRESOLVED: RowProject = {
  options: NO_PROJECT_OPTIONS,
  cityOptions: [],
  declaredCityIds: null,
  allCitiesToday: null,
  /*
   * Nothing resolved, so the picker falls back to the `/regions` master rather
   * than narrowing against a project it could not read — narrowing on an unknown
   * is how a stored region silently leaves a whole-set REPLACE.
   */
  projectRegions: null,
  /*
   * ⚠️ `false`, and it is the ONE default here that reads like a narrowing and
   * is not. Nothing resolved is not "this project declares no cities"; it is
   * "we cannot say", and this gate opens a control, so the unknown must not
   * open it. See {@link showsRegionsControl}.
   */
  projectHasNoCities: false,
  projectHint: undefined,
  projectFootnotes: NO_PROJECT_FOOTNOTES,
  cityHint: undefined,
  citiesUnavailable: true,
  showProject: true,
};

/**
 * ⚠️ Project and Cities are TWO columns and ONE decision. Choosing a project
 * clears the city selection in the SAME patch, because the server's guard runs
 * on the post-merge state and would otherwise refuse naming cities nobody
 * touched. Splitting the rendering must never split that patch.
 *
 * ⚠️ **`allCities` SURVIVES the move, and clearing `cityIds` is still
 * right.** The flag is not a set that a different project could strand — it
 * re-reads as "all of whatever project this line now names" — so dropping it
 * would throw away an intent the move does not contradict. The explicit set
 * held aside belonged to the OLD project and goes with everything else. The
 * resulting PATCH carries `{ projectId, allCities }`, which is precisely the
 * shape `patchRevalidatesStoredCities` says is safe.
 *
 * ⚠️ **`citiesLostByMovingProject` is not called from this grid, by either
 * control, and never was.** The city selection is cleared on the move rather
 * than carried across, so there is nothing to warn about. If such a warning is
 * ever added here, read that function's doc comment first: it must be asked
 * `allCities` before anything else, and must never be handed `line.cities` —
 * on a flagged line that is the RESOLVED set, and it would produce a confident,
 * named list of cities the move cannot possibly lose.
 */
function ProjectControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
}: ControlProps & { resolved: RowProject }) {
  /*
   * ⚠️ **Per ROW, and `current` on every mount — owner's instruction.** It is a
   * filter over what is offered and NOT a value, so it is deliberately local
   * state and is never written anywhere: a saved pill would be a second
   * authority over what a person may pick, and the row's own project already
   * says which half it came from.
   *
   * A shared grid-level pill would mean switching to Previous on row 3 silently
   * re-listing rows 1 and 2, which is a dropdown changing under somebody who
   * never opened it.
   */
  const [scope, setScope] = useState<ProjectCycleScope>(DEFAULT_PROJECT_CYCLE_SCOPE);
  const options = resolved.options[scope];

  return (
    <Field
      label="Project"
      /*
       * ⚠️ **Labelled even under `compact`, and that is not the rule slipping.**
       * `hideLabel` is right when the COLUMN HEADER labels the cell; since this
       * control moved inside the Item cell there is no header naming it, and
       * three unlabelled comboboxes stacked in one cell name nothing. This is a
       * label, not a hint — the no-prose-in-a-grid-cell rule is untouched, and
       * `compact` still drops the hint exactly as it did before.
       */
      error={problems.fieldErrors.projectId}
      {...(!compact && resolved.projectHint ? { hint: resolved.projectHint } : {})}
    >
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.projectId}
          onChange={(value) => onChange({ projectId: value, cityIds: [] })}
          options={options}
          emptyOptionLabel="No project"
          placeholder="No project"
          searchPlaceholder="Search projects"
          header={<ProjectScopePills value={scope} onChange={setScope} />}
          /*
           * ⚠️ Disabled on the WHOLE row's project list, never on this pill's.
           * An empty Current with rows under Previous must still open, or the
           * only control that could reach them is behind a disabled trigger.
           */
          disabled={disabled || PROJECT_CYCLE_SCOPES.every((s) => resolved.options[s].length === 0)}
          invalid={Boolean(problems.fieldErrors.projectId)}
          {...(resolved.projectFootnotes[scope]
            ? { footnote: resolved.projectFootnotes[scope] }
            : {})}
        />
      )}
    </Field>
  );
}

/**
 * The line's PRICE TIER (owner 2026-10-27), directly under the project picker.
 *
 * Offers the tiers the line's project prices for this product and currency,
 * plus the default tier always (`tierOptionsFor`). A stored tier the list no
 * longer offers is kept and named, so a saved value never renders blank.
 * `null` on the draft is the default tier; picking the default on a line that
 * stores none keeps it `null`, so that is not an edit. Matched by id, never by
 * a tier name.
 */
function TierControl({
  row,
  problems,
  disabled,
  onChange,
  tiers,
  offered,
  defaultTierId,
}: ControlProps & {
  tiers: readonly PriceTier[];
  offered: readonly PriceTier[];
  defaultTierId: number | null;
}) {
  const current = row.draft.priceTierId ?? defaultTierId;
  const options: ComboboxOption[] = offered.map((tier) => ({
    value: String(tier.id),
    label: tier.name,
  }));
  if (current !== null && !options.some((option) => option.value === String(current))) {
    const stored = tiers.find((tier) => tier.id === current);
    options.push({ value: String(current), label: stored?.name ?? String(current) });
  }
  const source = row.saved ?? row.convert ?? row.quoteConvert;
  const baseTier = source === null ? null : (source.priceTierId ?? null);
  return (
    <Field label="Price" error={problems.fieldErrors.priceTierId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={current === null ? '' : String(current)}
          onChange={(value) => {
            if (value === '') return;
            const id = Number(value);
            const next = id === defaultTierId && baseTier !== defaultTierId ? null : id;
            if (next !== row.draft.priceTierId) onChange({ priceTierId: next });
          }}
          options={options}
          searchPlaceholder="Search price tiers"
          disabled={disabled || options.length === 0}
          invalid={Boolean(problems.fieldErrors.priceTierId)}
        />
      )}
    </Field>
  );
}

/**
 * The Current / Previous pills, inside the project dropdown.
 *
 * ⚠️ **Current means current AND UPCOMING** — the owner's own clarification, and
 * the reason the rule is one `endDate >= today` comparison rather than two. See
 * `src/lib/project-cycle-scope.ts`.
 *
 * ⛔ **No hint, no sentence, no tooltip. The two labels are the whole
 * explanation**, which is what keeps this inside the no-helper-text rule while
 * rendering in a control that lives inside a desktop grid cell. The only prose
 * the popover may carry is the `footnote`, which states a fact about the data.
 *
 * `SegmentedControl` rather than hand-rolled pills: it already gives the group
 * label, `aria-pressed` per segment, a tick so selection is not colour alone,
 * and a constant width across selections. Its segments size to their content
 * and the popover is `width: max-content` with a 240px floor, so two short
 * labels fit at 320px.
 */
function ProjectScopePills({
  value,
  onChange,
}: {
  value: ProjectCycleScope;
  onChange: (next: ProjectCycleScope) => void;
}) {
  return (
    <SegmentedControl
      label="Show projects from"
      value={value}
      onChange={onChange}
      options={PROJECT_CYCLE_SCOPES.map((scope) => ({
        value: scope,
        label: PROJECT_CYCLE_SCOPE_LABELS[scope],
      }))}
    />
  );
}

/**
 * What "all cities" means, in one sentence, wherever it is said.
 *
 * One constant because it is said in three places — the tick box's `title`, the
 * wide layout's own line of prose, and the compact cell's tooltip — and three
 * wordings of one contract is how two of them come to be wrong.
 */
const ALL_CITIES_MEANING =
  'Follows the line’s project — a city added there appears here too.';

/**
 * The Cities cell: a tick box that means ALL, and — while it is off — the
 * explicit picker.
 *
 * ⚠️ **This cell is the quote grid's, not a second opinion about it.**
 * `QuoteLineItemsTable`'s `CitiesControl` is the reference — read it before
 * touching this one. Ticking PARKS the explicit set rather than clearing it;
 * unticking a line that arrived flagged opens the picker empty rather than
 * pre-filled with today's resolution, because a flagged line's `cities` is the
 * RESOLVED set, not a stored one, and a snapshot would silently stop
 * following the project. Do not add a "these cities will be lost" warning off
 * `citiesLostByMovingProject` here, or feed it a flagged line's `cities` — same
 * reason. A divergence from the quote grid here is a bug in whichever file
 * drifted.
 */
function CitiesControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
  allCitiesLocked,
}: ControlProps & {
  resolved: RowProject;
  /**
   * The "All cities" FLAG cannot be changed, while the city PICKER beside it
   * stays live.
   *
   * ⚠️ **`true` in convert mode, and it is the request that decides it.** A
   * `lineSet` entry carries `cityIds` and has **no `allCities` key at all**, so
   * ticking the box would collect a choice the conversion cannot express — and
   * this grid's own `convertShapeOf` refuses to read the flag for exactly that
   * reason. The picker underneath it is one of the things a conversion CAN carry,
   * which is why this locks the flag alone rather than the whole cell.
   */
  allCitiesLocked: boolean;
}) {
  const refusal = row.refusal;
  const invalidCityValues =
    refusal !== null && refusal.path === 'cityIds' ? refusal.offendingCityIds.map(String) : [];
  const fixIsOnTheProject =
    refusal !== null && refusal.path === 'cityIds' && refusal.fixedOn === 'the-project';
  /*
   * Refusal 6 — "that project declares no cities" — arrives on `cityIds` and is
   * fixed on `/projects/{id}`. Marking the control and saying no more sends the
   * person to a picker that is empty by definition.
   */
  const fixLocation = fixIsOnTheProject
    ? 'Fix it on the project: give it cities.'
    : null;

  const flagged = row.draft.allCities;
  // The picker's own hint describes choosing from a list, so it has nothing to
  // say to a flagged row. A refusal's location still does.
  const cityHint = fixLocation ?? (flagged ? undefined : resolved.cityHint);
  /*
   * ⚠️ **Both keys, joined — never `a ?? b` between them.** A 422 can name
   * `cityIds` and `allCities` in the same response, and this cell is the only
   * control either one can be marked on. Coalescing would silently drop one of
   * two true statements; dropping `allCities` in particular would be worse than
   * showing nothing, because a path that binds to no control still counts
   * towards "one field needs attention" while highlighting nothing.
   */
  const cityError =
    [problems.fieldErrors.cityIds, problems.fieldErrors.allCities]
      .filter((message): message is string => message !== undefined)
      .join(' ') || undefined;
  /*
   * The two states the switch can strand somebody in, and at most one is ever
   * true — the first needs the box ticked and the second needs it unticked — so
   * this is a decision between exclusive cases, not a coalesce between two live
   * facts. See this control's header for why neither is repaired automatically.
   */
  const parkedCities = flagged ? row.draft.cityIds.length : 0;
  const switchNote =
    parkedCities > 0
      ? `Saving discards the ${parkedCities} ${
          parkedCities === 1 ? 'city' : 'cities'
        } chosen here. Untick to keep ${parkedCities === 1 ? 'it' : 'them'}.`
      : !flagged && row.saved?.allCities === true && row.draft.cityIds.length === 0
        ? 'Was every city. Choose the ones it runs in, or tick again.'
        : null;
  /*
   * What the flag means TODAY. The project is the live source; a saved flagged
   * line's own `cities` is the same set resolved a moment earlier and stands in
   * when the project cannot be resolved at all. Not a drop between two true
   * things — one fact, read from the better source when there is one.
   */
  const resolvedCities = resolved.allCitiesToday;
  const resolvedText =
    resolvedCities === null ? '' : resolvedCities.map((city) => city.label).join(', ');

  /*
   * One key per change, and never both. The draft is allowed to hold a flag and
   * a parked set at once — {@link createPayload} and {@link updatePayload} are
   * what pick a single one for the wire — so this handler has nothing to
   * reconcile and deliberately reconciles nothing.
   */
  function setFlag(next: boolean) {
    onChange({ allCities: next });
  }

  /**
   * The flag's own line: the RULE first, the resolved list second and dated.
   *
   * ⚠️ Prose, never chips. Every chip in this grid is something a person put
   * there and can take away again; these are neither, and a row of them under a
   * ticked box is indistinguishable from a hand-picked set. The compact cell
   * truncates the list and the cell's `title` carries it whole.
   */
  const resolvedLine =
    resolvedCities === null ? (
      <p className="text-2xs text-fg-muted">
        {row.draft.projectId === ''
          ? 'No project on this line yet.'
          : 'This line’s project can’t be read, so its cities can’t be listed.'}
      </p>
    ) : resolvedCities.length === 0 ? (
      <p className="text-2xs text-fg-muted">
        Every city its project runs in — today, none.
      </p>
    ) : (
      <p className="text-2xs text-fg-muted" title={resolvedText}>
        <span className="font-medium text-fg-secondary">Every city its project runs in</span>
        {' — today '}
        {compact && resolvedCities.length > 3
          ? `${resolvedCities
              .slice(0, 2)
              .map((city) => city.label)
              .join(', ')} +${resolvedCities.length - 2} more`
          : resolvedText}
      </p>
    );

  const control = (
    <Field
      label="Cities"
      /* Labelled under `compact` too — see {@link ProjectControl}: no column
         header names this control any more. */
      {...(cityError ? { error: cityError } : {})}
      {...(!compact && cityHint ? { hint: cityHint } : {})}
    >
      {(fieldProps) =>
        flagged ? (
          /*
           * A GROUP rather than one control, and the Field's wiring lands on the
           * group: the tick box carries its own visible label, and giving it the
           * Field's `id` as well would make clicking the cell's "Cities" heading
           * toggle the flag. `aria-label` replaces the `htmlFor` association a
           * group cannot have.
           */
          <div
            className="grid gap-1"
            role="group"
            aria-label="Cities"
            id={fieldProps.id}
            aria-describedby={fieldProps['aria-describedby']}
          >
            <Checkbox
              label="All cities"
              checked
              disabled={disabled || allCitiesLocked}
              onChange={(event) => setFlag(event.target.checked)}
              title={ALL_CITIES_MEANING}
            />
            {resolvedLine}
            {!compact && <p className="text-2xs text-fg-subtle">{ALL_CITIES_MEANING}</p>}
            {switchNote !== null && <p className="text-2xs text-fg-muted">{switchNote}</p>}
          </div>
        ) : (
          <div className="grid gap-1">
            <MultiCombobox
              {...fieldProps}
              values={row.draft.cityIds.map(String)}
              invalidValues={invalidCityValues}
              onChange={(values) => onChange({ cityIds: values.map(Number) })}
              options={resolved.cityOptions}
              placeholder="Not city-specific"
              searchPlaceholder="Search this project’s cities"
              disabled={disabled || resolved.citiesUnavailable}
              invalid={Boolean(problems.fieldErrors.cityIds)}
              maxSelected={MAX_CITIES_PER_LINE}
              {...(compact
                ? { maxVisibleChips: 0, summaryNoun: { singular: 'city', plural: 'cities' } }
                : {})}
            />
            {/* Offered even on a row with no project: the refusal it collects
                names the missing project, which is more use than a control that
                is not there. Hidden only when there is nothing to tick it with —
                and on a conversion, where the request has no key for the flag. */}
            {!disabled && !allCitiesLocked && (
              <Checkbox
                label="All cities"
                checked={false}
                onChange={(event) => setFlag(event.target.checked)}
                title={ALL_CITIES_MEANING}
              />
            )}
            {switchNote !== null && <p className="text-2xs text-fg-muted">{switchNote}</p>}
          </div>
        )
      }
    </Field>
  );
  const withFixLocation =
    fixLocation === null ? (
      control
    ) : (
      <div className="grid gap-1">
        {control}
        <p className="text-2xs text-fg-muted">{fixLocation}</p>
      </div>
    );
  /*
   * The compact cell is ~125px wide, so everything the wide layout says in prose
   * has to survive as a tooltip instead. A flagged cell's tooltip carries the
   * rule AND the whole resolved list, because the cell itself can only fit a
   * truncated version of the second.
   */
  const cellTitle = flagged
    ? [ALL_CITIES_MEANING, resolvedText === '' ? null : `Today: ${resolvedText}`]
        .filter((part): part is string => part !== null)
        .join(' ')
    : cityHint;
  if (!compact || !cellTitle) return withFixLocation;
  return <div title={cellTitle}>{withFixLocation}</div>;
}

/**
 * SEED's regions of focus for this line — the geography a line has when it has
 * no project to take one from.
 *
 * ⚠️ **This is the control the feature shipped WITHOUT.** `regionIds` was
 * accepted on write and returned on every read while no invoice or quote screen
 * could send it: the picker was built into `InvoiceLineFormModal`, which nothing
 * mounted — ✅ **and that file was DELETED on 2026-09-19.** The name survives
 * here as the record of where the picker used to sit; it is not live code. See
 * this file's header — the grid is the live editing surface.
 *
 * ## ⚠️ TWO rows reach it now, and they offer DIFFERENT LISTS
 *
 * *SUPERSEDED, 2026-08-31: "no project to narrow against. This control only
 * exists on rows that have no project, so there is no second list to filter by."*
 * The owner added the second case, so there is now a project on most of the rows
 * that render this:
 *
 * | the row | the picker offers | “All regions” |
 * | --- | --- | --- |
 * | no project at all | the `/regions` **master** | **absent** |
 * | a project declaring **no cities** | that **project's own** regions | shown |
 *
 * A projectless line has nothing to be "all" OF — the only bounded set available
 * would be SEED's whole master, and ticking a box that meant *every region SEED
 * sells in* is a claim about the business, not about the line. So the checkbox
 * renders only where a project supplies the bound.
 *
 * ## ⛔ “All regions” IS A SELECT-ALL. IT IS NOT `allCities`, AND MUST NOT BECOME IT
 *
 * ⚠️ **There is no `allRegions` key on the wire and none may be invented** —
 * measured in `invoice-products.schema.ts` / `quote-products.schema.ts`, which
 * declare `allCities` and nothing beside it, and both are `.strict()`. So this
 * box writes an EXPLICIT set of ids and there is nothing else it could write.
 *
 * That makes the two boxes look alike and mean different things, which is the
 * whole hazard here:
 *
 * - **`allCities` is a live RULE.** The server re-resolves it against the
 *   project on every read, so a city added to the project tomorrow is on the
 *   line tomorrow. That is why its picker DISAPPEARS while it is ticked.
 * - **“All regions” is a SNAPSHOT.** A region added to the project tomorrow is
 *   not on this line. That is why the picker STAYS, showing the explicit chips
 *   it just selected — they are the value.
 *
 * ⚠️ **Do not "finish" this by hiding the picker to match the city cell.** The
 * chips are the one thing on screen that distinguishes a set from a rule.
 *
 * ⚠️ **The tick is DERIVED, never stored.** {@link Draft} gains no flag: the box
 * is checked when the draft equals the project's set and unchecks itself when the
 * project gains a region — the snapshot going stale in public, which is the
 * visible wrong answer rather than the silent one.
 *
 * ⚠️ **Rendered only by {@link showsRegionsControl}.** Hiding never touches
 * `draft.regionIds` — a regions write REPLACES the set.
 *
 * ⚠️ **Its quote twin must move with it** — see this file's sibling note.
 * A region recorded on a quote line is copied to the invoice line by `convert`,
 * so a control that exists on one document and not the other produces two
 * different answers about one sale.
 */
function RegionsControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  loading,
  projectRegions,
}: ControlProps & {
  options: MultiComboboxOption[];
  loading: boolean;
  /** The resolved project's own regions, or `null` when there is no project to take a set from. See {@link RowProject.projectRegions}. */
  projectRegions: { id: number; name: string }[] | null;
}) {
  /*
   * One key, one control. Unlike the city cell there is no second path a 422 can
   * arrive on — `regionIds` is the only region key this grid sends, and the
   * checkbox below writes that same key — so there is nothing to join and no
   * coalesce that could drop half of a true statement.
   */
  const error = problems.fieldErrors.regionIds;

  /*
   * ⚠️ Offered only when the project declares regions. A box reading "All
   * regions" over a project that has none can only ever select nothing, which is
   * the affordance-with-one-outcome this codebase refuses everywhere else.
   */
  const bounded = projectRegions !== null && projectRegions.length > 0 ? projectRegions : null;
  /*
   * ⚠️ Set equality, both directions. `length` alone would tick the box for a
   * line holding the right NUMBER of the wrong regions — including one the
   * project no longer declares, which is exactly the pinned-back option.
   */
  const chosen = new Set(row.draft.regionIds);
  const isAll =
    bounded !== null &&
    chosen.size === bounded.length &&
    bounded.every((region) => chosen.has(region.id));

  const control = (
    <Field
      label="Regions of focus"
      hideLabel={compact}
      error={error}
      {...(!compact && projectRegions === null
        ? // True only on the projectless row. On a project-backed one the cities
          // control directly above already states that the project has none, and
          // repeating it here would be prose for its own sake.
          { hint: 'Not sold through projects, so no cities. Records the markets instead.' }
        : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          {bounded !== null && (
            <Checkbox
              label="All regions"
              checked={isAll}
              disabled={disabled}
              onChange={(event) =>
                /*
                 * ⚠️ ONE key, and it writes the ids OUT — there is no flag to
                 * set. Unticking clears rather than handing a parked set back,
                 * because ticking never parked one: it replaced the selection
                 * with the project's, and re-ticking rebuilds exactly the same
                 * set.
                 */
                onChange({
                  regionIds: event.target.checked ? bounded.map((region) => region.id) : [],
                })
              }
            />
          )}
          <MultiCombobox
            {...fieldProps}
            values={row.draft.regionIds.map(String)}
            /*
             * The order handed back is kept as it comes. It is not stored and
             * {@link regionsKey} ignores it, so re-sorting here would be churn
             * that changes nothing — and sorting a set the user just arranged is
             * the kind of tidying that reads as a bug.
             */
            onChange={(values) => onChange({ regionIds: values.map(Number) })}
            options={options}
            placeholder="No regions"
            searchPlaceholder="Filter regions"
            disabled={disabled}
            invalid={Boolean(error)}
            loading={loading}
            /*
             * ⚠️ Unlike the CITIES control on this same grid, regions have no
             * `allCities`-style flag to be confused with, so the row is the
             * only way to say "all of them" here and cannot mean two things.
             */
            selectAll
            {...(compact
              ? { maxVisibleChips: 0, summaryNoun: { singular: 'region', plural: 'regions' } }
              : {})}
          />
        </div>
      )}
    </Field>
  );

  /*
   * ⚠️ **The one sentence that keeps this box from being read as `allCities`.**
   * It states the fact a reader cannot otherwise see — that the set is frozen —
   * and prevents a specific wrong conclusion about a value that decides Xero
   * tracking, which is the category the no-helper-text rule protects rather than
   * the one it bans. It renders only while the box is ticked, and never inside a
   * desktop grid cell, where no prose may go.
   */
  if (compact || !isAll) return control;
  return (
    <div className="grid gap-1">
      {control}
      <p className="text-2xs leading-snug text-fg-muted">
        This line’s own set, as it stands today. A region added to the project
        later is not added here.
      </p>
    </div>
  );
}


function CityChips({ cities }: { cities: LineCity[] }) {
  if (cities.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1">
      {cities.map((city) => (
        <li key={city.id}>
          <Badge tone="neutral" variant="outline" wrap>
            {cityLabel(city)}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

/**
 * Credit-note cities on a SAVED invoice line (owner 2026-10-27; contract
 * `credit-cities-contract.md`). Read-only, and read from `row.saved` only — the
 * server derives all three fields, a draft row has none.
 *
 * ⚠️ `undefined` on a field is an OLDER API and renders exactly as before:
 * nothing. Never collapse it to `[]` / `false` and then reason about it.
 *
 * - `cancelledCityIds` (APPROVED credit notes): struck-through chips.
 * - `creditedCityIds` minus those (Draft / Awaiting approval credit notes):
 *   muted chips reading "credit pending" — subtle, nothing has changed yet.
 *
 * Names come from the line's `effectiveCities` (else `cities` on an older API),
 * so an all-project-cities line (no stored cities) still names them; an id that is not on the line (the
 * project lost the city since) is skipped rather than shown as a bare number.
 */
function LineCreditCities({ line }: { line: InvoiceProduct | null }) {
  if (line === null) return null;
  const cancelledIds = line.cancelledCityIds;
  const creditedIds = line.creditedCityIds;
  if (cancelledIds === undefined && creditedIds === undefined) return null;
  const cancelled = new Set(cancelledIds ?? []);
  const pending = new Set((creditedIds ?? []).filter((id) => !cancelled.has(id)));
  // A stored city carries its country, so it is written with `cityLabel`; an
  // effective-only city (all-project-cities line) has just its name.
  const stored = new Map(line.cities.map((city) => [city.id, city]));
  const labelOf = (city: { id: number; name: string }): string => {
    const full = stored.get(city.id);
    return full === undefined ? city.name : cityLabel(full);
  };
  const marked = (line.effectiveCities ?? line.cities).filter(
    (city) => cancelled.has(city.id) || pending.has(city.id),
  );
  if (marked.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1">
      {marked.map((city) =>
        cancelled.has(city.id) ? (
          <li key={city.id}>
            <Tooltip label="Cancelled by credit note">
              <span>
                <Badge tone="neutral" variant="outline" wrap>
                  <span className="text-fg-subtle line-through">{labelOf(city)}</span>
                  <span className="sr-only"> (cancelled by credit note)</span>
                </Badge>
              </span>
            </Tooltip>
          </li>
        ) : (
          <li key={city.id}>
            <Badge tone="neutral" variant="outline" wrap>
              <span className="text-fg-muted">{labelOf(city)} · credit pending</span>
            </Badge>
          </li>
        ),
      )}
    </ul>
  );
}

/** The whole line is cancelled by an approved credit note. `undefined` (older API) shows nothing. */
function LineCancelledBadge({ line }: { line: InvoiceProduct | null }) {
  if (line?.isCancelled !== true) return null;
  return <Badge tone="danger">Cancelled</Badge>;
}

function NumberControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  field,
  label,
  placeholder,
  hint,
  step,
}: ControlProps & {
  field: 'quantity' | 'discount' | 'taxRate';
  label: string;
  placeholder: string;
  hint?: string;
  /** The product's quantity step, as the box's `step` and `min`. */
  step?: number | null;
}) {
  function emit(value: string) {
    if (field === 'quantity') onChange({ quantity: value });
    else if (field === 'discount') onChange({ discount: value });
    else onChange({ taxRate: value });
  }
  return (
    <Field
      label={label}
      hideLabel={compact}
      error={problems.fieldErrors[field]}
      {...(!compact && hint ? { hint } : {})}
    >
      {(fieldProps) => (
        <>
          <Input
            {...fieldProps}
            inputMode={field === 'quantity' ? 'numeric' : 'decimal'}
            value={row.draft[field]}
            onChange={(event) => emit(event.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            invalid={Boolean(problems.fieldErrors[field])}
            className="text-right tabular-nums"
            /*
             * A stepped product's box steps by its multiple. `type="number"`
             * only there, so every other box keeps its plain text entry.
             */
            {...(step !== undefined && step !== null ? { type: 'number', step, min: step } : {})}
            {...(compact && hint ? { title: hint } : {})}
          />
        </>
      )}
    </Field>
  );
}

/**
 * The Disc cell — **an AMOUNT or a PERCENTAGE, and it stores whichever it took.**
 *
 * 🔴 *SUPERSEDED, in full: "Discount — a PERCENTAGE, or a money amount
 * converted into one … The percentage is the only thing that exists … The
 * amount box is a client-side convenience over that column … Nobody should
 * later 'fix' this by adding a `discountAmount` field; there is no column for
 * one … The conversion is usually lossy, and the row says so … Refuse rather
 * than mangle."* `f_invoice_products.discount_amount` exists, so an amount is
 * stored as the amount and **nothing is converted anywhere**.
 *
 * What went with the conversion, and must not come back: the `DiscountAmount`
 * plumbing, the four refusals for amounts no percentage could express, and the
 * line under the box stating that the STORED percentage takes a different
 * figure off than the one just typed. That sentence was the apology for the
 * loss, and the loss was in POUNDS — £1,000 off an £18,000 line rounded to
 * `5.56%`, taking £1,000.08.
 *
 * ⚠️ **The two columns are MUTUALLY EXCLUSIVE**, so the mode is not decoration:
 * `draft.discountAmount === null` is percentage mode, a string is amount mode,
 * and {@link discountWriteOf} turns the mode into the pair the wire carries.
 * Setting one while the other stays stored is a 422 naming `discountAmount`.
 *
 * ⚠️ **The mode is DRAFT state now, not view state**, because there is a column
 * behind it — so switching modes is revertible, reaches `ROW_WRITE_KEYS`, and
 * is part of what makes a row dirty. It writes only the box on screen: touching
 * the other one is how a percentage silently changes while somebody types an
 * amount.
 *
 * ⚠️ *SUPERSEDED: "The mode is not part of the draft. It is per-row screen
 * state in the grid, so it cannot reach a payload, cannot make a row dirty and
 * cannot appear in `ROW_WRITE_KEYS`."* All three are the opposite now, and that
 * is exactly what the second column bought.
 */
function DiscountControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  onModeChange,
}: ControlProps & {
  /** `true` switches the box to money, `false` back to a percentage. */
  onModeChange: (toAmount: boolean) => void;
}) {
  const amountText = row.draft.discountAmount;
  const amountMode = amountText !== null;

  /*
   * Exactly one of the two can be wrong at a time, because exactly one is on
   * screen — `validateRow` reports the percentage at `discount` and the amount
   * at `discountAmount`, and a server 422 names whichever key was sent. Reading
   * the live one is what keeps a stale refusal on the hidden half from
   * colouring the control the person is actually using.
   */
  const error = amountMode ? problems.fieldErrors.discountAmount : problems.fieldErrors.discount;

  return (
    <Field
      /*
       * The accessible name says WHICH figure the box is taking, in both
       * layouts — under `compact` it is the only thing that does.
       *
       * ⚠️ **It no longer names the CURRENCY, and that is the point.** It read
       * `Discount amount in EUR`, so the same control announced itself
       * differently on every document and said only `Discount amount` on one
       * whose company was not yet chosen. The unit belongs on the figure; the
       * label names the kind of figure.
       */
      label={amountMode ? 'Discount (amount)' : 'Discount (percentage)'}
      hideLabel={compact}
      {...(error ? { error } : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          <Input
            {...fieldProps}
            inputMode="decimal"
            value={amountMode ? amountText : row.draft.discount}
            onChange={(event) => {
              // ⚠️ Writes ONLY the box that is on screen. The other column is a
              // different figure, not another spelling of this one.
              if (amountMode) onChange({ discountAmount: event.target.value });
              else onChange({ discount: event.target.value });
            }}
            placeholder={amountMode ? '0.00' : '0'}
            disabled={disabled}
            invalid={Boolean(error)}
            className="text-right tabular-nums"
          />
          {/*
            ⚠️ **The switch is a SEGMENTED CONTROL under the box, not a glyph
            inside it.** It was an icon-only button in the input's trailing
            slot, labelled `%` or the document's currency code — which made it
            read as a unit annotation on the number rather than as one of two
            choices, and made it say `EUR`, `GBP` or `Amt` depending on the
            document and on whether a company had been picked yet. Two named
            options, always the same two words, is what the owner asked for.

            Moving it out of the input also gives the figure back the 36px the
            trailing slot's `pr-9` was reserving.
          */}
          {!disabled && (
            <SegmentedControl
              label={DISCOUNT_MODE_GROUP_LABEL}
              value={discountModeOf(amountText)}
              options={DISCOUNT_MODE_OPTIONS}
              /*
               * ⚠️ Moves the switch and NOTHING else. Pre-filling the amount
               * from what the percentage takes off — which this used to do — is
               * the deleted conversion arriving from the other direction, and
               * it would overwrite a figure somebody typed with one nobody
               * chose.
               */
              onChange={(mode) => onModeChange(mode === 'amount')}
            />
          )}
        </div>
      )}
    </Field>
  );
}

const EDIT_RATE_OPTION = '__edit-rate__';

/**
 * "Tax rate" is TWO fields and ONE control.
 *
 * `taxRate` is our percentage and drives the arithmetic; `xeroTaxType` is
 * Xero's tenant-specific CODE and is **not derivable from the percentage** —
 * `ZERORATEDOUTPUT`, `NONE` and `EXEMPTOUTPUT` are all 0% in this tenant. They
 * were two boxes once and shipped disagreeing, with the money following the one
 * nobody was reading. Nothing here can set one alone.
 *
 * The bare `%` fallback is required rather than a nicety: the rate list belongs
 * to the tenant, so it is unavailable while Xero is disconnected, while the read
 * is failing, and when the tenant lists none.
 *
 * 🔴 **THE PERCENTAGE IS READ-ONLY ON A COMPANY-SOURCED LINE. THE XERO
 * CODE NEVER IS.** Owner, third position of 2026-08-30 — the sequence was
 * read-only, then always editable, then this. Mirrors
 * `QuoteLineItemsTable.tsx`, which carries the long version.
 *
 * *SUPERSEDED, and it is the version most likely to be remembered: "THE
 * CONTROL IS ALWAYS EDITABLE — the owner reversed the read-only ruling on
 * 2026-08-30 … Nothing here is locked by that field any more … The rate and the
 * CODE are editable TOGETHER, and that pairing survives the reversal — it is
 * now the reason for it."* The pairing argument is still sound and its
 * conclusion moved: both keys still travel in one patch, but only the picker
 * writes them, and the bare percentage box is withheld while the company owns
 * the figure.
 *
 * ⚠️ **The lock touches the `%` box and the option that reveals it, and
 * nothing else.** The Combobox stays enabled because it is the mitigation: a
 * rate the code disagrees with is `XERO_TAX_RATE_MISMATCH` at push time, and
 * choosing a different Xero rate rewrites the pair in agreement. A code choice
 * therefore moves a "read-only" percentage — that is the point of leaving the
 * code open, not a hole in the lock.
 *
 * ⚠️ **`taxRateSource` did NOT go away and is still required.** `'company'`
 * says the company states a rate and this line holds it — a gate on the
 * percentage AND a sentence to show. See {@link isCompanySourcedTaxRate}.
 *
 * ⚠️ **The escape hatch outranks the lock.** A `taxRate` the server has refused
 * is typeable here whatever the provenance says: an uncorrectable row is worse
 * than an editable one.
 */
function TaxRateControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  rates,
  ratesLoading,
  ratesError,
  connected,
  companySalesTax,
}: ControlProps & {
  rates: XeroTaxRate[];
  ratesLoading: boolean;
  ratesError: string | null;
  connected: boolean;
  /** For DISPLAY only — see {@link CompanySalesTax}. Nothing here is sent. */
  companySalesTax: CompanySalesTax;
}) {
  const stored = row.draft.xeroTaxType;
  // Only the tax types set on the billing-region defaults (owner, 2026-10-12).
  const allowedTaxCodes = useDocumentTaxCodes();
  /*
   * ⚠️ *SUPERSEDED, and it named the wrong migration: "Migration 096 writes the
   * company's `xeroSalesTaxRate` onto a line where the company has one."*
   * **096 is the region split.** The column arrived in
   * `092_add_companies_sales_tax_rate.sql`, and the seeding is SERVICE-LAYER
   * TypeScript (`input.taxRate ?? companyRate ?? undefined`) rather than SQL —
   * which matters, because somebody reading 096 to check this finds a migration
   * about regions and no answer either way.
   */
  /*
   * ⚠️ **WHERE THIS LINE'S RATE CAME FROM.** The server writes the company's
   * `xeroSalesTaxRate` onto a line where the company has one, and
   * `taxRateSource: 'company'` records that it did.
   *
   * 🔴 **IT IS A GATE ON THE PERCENTAGE AGAIN — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "IT IS NOT A GATE. THE OWNER REVERSED THAT ON
   * 2026-08-30 … The rate is the line's to change again, so this is read for
   * ONE purpose: a quiet hint … Do not reattach it to `disabled`. A hidden or
   * frozen tax control is how a line goes out at `0.00` with nobody able to say
   * so — and a read-only `0.00` is indistinguishable from a real company rate
   * of zero."*
   *
   * ⚠️ **That `0.00` objection is answered rather than ignored, and
   * `rateLocked` below is where the answer lives** — the lock requires a
   * percentage to actually be present, so nothing freezes over a blank or an
   * invented zero.
   *
   * ⚠️ **It gates the PERCENTAGE ONLY. Never the Combobox.** The code picker
   * open is what keeps `XERO_TAX_RATE_MISMATCH` clearable from this grid, and
   * it is the entire reason the lock could come back.
   *
   * Read from `row.saved`, so a row that has never been saved has no provenance
   * to report, shows no hint, and is never locked.
   */
  const companySourced = isCompanySourcedTaxRate(row.saved?.taxRateSource);
  /*
   * The sentence: where the figure came from, and the two places it can be
   * changed. It is the one place the company percentage is guaranteed to be
   * legible — with a code chosen the trigger reads the code's NAME and the
   * figure appears nowhere else in the cell. `undefined` when the line states
   * its own rate, so nothing is said about a control behaving ordinarily.
   */
  const companyReason = companySourced ? companyTaxRateReason(row.draft.taxRate) : undefined;
  /*
   * ⚠️ Declared HERE rather than beside `showRateBox`: the disconnected
   * fallback returns before that point and applies the identical rule.
   */
  const rateNeedsFixing = problems.fieldErrors.taxRate !== undefined;
  /*
   * ⚠️ **THE LOCK, AND BOTH EXTRA CONJUNCTS ARE LOAD-BEARING.**
   *
   * `row.draft.taxRate.trim() !== ''` answers the objection that reversed this
   * ruling once already: a frozen blank, or a frozen `0.00` the company never
   * stated, is indistinguishable from a real zero-rating. The server only
   * reports `'company'` when the company states a percentage, so this should
   * always hold — and where it somehow does not, the control **fails OPEN**
   * rather than freezing over a figure nobody chose.
   *
   * `!rateNeedsFixing` is the escape hatch and outranks the lock: a rate the
   * server has refused must be typeable here whatever the provenance says.
   *
   * ⚠️ **It is never passed to the Combobox.** Read the two `disabled` props
   * below — the `%` box takes it, the picker does not.
   */
  const rateLocked = companySourced && row.draft.taxRate.trim() !== '' && !rateNeedsFixing;
  const [rateBoxAsked, setRateBoxAsked] = useState(false);
  const unavailable = !connected || ratesError !== null || (!ratesLoading && rates.length === 0);
  if (unavailable) {
    return (
      <NumberControl
        row={row}
        problems={problems}
        /*
         * ⚠️ The caller's own `disabled` — read-only mode, a save in flight, a
         * permission denial — OR the company owning the figure.
         *
         * ⚠️ *SUPERSEDED: "and nothing else … A company-sourced rate no longer
         * disables anything; it is reported in the hint below instead."* It
         * disables again, per the owner's third position of 2026-08-30. This is
         * the ONE branch where the box is the whole control, so the hint below
         * carries the refusal AND both routes out; a locked box whose only
         * explanation is its own greyness is the failure this repo names
         * repeatedly.
         */
        disabled={disabled || rateLocked}
        compact={compact}
        onChange={onChange}
        field="taxRate"
        label="Tax rate"
        placeholder="0"
        /* The ownership clause leads when there is one — it is the reason the
         * box cannot be typed in, which the reader needs before an explanation
         * of a list they were not going to get anyway. (That sentence was
         * written for the original read-only ruling, went false during the
         * 2026-08-30 reversal with nobody correcting it, and is true again under
         * the third position — it leads exactly when `rateLocked` holds, and
         * explains the VALUE rather than a refusal in the two fail-open cases.) */
        hint={[
          ...(companyReason === undefined ? [] : [companyReason]),
          !connected
            ? 'Xero is not connected, so rates can’t be listed.'
            : (ratesError ?? 'This Xero organisation lists no tax rates.'),
          stored === ''
            ? 'This line uses the company’s Xero sales default for its tax code.'
            : `Xero tax code on this line: ${stored}`,
        ].join(' ')}
      />
    );
  }
  const offered = lineTaxRates(rates, stored, allowedTaxCodes);
  const options: ComboboxOption[] = offered.map((rate) => {
    if (!hasEffectiveRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        hint: `${taxRateHint(rate)} · already on this line — Xero publishes no percentage for it, so choosing it here would record no rate; choose a rate below`,
      };
    }
    const rounded = percentForDraft(rate.effectiveRate);
    const rephrased = rounded !== String(rate.effectiveRate);
    const hint = rephrased
      ? `${taxRateHint(rate)} · recorded here as ${rounded}%`
      : taxRateHint(rate);
    if (!isSalesTaxRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        hint: `${hint} · already on this line — Xero refuses an expense rate on a sales document, so choose a sales rate below`,
      };
    }
    return { value: rate.taxType, label: rate.name, hint };
  });
  // A stored code Xero no longer lists still has to be selectable, or the row
  // cannot be saved without silently changing its tax treatment.
  if (stored !== '' && !options.some((option) => sameTaxCode(option.value, stored))) {
    options.unshift({ value: stored, label: stored, hint: 'no longer listed by Xero' });
  }
  /*
   * 🔴 **A LOCKED LINE NEVER OPENS THE BOX — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "It is, since the owner's reversal of
   * 2026-08-30, so the provenance of the figure has no say in whether the box
   * opens."* It has a say again, and `!rateLocked` is where it is said.
   *
   * ⚠️ **`rateLocked` is a conjunct of the FIRST disjunct only, and that is the
   * escape hatch surviving intact.** `rateNeedsFixing` still opens the box on
   * its own, and it cannot contradict the lock, because `rateLocked` already
   * requires `!rateNeedsFixing` — the two can never both hold.
   *
   * ⚠️ Also belt and braces against ORDERING: `rateBoxAsked` is view state that
   * outlives a save, so a `'line'`-sourced row with the box open can come back
   * `'company'`-sourced. Testing the lock here shuts the box on that save
   * rather than leaving a stale open one typeable.
   */
  const showRateBox = (stored === '' && rateBoxAsked && !rateLocked) || rateNeedsFixing;
  /*
   * 🔴 **WITHHELD ON A LOCKED LINE — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "The server no longer undoes it, so the option
   * is offered on every line whose code is blank."*
   *
   * ⚠️ **Withheld rather than listed-and-disabled**, because it is the second
   * half of one decision with the box itself: an option that opens a control
   * nobody may type in is a promise the next click breaks. What replaces it is
   * `companyReason`, which names the company AND says a different Xero rate is
   * the other way out — a route still open on this very picker.
   */
  if (stored === '' && !showRateBox && !rateLocked) {
    options.push({
      value: EDIT_RATE_OPTION,
      label: 'Enter a percentage instead…',
      hint: 'A rate with no Xero code — the company default decides the treatment.',
    });
  }
  const typedRate = row.draft.taxRate.trim();
  /*
   * 🔴 **WHAT A BLANK PERCENTAGE ON THIS ROW WILL ACTUALLY BE — the defect the
   * owner reported on 11 September 2026: *"the tax rate in quote / invoice line
   * items should be the company master one and not the Xero default"*.**
   *
   * The DATABASE was already right. `invoice-products.service.ts` resolves a
   * created line's rate as `input.taxRate ?? companyRate ?? undefined`, so a
   * blank box on a new row has always been saved at the company's percentage.
   * What was wrong was the SCREEN: `companySourced` below reads
   * `row.saved?.taxRateSource`, which no unsaved row has, so the empty option
   * read `Xero default` and the Amount cell previewed tax at 0.00 beside it.
   *
   * ⚠️ **`row.saved === null` is the gate, and it is not tidiness.** On an
   * EXISTING line a blank percentage means *omit the key*, which leaves the
   * stored rate alone — the company's figure is not what happens there, so
   * naming it would be a label for a save nobody is about to make. Identical
   * reasoning to {@link previewInputOf}'s, and the two must agree: a cell that
   * says `20.00%` over an Amount computed at 0% is worse than either alone.
   *
   * ⚠️ **A COPIED quote row never reaches this control at all** — it renders
   * {@link QuoteConvertedTaxRateCell}, which reads the quote line's own rate —
   * so the `quoteConvert` arm `previewInputOf` needs has no counterpart here.
   *
   * ⚠️ **`!showRateBox`** keeps the existing rule that the echo stands down once
   * the number has a box of its own, so one cell never prints two percentages.
   *
   * ⚠️ **A rate of `"0.00"` IS a rate and reaches this** — 63 of the 100 live
   * companies measured on 11 September 2026 are genuinely 0% (`NONE` and
   * `ECZROUTPUTSERVICES`). Only `null`, *the company states none*, falls
   * through to the wording below. Never `?? 0`, never `'0.00'` read as absence.
   */
  const companyRateSeed =
    row.saved === null &&
    typedRate === '' &&
    !showRateBox &&
    companySalesTax.known &&
    companySalesTax.rate !== null &&
    companySalesTax.rate.trim() !== ''
      ? companySalesTax.rate.trim()
      : null;
  /*
   * ⚠️ **On a company-sourced line the WORDS change, and this is the only place
   * "which it is" survives without a hover.**
   *
   * ⚠️ *SUPERSEDED, and it was stale under BOTH later positions: "A disabled
   * `<button>` swallows pointer events in Chrome, so neither its own `title`
   * nor the wrapper's is reachable by hovering the trigger."* That was written
   * when `taxRateSource: 'company'` disabled the TRIGGER; it has not since the
   * 2026-08-30 reversal, and still does not under the third position — only the
   * `%` box locks, never the picker. The reason survives on a plainer fact:
   * **a `title` needs a pointer and a touch device has none**, so the source
   * has to be readable in the cell rather than only on hover.
   *
   * `Company rate` and `Xero default` are the same length to the character, so
   * this cannot change what the `table-fixed` column truncates, and the rate
   * still leads so truncation may only ever eat the wording. It says where the
   * figure came FROM, never what it amounts to — `0.00% · Company rate` is a
   * rate an administrator chose, and must not be readable as "no tax".
   */
  /*
   * ⚠️ **`Company rate` is REUSED rather than a new word being invented**, and
   * that is what keeps the character discipline the paragraph above depends on:
   * it is the same 12 characters as `Xero default`, so this branch cannot change
   * what the `table-fixed` column truncates. The rate still LEADS, so truncation
   * may only ever eat the wording — `20.00% · Company ra…` still cannot be
   * misread as "no tax".
   *
   * ⚠️ It says where the figure COMES FROM, never what it amounts to.
   * `0.00% · Company rate` is a treatment an administrator chose — 63 of 100
   * live companies — and must not read as an absence of one.
   */
  const emptyLabel =
    companyRateSeed !== null
      ? `${formatDecimalString(companyRateSeed)}% · Company rate`
      : typedRate === '' || showRateBox
        ? companySourced
          ? 'Company rate'
          : 'Xero default'
        : `${typedRate}% · ${companySourced ? 'Company rate' : 'Xero default'}`;
  const withheldExpense = expenseRateCount(rates);
  const withheldUnquantified = unquantifiedSalesRateCount(rates);
  const injected = offered.find((rate) => !isSalesTaxRate(rate) || !hasEffectiveRate(rate));
  const withheldClauses: string[] = [];
  if (withheldExpense === 1) {
    withheldClauses.push(
      'one expense rate is not listed — Xero refuses those on sales documents',
    );
  } else if (withheldExpense > 1) {
    withheldClauses.push(
      `${withheldExpense} expense rates are not listed — Xero refuses those on sales documents`,
    );
  }
  if (withheldUnquantified === 1) {
    withheldClauses.push(
      'one sales rate is not listed — no percentage published',
    );
  } else if (withheldUnquantified > 1) {
    withheldClauses.push(
      `${withheldUnquantified} sales rates are not listed — no percentage published`,
    );
  }
  const listSentence =
    injected !== undefined
      ? !isSalesTaxRate(injected)
        ? 'Sales rates only, plus the expense code on this line — it will not push until that changes.'
        : 'Sales rates only, plus the code on this line — no percentage published for it.'
      : withheldClauses.length === 0
        ? 'Sales rates only.'
        : `Sales rates only — ${withheldClauses.join('; ')}.`;
  /*
   * ⚠️ **WHICH rate "the company's Xero sales default" actually is — NAMED, and
   * never turned into a percentage.**
   *
   * The sentence below has always said blank means the company's default; with
   * the company read, it can say which one, which is the difference between a
   * rule and an answer. It is `rates` and not `offered` deliberately: this only
   * ever NAMES the rate, so a default that happens to be withheld from the
   * picker still has a true name to print.
   *
   * ⛔ **`name` ONLY. The company's `xeroSalesTaxRate` is the figure and the
   * server owns it** — resolving a percentage from this code against Xero's
   * published `EffectiveRate` would be a second implementation of a number
   * printed on a customer's document. See {@link CompanySalesTax}.
   *
   * ⚠️ **`sameTaxCode`, never `===`.** Casing is normalised server-side, so a
   * stored `output2` and a live `OUTPUT2` are one rate; an exact compare would
   * miss and this clause would silently vanish on exactly the companies it is
   * most wanted for. It is also why the code itself is NOT printed when the
   * lookup misses — a bare unlisted code beside a live list reads as the
   * "no longer listed by Xero" phantom rather than as an unresolved name.
   */
  const companyDefaultCode =
    companySalesTax.known &&
    companySalesTax.taxType !== null &&
    companySalesTax.taxType.trim() !== ''
      ? companySalesTax.taxType.trim()
      : null;
  const companyDefaultName =
    companyDefaultCode === null
      ? undefined
      : rates.find((rate) => sameTaxCode(rate.taxType, companyDefaultCode))?.name;
  const companyDefaultClause =
    companyDefaultName === undefined ? '' : ` This company’s default is ${companyDefaultName}.`;
  const footnote =
    rates.length > 0
      ? `${listSentence} Blank means the company’s Xero sales default, never “no tax”.${companyDefaultClause}`
      : undefined;
  const errorText =
    [problems.fieldErrors.xeroTaxType, showRateBox ? undefined : problems.fieldErrors.taxRate]
      .filter((text): text is string => text !== undefined)
      .join(' ') || undefined;
  /* The rate clause stands down when `companyReason` is carrying the figure, so
   * the `title` states it once. Not a `??` dropping one of two facts — they are
   * the same fact, and printing it twice reads as two numbers that match. */
  const fullMeaning =
    stored === ''
      ? `Blank means the company’s Xero sales default, never “no tax”.${companyDefaultClause}${
          typedRate === '' || companySourced ? '' : ` This line’s rate is ${typedRate}%.`
        }`
      : `Xero tax code on this line: ${stored}.`;
  const selectedLabel = options.find((option) => option.value === stored)?.label;
  const fullTitle = selectedLabel === undefined ? fullMeaning : `${selectedLabel} — ${fullMeaning}`;
  const control = (
    <Field
      label="Tax rate"
      hideLabel={compact}
      /*
       * ⚠️ The `disabledReason`, and ONLY in the card. `Field` renders a hint as
       * a paragraph under the control, which the desktop grid forbids inside a
       * cell — there it reaches the reader on the `title` below instead. The
       * card is where 320px lands, and 320px has no hover.
       */
      {...(!compact && companyReason !== undefined ? { hint: companyReason } : {})}
      {...(errorText ? { error: errorText } : {})}
    >
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={stored}
          onChange={(value) => {
            if (value === EDIT_RATE_OPTION) {
              setRateBoxAsked(true);
              return;
            }
            setRateBoxAsked(false);
            const rate = rates.find((candidate) => candidate.taxType === value);
            /*
             * ⚠️ The code alone when the rate carries no percentage, and both
             * together otherwise. Choosing "Xero default" deliberately leaves
             * `taxRate` alone: there is no percentage attached to "whatever the
             * company says", and writing a 0 there would silently rewrite the
             * line's arithmetic.
             */
            if (rate === undefined || !hasEffectiveRate(rate)) onChange({ xeroTaxType: value });
            else onChange({ xeroTaxType: value, taxRate: percentForDraft(rate.effectiveRate) });
          }}
          options={options}
          emptyOptionLabel={emptyLabel}
          placeholder={emptyLabel}
          searchPlaceholder="Search tax rates"
          loading={ratesLoading}
          /*
           * 🔴 **THE CALLER'S `disabled` AND NEVER `rateLocked`. This is
           * the whole reason the percentage may be locked at all.**
           *
           * A rate the code disagrees with is `XERO_TAX_RATE_MISMATCH` at push
           * time — a refusal naming this line that can only be cleared by moving
           * one of the two keys. With the percentage read-only this picker is
           * the only key that moves, and it writes the code and its own
           * percentage in ONE patch, landing the pair in agreement.
           *
           * ⚠️ **So a code choice DOES change a "read-only" rate, and that is
           * correct.** Adding `|| rateLocked` here to make the cell look
           * consistent strands every mismatched company-sourced line — which is
           * exactly what the 2026-08-30 reversal was reacting to.
           */
          disabled={disabled}
          invalid={Boolean(errorText)}
          {...(footnote ? { footnote } : {})}
        />
      )}
    </Field>
  );
  const stacked = !showRateBox ? (
    control
  ) : (
    <div className="grid gap-1.5">
      {control}
      <NumberControl
        row={row}
        problems={problems}
        /*
         * ⚠️ **The caller's own `disabled` only, and `rateLocked` is provably
         * false here rather than merely unlikely.** `showRateBox` is the only
         * way into this branch and both its disjuncts exclude the lock — the
         * first by testing `!rateLocked`, the second because `rateLocked`
         * itself requires `!rateNeedsFixing`. Passing the lock here would be
         * dead code; reading its absence as "the lock was forgotten" is the
         * misreading this comment exists to stop.
         */
        disabled={disabled}
        compact={compact}
        onChange={onChange}
        field="taxRate"
        label="Rate %"
        placeholder="0"
        hint={
          rateNeedsFixing
            ? 'Correct this line’s tax percentage here.'
            : 'No Xero tax code on this line, so the company default decides the treatment.'
        }
      />
    </div>
  );
  if (!compact) return stacked;
  /*
   * The grid's only voice. The ownership clause goes on the `title` rather than
   * into the cell, per the no-prose-in-a-grid-cell rule, and is appended rather
   * than substituted: what the cell shows and why it cannot be changed are two
   * facts, and the reader needs both.
   */
  return (
    <div title={companyReason === undefined ? fullTitle : `${fullTitle} ${companyReason}`}>
      {stacked}
    </div>
  );
}

/**
 * What the Tax rate cell says on a CONVERTED row, in its three honest states.
 *
 * ⚠️ **The unread state names its SOURCE and never a figure.** The company record
 * has not been read — in flight, failed, or the form has moved on — and reporting
 * that as an absence is a claim about a customer's tax treatment sourced from a
 * request that never landed.
 */
function convertedTaxRateCopy(rate: CompanySalesTax): { value: string; title: string } {
  if (!rate.known) {
    return {
      value: 'From the company',
      title:
        'This rate comes from the company record, which has not been read yet. It is not editable on a conversion.',
    };
  }
  /*
   * ⚠️ An empty string joins `null` rather than reaching the formatter, which
   * answers an em dash for it — `—% · Company rate` would be a figure-shaped
   * nothing. A DECIMAL column cannot produce one today; this costs a line and
   * removes the one output that could not be read as either state.
   */
  if (rate.rate === null || rate.rate.trim() === '') {
    /*
     * ⚠️ **The INVOICE sentence, and it is deliberately NOT the quote grid's.**
     * There the conversion goes ahead and the lines arrive at 0.00; here
     * `POST /deals/{id}/invoices` REFUSES with `COMPANY_HAS_NO_TAX_RATE` before
     * the transaction opens, so nothing is created. Telling somebody the lines
     * will arrive un-taxed would describe an outcome the server makes impossible
     * on this route — and in the reassuring direction, since they would go
     * looking afterwards for lines to correct rather than setting the rate now.
     * `CONVERTED_INVOICE_LINE_TAX_RATE` in `@/lib/deal-conversion` states the
     * same split, and the two are worded to agree.
     */
    return {
      value: 'No company rate',
      title:
        'This company states no tax rate, so raising an invoice from this deal is refused rather than billing no tax. Set one on the company record.',
    };
  }
  return {
    value: `${formatDecimalString(rate.rate)}% · Company rate`,
    title:
      'The company’s own rate, applied to every line by the conversion. Change it on the company record.',
  };
}

/**
 * The Tax rate cell on a CONVERTED row — a READOUT, and nothing that takes a
 * value.
 *
 * ⚠️ **THE COLUMN STAYS AND THE CONTROL GOES, and both halves are the point.**
 * The column stays because this grid is meant to read as the New Invoice grid
 * reads, and a column that vanishes on one path is a difference somebody has to
 * account for. The control goes because `POST /deals/{id}/invoices` has no
 * `taxRate` key and no `xeroTaxType` key — the body schema is `.strict()`, so
 * either would be a 422 — which means a live dropdown here would take a choice
 * and discard it silently. **Read-only is not the same as ignored**, and only the
 * first is honest.
 *
 * ⚠️ **`TaxRateControl` is deliberately NOT reused with `disabled`.** It reads
 * `row.draft.taxRate` and `row.draft.xeroTaxType`, which a converted row does not
 * have — see {@link convertRowOf} — so a disabled instance would render a frozen
 * `0%` and a frozen "use the company default", both of them values nobody
 * measured. **A disabled control still makes a claim**, and it would be making
 * the one claim this cell exists to avoid.
 *
 * ⚠️ **Nothing here computes the percentage from a tax CODE.** The server derives
 * and owns this figure; a second implementation of it, printed on an invoice, is
 * the shape this integration has already been wrong about three times.
 */
function ConvertedTaxRateCell({ compact, rate }: { compact: boolean; rate: CompanySalesTax }) {
  const { value, title } = convertedTaxRateCopy(rate);
  if (compact) {
    /*
     * ⚠️ No prose inside a desktop grid cell — the rule this whole file is
     * written against. The sentence lives on a `title`, which is where every
     * other cell's explanation already went.
     */
    return (
      <span className="block truncate text-sm text-fg-muted" title={title}>
        {value}
      </span>
    );
  }
  /*
   * The stacked card is not a grid, so it keeps its label — and the sentence
   * survives the no-helper-text prohibition on the exception that matters here:
   * it says WHY a control is absent. Without it an inert cell reads as a
   * temporarily unavailable dropdown.
   */
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-fg-secondary">Tax rate</span>
      <span className="text-sm text-fg-muted">{value}</span>
      <span className="text-2xs leading-snug text-fg-muted">{title}</span>
    </div>
  );
}

/**
 * The Tax rate cell on a COPIED QUOTE row — a readout, of a DIFFERENT figure
 * from {@link ConvertedTaxRateCell}'s.
 *
 * ⚠️ **THIS IS THE QUOTE LINE'S OWN RATE, NOT THE COMPANY'S, and conflating the
 * two would print a number the invoice will not hold.** A deal line has no tax
 * column at all, so that path can only show one company-wide figure; a quote
 * line carries its own `tax_rate` AND its own `xero_tax_type`, and
 * `copyLinesToInvoice` carries both across AS A PAIR. So the honest reading here
 * is per row, off the line, and it is exactly what the invoice line will hold.
 *
 * ⚠️ **A READOUT because `lines` HAS NO TAX KEY — an explicit backend ruling,
 * not an omission.** `quoteLineOverrideSchema` carries a ⛔ note saying neither
 * `taxRate` nor `xeroTaxType` may ever be added: the two feed two different
 * systems (EMERGE's generated `line_tax` from the percentage, Xero's own rate
 * table from the code), so a request able to move one of them is a request able
 * to make the two documents disagree with nothing looking wrong on either. The
 * body is `.strict()`, so a live control would take a choice and collect a 422 —
 * or worse, be dropped. **Read-only is not the same as ignored.**
 *
 * ⚠️ **`TaxRateControl` is deliberately NOT reused with `disabled`**, for the
 * reason its sibling gives: a disabled control still makes a claim, and the
 * claim it would make is that the value is merely temporarily unavailable.
 *
 * Where the rate is edited instead: on the QUOTE, before converting.
 */
function QuoteConvertedTaxRateCell({ compact, rate }: { compact: boolean; rate: string }) {
  /*
   * ⚠️ An empty string joins nothing rather than reaching the formatter, which
   * answers an em dash — `—%` would be a figure-shaped nothing. `tax_rate` is
   * `NOT NULL` on a quote line so this is unreachable today; it costs a line and
   * removes the one output that could be read as either state.
   */
  const value = rate.trim() === '' ? 'From the quote' : `${formatDecimalString(rate)}%`;
  const title =
    'The rate on the quote line, copied to the invoice unchanged. It is not editable on a conversion — change it on the quote.';
  if (compact) {
    /* No prose inside a desktop grid cell; the sentence lives on a `title`. */
    return (
      <span className="block truncate text-sm text-fg-muted" title={title}>
        {value}
      </span>
    );
  }
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-fg-secondary">Tax rate</span>
      <span className="text-sm text-fg-muted">{value}</span>
      <span className="text-2xs leading-snug text-fg-muted">{title}</span>
    </div>
  );
}

/**
 * ⚠️ **The catalogue FIGURE, not the word "catalogue".**
 *
 * `catalogPrice` is three-valued and the three are not interchangeable:
 *
 * - a string — the product's own `unitPrice`, including `"0.00"`, which is a
 *   real price of zero;
 * - `null` — the product is read and records NO price. Blank on this line then
 *   snapshots nothing, so the line carries no price either;
 * - `undefined` — nothing has read it. The product list is capped at 100 rows
 *   and can fail outright, so this is a genuine third state and must never be
 *   rendered as `0.00` or as "no catalogue price".
 *
 * Never `Number()` it and never `?? 0`; `formatDecimalString` is the renderer.
 * None of this changes what is SENT — `inherit` still omits `unitPrice`, which
 * is the only spelling of "snapshot the catalogue price".
 */
function PriceControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  currency,
  catalogPrice,
  proRated,
  projectPriced,
  repricedPrice,
  allowNoPrice,
  unitLabel,
}: ControlProps & {
  currency: string | null;
  /** What blank inherits: the project price, else the catalogue price pro-rated to the line's cities. */
  catalogPrice: string | null | undefined;
  /** Whether `catalogPrice` is a pro-rated figure rather than the catalogue's own. */
  proRated: boolean;
  /** Whether the inherited (or repriced) figure is a PROJECT price rather than the catalogue's. */
  projectPriced: boolean;
  /**
   * A SAVED line's untyped price, repriced by the server for its moved cities —
   * `undefined` when the stored price stands. Shown in the box; never sent.
   */
  repricedPrice: string | null | undefined;
  /** The product's singular unit — the price is "per <unitLabel>". */
  unitLabel: string | null;
  /**
   * Whether the "record no price at all" toggle may be offered.
   *
   * ⚠️ **REQUIRED, and `false` in convert mode.** `PriceMode` is a TRI-state and
   * the deal→invoice contract is not: it carries a price string or it carries
   * nothing, and there is no `unitPrice: null` key to send on `lineSet` or on
   * `lines`. Offering the toggle there would offer a state the request cannot
   * express — a control whose action cannot be performed. Not optional, so a
   * fourth call site cannot inherit a default that happens to be right for three
   * of them.
   */
  allowNoPrice: boolean;
}) {
  const mode = row.draft.priceMode;
  /*
   * ⚠️ **A CONVERTED row is not a new one**, and `isNew` decides which sentence
   * the empty box gets. On a new invoice line blank snapshots the catalogue
   * price; on a converted row blank on a line that HAD a price is a refusal,
   * because the request has no way to say "no price". Three states, not two.
   */
  const isNew = row.saved === null && row.convert === null;
  const noPrice = mode === 'none';
  const catalogueFigure =
    typeof catalogPrice === 'string'
      ? `${formatDecimalString(catalogPrice)}${currency ? ` ${currency}` : ''}`
      : null;
  const hint = noPrice
    ? 'No price at all — not a price of zero.'
    : row.convert !== null
      ? /*
         * ⚠️ Nothing about "blank" here, because blank is not an instruction on
         * this path — it is either a line that records no price or a figure
         * somebody has cleared, and the second is refused by name.
         */
        currency
        ? `In ${currency}.`
        : undefined
      : mode === 'inherit'
        ? isNew
          ? catalogueFigure !== null
            ? projectPriced
              ? `Blank uses the project price, ${catalogueFigure}.`
              : proRated
                ? `Blank uses the catalogue price for these cities, ${catalogueFigure}.`
                : `Blank uses the catalogue price, ${catalogueFigure}.`
            : catalogPrice === null
              ? 'No catalogue price, so blank leaves this line with none.'
              : 'Blank uses the catalogue price.'
          : repricedPrice !== undefined
            ? projectPriced
              ? `Blank uses the project price, ${formatDecimalString(repricedPrice)}.`
              : `Blank uses the catalogue price for these cities, ${formatDecimalString(repricedPrice)}.`
            : `Blank leaves ${formatDecimalString(row.saved?.unitPrice ?? null)} unchanged.`
        : currency
          ? `In ${currency}.`
          : undefined;
  const toggleLabel = noPrice
    ? 'Record a price on this line'
    : 'Record this line as carrying no price at all';
  return (
    <Field
      label="Price"
      hideLabel={compact}
      error={problems.fieldErrors.unitPrice}
      {...(!compact && hint ? { hint } : {})}
    >
      {(fieldProps) => (
        <>
        <Input
          {...fieldProps}
          inputMode="decimal"
          value={
            noPrice
              ? ''
              : /*
                 * A saved line's untouched price follows its cities: the box
                 * shows what the server will store, while the draft keeps the
                 * stored figure so the PATCH still omits `unitPrice`.
                 */
                mode === 'value' && typeof repricedPrice === 'string'
                ? repricedPrice
                : row.draft.price
          }
          onChange={(event) => {
            const next = event.target.value;
            /*
             * ⚠️ Emptying the box returns to `inherit` — the key is OMITTED, so
             * a new line snapshots the catalogue and an existing one keeps what
             * it has. It must never become `value` with an empty string.
             */
            onChange({ price: next, priceMode: next === '' ? 'inherit' : 'value' });
          }}
          /*
           * ⚠️ The FIGURE when it is known, so somebody deciding whether to
           * override the catalogue can see what they would be overriding. The
           * currency code is left off the placeholder alone: the box is 124px
           * of content and the code is already on the Amount cell beside it.
           */
          placeholder={
            noPrice
              ? 'No price'
              : row.convert !== null
                ? /*
                   * The deal's own figure is already IN the box; the placeholder
                   * only shows once somebody empties it, where it says what a
                   * price looks like rather than promising a default that does
                   * not exist on this path.
                   */
                  '0.00'
                : isNew
                  ? typeof catalogPrice === 'string'
                    ? formatDecimalString(catalogPrice)
                    : catalogPrice === null
                      ? 'No catalogue price'
                      : 'Catalogue'
                  : typeof repricedPrice === 'string'
                    ? formatDecimalString(repricedPrice)
                    : 'Unchanged'
          }
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.unitPrice)}
          className="text-right tabular-nums"
          {...(hint ? { title: hint } : {})}
          {...(disabled || !allowNoPrice
            ? {}
            : {
                trailingSlot: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    leadingIcon={<NoPriceIcon />}
                    aria-pressed={noPrice}
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    onClick={() =>
                      onChange(
                        noPrice
                          ? { priceMode: 'inherit', price: '' }
                          : { priceMode: 'none', price: '' },
                      )
                    }
                  />
                ),
              })}
        />
        {unitLabel !== null && (
          <span className="block truncate text-right text-xs text-fg-muted" title={`per ${unitLabel}`}>
            per {unitLabel}
          </span>
        )}
        </>
      )}
    </Field>
  );
}

/**
 * Amount: the figure, and — on every row a request can express it on — a box to
 * type one INTO. The three conversion branches below stay read-only and say why;
 * everything else is {@link EditableAmount}.
 *
 * ⚠️ The preview is `@/lib/decimal`'s and never re-derived here. The order is
 * correctness: subtotal rounded, tax charged on the ALREADY-ROUNDED subtotal,
 * total as the sum of the two rounded parts. Rounding once at the end — the
 * obvious tidy-up — disagrees by a cent on some rows, and the stored value is
 * the one Xero reads. A row with no price shows no preview at all, because an
 * unpriced line and a line priced at zero produce identical strings — which is
 * exactly the row where the box below sets the PRICE.
 */
function AmountCell({
  row,
  dirty,
  currency,
  preview,
  /**
   * ⚠️ **REQUIRED, and it exists for the ADDED row.** A row somebody added on the
   * convert path has neither a `saved` line nor a `convert` one, so without this
   * it falls through to the ordinary draft branch and renders
   * `computeGeneratedLinePreview` — a figure computed at the blank draft's zero
   * tax, on a document whose tax comes from the COMPANY. That is the exact
   * "confident number about the wrong document" the carried-over branch below
   * refuses to print, arrived at by a different route.
   */
  convertMode,
  input,
  disabled,
  onChange,
}: {
  row: Row;
  dirty: boolean;
  currency: string | null;
  preview: GeneratedLinePreview | null;
  convertMode: boolean;
  /**
   * The SAME resolved figures `computeGeneratedLinePreview` reads for this row.
   *
   * ⚠️ **Passed in rather than re-derived, and that is the point.** The box
   * solves backwards from the figure the preview solves forwards to; deriving
   * the price twice is two chances to disagree, and the disagreement would be a
   * discount computed against a price the row is not actually using.
   */
  input: GeneratedLineInput;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  /*
   * ⚠️ **A CONVERTED ROW SHOWS NO PREVIEW, and that is a decision rather than an
   * omission.** `computeGeneratedLinePreview` is the right answer on the saved
   * and draft grids because they are previewing the document the figures belong
   * to. Here they do not: the figure that matters after a conversion is the
   * INVOICE line's, whose tax comes from the COMPANY server-side — so a
   * deal-shaped preview, computed at the zero tax `f_deal_products` has no column
   * for, would be a confident number about the wrong document. It would also be
   * wrong in the reassuring direction, reading as a total somebody could quote
   * down the phone.
   *
   * So an untouched row shows the deal's own stored `lineTotal` — at least true
   * of the deal — and an edited one says the figure is being recalculated,
   * because that is the whole of what is known.
   */
  if (convertMode && row.convert === null) {
    /*
     * ⚠️ A line ADDED here has no stored figure on either document and no honest
     * preview — its tax is the company's and is applied server-side — so it says
     * what is being done rather than a number nobody measured.
     */
    return <span className="text-xs text-fg-subtle">Calculated on conversion</span>;
  }
  if (row.convert !== null) {
    if (dirty) {
      return <span className="text-xs text-fg-subtle">Recalculated on conversion</span>;
    }
    const dealTotal = row.convert.lineTotal;
    return (
      <span className="tabular-nums text-sm text-fg">
        {formatDecimalString(dealTotal)}
        {dealTotal !== null && currency && (
          <span className="ml-1 text-xs text-fg-muted">{currency}</span>
        )}
      </span>
    );
  }
  return (
    <EditableAmount
      row={row}
      dirty={dirty}
      currency={currency}
      preview={preview}
      input={input}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

/**
 * The Amount cell, **typed into**.
 *
 * Owner's instruction, 2026-09-12: *"make the line item amount editable and auto
 * calc between sku price, discount, line item amount — 2 filled should auto
 * calculate the third."* Ported from the quote grid, which is the reference: the
 * two must not word or solve this differently, because a person billing a quote
 * meets both on the way to one document.
 *
 * ⛔ **THE AMOUNT IS A GENERATED COLUMN AND IS NEVER SENT.** `line_subtotal`,
 * `line_tax` and `line_total` are `GENERATED STORED`; naming one in a write is a
 * 422 from the driver, which is why `ROW_WRITE_KEYS` has never carried them.
 * So typing here is not a write — it is a QUESTION: *which writable field,
 * changed to what, makes this line come out at this figure?* `solveLineAmount`
 * in `@/lib/decimal` is the one place that is answered, shared with the quote
 * and deal grids, and it answers by WHICH FIELD IS MISSING:
 *
 *   no price yet  ->  the amount SETS the price      (amount ÷ quantity)
 *   a price set   ->  the amount sets the DISCOUNT   (quantity × price − amount)
 *
 * which is the three-way rule: fill any two of price, discount and amount and
 * the third follows.
 *
 * ⚠️ **THE BOX IS THE PRE-TAX SUBTOTAL — owner's decision, taken against the
 * alternative.** Tax is charged on the already-rounded subtotal, so solving back
 * from a tax-INCLUSIVE total is not always possible to the penny: at 20% the
 * reachable totals step in 1.2p and a figure between two of them can only be
 * approximated. Against the subtotal every answer is exact. Where the line
 * carries tax, the tax-inclusive total is shown UNDER the box — it is the figure
 * that used to be in this cell, and removing it silently would change what a
 * money column means.
 *
 * 🔴 **THE DISCOUNT IS SOLVED IN THE MODE THE LINE IS ALREADY IN — owner's
 * instruction, 2026-09-13: *"on line item amount change the discount type should
 * not change and be calculated in the type it is in."*** ⚠️ *SUPERSEDED: "**The
 * discount is solved as an AMOUNT and never as a percentage**, and the line's
 * mode is flipped to match, saying so … **Never write a rounded percentage
 * here.**"*
 *
 * ⚠️ **THE ARITHMETIC THAT MOTIVATED THAT OLD RULE IS UNTOUCHED, WHICH IS WHY
 * THE COST IS SAID RATHER THAN AVOIDED.** `discount` is `DECIMAL(5,2)`, so the
 * subtotals a percentage can express are `quantity × unitPrice × 0.0001` apart —
 * **£1.80 on an 18,000 line**. Type 17,000 against an 18,000 gross and the
 * nearest percentage is 5.56%, a discount of 1,000.80 and a line of 16,999.20.
 * That is the loss migration 111 exists to have removed and
 * `percentageForAmount()` was deleted for — so it is **REPORTED**: `exact:
 * false` names the percentage written AND the figure the line will actually come
 * to. ⛔ **It is never rounded away and never refused**; the owner chose to keep
 * the mode and be told, over an exact figure in a column nobody picked.
 *
 * ⚠️ **A LINE WITH NO DISCOUNT STILL SOLVES AN EXACT AMOUNT, AND THAT IS A
 * DECISION, NOT THE OLD BEHAVIOUR SURVIVING BY ACCIDENT.** `discountAmount ===
 * null` is percentage mode, so every untouched line in the grid reads as
 * percentage — the DEFAULT, not a choice. Preserving it would make the
 * commonest edit here lossy for nobody's benefit. {@link percentageModeIsChosen}
 * is the one place that question is answered, and on a fresh line the cell
 * solves an exact `discountAmount` and still says the mode flipped.
 *
 * ⚠️ **It commits on BLUR, not on the keystroke.** Solving mid-type would write
 * a discount off `6`, then `60`, then `600` on the way to `6000`, and each of
 * those is a real edit to a real money column that the row's Unsaved badge would
 * report. The derived figure returns the moment the box is emptied.
 */
function EditableAmount({
  row,
  dirty,
  currency,
  preview,
  input,
  disabled,
  onChange,
}: {
  row: Row;
  dirty: boolean;
  currency: string | null;
  preview: GeneratedLinePreview | null;
  input: GeneratedLineInput;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  /** `null` means the box is showing the derived figure rather than a typed one. */
  const [typed, setTyped] = useState<string | null>(null);
  /**
   * What the last commit did, or why it could not.
   *
   * ⚠️ **NOT helper text.** It renders only after somebody has typed and only
   * where the outcome is otherwise invisible — a refusal, a mode flip, or a
   * division that does not come out even. The no-helper-text rule forbids prose
   * attached to a control; this names a state the reader cannot otherwise see,
   * which is the documented exception.
   */
  const [note, setNote] = useState<{ tone: 'error' | 'warn'; text: string } | null>(null);

  /*
   * ⚠️ **A COPIED QUOTE ROW HAS AN HONEST FIGURE, and `row.saved` is not where
   * it lives.** `saved` is `null` on every one of these rows, so reading it
   * alone would leave an untouched copied line blank beside a real quantity and
   * a real price — reading as a line worth nothing.
   *
   * ⚠️ **UNLIKE THE DEAL PATH, THIS FIGURE IS TRUE, and the reason is the tax
   * rate.** {@link AmountCell} refuses to preview a converted DEAL row because
   * `f_deal_products` has no tax column and the invoice's rate comes from the
   * COMPANY, so any figure would be computed at a zero that is not the
   * document's — which is why those rows never reach this component at all. A
   * QUOTE line carries its own `tax_rate` and the conversion copies it verbatim,
   * so this row's inputs are exactly the invoice line's — see
   * `quoteConvertRowOf`, which seeds the rate for this reason.
   *
   * So an untouched copied row shows the QUOTE's own generated figures, which
   * are the database's arithmetic rather than ours and are what the invoice line
   * will hold to the penny.
   */
  const clean = row.saved ?? row.quoteConvert;
  const derived = dirty ? (preview?.lineSubtotal ?? null) : (clean?.lineSubtotal ?? null);
  const taxed = dirty ? (preview?.lineTotal ?? null) : (clean?.lineTotal ?? null);
  /*
   * The two agree on a line with no tax — printing the same figure twice would
   * read as a second, different fact.
   */
  const showsTaxed = taxed !== null && derived !== null && taxed !== derived;

  const shown = typed ?? (derived === null ? '' : derived);

  /*
   * ⚠️ **READ-ONLY IS PLAIN TEXT, NEVER A DISABLED BOX.** The house rule, and it
   * is about what the two SAY: a disabled control reads as something the person
   * has not earned the right to use, where text reads as a fact. A `viewer`
   * meets this cell on every line of every invoice, and a greyed-out input on
   * all of them would report a permissions problem that does not exist.
   *
   * It also covers the transient `saving` state, where an input would invite a
   * keystroke that goes nowhere.
   *
   * The figure is the tax-INCLUSIVE total — what this cell showed before it
   * became editable — so a read-only reader sees exactly what they saw before,
   * and a copied quote row still reads from `clean` rather than going blank.
   */
  if (disabled) {
    const readOnlyFigure = dirty ? (preview?.lineTotal ?? null) : (clean?.lineTotal ?? null);
    return (
      <span
        className="tabular-nums text-sm text-fg"
        {...(dirty ? { title: 'Preview — recalculated by the server on save.' } : {})}
      >
        {formatDecimalString(readOnlyFigure)}
        {readOnlyFigure !== null && currency && (
          <span className="ml-1 text-xs text-fg-muted">{currency}</span>
        )}
      </span>
    );
  }

  const commit = () => {
    if (typed === null) return;
    const amount = typed.trim();
    // Emptying the box hands the cell back to the derivation, exactly as
    // "Reset to derived" does on the description. It is not a figure of zero.
    if (amount === '') {
      setTyped(null);
      setNote(null);
      return;
    }

    const solution = solveLineAmount({
      // A blank quantity box means the column default of 1, which is what the
      // preview beside this already assumes.
      quantity: input.quantity.trim() === '' ? '1' : input.quantity,
      unitPrice: input.unitPrice,
      amount,
      /*
       * 🔴 **THE MODE IS PRESERVED, not re-decided — owner's instruction,
       * 2026-09-13.** Read from `row.draft` rather than from `input`: the draft
       * is what the discount box above is bound to and what `discountWriteOf`
       * will turn into a payload, so the branch taken here and the column
       * eventually written cannot come apart.
       *
       * ⛔ **NOT `row.draft.discountAmount === null`.** That is true of every
       * line nobody has discounted — percentage is the DEFAULT there — so
       * passing it would push the commonest edit in this grid down the lossy
       * branch. {@link percentageModeIsChosen} also refuses `'0'` and `'0.00'`,
       * because a zero percentage is indistinguishable from the column's own
       * default and is nobody's choice.
       */
      keepPercentage: percentageModeIsChosen(row.draft.discountAmount, row.draft.discount),
    });

    if (solution === null) {
      setNote({ tone: 'error', text: 'A figure, up to two decimal places.' });
      return;
    }

    if (solution.kind === 'above-gross') {
      /*
       * `discountAmount` is non-negative by CHECK, so an amount above
       * quantity × price cannot be reached by discounting — the price has to
       * move. The gross is named because "too large" without it is a refusal
       * nobody can act on.
       */
      setNote({
        tone: 'error',
        text: `More than this line’s ${formatDecimalString(solution.gross)}${
          currency ? ` ${currency}` : ''
        } — raise the price, or clear it and let the amount set one.`,
      });
      return;
    }

    if (solution.kind === 'percentage-unreachable') {
      /*
       * ⛔ **A REFUSAL, AND IT MUST NOT FALL BACK TO THE AMOUNT COLUMN.**
       * `discount` is `CHECK (0..100)`, so on a gross of zero or below no
       * percentage produces a usable discount at all — every one of them is
       * nothing, or moves the line the way `discount_amount`'s own non-negative
       * CHECK forbids. A deduction line's gross is negative, which is how this
       * is reached in practice.
       *
       * Solving into `discountAmount` here would work arithmetically and would
       * be exactly the silent mode change the owner's 2026-09-13 instruction
       * removed — the reader would type a figure, get it, and find their
       * percentage gone with nothing on screen saying so. So it is said, in the
       * voice of `above-gross` above: the gross is named, because "a percentage
       * cannot" without the figure is a refusal nobody can act on, and both
       * things that CAN reach the figure are named beside it.
       */
      setNote({
        tone: 'error',
        text: `A percentage can’t discount this line’s ${formatDecimalString(solution.gross)}${
          currency ? ` ${currency}` : ''
        } — change the price, or switch the discount to an amount.`,
      });
      return;
    }

    if (solution.kind === 'discount-percentage') {
      /*
       * ⚠️ **The percentage, and NOTHING ELSE** — the same one-key write the
       * discount box itself makes in this mode. Touching `discountAmount` here,
       * even to `null` it "for tidiness", is the mode change this branch exists
       * to avoid; `discountWriteOf` reads `discountAmount === null` as percentage
       * mode and will carry `{discount, discountAmount: null}` on its own.
       */
      onChange({ discount: solution.discountPercent });
      setTyped(null);
      setNote(
        solution.exact
          ? /*
             * Nothing happened that the reader cannot see: the mode is the one
             * they chose and the figure is the one they typed. A note here would
             * be prose attached to a control, which is the thing the
             * no-helper-text rule forbids outright.
             */
            null
          : {
              tone: 'warn',
              /*
               * ⚠️ **`exact: false` is SAID, never rounded away.** `discount` is
               * `DECIMAL(5,2)`, so the subtotals a percentage can express are
               * `quantity × unitPrice × 0.0001` apart — £1.80 on an 18,000 line.
               * BOTH figures are named on purpose: the percentage alone does not
               * tell anybody what the line comes to, and the line total alone
               * does not tell them what was written to the column they chose.
               * `solution.amount` is read back through the preview's own
               * division, so this sentence and the figure in the box beside it
               * cannot disagree.
               */
              text: `Nearest percentage is ${solution.discountPercent}%, so the line comes to ${formatDecimalString(
                solution.amount,
              )}${currency ? ` ${currency}` : ''}.`,
            },
      );
      return;
    }

    if (solution.kind === 'unit-price') {
      onChange({ price: solution.unitPrice, priceMode: 'value' });
      setTyped(null);
      setNote(
        solution.exact
          ? null
          : {
              tone: 'warn',
              // ⚠️ `exact: false` is SAID, never rounded away. 10,000 over 3 is
              // 3,333.33 a unit and a line of 9,999.99 — a penny under what
              // somebody typed, on a figure a customer is billed against.
              text: `Doesn’t divide evenly — priced at ${formatDecimalString(
                solution.unitPrice,
              )} a unit, so the line is a little under.`,
            },
      );
      return;
    }

    /*
     * `discount-amount` — and reaching it at all means percentage mode was NOT
     * chosen on this line, because `keepPercentage` would have taken one of the
     * two branches above. So this is the fresh-line case the owner ruled on:
     * nothing to preserve, and flipping the mode is what makes the typed figure
     * land to the penny.
     *
     * ⚠️ **The flip test is unchanged and still fires exactly when the mode
     * genuinely moves.** `discountAmount === null` means the box on screen was
     * showing a percentage and is about to show money — a change nobody asked
     * for, which has to be stated. On a line already in amount mode nothing
     * moves and there is nothing to say.
     */
    const flipped = row.draft.discountAmount === null;
    onChange({ discountAmount: solution.discountAmount });
    setTyped(null);
    setNote(
      flipped
        ? {
            tone: 'warn',
            text: `Discount set to ${formatDecimalString(
              solution.discountAmount,
            )} as an amount, not a percentage.`,
          }
        : null,
    );
  };

  /*
   * ⚠️ **The conversion writes this row, not a save** — the copied line becomes
   * an invoice line inside `POST /quotes/{id}/invoices`' transaction. One word,
   * and it is the difference between a figure the reader can go and look at
   * after pressing Save and one that does not exist until they convert.
   */
  const recalculatedBy = row.quoteConvert === null ? 'save' : 'conversion';

  return (
    <div className="grid justify-items-end gap-0.5">
      <Input
        inputMode="decimal"
        aria-label="Amount before tax"
        value={shown}
        onChange={(event) => {
          setTyped(event.target.value);
          setNote(null);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        placeholder={derived === null ? 'Amount' : ''}
        disabled={disabled}
        invalid={note?.tone === 'error'}
        className="text-right tabular-nums"
        title={
          dirty
            ? `Before tax. Preview — recalculated by the server on ${recalculatedBy}.`
            : 'Before tax. Type a figure to set the discount, or the price on a line that has none.'
        }
      />
      {/*
        ⚠️ **THE CURRENCY CODE, WHICH USED TO RIDE ON A LINE THAT DOES NOT
        ALWAYS RENDER.** Before this the code appeared ONLY inside the
        `incl. tax` line below, so a line at a 0% rate — where `taxed` equals
        `derived` and that line is deliberately suppressed — printed a bare
        figure with no code at all. Reported by the owner. The read-only branch
        above has always carried its own code; only the editable cell was bare.

        ⚠️ **GUARDED ON `!showsTaxed`, because the two are the SAME fact.** Drop
        the guard and a taxed line prints the code twice, once under the box and
        once in the `incl. tax` line — which reads as two different currencies
        rather than one stated twice.

        ⚠️ **And on a non-empty box, or it is an ORPHAN CODE** — `GBP` alone
        under an empty input names the unit of a figure that is not there.

        It is the UNIT of the one figure in the box, never a second figure — the
        same treatment {@link DealLineItemsTable} already ships, and stacked
        rather than inline because an input cannot carry a suffix. **It costs
        HEIGHT, not width**, which is why no column moved for it.
      */}
      {!showsTaxed && currency && shown.trim() !== '' && (
        <span className="text-2xs text-fg-muted">{currency}</span>
      )}
      {showsTaxed && (
        <span className="text-xs text-fg-muted tabular-nums">
          {formatDecimalString(taxed)}
          {/*
            ⚠️ **`incl. tax`, THE SAME WORDS THE QUOTE GRID USES.** This read
            `with tax` until a browser run caught the two grids wording one fact
            two ways — observed live on quote 388 (`12,960.00 GBP incl. tax`)
            against invoice 848 (`10,800.00 GBP with tax`), both deployed.
            **A person billing a quote meets both on the way to one document**,
            which is the reason this grid's own header gives for the two not
            being allowed to diverge. Move them together or not at all.
          */}
          {currency && ` ${currency}`} incl. tax
        </span>
      )}
      {note && (
        <span
          className={cx('text-xs', note.tone === 'error' ? 'text-danger' : 'text-fg-muted')}
          role={note.tone === 'error' ? 'alert' : undefined}
        >
          {note.text}
        </span>
      )}
    </div>
  );
}

/**
 * The campaign-inclusions editor, shown only on a product an administrator has
 * linked inclusions to.
 *
 * ⚠️ **The picker is NARROWED to the linked set, and that is the check rather
 * than a tidy-up** — writing `inclusions` takes any active inclusion id, so an
 * unnarrowed picker lets somebody tick one the administrator never linked, it
 * saves cleanly, and it prints on the customer's invoice. Nothing downstream
 * refuses it.
 *
 * ⚠️ **Narrowing the OPTIONS never narrows the DRAFT.** A write REPLACES the
 * whole set, so an entry filtered off the screen would be an entry deleted on
 * the next save, with a 200 and no warning. Such entries get their own block.
 */
function InclusionsEditor({
  row,
  problems,
  disabled,
  master,
  masterFailed,
  offerUnread,
  offeredIds,
  onChange,
}: {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  master: CampaignInclusion[];
  masterFailed: boolean;
  offerUnread: boolean;
  offeredIds: ReadonlySet<number> | null;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const chosen = new Map(row.draft.inclusions.map((entry) => [entry.inclusionId, entry.quantity]));
  const retired: LineInclusion[] = (row.saved?.inclusions ?? []).filter(
    (entry) => chosen.has(entry.id) && !master.some((candidate) => candidate.id === entry.id),
  );
  const offered = master.filter((candidate) => offeredIds === null || offeredIds.has(candidate.id));
  const unlinked = master.filter(
    (candidate) => offeredIds !== null && !offeredIds.has(candidate.id) && chosen.has(candidate.id),
  );
  // `narrowedAway` gone with the caption it gated (removed 2026-09-05).

  /*
   * `422 INCLUSION_NOT_OFFERED_IN_REGION` — migration 096. Read off the REFUSAL
   * rather than off `fieldErrors`, because only the refusal knows this is the
   * one refusal in the line guard with THREE destinations: the line, the
   * PRODUCT (admin-only, another screen) and the PROJECT.
   *
   * ⚠️ The indexes are positions in the array we SENT, and the payload is built
   * from `draft.inclusions` in order. An index the draft no longer holds is
   * dropped, so a stale refusal marks nothing rather than the wrong chip.
   */
  const rowRefusal = row.refusal;
  const regionRefusal =
    rowRefusal !== null && rowRefusal.kind === 'inclusion-not-offered-in-region'
      ? rowRefusal
      : null;
  const refusedIds = new Set(
    (regionRefusal?.offendingInclusionIndexes ?? [])
      .map((index) => row.draft.inclusions[index]?.inclusionId)
      .filter((id): id is number => id !== undefined),
  );

  function toggle(inclusionId: number, checked: boolean) {
    if (checked) {
      onChange({ inclusions: [...row.draft.inclusions, { inclusionId, quantity: '1' }] });
    } else {
      onChange({
        inclusions: row.draft.inclusions.filter((entry) => entry.inclusionId !== inclusionId),
      });
    }
  }
  function setQuantity(inclusionId: number, quantity: string) {
    onChange({
      inclusions: row.draft.inclusions.map((entry) =>
        entry.inclusionId === inclusionId ? { ...entry, quantity } : entry,
      ),
    });
  }

  return (
    <div className="grid gap-3">
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— saving replaces the whole set</span>
      </p>
      {masterFailed && <ErrorBlock title="The campaign-inclusions list could not be loaded." />}
      {offerUnread && (
        <Notice tone="warning">
          Couldn’t check what this item offers, so all are shown. Anything chosen will print.
        </Notice>
      )}
      {problems.fieldErrors.inclusions && (
        <ErrorBlock title={problems.fieldErrors.inclusions}>
          {/*
            The server's sentence is the TITLE and this sits beneath it, never in
            front — it names the inclusion, the region and the product, none of
            which this app can derive. Ours says only where the fix is, and two
            of the three places are not on this line.
          */}
          {regionRefusal !== null && (
            <ul className="grid list-disc gap-0.5 pl-4">
              <li>
                Choose another inclusion, or a project in a region it is offered in.
              </li>
              <li>
                Widen what the item offers, on the Products screen.
              </li>
              <li>Give this line’s project the region, on the project record.</li>
            </ul>
          )}
        </ErrorBlock>
      )}
      {/*
        🔴 Three captions went in the owner's stricter pass, 2026-09-05 — none
        was an `EmptyState`: "Only inclusions linked to this item are listed.",
        "No campaign inclusions are available." and "The inclusions linked to
        this item are no longer active." Same removal on the quote grid, where
        the reasoning for the last two being two sentences is recorded.
      */}
      <ul className="grid gap-2 sm:grid-cols-2">
        {offered.map((inclusion) => {
          const quantityText = chosen.get(inclusion.id);
          const picked = quantityText !== undefined;
          // The parsed `quantity` was read only by the Checkbox `description`
          // removed on 2026-09-05.
          return (
            <li
              key={inclusion.id}
              className={cx(
                'flex items-start gap-2',
                // Every chip the server named, marked in one pass.
                refusedIds.has(inclusion.id) &&
                  'rounded-md bg-danger-soft px-2 py-1 ring-1 ring-danger-border',
              )}
            >
              <Checkbox
                label={inclusion.name}
                /*
                  🔴 **The Checkbox `description` is GONE — owner's stricter
                  pass, 2026-09-05.** It rendered `renderInclusion(...)`, the
                  exact wording that PRINTS on the customer's document, under
                  each ticked inclusion. Same removal on the quote grid.
                */
                checked={picked}
                disabled={disabled}
                onChange={(event) => toggle(inclusion.id, event.target.checked)}
              />
              {picked && (
                <Input
                  aria-label={`Count for ${inclusion.name}`}
                  inputMode="numeric"
                  value={quantityText ?? ''}
                  onChange={(event) => setQuantity(inclusion.id, event.target.value)}
                  disabled={disabled}
                  className="w-16 shrink-0 text-right tabular-nums"
                />
              )}
            </li>
          );
        })}
      </ul>
      {unlinked.length > 0 && (
        <div className="grid gap-1.5 rounded-md border border-border bg-surface px-3 py-2">
          {/* ⚠️ NOT the "retired" wording. A retired id cannot be re-sent at all;
              one of these saves and prints. One label for both would be false
              about a master row and misleading about a document. */}
          <p className="text-xs font-medium text-fg-secondary">
            On this line, but not offered on this item
          </p>
          <p className="text-2xs text-fg-muted">
            Not linked to this item, but already on the line — they stay and will print.
          </p>
          {unlinked.map((inclusion) => {
            const quantity = Number(chosen.get(inclusion.id) ?? '1');
            return (
              <div key={inclusion.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-fg-secondary">
                  {isValidInclusionQuantity(quantity)
                    ? renderInclusion(inclusion, quantity)
                    : inclusion.name}
                </span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(inclusion.id, false)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {retired.length > 0 && (
        <Notice
          tone="warning"
          title="Retired, and kept only while this line’s inclusions are left untouched"
        >
          <div className="grid gap-1.5">
          {retired.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between gap-2">
              <span className="text-xs text-fg-secondary">
                {renderInclusion(entry, Number(chosen.get(entry.id) ?? entry.quantity))}
              </span>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => toggle(entry.id, false)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          </div>
        </Notice>
      )}
    </div>
  );
}

/**
 * The line's inclusions in words. The wording comes from `@/lib/inclusions` and
 * is never re-implemented: `unitSingular` at exactly 1, `unitPlural` otherwise,
 * both forms stored, nothing pluralised in code.
 *
 * A count of zero never renders.
 *
 * ## ⚠️ `error` is here because a count of zero renders NOTHING
 *
 * The inclusions editor lives inside the collapsed **Details** panel, so
 * `problems.fieldErrors.inclusions` has no home on the row itself — and the one
 * refusal that fires on a set of zero is precisely the one this component would
 * otherwise say nothing about. Without it, choosing an offering item disables Save
 * and flags the row while the row shows no reason at all: the unnameable blocked
 * state CLAUDE.md forbids.
 *
 * ⚠️ **This is a FIELD ERROR, which is an explicit exception to both the
 * no-helper-text prohibition and the no-prose-in-a-desktop-cell rule.** It renders
 * only while the row is in breach and never as standing advice; there is no `hint`
 * here and none may be added.
 *
 * It is rendered whether or not Details is open, so it is briefly duplicated by the
 * editor's own `ErrorBlock`. That is deliberate: on a wide grid the cell says
 * **which row**, the editor says **where to fix it**, and a message that vanished
 * on expanding would be the jumpier of the two behaviours.
 */
function InclusionSummary({
  row,
  master,
  variant = 'chips',
  source = 'draft',
  error,
}: {
  row: Row;
  master: CampaignInclusion[];
  variant?: 'chips' | 'count';
  source?: 'draft' | 'saved';
  error?: string | undefined;
}) {
  const note =
    error === undefined ? null : (
      <p className="mt-1 text-2xs leading-snug text-danger">{error}</p>
    );
  const entries =
    source === 'draft'
      ? row.draft.inclusions
      : row.saved === null
        ? []
        : draftOf(row.saved).inclusions;
  if (entries.length === 0) return note;
  function wordingOf(inclusionId: number, quantity: number): string | null {
    const fromMaster = master.find((candidate) => candidate.id === inclusionId);
    if (fromMaster) return renderInclusion(fromMaster, quantity);
    const fromLine = (row.saved?.inclusions ?? []).find((entry) => entry.id === inclusionId);
    return fromLine ? renderInclusion(fromLine, quantity) : null;
  }
  const named = entries
    .map((entry) => ({
      id: entry.inclusionId,
      text: wordingOf(entry.inclusionId, Number(entry.quantity || '1')),
    }))
    .filter((entry): entry is { id: number; text: string } => entry.text !== null);
  if (named.length === 0) return note;
  return (
    <>
      {variant === 'count' ? (
        <p
          className="mt-1 text-2xs text-fg-muted"
          title={named.map((entry) => entry.text).join(' · ')}
        >
          {named.length === 1 ? '1 inclusion' : `${named.length} inclusions`}
        </p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-1">
          {named.map((entry) => (
            <li key={entry.id}>
              <Badge tone="neutral" variant="outline" wrap>
                {entry.text}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {note}
    </>
  );
}

/**
 * What stands where the editor would be on a line whose product does not offer
 * inclusions.
 *
 * A non-qualifying line that HOLDS inclusions shows them rather than nothing:
 * the owner chose *uneditable*, not *invisible*, and they print on the
 * customer's document — a screen that omitted them would be describing a
 * different document from the one that was sent.
 */
function InclusionsNotOffered({
  row,
  master,
  denialReason,
  orphaned,
}: {
  row: Row;
  master: CampaignInclusion[];
  denialReason: string | null;
  orphaned: boolean;
}) {
  if (!orphaned && denialReason === null) return null;
  if (!orphaned) {
    return (
      <div className="grid gap-1">
        <p className="text-xs font-medium text-fg-secondary">Campaign inclusions</p>
        <p className="text-xs text-fg-muted">{denialReason}</p>
      </div>
    );
  }
  const savedEntries = row.saved === null ? [] : draftOf(row.saved).inclusions;
  const preserved = savedEntries.length > 0;
  const draftDiverged =
    preserved && inclusionsKey(row.draft.inclusions) !== inclusionsKey(savedEntries);
  return (
    <div className="grid gap-1">
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— on this line, and read-only</span>
      </p>
      <InclusionSummary row={row} master={master} source={preserved ? 'saved' : 'draft'} />
      <p className="text-2xs text-fg-muted">
        {denialReason !== null && `${denialReason} `}
        {preserved
          ? 'Saving from here leaves them untouched.'
          : 'This item carries no campaign inclusions, so saving records none.'}
      </p>
      {draftDiverged && (
        <p className="text-2xs text-fg-muted">
          Changes made after switching the item will not be saved.
        </p>
      )}
    </div>
  );
}

/** The outcome of one save run. Partial success is an ordinary outcome here. */
interface RunSummary {
  saved: number;
  failed: number;
  halted: boolean;
}

/* ── The grid ─────────────────────────────────────────────────────────────── */

export function InvoiceLineItemsTable(props: InvoiceLineItemsTableProps) {
  const {
    cities,
    currency,
    canEdit,
    canDelete,
    deleteDenialReason = DEFAULT_DELETE_DENIAL,
    contextNote,
  } = props;

  /*
   * `mode` is deliberately NOT destructured above: the union narrows on
   * `props.mode`, and destructuring it would sever that link and make every
   * mode-only prop optional at every call site inside.
   */
  const draftMode = props.mode === 'draft';
  const convertMode = props.mode === 'convert';
  /**
   * A QUOTE is being converted — see {@link InvoiceLineItemsQuoteConvertProps}.
   *
   * ⚠️ **Deliberately a SEPARATE flag from `convertMode`, never `||`-ed with
   * it.** The two modes emit different arrays with opposite meanings for the
   * rows nobody touched, so every existing `convertMode` branch has to be looked
   * at and decided rather than widened. Where the two genuinely agree — no
   * request is issued from here, and there is no invoice id — the condition
   * below names both explicitly.
   */
  const quoteConvertMode = props.mode === 'quote-convert';
  /** Either conversion: no line request is issued and no invoice exists yet. */
  const anyConvertMode = convertMode || quoteConvertMode;
  /**
   * `null` in draft and both convert modes, and it is read as the "issue no
   * request" switch throughout rather than as a missing value — there is no
   * invoice to address, so there is no id, and every request path guards on it.
   */
  /*
   * ⚠️ **WRITTEN AS AN EXCLUSION, AND IT MUST STAY ONE. `mode` IS OPTIONAL ON
   * THE SAVED VARIANT (`mode?: 'saved'`), SO `undefined` IS SAVED MODE.**
   *
   * *SUPERSEDED: `props.mode === 'saved' ? props.invoiceId : null`, and the same
   * inclusion test on the two callbacks below.* Both saved-mode callers omit the
   * prop -- `InvoiceDetailPage` and `InvoiceFormModal` -- so `props.mode` was
   * `undefined`, every test answered false, and `invoiceId` collapsed to `null`.
   *
   * THE CONSEQUENCE WAS NOT A MISSING ID, IT WAS A GRID THAT NEVER LOADED. The
   * load effect early-returns on a null id WITHOUT clearing `loading`, and
   * `loading` starts `true` in saved mode -- so the skeletons rendered for ever,
   * no request was ever made, and nothing errored. Measured against production
   * on 2026-08-31: `GET /invoices/61/products` returns 200 with two lines, and
   * the browser never asked for it. Invoice line items were invisible on the
   * detail page AND in the edit modal.
   *
   * ⚠️ `onLinesChanged` and `onXeroPush` were silently dropped the same way, so
   * a saved-mode line write refreshed no totals and reported no Xero outcome.
   *
   * `QuoteLineItemsTable` has always had this right -- `props.mode === 'draft'
   * || props.mode === 'convert' ? null : props.quoteId` -- which is why quote
   * lines rendered and invoice lines did not. **Match that shape here: name the
   * modes that have NO id, and let everything else keep it.** An inclusion test
   * against an optional discriminant is false for the default case, which is the
   * one nobody writes down.
   */
  const noInvoiceModes =
    props.mode === 'draft' || props.mode === 'convert' || props.mode === 'quote-convert';
  const invoiceId = noInvoiceModes ? null : props.invoiceId;
  const onLinesChanged = noInvoiceModes ? undefined : props.onLinesChanged;
  const onXeroPush = noInvoiceModes ? undefined : props.onXeroPush;
  /*
   * ⚠️ Narrowed through `noInvoiceModes` for the same reason the four above it
   * are: an inclusion test against an optional discriminant is false for the
   * DEFAULT case, which is saved mode — the one case these two exist for. That
   * mistake dropped `invoiceId`, `onLinesChanged` and `onXeroPush` here once
   * already and rendered an empty grid with nothing erroring.
   */
  const onUnsavedChange = noInvoiceModes ? undefined : props.onUnsavedChange;
  const registerSave = noInvoiceModes ? undefined : props.registerSave;
  /* Narrowed the same way, for the same reason. See `onRowsSaved`. */
  const onRowsSaved = noInvoiceModes ? undefined : props.onRowsSaved;
  /*
   * ⚠️ **BOTH `draft` AND `quote-convert` EMIT DRAFT PAYLOADS**, because on the
   * quote path a row somebody ADDS is written exactly as a draft row is — its
   * own `POST /invoices/{id}/products` after the conversion lands. The copied
   * rows are a different population and go up through `onQuoteOverridesChange`.
   */
  const onDraftLinesChange =
    props.mode === 'draft' || props.mode === 'quote-convert'
      ? props.onDraftLinesChange
      : undefined;
  const onDraftBlockedChange =
    props.mode === 'draft' || props.mode === 'quote-convert'
      ? props.onDraftBlockedChange
      : undefined;
  /*
   * ── QUOTE-CONVERT MODE ────────────────────────────────────────────────────
   *
   * The parent owns the read, as it does on the deal path: the conversion is the
   * parent's request and the quote's lines are what it is describing.
   */
  const quoteLines = props.mode === 'quote-convert' ? props.quoteLines : null;
  const quoteLinesLoading = props.mode === 'quote-convert' ? props.quoteLinesLoading : false;
  const quoteLinesError = props.mode === 'quote-convert' ? props.quoteLinesError : null;
  const quoteLineErrors =
    props.mode === 'quote-convert' ? props.quoteLineErrors : EMPTY_QUOTE_LINE_ERRORS;
  const onQuoteOverridesChange =
    props.mode === 'quote-convert' ? props.onQuoteOverridesChange : undefined;
  /*
   * ── CONVERT MODE ──────────────────────────────────────────────────────────
   *
   * The parent owns the read, so this mode takes its three states as props
   * rather than fetching: `POST /deals/{id}/invoices` is the parent's request
   * and the deal's basket is what it is describing. Nothing below writes a line.
   */
  const dealLines = props.mode === 'convert' ? props.dealLines : null;
  const dealLinesLoading = props.mode === 'convert' ? props.dealLinesLoading : false;
  const dealLinesError = props.mode === 'convert' ? props.dealLinesError : null;
  /*
   * ⚠️ **Both conversion modes have one**, and it means the same thing on each:
   * the conversion has landed, the invoice exists, and a retried Save re-sends
   * the HEADER alone — so every box on every row must go dead or somebody types
   * a price that nothing will ever send.
   */
  const convertLocked =
    props.mode === 'convert' || props.mode === 'quote-convert' ? props.convertLocked : false;
  const convertLineErrors = props.mode === 'convert' ? props.convertLineErrors : EMPTY_LINE_ERRORS;
  const onConvertLinesChange = props.mode === 'convert' ? props.onConvertLinesChange : undefined;
  const onConvertBlockedChange =
    props.mode === 'convert' ? props.onConvertBlockedChange : undefined;
  /*
   * The COMPANY figure the Tax rate column reads out. Unread in every other
   * mode, where the column is a live control and this is never consulted.
   */
  /*
   * The COMPANY figure the Tax rate column reads out in convert mode — and, on
   * every other mode, the figure a NEW row's blank percentage will be given by
   * the server. One prop, read once, and never sent anywhere.
   *
   * ⚠️ **`?? COMPANY_SALES_TAX_UNREAD` is the compatibility half and it is not
   * defensive padding.** The prop is optional on the common props so a mount
   * with no company read of its own — `InvoiceDetailPage` — keeps rendering
   * exactly as it did. `{ known: false }` must therefore be indistinguishable
   * from the state before this prop existed, everywhere it is read.
   */
  const companySalesTax = props.companySalesTax ?? COMPANY_SALES_TAX_UNREAD;
  /*
   * ⚠️ **READ ONCE, IN THE `useState` INITIALISER BELOW, AND NOWHERE ELSE.**
   * It is deliberately NOT in any dependency array: an effect that re-ran would
   * put back rows the person had deleted, and a parent building the array
   * inline — which is what a modal will do — would re-seed on every keystroke
   * in its own header fields. Same hazard `emitLines`'s ref guards against,
   * from the other direction.
   *
   * ⚠️ `draft` mode ONLY, though `quote-convert` also emits draft payloads: the
   * quote's own lines are the seed on that path.
   */
  const initialDraftRows = props.mode === 'draft' ? props.initialDraftRows : undefined;

  /*
   * ⚠️ **SEEDED IN THE INITIALISER, NEVER IN AN EFFECT.** A lazy initialiser
   * runs exactly once per mount, which is the only place a seed can be applied
   * without ever fighting the person using the grid. The saved-mode load effect
   * returns early while `invoiceId` is null, and the two conversion seeding
   * effects are gated on their own modes, so nothing downstream can overwrite
   * these rows.
   */
  const [rows, setRows] = useState<Row[]>(() => {
    const seeded = (initialDraftRows ?? []).map(seededRowOf);
    // A new invoice opens with one blank row to fill in (owner, 2026-10-12).
    return draftMode && seeded.length === 0 ? [blankRowOf(DEFAULT_BLANK_ROW_KEY)] : seeded;
  });
  /**
   * What the seeded products are CALLED, captured at mount beside the rows.
   *
   * ⚠️ **`useProducts()` fetches `active: true`, so a product retired since the
   * plan was written is NOT in the option list** — and a seeded row is the first
   * draft row that can hold a `productId` its owner never picked from that list.
   * Without this the Item cell renders BLANK over a real id: Save stays enabled,
   * nothing is refused here, and the line is POSTed naming a product the person
   * was never shown. `productOptionsFor` already keeps exactly this promise for
   * a SAVED row, and this is that same rule extended to the population that can
   * now reach it.
   *
   * The name costs no lookup: `PlannedProductLine` carries it, for the reason
   * the prefill's company and contact carry theirs.
   *
   * ⚠️ **One-shot, like the rows.** No dependency array, because the parent maps
   * the seed inline and a memo over it would rebuild on every keystroke.
   */
  const [seededProducts] = useState<ReadonlyMap<number, ProductRow>>(
    () =>
      new Map(
        (initialDraftRows ?? []).map((line) => [
          line.productId,
          {
            id: line.productId,
            productName: line.productName,
            productCode: line.productCode,
            /*
             * ⚠️ `null` — a PLAN CARRIES NO PRICE, and this must never be read
             * as one. It reaches the option list only, never a payload and never
             * `previewInputOf`'s `catalogPrice`, which resolves off
             * `products.rows`. A figure invented here would preview a total the
             * catalogue is going to contradict.
             */
            unitPrice: null,
            /*
             * ⚠️ `null` for the same reason, and here it is also the SAFE
             * answer: `null` means NO RECOMMENDATION, so a seeded row discounts
             * nothing until the real catalogue lands. A plan records what is to
             * be sold and how much of it; it has never carried a discount, and
             * inventing one here would put a figure on a customer's invoice that
             * no product actually recommends. `recommendedDiscounts` below
             * resolves off `products.rows` alone for exactly this reason.
             */
            recommendedDiscountAmount: null,
            /* A plan carries no unit fields either; `productUnits` reads the catalogue. */
            quantityStep: null,
            unitLabel: null,
          },
        ]),
      ),
  );
  /**
   * What the seeded PROJECTS are called, captured at mount beside the rows.
   * **The quote grid carries this verbatim; the two move together.**
   *
   * ⚠️ **THE EXACT COUNTERPART OF `seededProducts`, AND FOR A WIDER FAILURE.**
   * A `Combobox` falls back to its placeholder when `value` is absent from
   * `options`, and this picker's list is narrower than the catalogue's in four
   * ways at once: ONE page of 100 projects, ACTIVE only, filtered to the row's
   * product, then **split by cycle scope and opened on `current`**. So a seeded
   * project is missing from what the row can see whenever it sits on the
   * `previous` pill — routine for a board whose rows are projects across
   * cycles — or has been retired, or the fetch has simply not landed, which is
   * true on every first paint. Without a name to pin, the control renders BLANK
   * over a real `draft.projectId` that `createPayload` still sends. **The
   * screen would say no project and the wire would say one**, which is the
   * whole failure the seed exists to prevent, arriving one layer down.
   *
   * The name costs no lookup: `PlannedProductLine.project` carries it, for the
   * reason `productName` beside it does.
   *
   * ⚠️ **One-shot, like the rows and like `seededProducts`.**
   */
  const [seededProjects] = useState<ReadonlyMap<number, string>>(
    () =>
      new Map(
        (initialDraftRows ?? [])
          .map((line) => line.project)
          .filter((project) => project !== undefined)
          .map((project) => [project.id, project.name]),
      ),
  );
  /*
   * Nothing is fetched in draft or convert mode, so there is nothing to wait for
   * — starting `true` would render the skeleton for ever over a grid that is
   * already ready. Convert mode has a loading state all the same; it arrives as a
   * prop, because the request belongs to the parent.
   */
  const [loading, setLoading] = useState(!draftMode && !anyConvertMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [newRowSeq, setNewRowSeq] = useState(0);

  const products = useProducts();
  const inclusions = useCampaignInclusions();
  const projects = useActiveProjects();
  /*
   * Which cycles sit under Current and which under Previous. ONE read for the
   * whole grid -- the pill is per row, the /cycles fetch behind it is not, and
   * a hook per row is a request per row against a shared rate limit.
   *
   * A null member means the cycles could not be read and EVERY project stays on
   * offer under that pill -- see useCycleScopeIds. Never an empty set.
   */
  const cycleScopeIds = useCycleScopeIds();
  /*
   * One read of the `/regions` master for the whole grid, for the same reason
   * `useActiveProjects` is one read: a hook per row is one request per row of a
   * list that is identical on all of them.
   *
   * It is unconditional. The master is six rows and the request is cheap, and
   * gating it on “does any row show the control” would leave the picker empty on
   * the first render after somebody switches a line to a projectless product —
   * the one moment it is wanted.
   */
  const regions = useReferenceOptions('regions');
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  /*
   * The line-note gate on a STORED line: `invoices.update` alone. `canEdit`
   * also folds in create and the page's own read gate, and a note is allowed
   * on a locked invoice whose other cells are read-only.
   */
  const { ability } = useAuth();
  const canUpdateLineNotes = ability.canUpdate('invoices');
  /*
   * ⚠️ **NOT FETCHED IN THE DEAL CONVERT MODE, and that is a rate-limit decision
   * rather than tidiness.** `GET /xero/tax-rates` is a rate-limited call against
   * a tenant budget of 60/min and 5,000/day, and on that path EVERY row's Tax
   * rate cell is a readout — neither `lines` nor `lineSet` carries a `taxRate`
   * or an `xeroTaxType` — so there is no control to populate. **Removing the
   * control without gating the hook keeps the whole cost.**
   *
   * ⚠️ **IT IS `convertMode` AND NOT `anyConvertMode`, DELIBERATELY.** On the
   * QUOTE conversion the grid holds two populations: a copied row's cell is a
   * readout of its own stored rate, but a row somebody ADDS there is an ordinary
   * draft row written by its own POST, and that POST carries both tax keys — so
   * it has a live `TaxRateControl` that needs this list. Widening the gate to
   * both modes would leave that picker permanently empty on the one screen where
   * a new line's tax has to be chosen.
   */
  const taxRates = useXeroTaxRates(xeroConnected && !convertMode);

  const load = useCallback(
    (signal: AbortSignal) => {
      /*
       * ⚠️ Draft mode reads NOTHING. There is no invoice and therefore no basket
       * to load; the rows are whatever the person has typed and nothing else.
       */
      if (invoiceId === null) return Promise.resolve();
      return api
        .listInvoiceProducts(invoiceId, signal)
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.data.map(rowOf));
          setLoadError(null);
        })
        .catch((error: unknown) => {
          if (signal.aborted) return;
          setRows([]);
          setLoadError(describeFailure(error).message);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [invoiceId],
  );

  useEffect(() => {
    if (invoiceId === null) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, reloadToken, invoiceId]);

  /*
   * ⚠️ **CONVERT MODE SEEDS ITS ROWS FROM THE PROP, and re-seeds whenever that
   * array's IDENTITY changes.** The parent reads the deal's basket once and holds
   * it in state, so this runs once per deal — but a parent that rebuilt the array
   * inline would wipe whatever somebody is typing, with no error and nothing on
   * screen to explain it. Said on the prop as well as here.
   *
   * `dealLines === null` is "not read", which is not "none": it leaves the rows
   * alone rather than emptying them, so a failed read cannot present itself as a
   * deal with no products.
   *
   * ⚠️ **oxlint warns `react(set-state-in-effect)` HERE AND MUST NOT BE OBEYED
   * LITERALLY.** Its advice — "derive the value during render" — is exactly wrong
   * for this state: `rows` is not derived from `dealLines`, it is SEEDED from it
   * and then EDITED, so deriving it during render would recompute every row from
   * the deal on every keystroke and throw the edit away. That is the whole feature.
   *
   * ⚠️ `QuoteLineItemsTable` carries the byte-identical effect and oxlint reports
   * **nothing** for that file — measured 2026-08-31, linting it alone produced no
   * output at all. So the absence of a warning there is not evidence that this one
   * is a real defect; the two grids are meant to behave identically, and silencing
   * this by restructuring only this copy is how they start to diverge.
   */
  useEffect(() => {
    if (!convertMode || dealLines === null) return;
    setRows(dealLines.map(convertRowOf));
  }, [convertMode, dealLines]);

  /*
   * ⚠️ **THE QUOTE CONVERSION SEEDS ITS COPIED ROWS AND KEEPS THE ADDED ONES.**
   * This is the one place the two-population design shows up as a difference in
   * the seeding itself, and a straight `setRows(quoteLines.map(...))` — which is
   * what the deal effect above does and what this looked like at first — would
   * DELETE every row somebody had added the moment the prop's identity changed.
   *
   * `quoteLines === null` is "not read", not "none": the rows are left alone, so
   * a failed read cannot present itself as a quote with no lines. Every one of
   * them still converts either way.
   *
   * ⚠️ **oxlint warns `react(set-state-in-effect)` here too and must not be
   * obeyed literally**, for the reason spelled out on the effect above: `rows`
   * is SEEDED from the prop and then EDITED, so deriving it during render would
   * recompute it on every keystroke and throw the edit away.
   */
  useEffect(() => {
    if (!quoteConvertMode || quoteLines === null) return;
    setRows((current) => [
      ...quoteLines.map(quoteConvertRowOf),
      ...current.filter((row) => row.quoteConvert === null),
    ]);
  }, [quoteConvertMode, quoteLines]);

  /*
   * The name maps below all read the reference list FIRST and the stored lines
   * SECOND, so a product, project or city the active list no longer offers is
   * still nameable from the record that holds it. A description that silently
   * dropped a city name would diverge from the string the backend composes.
   *
   * ⚠️ **`sourceLineOf`, not `row.saved`.** On a convert row the stored line is
   * the DEAL's, and reading `saved` alone would leave every name on the screen
   * unresolvable — the picker lists are capped and active-only, so a product or
   * city the deal holds and the catalogue no longer offers would simply blank.
   */
  const productNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of products.rows) map.set(row.id, row.productName);
    for (const row of rows) {
      const line = sourceLineOf(row);
      if (line) map.set(line.product.id, line.product.productName);
    }
    return map;
  }, [products.rows, rows]);

  const cityNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const city of cities) map.set(city.id, city.name);
    for (const project of projects.rows) {
      for (const city of project.cities) map.set(city.id, city.name);
    }
    for (const row of rows) {
      for (const city of sourceLineOf(row)?.cities ?? []) map.set(city.id, city.name);
    }
    return map;
  }, [cities, projects.rows, rows]);

  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const project of projects.rows) map.set(project.id, project.name);
    for (const row of rows) {
      const stored = sourceLineOf(row)?.project;
      if (stored) map.set(stored.id, stored.name);
    }
    return map;
  }, [projects.rows, rows]);

  /**
   * Every region name this table can honestly resolve.
   *
   * ⚠️ **On a PROJECTLESS line this is the only geography the customer's
   * invoice states** — `geographySegment` in `@/lib/line-description` is
   * cities-else-regions. The backend composes the same sentence from the line's
   * own regions, so a name this map cannot resolve is dropped from the preview
   * while the customer's document still names it.
   *
   * The saved row goes in LAST, and that is the half that matters:
   * `useReferenceOptions('regions')` queries `isActive: true`, so a line sitting
   * on a RETIRED region resolves through nothing else — while the read that
   * loaded the line carries no `is_active` filter and names it perfectly.
   */
  const regionNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const record of regions.records) map.set(record.id, record.name);
    for (const project of projects.rows) {
      for (const region of project.regions) map.set(region.id, region.name);
    }
    for (const row of rows) {
      for (const region of row.saved?.regions ?? []) map.set(region.id, region.name);
    }
    return map;
  }, [regions.records, projects.rows, rows]);

  /**
   * Every campaign inclusion this table can name and give a unit for.
   *
   * ⚠️ **Read only as input to the pinned composer, and LIVE ON INVOICES since
   * backend `3cd2e1c`.** `xero-invoice.payload.ts` imports `inclusionsSegment`
   * from the quote builder, so the bracketed segment prints on the customer's
   * invoice exactly as it does on the quote. A name or a unit this map cannot
   * resolve is dropped from the preview while the customer's document prints it.
   *
   * Saved entries go in AFTER the master's list, the same rule as
   * {@link projectNames}: the master fetches `?isActive=true`, so an inclusion
   * RETIRED since the line was written resolves through nothing else. It still
   * prints on the customer's document, so dropping it would describe a document
   * we are not sending.
   */
  const inclusionMasters = useMemo(() => {
    const map = new Map<number, Pick<LineInclusion, 'name' | 'unitSingular' | 'unitPlural'>>();
    for (const entry of inclusions.rows) map.set(entry.id, entry);
    for (const row of rows) {
      for (const entry of row.saved?.inclusions ?? []) map.set(entry.id, entry);
    }
    return map;
  }, [inclusions.rows, rows]);

  /*
   * ⚠️ The item CODE is a join key in its own right: it is the guard on the
   * project-name substitution in `composeLineDescription`, so a missing code
   * here heads the sentence with the product name where the backend heads it
   * with the project's. See `hasXeroItemCode` in `@/lib/line-description`.
   */
  const productCodes = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const row of products.rows) map.set(row.id, row.productCode);
    for (const row of rows) {
      if (row.saved) map.set(row.saved.product.id, row.saved.product.productCode);
    }
    return map;
  }, [products.rows, rows]);

  const rowProjects = useMemo(() => {
    const map = new Map<string, RowProject>();
    /*
      🔴 **The LIST-STATE project footnote is GONE — owner's stricter pass,
      2026-09-05, and it must not come back.** It read "Project list couldn't
      load, so the project can't be changed here." on a failed read, or
      "Showing the first 100 active projects — there are more." on a truncated
      one. The lock on a failed read still happens and now says nothing.

      ⚠️ **The TRUNCATED half has no subject any more (2026-09-15):** the grid
      pages, so the list is complete or it failed. What that sentence would have
      been reporting — 18 projects missing, the same 18 each time — went unsaid
      for as long as it was removed, and that is what made an empty
      `Virtual Masterclasses` picker unreportable. See `useActiveProjects`.

      ⚠️ **The footnote slot is in use again as of 2026-09-11 and this decision
      is NOT reversed.** What it now carries is `projectScopeFootnote` — "No
      projects in Current. 5 in Previous." — which is a fact about a control the
      reader can act on in one click, not a report on the fetch. **Do not read
      its return as permission to re-add the truncation or failed-read
      sentences**; that is the owner's call, not this file's.
    */
    for (const row of rows) {
      const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
      /*
       * ⚠️ **The test is the project's whole product SET, via
       * `projectSellsProduct`** — migration 156, and the singular key it
       * replaced was only the primary. Matching the primary excluded a project
       * selling this product as a SECONDARY, which is exactly the case 156 was
       * built for: a travel line under a festival project. The exclusion was
       * silent — the project was simply absent from the list, reading as one
       * nobody had set up. One helper for all three grids; three copies of this
       * predicate drift and nothing on screen shows it.
       */
      const forProduct =
        productId === null
          ? projects.rows
          : projects.rows.filter((project) => projectSellsProduct(project, productId));
      const toProjectOption = (project: (typeof forProduct)[number]): ComboboxOption => {
        const period = project.cycle
          ? project.intake
            ? `${project.cycle.name} · ${project.intake.name}`
            : project.cycle.name
          : null;
        // ⚠️ The PRIMARY is the right read here, and `products[0]` IS the
        // primary — first by the read's own `ORDER BY`, an API guarantee rather
        // than an incidental ordering. The hint names what picking this project
        // will put in the item box, and that is `productOfProject`, which seeds
        // the primary.
        const productName = productId === null ? (project.products[0]?.name ?? null) : null;
        const hint = [productName, period].filter((part) => part !== null).join(' · ');
        return {
          value: String(project.id),
          label: project.name,
          ...(hint !== '' ? { hint } : {}),
        };
      };
      const savedProject = row.saved?.project ?? null;
      /*
       * ✅ **THE SAME PIN FOR A SEEDED ROW, which has no saved line to pin from
       * — 2026-09-11.** `productOptionsFor` already carries this branch for the
       * Item cell; this is it for the project cell, and the reason is stronger
       * here because this list is narrowed four ways (see `seededProjects`)
       * where the catalogue's is narrowed one.
       *
       * ⚠️ **Keyed on the row's CURRENT draft project, never on the seed.**
       * Changing the item clears `projectId`, and a pin keyed on the seed would
       * go on offering a project the row no longer holds.
       */
      const seededProjectName =
        row.draft.projectId === ''
          ? undefined
          : seededProjects.get(Number(row.draft.projectId));
      const pinnedProject =
        savedProject !== null
          ? { value: String(savedProject.id), label: savedProject.name, hint: 'no longer offered' }
          : seededProjectName !== undefined
            ? {
                value: row.draft.projectId,
                label: seededProjectName,
                hint: 'no longer offered',
              }
            : null;
      /*
       * Current / Previous, owner request 2026-09-11 — Current (which means
       * current AND upcoming) is the default and reproduces the 2026-09-09
       * open-cycles filter exactly. Previous is what makes the closed-cycle
       * projects that filter hid reachable again.
       *
       * SPLIT HERE AND NOT INTO forProduct, WHICH IS THE WHOLE POINT.
       * `selected` below still resolves against the unfiltered list, so a line
       * already on a closed cycle keeps its project, its cities and its
       * regions -- and `savedProject` is pinned into BOTH pills by
       * projectOptionsByScope, so the control can always render the value the
       * row holds. Filter forProduct instead and 113 historical lines lose all
       * of it.
       */
      const { options, counts: scopeCounts } = projectOptionsByScope(
        forProduct,
        cycleScopeIds,
        toProjectOption,
        pinnedProject,
      );
      const projectFootnotes: Record<ProjectCycleScope, string | undefined> = {
        current: projectScopeFootnote('current', scopeCounts),
        previous: projectScopeFootnote('previous', scopeCounts),
      };
      const selected =
        row.draft.projectId === ''
          ? null
          : (forProduct.find((project) => String(project.id) === row.draft.projectId) ?? null);
      const resolvable = !projects.loading && !projects.failed && !projects.truncated;
      const declaredCityIds =
        selected !== null
          ? new Set(selected.cities.map((city) => city.id))
          : // No project chosen means no cities are declared — a definite answer,
            // but only once the list has settled. Otherwise `null`: cannot say.
            row.draft.projectId === '' && resolvable
            ? new Set<number>()
            : null;
      const cityOptions: MultiComboboxOption[] = (selected?.cities ?? []).map((city) => ({
        value: String(city.id),
        label: cityLabel(city),
      }));
      for (const city of row.saved?.cities ?? []) {
        if (!row.draft.cityIds.includes(city.id)) continue;
        if (cityOptions.some((option) => option.value === String(city.id))) continue;
        cityOptions.push({
          value: String(city.id),
          label: cityLabel(city),
          hint: 'no longer on this project',
        });
      }
      /*
       * The live answer to "which cities does the flag mean today". The chosen
       * project's own list is authoritative — it is what the server resolves
       * against. When the project list cannot supply it, a saved flagged line's
       * server-resolved `cities` is the next best, and ONLY while the row still
       * names the project it was saved under: a set resolved against one
       * project says nothing about another.
       */
      const savedProjectId =
        row.saved === null || row.saved.project === null ? '' : String(row.saved.project.id);
      const allCitiesToday: { id: number; name: string; label: string }[] | null =
        selected !== null
          ? selected.cities.map((city) => ({
              id: city.id,
              name: city.name,
              label: cityLabel(city),
            }))
          : row.saved !== null && row.saved.allCities && row.draft.projectId === savedProjectId
            ? row.saved.cities.map((city) => ({
                id: city.id,
                name: city.name,
                label: cityLabel(city),
              }))
            : null;
      /*
       * The project's OWN region set, which is what the region picker offers on
       * a project-backed row — see {@link RowProject.projectRegions}. `null`
       * when the project could not be resolved at all, which is a different fact
       * from a project that declares none.
       */
      const projectRegions =
        selected === null
          ? null
          : selected.regions.map((region) => ({ id: region.id, name: region.name }));
      /*
       * ⚠️ **Three conditions, and all three are load-bearing.** A project must
       * be CHOSEN (an empty box says nothing about a project nobody has picked),
       * RESOLVED (`selected !== null`, so a truncated or failed list cannot be
       * read as "declares nothing"), and its own list empty. Anything short of
       * that is `false`, because this opens a control and the gate above it
       * fails towards hiding.
       */
      const projectHasNoCities =
        row.draft.projectId !== '' && selected !== null && selected.cities.length === 0;
      const citiesUnavailable = row.draft.projectId === '' || cityOptions.length === 0;
      const cityHint =
        row.draft.projectId === ''
          ? // Said plainly rather than as a fault. Most lines name no cities.
            'Cities come from the project. Without one, none.'
          : cityOptions.length === 0
            ? selected === null
              ? 'This project’s cities are not listed here.'
              : 'This project has no cities. Add them on the project.'
            : undefined;
      const settled = !projects.loading && !projects.failed;
      const projectHint =
        productId === null
          ? undefined
          : settled && forProduct.length === 0
            ? projects.truncated
              ? undefined
              : 'No active project sells this product.'
            : undefined;
      /*
       * ⚠️ **The server's own predicate, reproduced — and it fails towards
       * SHOWING.** `forProduct` is `projects.rows` filtered by this row's
       * product, which is exactly `GET /projects?productId=N&isActive=true`,
       * which is exactly the guard's `product_id = ? AND is_active = 1`. So the
       * only case that HIDES the block is: the list has settled, is complete,
       * and definitely names no active project for the product on this line.
       *
       * Everything else keeps it. `!settled` covers loading and a failed read;
       * `projects.truncated` covers a 101st project we have not seen; and a line
       * that already names a project keeps its own block or that project would
       * become invisible and unremovable.
       *
       * 🔴 **A ROW WITH NO PRODUCT NOW HIDES IT — owner's decision, 2026-09-19.**
       * ⚠️ *SUPERSEDED: "and a row with no product yet keeps it because
       * project-first entry is how half of this grid is used — choosing a
       * project there SETS the item."*
       *
       * ⛔ **THE ACCEPTED COST, RECORDED AS A DECISION AND NOT AS A TIDY-UP:
       * THAT PROJECT-FIRST PATH IS GONE.** A line could be resolved from EITHER
       * end; there is now one direction, item first. Nothing errors and nothing
       * is refused — the second way in simply is not offered, so somebody who
       * worked project-first meets a row that looks like it has lost a control
       * rather than one waiting for an item. **Do not "restore" it as a bug
       * fix**: it was put to the owner with this cost named and taken anyway.
       *
       * ⚠️ **The two UNRESOLVED terms are untouched and must stay that way.**
       * `!settled` and `truncated` are not evidence a product has no projects,
       * and the line whose product IS sold through projects and names none is
       * refused at write time with nothing on screen to satisfy it. Only the
       * "no product yet" term moved; the definite-no term below is unchanged.
       *
       * ⚠️ `holdsProject` stays FIRST, so a row that already names a project is
       * never hidden by the new term — the change reaches only rows where
       * nothing has been chosen at either end.
       *
       * ⚠️ Hidden is not an error. `Meet Up`, `Profile Evaluations` and
       * `Miscellaneous` have no projects and never will, and a line with no
       * project is the ordinary case, so nothing is said about the absence.
       */
      const holdsProject = row.draft.projectId !== '' || (row.saved?.project ?? null) !== null;
      const showProject = holdsProject
        ? true
        : productId === null
          ? false
          : !settled || projects.truncated || forProduct.length > 0;
      map.set(row.key, {
        options,
        cityOptions,
        declaredCityIds,
        allCitiesToday,
        projectRegions,
        projectHasNoCities,
        projectHint,
        projectFootnotes,
        cityHint,
        citiesUnavailable,
        showProject,
      });
    }
    return map;
  }, [
    rows,
    projects.rows,
    projects.loading,
    projects.failed,
    projects.truncated,
    /*
     * Listed for honesty rather than for reactivity: it is `useState` with no
     * setter, so its identity never changes and it can never trigger a
     * recompute. Leaving a value this memo READS out of an already
     * hand-maintained list is how the next member added beside it gets left out
     * too — and that one may well move.
     */
    seededProjects,
    /*
     * cycleScopeIds ARRIVES LATE, AND LEAVING IT OUT MAKES THE SPLIT INERT.
     * useCycleScopeIds answers UNKNOWN_CYCLE_SCOPES while /cycles is in flight
     * and a real index once it lands, so a memo that does not depend on it
     * computes the UNFILTERED list on first paint and never recomputes -- both
     * pills then show everything, with nothing failing and nothing on screen to
     * show it. Caught by lint, not by review, the last time this changed.
     */
    cycleScopeIds,
  ]);

  const retiredInclusionIds = useMemo(() => {
    const active = new Set(inclusions.rows.map((row) => row.id));
    const retired = new Set<number>();
    for (const row of rows) {
      for (const entry of row.saved?.inclusions ?? []) {
        if (!active.has(entry.id)) retired.add(entry.id);
      }
    }
    return retired;
  }, [inclusions.rows, rows]);

  // A stable identity for the set of chosen products, so the offers effect does
  // not re-run on every keystroke elsewhere in the grid.
  const productIdKey = rows.map((row) => row.draft.productId).join(',');
  const inclusionProductIds = useMemo(() => {
    const ids = new Set<number>();
    for (const id of productIdKey.split(',')) {
      if (id !== '') ids.add(Number(id));
    }
    return [...ids].sort((a, b) => a - b);
  }, [productIdKey]);
  const inclusionOffers = useProductInclusionOffers(inclusionProductIds);

  const inclusionsByKey = useMemo(() => {
    const map = new Map<string, RowInclusions>();
    for (const row of rows) {
      const offer = inclusionsOfferOf(row, inclusionOffers);
      map.set(row.key, {
        offers: productOffersInclusions(offer),
        offerUnread: offer === null,
        offeredIds: offeredInclusionIds(offer),
        denialReason: inclusionsDenialReason(offer),
        orphaned: hasOrphanedInclusions(offer, row.draft.inclusions.length),
      });
    }
    return map;
  }, [rows, inclusionOffers]);

  const inclusionsFor = useCallback(
    (key: string): RowInclusions => inclusionsByKey.get(key) ?? INCLUSIONS_SHOWN,
    [inclusionsByKey],
  );

  const projectsFor = useCallback(
    (key: string): RowProject => rowProjects.get(key) ?? ROW_PROJECT_UNRESOLVED,
    [rowProjects],
  );

  /**
   * Whether this row's regions block is on screen — and therefore whether a save
   * may carry `regionIds` at all.
   *
   * ⚠️ **ONE answer, read by the render AND by both payload builders**, the
   * same arrangement `projectsFor` and `inclusionsFor` have and for the same
   * reason. A second test here would be a second answer, and the two would
   * diverge on exactly the row where it mattered: a hidden control emitting a
   * full REPLACE.
   */
  const showsRegions = useCallback(
    (row: Row): boolean =>
      showsRegionsControl(
        row,
        projectsFor(row.key).showProject,
        /*
         * ⚠️ Both come from the SAME resolved row, and they default in opposite
         * directions — `showProject` is `true` when nothing is known, this is
         * `false`. `ROW_PROJECT_UNRESOLVED` is where that pair is set; the two
         * fail towards showing the project and hiding the regions respectively,
         * which is what keeps an unresolved row from offering a geography it may
         * have no business recording.
         */
        projectsFor(row.key).projectHasNoCities,
      ),
    [projectsFor],
  );

  /**
   * The other half of the same decision — see {@link showsCitiesControl}.
   *
   * ⚠️ **A RENDER answer and nothing more.** Unlike `showsRegions`, no payload
   * builder takes this: cities are written under `showProject`, which this does
   * not touch. That asymmetry is deliberate and is what makes hiding the control
   * incapable of dropping a stored city.
   */
  const showsCities = useCallback(
    (row: Row): boolean =>
      showsCitiesControl(
        row,
        projectsFor(row.key).showProject,
        projectsFor(row.key).projectHasNoCities,
      ),
    [projectsFor],
  );

  /*
   * ── WHAT A LINE STARTS WITH ───────────────────────────────────────────────
   *
   * A stable identity for the `(product, quantity)` pairs a preset is wanted
   * for, built the way `productIdKey` above is and for the same reason: a memo
   * over `rows` rebuilds on every keystroke anywhere in the grid, and this one
   * drives a REQUEST. Both halves are integers, so the string round-trip is
   * total.
   *
   * ⚠️ **Only rows {@link seedsPresets} admits contribute**, so a saved line, a
   * converted deal line and a COPIED QUOTE LINE never cause a read at all —
   * cheaper, and it keeps "which rows may be changed" a single answer rather
   * than a fetch gate and a seed gate that can disagree.
   */
  const presetPairKey = rows
    .map((row) => {
      if (!seedsPresets(row) || row.draft.productId === '') return '';
      const quantity = presetQuantityOf(row.draft.quantity);
      return quantity === null
        ? ''
        : presetPairKeyOf(
            Number(row.draft.productId),
            quantity,
            presetProjectIdOf(row.draft.projectId),
          );
    })
    .filter((entry) => entry !== '')
    .join(',');
  const presetPairs = useMemo<readonly PresetPair[]>(() => {
    const seen = new Set<string>();
    const pairs: PresetPair[] = [];
    for (const entry of presetPairKey.split(',')) {
      if (entry === '' || seen.has(entry)) continue;
      seen.add(entry);
      const [productId, quantity, projectId = ''] = entry.split(':');
      pairs.push({
        productId: Number(productId),
        quantity: Number(quantity),
        projectId: projectId === '' ? null : Number(projectId),
      });
    }
    return pairs;
  }, [presetPairKey]);
  const inclusionPresets = useInclusionPresets(presetPairs);

  /**
   * Each read product's recommended discount amount.
   *
   * ⚠️ **`products.rows` ONLY, never `seededProducts`** — a plan carries no
   * recommendation, so a seeded product's entry is `null` by construction and
   * folding the two maps would say "no recommendation" about a product that has
   * one, until the catalogue landed and silently changed its mind.
   *
   * ⚠️ **A lookup miss and a stored `null` are collapsed below, and here that is
   * safe where {@link catalogPrices} could not do it**: `recommendedDiscountSeed`
   * answers "change nothing" for both, and this memo is a dependency of the seed
   * effect, so an unread product simply seeds nothing until the list arrives and
   * the effect runs again.
   */
  const recommendedDiscounts = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) map.set(product.id, product.recommendedDiscountAmount);
    return map;
  }, [products.rows]);

  /**
   * Each read product's unit fields — step and label. A product nobody has read
   * answers `undefined`, which every rule in `@/lib/product-units` reads as "no
   * step, no unit": the server stays the boundary for it.
   */
  const productUnits = useMemo(() => {
    const map = new Map<number, ProductUnitFields>();
    for (const product of products.rows) {
      map.set(product.id, { quantityStep: product.quantityStep, unitLabel: product.unitLabel });
    }
    return map;
  }, [products.rows]);

  /**
   * What the seed has put on each row so far, keyed by `row.key`.
   *
   * ⚠️ **A REF, not state and not part of the draft** — see {@link RowPresetSeed}.
   * It is written only from inside the effect below, immediately before the
   * matching `setRows`, so the two can never describe different rows.
   */
  const seededPresets = useRef<Map<string, RowPresetSeed>>(new Map());

  /**
   * Seed the recommended discount and the quantity's inclusion preset into the
   * DRAFT — nothing here writes to the server.
   *
   * ⚠️ **oxlint warns `react(set-state-in-effect)` HERE AND MUST NOT BE OBEYED
   * LITERALLY**, for the reason spelled out on the two conversion seed effects
   * above: `rows` is not DERIVED from the preset, it is SEEDED from it and then
   * EDITED, so deriving it during render would recompute every row on every
   * keystroke and throw the edit away. That is the whole feature.
   *
   * ⚠️ **IT CONVERGES IN EXACTLY ONE EXTRA PASS, and that is a property of
   * `@/lib/line-presets` rather than of this loop.** Both rules return `null`
   * once the line already holds what they would put there, so the pass after a
   * patch produces an empty `patches` and returns before touching state.
   *
   * ⛔ **IT DOES NOT CLEAR `error`, `fieldErrors` OR `refusal`, unlike
   * {@link patchRow}.** A seed is not a person's edit: wiping a server refusal
   * because a catalogue read landed would take a sentence off the screen that
   * nobody had answered.
   */
  useEffect(() => {
    /*
     * ⚠️ Both gates are about whether a change may be PROPOSED at all. A reader
     * who cannot edit, or a conversion that has already landed and put every box
     * dead, must not find figures moving under them — `convertLocked` in
     * particular means a retried Save re-sends the header alone, so a seed there
     * would be a draft edit nothing could ever send.
     */
    if (!canEdit || convertLocked) return;
    const patches = new Map<string, Partial<Draft>>();
    const records = new Map<string, RowPresetSeed>();
    for (const row of rows) {
      if (!seedsPresets(row) || row.state === 'saving' || row.draft.productId === '') continue;
      const productId = Number(row.draft.productId);
      const seeded = seededPresets.current.get(row.key) ?? NO_ROW_PRESET_SEED;
      const patch: Partial<Draft> = {};
      const record: RowPresetSeed = { ...seeded };

      /*
       * ⚠️ **A seeded AMOUNT puts the line in amount mode**, because the two
       * discount columns are mutually exclusive and both non-zero is a 422. The
       * rule refuses to do it over a real percentage or over an amount somebody
       * typed; this call site must not soften either by re-deriving the state it
       * passes — `draft.discountAmount === null` IS percentage mode.
       */
      /*
       * A PER-UNIT product recommends a discount per unit, and the line's
       * `discountAmount` discounts the whole line — so the figure seeded is the
       * per-unit amount times the quantity. Because the rule re-seeds only while
       * the line still holds the figure it seeded, a quantity change rescales an
       * untouched discount and leaves a typed one alone. An empty or non-whole
       * quantity answers `null`: change nothing.
       */
      const recommendedForLine = isPerUnitProduct(productUnits.get(productId))
        ? perUnitDiscountTotal(recommendedDiscounts.get(productId) ?? null, row.draft.quantity)
        : (recommendedDiscounts.get(productId) ?? null);
      const discount = recommendedDiscountSeed(recommendedForLine, {
        discountAmount: row.draft.discountAmount,
        discount: row.draft.discount,
        seededDiscountAmount: seeded.discountAmount,
      });
      if (discount !== null) {
        patch.discountAmount = discount;
        record.discountAmount = discount;
      }

      /*
       * ⛔ **NOT SEEDED WHERE THE BLOCK IS NOT SHOWN.** `createPayload` and
       * `updatePayload` OMIT `inclusions` for a row whose editor is hidden —
       * emitting `[]` would REPLACE the whole set with nothing — so a seed landing
       * there would be invisible, unsendable draft state at best. `inclusionsFor`
       * is the one answer the render and both payload builders already read, so
       * asking it here cannot disagree with either.
       *
       * ⚠️ An UNREAD offer answers `offers: true` and is seeded, deliberately: it
       * shows the editor, so the seed lands somewhere a person can see and change
       * it. If the offer then comes back "none linked" the block hides and the
       * payload omits the key — nothing reaches the wire, and `isDirty` agrees
       * with the payload, so the row cannot light up over a set it will not send.
       */
      if (inclusionsFor(row.key).offers) {
        const quantity = presetQuantityOf(row.draft.quantity);
        const resolved =
          quantity === null
            ? null
            : (inclusionPresets.get(
                presetPairKeyOf(productId, quantity, presetProjectIdOf(row.draft.projectId)),
              ) ?? null);
        /*
         * ⚠️ **`?? null` above is "not read", which the rule turns into CHANGE
         * NOTHING.** It is never `?? []`: an empty array would read as a band
         * presetting nothing and CLEAR a set the person may have chosen.
         *
         * ⛔ **The entries are NOT narrowed to `offeredIds`.** An inclusion an
         * administrator put in a band and not in the link table renders in the
         * editor's own "on this line, but not offered on this item" block, which
         * is a visible oddity somebody reports; dropping it silently is a
         * deliberate preset quietly going missing.
         */
        const next = inclusionPresetSeed(
          resolved,
          lineInclusionDraftsOf(row.draft.inclusions),
          seeded.inclusions,
        );
        if (next !== null) {
          patch.inclusions = inclusionDraftsOf(next);
          record.inclusions = next;
        }
      }

      if (patch.discountAmount === undefined && patch.inclusions === undefined) continue;
      patches.set(row.key, patch);
      records.set(row.key, record);
    }
    if (patches.size === 0) return;
    for (const [key, record] of records) seededPresets.current.set(key, record);
    setRows((current) =>
      current.map((row) => {
        const patch = patches.get(row.key);
        return patch === undefined ? row : { ...row, draft: { ...row.draft, ...patch } };
      }),
    );
  }, [canEdit, convertLocked, rows, recommendedDiscounts, productUnits, inclusionPresets, inclusionsFor]);

  const duplicatePairs = useMemo(() => duplicatePairsOf(rows), [rows]);

  /**
   * Each read product's own `unitPrice`.
   *
   * ⚠️ **A `Map` lookup misses in two ways and the caller must tell them
   * apart**: a stored `null` is "this product records no price", `undefined` is
   * "nothing has read this product" — the list is capped at 100 rows and can
   * fail outright. {@link PriceControl} keeps them separate; the preview below
   * deliberately collapses them, because an unread price and an absent one both
   * mean there is no figure to preview.
   */
  const catalogPrices = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) map.set(product.id, product.unitPrice);
    return map;
  }, [products.rows]);

  /*
   * ⚠️ **A row with no item answers `undefined`, not `null`.** `null` is the
   * claim "this product records no price", and a row that has not chosen a
   * product has made no such claim about anything — saying so would print "No
   * catalogue price" over an empty item box.
   */
  const catalogPriceFor = useCallback(
    (row: Row): string | null | undefined =>
      row.draft.productId === '' ? undefined : catalogPrices.get(Number(row.draft.productId)),
    [catalogPrices],
  );

  /*
   * ── CITY PRO-RATA (owner, 2026-10-18) ─────────────────────────────────────
   *
   * The catalogue price is the FULL-TOUR rate; a line picking some of its
   * project's cities inherits `price × picked ÷ N`. N is the project's own
   * city count, read off the same `projects.rows` the city picker offers from.
   * A project this list has NOT read (inactive, or the list still loading) has
   * an UNKNOWN count, never 0: no pro-rata is previewed and no "follows its
   * cities" decision is made — the stored price shows and the server decides.
   *
   * ⚠️ *SUPERSEDED 2026-10-27: "Nothing here is ever SENT."* A new row still
   * omits `unitPrice` when only this catalogue rule applies (the server
   * resolves the same rule), but a computed PROJECT price, and any repriced
   * figure on a saved line, is now sent — see the project pricing block below.
   * Typed prices are the person's and are shown, and sent, as typed.
   *
   * On the deal-convert path a row's box holds the deal line's own price; when
   * its product, project or cities move and that price was not typed over, the
   * server reprices it for the new selection (owner 2026-10-18), so the box
   * previews that figure while `lineSet` still omits `unitPrice`.
   */
  const projectCityCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const project of projects.rows) map.set(project.id, project.cities.length);
    return map;
  }, [projects.rows]);

  /*
   * ── PROJECT PRICING (owner, 2026-10-27) ───────────────────────────────────
   *
   * An untyped line now follows its PROJECT PRICE — an entry per project ×
   * product × currency × tier — and falls back to the catalogue pro-rata above
   * only when its project has no entry. Both rules are `computeLinePrice`
   * (`@/lib/line-pricing`); this grid decides only when a line follows and
   * what is sent. Unlike the pro-rata, a computed price IS sent (see
   * {@link PricedLineWrite}). Typed prices are the person's and never move.
   *
   * While a row's project prices (or the tier master) are still loading, its
   * price is UNKNOWN — `undefined` — and nothing is previewed or repriced: a
   * catalogue figure shown for a moment and then replaced would be a price
   * nobody chose.
   */
  const pricedProjectIds = useMemo(() => {
    const ids: number[] = [];
    for (const row of rows) {
      if (row.draft.projectId !== '') ids.push(Number(row.draft.projectId));
      const source = row.saved ?? row.convert ?? row.quoteConvert;
      if (source !== null && source.project !== null) ids.push(source.project.id);
    }
    return ids;
  }, [rows]);
  const projectPrices = useProjectPrices(pricedProjectIds);
  /*
   * The currency the STORED lines were priced in: the document currency as this
   * grid first saw it. A saved untyped line whose currency moves since then is
   * repriced like one whose cities moved.
   */
  const [pricingBaseCurrency] = useState(currency);

  /**
   * The price a line with these inputs inherits, or `undefined` while anything
   * it needs is unread. `unitPrice: null` is a product with no catalogue price
   * and no project price.
   */
  const linePriceFor = useCallback(
    (args: LinePriceArgs): ComputedLinePrice | undefined => {
      if (args.productId === '') return undefined;
      const productId = Number(args.productId);
      const catalog = catalogPrices.get(productId);
      let entries: readonly ProjectPriceEntry[] = [];
      if (args.projectId !== '') {
        const read = projectPrices.entriesByProject.get(Number(args.projectId));
        if (read === undefined && projectPrices.loading) return undefined;
        entries = read ?? [];
      }
      if (entries.length > 0 && projectPrices.defaultTierId === null && projectPrices.loading) {
        return undefined;
      }
      const projectCityCount =
        args.projectId === '' ? 0 : projectCityCounts.get(Number(args.projectId));
      const proRates = !args.allCities && args.cityCount > 0;
      if (projectCityCount === undefined && (entries.length > 0 || proRates)) return undefined;
      const result = computeLinePrice({
        entries,
        defaultTierId: projectPrices.defaultTierId,
        productId,
        currency: args.currency,
        priceTierId: args.priceTierId,
        selectedCityCount: args.cityCount,
        projectCityCount: projectCityCount ?? 0,
        allProjectCities: args.allCities,
        // `''` is the server's default of 1; anything not a whole number falls to
        // the catalogue rule inside `computeLinePrice`.
        quantity: args.quantity === '' ? 1 : Number(args.quantity),
        catalogUnitPrice: catalog ?? null,
      });
      if (result.source === 'catalogue' && catalog === undefined) return undefined;
      return result;
    },
    [
      catalogPrices,
      projectCityCounts,
      projectPrices.entriesByProject,
      projectPrices.loading,
      projectPrices.defaultTierId,
    ],
  );

  /** The price this row's DRAFT selection inherits, whatever its mode. */
  const inheritedLinePriceFor = useCallback(
    (row: Row): ComputedLinePrice | undefined =>
      linePriceFor(
        linePriceArgsOf(row.draft, currency, row.saved === null ? '' : String(row.saved.quantity)),
      ),
    [linePriceFor, currency],
  );

  /** What a blank Price box on this row inherits: its project price, else the catalogue pro-rata. */
  const inheritedPriceFor = useCallback(
    (row: Row): string | null | undefined => inheritedLinePriceFor(row)?.unitPrice,
    [inheritedLinePriceFor],
  );

  /**
   * The price the server will store on a line whose price was never typed
   * over, once its product, project or cities move — or `undefined` when that
   * rule does not apply and the row's own price stands.
   *
   * A SAVED line: only when the box still holds the stored price (or is blank),
   * the stored price equals the pro-rata for the line's SAVED selection, and
   * the selection has moved. Then the PATCH omits `unitPrice` (as it already
   * does for an unchanged price) and the server reprices.
   *
   * A DEAL-CONVERT row: when the box still holds the deal line's own price, that
   * price equals the pro-rata of the deal line's OWN selection (never typed
   * over), and the selection has moved from the deal line's; `lineSet` then carries no
   * `unitPrice` and the server reprices to the pro-rata of the new selection.
   *
   * ⚠️ A stored `null` price is an explicit "no price" and is never repriced,
   * and an unknown city count (project not in the read list) previews nothing.
   */
  const repricedSavedLinePriceFor = useCallback(
    (row: Row): ComputedLinePrice | undefined => {
      const draft = row.draft;
      const selectionMovedFrom = (base: Draft): boolean =>
        draft.productId !== base.productId ||
        draft.projectId !== base.projectId ||
        draft.allCities !== base.allCities ||
        (!draft.allCities && citiesKey(draft.cityIds) !== citiesKey(base.cityIds));
      /*
       * Whether `stored` is still the price its OWN inputs inherit — under the
       * project price, or under the legacy pro-rata (a line priced before its
       * project had prices still follows). Anything else was typed over.
       */
      const follows = (stored: string, base: Draft, baseCurrency: string | null): boolean => {
        const own = linePriceFor(linePriceArgsOf(base, baseCurrency));
        if (own !== undefined && own.unitPrice !== null && decimalStringsEqual(own.unitPrice, stored)) {
          return true;
        }
        if (base.productId === '') return false;
        const catalog = catalogPrices.get(Number(base.productId));
        if (typeof catalog !== 'string') return false;
        const count = base.projectId === '' ? 0 : projectCityCounts.get(Number(base.projectId));
        const proRates = !base.allCities && base.cityIds.length > 0;
        if (count === undefined && proRates) return false;
        const legacy = proRataUnitPrice({
          catalogUnitPrice: catalog,
          projectCityCount: count ?? 0,
          selectedCityCount: base.allCities ? 0 : base.cityIds.length,
          allProjectCities: base.allCities,
        });
        return legacy !== null && decimalStringsEqual(legacy, stored);
      };
      if (convertMode) {
        /*
         * The deal-convert path keeps its 2026-10-18 shape: `lineSet` cannot
         * carry a tier and omits an unchanged price, so only a product, project
         * or cities move reprices a deal price that still follows its rule.
         */
        const source = row.convert;
        if (source === null || source.unitPrice === null) return undefined;
        if (draft.priceMode !== 'value' || draft.price !== source.unitPrice) return undefined;
        const seed = convertRowOf(source).draft;
        if (!selectionMovedFrom(seed)) return undefined;
        if (!follows(source.unitPrice, seed, currency)) return undefined;
        const next = inheritedLinePriceFor(row);
        return next !== undefined && next.unitPrice !== null ? next : undefined;
      }
      const saved = row.saved;
      if (saved === null || saved.unitPrice === null) return undefined;
      const untyped =
        draft.priceMode === 'inherit' ||
        (draft.priceMode === 'value' && draft.price === saved.unitPrice);
      if (!untyped) return undefined;
      const base = draftOf(saved);
      const baseCurrency = pricingBaseCurrency ?? currency;
      const quantityMoved =
        draft.quantity.trim() !== '' && draft.quantity.trim() !== base.quantity;
      const moved =
        selectionMovedFrom(base) ||
        draft.priceTierId !== base.priceTierId ||
        quantityMoved ||
        currency !== baseCurrency;
      if (!moved) return undefined;
      if (!follows(saved.unitPrice, base, baseCurrency)) return undefined;
      const next = inheritedLinePriceFor(row);
      return next !== undefined && next.unitPrice !== null ? next : undefined;
    },
    [
      catalogPrices,
      convertMode,
      currency,
      inheritedLinePriceFor,
      linePriceFor,
      pricingBaseCurrency,
      projectCityCounts,
    ],
  );

  /** The repriced figure alone, for the cells that show it. */
  const repricedSavedPriceFor = useCallback(
    (row: Row): string | null | undefined => repricedSavedLinePriceFor(row)?.unitPrice,
    [repricedSavedLinePriceFor],
  );

  /**
   * What this row's save SENDS as a computed price — see {@link PricedLineWrite}.
   * Never on either conversion path: `lineSet` and `lines` carry no tier, and
   * the server prices those lines itself.
   */
  const pricedWriteFor = useCallback(
    (row: Row): PricedLineWrite | null => {
      if (convertMode || row.quoteConvert !== null) return null;
      if (row.saved === null) {
        if (row.draft.priceMode !== 'inherit') return null;
        const next = inheritedLinePriceFor(row);
        if (next === undefined || next.source !== 'project_price' || next.unitPrice === null) {
          return null;
        }
        return {
          unitPrice: next.unitPrice,
          discountAmount: next.discountAmount,
          priceTierId: next.priceTierId,
        };
      }
      const next = repricedSavedLinePriceFor(row);
      if (next === undefined || next.unitPrice === null) return null;
      if (
        row.saved.unitPrice !== null &&
        decimalStringsEqual(next.unitPrice, row.saved.unitPrice) &&
        next.discountAmount === null
      ) {
        return null;
      }
      return {
        unitPrice: next.unitPrice,
        discountAmount: next.discountAmount,
        priceTierId: next.priceTierId,
      };
    },
    [convertMode, inheritedLinePriceFor, repricedSavedLinePriceFor],
  );

  const previewInputs = useMemo(() => {
    const map = new Map<string, GeneratedLineInput>();
    for (const row of rows) {
      map.set(
        row.key,
        previewInputOf(
          row,
          inheritedPriceFor(row) ?? null,
          companySalesTax,
          repricedSavedPriceFor(row),
          pricedWriteFor(row)?.discountAmount,
        ),
      );
    }
    return map;
    /*
     * ⚠️ **`companySalesTax` is a dependency because a new row's preview READS
     * it**, and a company changed under an open form must move the Amount cell
     * with the Tax rate label. It is expected to be a STABLE identity — the
     * parent hands over a memoised object — so this rebuilds when the company
     * read lands, not on every keystroke in the header fields.
     */
  }, [rows, inheritedPriceFor, repricedSavedPriceFor, pricedWriteFor, companySalesTax]);

  const previewByKey = useMemo(() => {
    const map = new Map<string, GeneratedLinePreview | null>();
    for (const row of rows) {
      const input = previewInputs.get(row.key);
      map.set(row.key, input === undefined ? null : computeGeneratedLinePreview(input));
    }
    return map;
  }, [rows, previewInputs]);

  /*
   * ── THE DESCRIPTION OF A PER-UNIT LINE ────────────────────────────────────
   *
   * A product with a `unitLabel` starts its line with `autoLineDescription` —
   * "SEED AI Visa Tutor: 50 licenses / Standard price … / Discounted price …" —
   * written into the draft as the line's own wording, because the backend's
   * composer knows nothing of units and `description: null` would push its
   * derived text instead.
   *
   * ⛔ **A hand edit is never overwritten.** The text is refreshed only while it
   * is still exactly the auto text for the arguments it was last written from
   * (or, on a stored line, for the stored figures). An empty description is
   * filled only on a row whose product or quantity has moved, and only if this
   * grid has not already written one there: a description somebody cleared
   * stays cleared, and a legacy line nobody touched is left alone.
   *
   * The quote grid carries the same rule; the two move together.
   *
   * ⚠️ oxlint's `react(set-state-in-effect)` warning on the effect below is not
   * to be obeyed, for the reason given on the preset seed effect: the text is
   * SEEDED and then edited, never derived.
   */
  const autoDescriptionArgs = useRef<Map<string, AutoLineDescriptionArgs>>(new Map());

  const unitDescriptionArgsOf = useCallback(
    (
      productId: number,
      quantity: string,
      unitPrice: string | null,
      discountAmount: string | null,
    ): AutoLineDescriptionArgs | null => {
      const unitLabel = unitLabelOf(productUnits.get(productId));
      const productName = productNames.get(productId);
      if (unitLabel === null || productName === undefined || currency === null) return null;
      if (!/^\d+$/.test(quantity.trim())) return null;
      const recommended = recommendedDiscounts.get(productId) ?? null;
      /*
       * The per-unit discount is named only while the line's amount is still
       * the recommended per-unit figure times this quantity. Any other amount
       * is somebody's own, and the description then states the standard price
       * alone rather than a per-unit figure nobody chose.
       */
      const discountPerUnit =
        recommended !== null &&
        discountAmount !== null &&
        discountAmount.trim() === perUnitDiscountTotal(recommended, quantity)
          ? recommended
          : null;
      return {
        productName,
        quantity: quantity.trim(),
        unitLabel,
        unitPrice,
        discountPerUnit,
        currency,
      };
    },
    [productUnits, productNames, recommendedDiscounts, currency],
  );

  useEffect(() => {
    if (!canEdit || convertLocked) return;
    const patches = new Map<string, string | null>();
    const records = new Map<string, AutoLineDescriptionArgs | null>();
    for (const row of rows) {
      if (row.state === 'saving') continue;
      const draft = row.draft;
      const source = row.saved ?? row.convert ?? row.quoteConvert;
      const baseline = unitBaselineOf(row);
      const moved =
        baseline === null ||
        baseline.productId !== draft.productId ||
        baseline.quantity !== draft.quantity;
      const productId = draft.productId === '' ? null : Number(draft.productId);
      /* `inherit` keeps the stored price on a stored line and snapshots the catalogue on a new one. */
      const repriced = repricedSavedPriceFor(row);
      const unitPrice =
        repriced !== undefined
          ? repriced
          : draft.priceMode === 'none'
          ? null
          : draft.priceMode === 'value'
            ? draft.price.trim() === ''
              ? null
              : draft.price.trim()
            : source !== null
              ? source.unitPrice
              : (inheritedPriceFor(row) ?? null);
      const args =
        productId === null
          ? null
          : unitDescriptionArgsOf(productId, draft.quantity, unitPrice, draft.discountAmount);
      const lastArgs = autoDescriptionArgs.current.get(row.key);
      const sourceArgs =
        source === null
          ? null
          : unitDescriptionArgsOf(
              source.product.id,
              String(source.quantity),
              source.unitPrice,
              source.discountAmount,
            );
      const current = draft.description;
      const isOurs =
        current !== null &&
        ((lastArgs !== undefined && isAutoDescription(current, lastArgs)) ||
          (sourceArgs !== null && isAutoDescription(current, sourceArgs)));

      if (args === null) {
        /*
         * The product is no longer sold per unit (or there is none): an
         * untouched auto text goes back to derivation. A product not yet read,
         * or a name or currency not yet known, changes nothing.
         */
        const notPerUnit =
          productId === null ||
          (productUnits.has(productId) && unitLabelOf(productUnits.get(productId)) === null);
        if (isOurs && notPerUnit) {
          patches.set(row.key, null);
          records.set(row.key, null);
        }
        continue;
      }
      if (current !== null && isAutoDescription(current, args)) {
        if (lastArgs !== args) autoDescriptionArgs.current.set(row.key, args);
        continue;
      }
      const empty = current === null || current.trim() === '';
      const write = empty ? moved && lastArgs === undefined : isOurs;
      if (!write) continue;
      patches.set(row.key, autoLineDescription(args));
      records.set(row.key, args);
    }
    if (patches.size === 0) return;
    for (const [key, args] of records) {
      if (args === null) autoDescriptionArgs.current.delete(key);
      else autoDescriptionArgs.current.set(key, args);
    }
    setRows((current) =>
      current.map((row) => {
        const description = patches.get(row.key);
        return description === undefined ? row : { ...row, draft: { ...row.draft, description } };
      }),
    );
  }, [
    canEdit,
    convertLocked,
    rows,
    productUnits,
    inheritedPriceFor,
    repricedSavedPriceFor,
    unitDescriptionArgsOf,
  ]);

  /*
   * ⚠️ **THE `discountAmounts` SCREEN-STATE MAP IS GONE, AND ITS OWN REASONING
   * IS WHY.** It lived here rather than on {@link Draft} because — in the
   * sentence it carried — on the draft it "would join `ROW_WRITE_KEYS`, count
   * towards `isDirty`, and offer itself to every payload builder — for a thing
   * the API has no column for". `discount_amount` exists now and IS sent, so
   * the amount is a `Draft` key like `description` before it: it dirties the
   * row, reaches the payload, survives a reload, and reverts with everything
   * else.
   */

  /**
   * Every CONVERT-MODE row's boxes and structure, the seed it is judged against,
   * and whether it has moved — resolved once, and read by the validator, the
   * dirty test and the payload alike.
   *
   * ⚠️ **EVERY row in convert mode, not only the ones seeded from a deal line.**
   * A row added on this screen has no `convert` line and IS an entry in the
   * `lineSet` about to be sent, so skipping it would leave the one kind of row
   * that can be sent with no product entirely unjudged. `seed === null` is what
   * marks it as new, and it is the same discriminator {@link convertLineSetEntryOf}
   * reads.
   *
   * ⚠️ **One change test, used everywhere.** `convertLineShapesMatch` against a
   * fresh seed is the same question {@link convertLineSetEntryOf} asks when it
   * decides which keys to emit, so a row cannot read as unchanged on screen while
   * a key for it is on the wire. A second rule for "has this moved" is exactly
   * how those two come apart.
   *
   * ⚠️ **The seed is built with THIS row's three offer flags, not with the deal
   * line's own.** Both sides of the comparison have to agree about which controls
   * are on screen, or changing a row's product — which is what makes those flags
   * move — would emit a clear of a set nobody can see.
   *
   * Empty in the other two modes, which is what makes every branch below fall
   * through to the behaviour it has always had.
   */
  const convertShapes = useMemo(() => {
    const map = new Map<
      string,
      { shape: ConvertLineShape; seed: ConvertLineShape | null; changed: boolean }
    >();
    if (!convertMode) return map;
    for (const row of rows) {
      const offersInclusions = inclusionsFor(row.key).offers;
      const offersProjects = projectsFor(row.key).showProject;
      const offersRegions = showsRegions(row);
      const shape = convertShapeOf(row.draft, offersInclusions, offersProjects, offersRegions);
      const seed =
        row.convert === null
          ? null
          : convertShapeOf(
              convertRowOf(row.convert).draft,
              offersInclusions,
              offersProjects,
              offersRegions,
            );
      map.set(row.key, {
        shape,
        seed,
        /* A row that was never on the deal has nothing to be unchanged from. */
        changed: seed === null || !convertLineShapesMatch(shape, seed),
      });
    }
    return map;
  }, [convertMode, rows, inclusionsFor, projectsFor, showsRegions]);

  const localProblems = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      /*
       * A new invoice opens with one blank row. Left untouched it is not judged,
       * so it neither blocks the create nor shows "Choose a product" before
       * anybody has started; `draftLines` already skips a row with no product.
       */
      if (draftMode && row.saved === null && isUntouchedBlankDraft(row.draft)) {
        map.set(row.key, { fieldErrors: {}, error: null, notice: null });
        continue;
      }
      /*
       * ⚠️ **A CONVERT ROW THAT HAS MOVED IS JUDGED BY `validateRow`, AND AN
       * UNTOUCHED ONE IS NOT JUDGED AT ALL.**
       *
       * `lineSet` carries the product, the project, the cities, the regions and
       * the inclusions, so all of that IS offered to change here — and a row whose
       * city set its project no longer declares is a row the server will refuse.
       * Skipping the check would be the "when in doubt, ENABLE" failure: a submit
       * that stays live and produces a 422 naming a control the grid never looked
       * at.
       *
       * **The skip is on `changed`, never on the mode.** An untouched carry-over
       * row emits `{dealLineId, sortOrder}` and nothing else, so not one of its
       * stored figures reaches the request and none of them can be refused —
       * checking them anyway is a control refusing to run over a figure the person
       * did not choose, and a legacy deal line would then block a conversion
       * nobody had touched. A row somebody ADDED has no seed, so it is always
       * `changed` and always judged, which is what tells them to choose a product
       * on the field rather than through a 422 at `lineSet.3.productId`.
       *
       * ⚠️ The tax percentage `validateRow` checks is `''` on every convert row —
       * the Tax cell is a readout and nothing writes `draft.taxRate` here — so
       * that branch is inert rather than lenient.
       */
      const converted = convertShapes.get(row.key);
      if (converted !== undefined && !converted.changed) {
        map.set(row.key, { fieldErrors: {}, error: null, notice: null });
        continue;
      }
      /*
       * ⚠️ **A COPIED QUOTE ROW IS JUDGED BY `validateConvertLineEdit`, NOT BY
       * `validateRow`, and the difference is not a shortcut.** `validateRow`
       * checks the product, the project, the city set against what the project
       * declares, the region set, the inclusion offers and the duplicate
       * `(product, project)` pair. **Not one of those can be changed on this
       * path and not one of them reaches the request** — `lines` has no key for
       * any of them, and the server copies them from the quote inside its own
       * transaction. Running it here would block a conversion over a stored
       * value the person cannot see, cannot edit, and did not choose: a quote
       * line whose project has since dropped a city would refuse to convert,
       * naming a control that is not on screen.
       *
       * What CAN be refused is exactly the five figures, so exactly those are
       * checked — with the same sentences the deal conversion uses on the same
       * columns, because they are literally the same function.
       *
       * ⚠️ **An UNTOUCHED copied row still runs it, unlike a carry-over row
       * above.** That is not an oversight: the boxes start from the quote's own
       * values, so an untouched row can only be clean, and running the check is
       * how a row that somebody edited and then edited BACK stops being marked.
       */
      if (row.quoteConvert !== null) {
        map.set(row.key, withQuantityStepCheck(row, {
          fieldErrors: validateConvertLineEdit(
            quoteConvertEditOf(row.draft),
            quoteConvertLineEditOf(row.quoteConvert),
          ),
          error: null,
          /*
           * ⚠️ No repeated-product notice on a CONVERT row, deliberately. These
           * rows are the quote's own lines carried across, so a repeat here is
           * one the quote already carries and was already pointed out on that
           * screen. Saying it again at the moment somebody is converting reads
           * as a new problem introduced by the conversion.
           */
          notice: null,
        }, productUnits));
        continue;
      }
      /*
       * ⚠️ **NO SCREEN STATE IS INJECTED HERE.** An argument once carried the
       * amount box's conversion refusal into this call, because the amount was
       * not on the draft and `validateRow` — pure over the draft — could not
       * see it. `discountAmount` IS on the draft now, so `validateRow` checks
       * it directly and the hand-off is gone with the conversion that needed it.
       *
       * ⚠️ *SUPERSEDED, and only the COUNT: "**NOTHING IS INJECTED HERE ANY
       * MORE.** A **sixth** argument carried the amount box's conversion
       * refusal…"* — there is a sixth argument again, and it is a different
       * kind of thing. The two below are RESOLVED ANSWERS from
       * `@/lib/product-inclusions` and `@/lib/use-line-project`, facts about the
       * catalogue that a pure function over one draft cannot derive for itself.
       * The rule the old sentence was protecting is untouched: **nothing a
       * CONTROL is holding may be handed in here**, because a validator that
       * reads the screen disagrees with the payload the moment a control
       * unmounts.
       */
      map.set(
        row.key,
        /*
         * The quantity-step refusal is a catalogue fact like the two above, and
         * it is judged only on a quantity edited or a product picked — see
         * {@link withQuantityStepCheck}.
         */
        withQuantityStepCheck(
          row,
          validateRow(
            row,
            duplicatePairs,
            retiredInclusionIds,
            projectsFor(row.key).declaredCityIds,
            inclusionsFor(row.key).offers,
          ),
          productUnits,
        ),
      );
    }
    return map;
  }, [
    draftMode,
    rows,
    convertShapes,
    duplicatePairs,
    retiredInclusionIds,
    projectsFor,
    inclusionsFor,
    productUnits,
  ]);

  // The server's own answer wins over ours: it saw the post-merge state.
  const problemsByKey = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      const local = localProblems.get(row.key) ?? { fieldErrors: {}, error: null, notice: null };
      /*
       * ⚠️ **A convert row's server refusals arrive from the PARENT, keyed by this
       * grid's ROW KEY**, because the array index a 422 names is a position in the
       * `lineSet` that was sent and only the parent holds that array — along with
       * the `rowKeys` array emitted beside it. They are merged the same way and
       * for the same reason as `row.fieldErrors`: the server's word is about the
       * request that was actually refused.
       *
       * ⚠️ **The condition is the MODE, not `row.convert !== null`.** A row ADDED
       * on this screen has no `dealLineId` and is the kind of row most likely to
       * be refused, so an id-keyed map could not address it at all.
       */
      /*
       * ⚠️ **THE QUOTE PATH KEYS BY THE QUOTE LINE'S ID, NOT BY THE ROW KEY, and
       * only a COPIED row can be addressed at all.** A 422 from
       * `POST /quotes/{id}/convert` names `lines.<n>.<field>` where `n` indexes
       * the SPARSE overrides array; the parent turns that index back into a
       * `quoteProductId` through `quoteConvertLineFieldErrors` and hands the map
       * down keyed that way, so this grid never has to reconstruct it.
       *
       * A row ADDED here is not in that request — it is written afterwards by
       * its own POST and reports on itself through `row.fieldErrors`, exactly as
       * a draft row does. That is why the branch tests `row.quoteConvert`
       * rather than the mode, which is the OPPOSITE of the deal path above and
       * is correct for the opposite reason: there, an added row IS in the
       * `lineSet` and could only be addressed by row key.
       */
      const fromServer = convertMode
        ? (convertLineErrors[row.key] ?? {})
        : row.quoteConvert !== null
          ? (quoteLineErrors[row.quoteConvert.id] ?? {})
          : row.fieldErrors;
      map.set(row.key, {
        fieldErrors: { ...local.fieldErrors, ...fromServer },
        error: row.error ?? local.error,
        /*
         * ⚠️ Carried through, never folded into `error`. Both can be true at
         * once — the server can refuse a row that also repeats a product — and
         * an `error ?? notice` would drop the notice precisely when the row is
         * under the most scrutiny.
         */
        notice: local.notice,
      });
    }
    return map;
  }, [rows, localProblems, convertLineErrors, convertMode, quoteLineErrors]);

  const dirtyKeys = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      /*
       * ⚠️ **`isDirty` answers `true` for every row whose `saved` is `null`, so a
       * convert row must never reach it.** Every carried-over row would read as
       * dirty for ever — "Edited" on the badge, "Recalculated on conversion" in
       * the Amount column, and a blocked count over rows nobody has touched.
       *
       * A row ADDED here is `changed: true` by construction (it has no seed), so
       * it lands in this set the way a draft row does and is judged and counted
       * the same way.
       */
      const converted = convertShapes.get(row.key);
      if (converted !== undefined) {
        if (converted.changed) set.add(row.key);
        continue;
      }
      /*
       * ⚠️ **A COPIED QUOTE ROW MUST NOT REACH `isDirty` EITHER**, for exactly
       * the reason the deal rows above must not: it answers `true` for every row
       * whose `saved` is `null`, so every copied line would read as edited for
       * ever — "Edited" on the badge, every one of them in the blocked count,
       * and an override array naming every line of the quote whether or not
       * anybody touched it.
       *
       * The test is the five figures against the quote line's own values, which
       * is the same comparison that decides what goes on the wire — so a row
       * marked Edited here is exactly a row that will appear in `lines`, and a
       * row edited and then edited back is neither.
       */
      if (row.quoteConvert !== null) {
        const seed = quoteConvertLineEditOf(row.quoteConvert);
        const edit = quoteConvertEditOf(row.draft);
        if (
          edit.quantity !== seed.quantity ||
          edit.unitPrice !== seed.unitPrice ||
          edit.discount !== seed.discount ||
          edit.discountAmount !== seed.discountAmount ||
          edit.description !== seed.description
        ) {
          set.add(row.key);
        }
        continue;
      }
      if (
        isDirty(
          row,
          inclusionsFor(row.key).offers,
          projectsFor(row.key).showProject,
          showsRegions(row),
          pricedWriteFor(row),
        )
      ) {
        set.add(row.key);
      }
    }
    return set;
  }, [rows, convertShapes, inclusionsFor, projectsFor, showsRegions, pricedWriteFor]);

  const blockedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const key of dirtyKeys) {
      const local = localProblems.get(key);
      if (!local) continue;
      if (Object.keys(local.fieldErrors).length > 0 || local.error !== null) set.add(key);
    }
    return set;
  }, [dirtyKeys, localProblems]);

  /* ── Draft mode: what goes upward ─────────────────────────────────────── */

  /**
   * The rows as POST bodies, in grid order. `null` in saved mode.
   *
   * ⚠️ **Blocked rows are WITHHELD, not emitted with their faults.** The parent
   * creates the invoice first and writes these afterwards, so a line the server
   * would refuse becomes a 422 arriving after a real document exists — against
   * a row the person can no longer see, on a screen that has already closed.
   * The count of what is being held back goes up separately, so the parent
   * refuses to submit instead. An invoice cannot be un-created; a row that
   * cannot be written has to stop the create, not be dropped after it.
   *
   * `sortOrder` is the row's index in the WHOLE grid, gaps included: it is a
   * display order, not a sequence, and keeping the grid's own positions means
   * the document prints in the order the person arranged.
   */
  const draftLines = useMemo(() => {
    if (!draftMode && !quoteConvertMode) return null;
    const lines: InvoiceProductCreateInput[] = [];
    rows.forEach((row, index) => {
      /*
       * ⚠️ **A COPIED QUOTE ROW IS NEVER A DRAFT PAYLOAD, and this is the single
       * most expensive line in the mode to get wrong.** The server copies those
       * lines inside the conversion's own transaction; emitting them here as
       * well would `POST /invoices/{id}/products` a second copy of every line of
       * the quote — an invoice demanding money TWICE for the same work, from a
       * screen where everything looked correct. Only rows somebody ADDED here
       * have no server-side copy and therefore need one.
       */
      if (row.quoteConvert !== null) return;
      if (row.draft.productId === '' || blockedKeys.has(row.key)) return;
      // The same builder the saved POST uses. There is no second one — which is
      // also what keeps `unitPrice` OMITTED under `inherit` on this path.
      lines.push(
        createPayload(
          row.draft,
          index,
          inclusionsFor(row.key).offers,
          projectsFor(row.key).showProject,
          showsRegions(row),
          pricedWriteFor(row),
        ),
      );
    });
    return lines;
  }, [
    draftMode,
    quoteConvertMode,
    rows,
    blockedKeys,
    inclusionsFor,
    projectsFor,
    showsRegions,
    pricedWriteFor,
  ]);

  /*
   * ⚠️ **On the quote path this counts BOTH populations, deliberately.** A
   * blocked copied row cannot be dropped the way a blocked draft row is — its
   * fault travels in `lines`, so the 422 lands on the CONVERSION itself and the
   * cost is no invoice at all. A blocked added row is withheld from the POSTs.
   * Either way the parent must refuse to submit, so one count serves both.
   */
  const draftBlockedCount = draftMode || quoteConvertMode ? blockedKeys.size : 0;

  /*
   * The callbacks live in refs so the emission depends on the CONTENT and never
   * on the parent's render. An inline arrow — which is what a modal will pass —
   * has a new identity every render, and in the deps it would re-emit on every
   * keystroke in the invoice's own header fields.
   */
  const emitLines = useRef(onDraftLinesChange);
  const emitBlocked = useRef(onDraftBlockedChange);
  useEffect(() => {
    emitLines.current = onDraftLinesChange;
    emitBlocked.current = onDraftBlockedChange;
  });

  useEffect(() => {
    if (draftLines === null) return;
    emitLines.current?.(draftLines);
  }, [draftLines]);

  useEffect(() => {
    if (!draftMode && !quoteConvertMode) return;
    emitBlocked.current?.(draftBlockedCount);
  }, [draftMode, quoteConvertMode, draftBlockedCount]);

  /* ── Quote-convert mode: the COPIED rows' respellings ─────────────────── */

  /**
   * The `lines` array `POST /quotes/{id}/convert` takes — **only the rows that
   * MOVED**, in the order the quote's lines were read.
   *
   * ⚠️ **SPARSE, and that is the contract rather than an optimisation.** A key
   * restating a stored value is a key the server can refuse for a figure nobody
   * chose; and a line left out is still copied, unchanged, which is the whole
   * difference from the deal path's `lineSet`. `quoteConvertLineOverrides` walks
   * `quoteLines` rather than `rows`, so the array's ORDER is the quote's own —
   * which is what makes the index a 422 names routable back to a line.
   *
   * ⚠️ **`[]` means "send no `lines` key at all"**, and the parent omits it. An
   * empty array is a different instruction from an absent one.
   *
   * ⚠️ **`null` until the quote's lines are read**, never `[]`: a grid that has
   * not loaded must be unable to say anything rather than able to say *none*.
   */
  const quoteOverrides = useMemo(() => {
    if (!quoteConvertMode || quoteLines === null) return null;
    const edits: Record<number, ConvertLineEdit> = {};
    // The edited internal note rides beside the figures; sent only when it moved.
    const notes: Record<number, string | null> = {};
    for (const row of rows) {
      if (row.quoteConvert === null) continue;
      edits[row.quoteConvert.id] = quoteConvertEditOf(row.draft);
      notes[row.quoteConvert.id] = row.draft.notes;
    }
    return quoteConvertLineOverrides(quoteLines, edits, notes);
  }, [quoteConvertMode, quoteLines, rows]);

  /* Refs for the reason the draft emitters use them — content, not identity. */
  const emitQuoteOverrides = useRef(onQuoteOverridesChange);
  useEffect(() => {
    emitQuoteOverrides.current = onQuoteOverridesChange;
  });

  /*
   * ⚠️ **A `null` IS EMITTED, and the guard is on the MODE rather than on the
   * value** — the same arrangement the deal path uses. `null` means both "not
   * this mode" and "the quote's lines have not been read", and a parent left
   * holding the previous value would carry overrides from a quote it is no
   * longer looking at.
   */
  useEffect(() => {
    if (!quoteConvertMode) return;
    emitQuoteOverrides.current?.(quoteOverrides);
  }, [quoteConvertMode, quoteOverrides]);

  /* ── Convert mode: what goes upward ───────────────────────────────────── */

  /**
   * The lines as they stand — **exactly the `lineSet` array
   * `POST /deals/{id}/invoices` takes** — in GRID ORDER, with the row key each
   * entry came from and whether the set is untouched.
   *
   * ⚠️ **IT ITERATES THE ROWS, and it has to.** `lineSet` is the FINAL list: rows
   * can be added, removed and reordered relative to the deal, so the deal's own
   * array can no longer describe it. The index is the routing key a 422 names —
   * a position in `rows` — and `rowKeys` is what carries that fact upward so the
   * parent never has to reconstruct it.
   *
   * ⚠️ **`entries` and `rowKeys` are built in ONE pass and pushed together.** Two
   * loops would agree the day they were written; one loop makes `entries[i]` and
   * `rowKeys[i]` the same row by construction.
   *
   * ⚠️ **`null` until the deal's lines are read**, and never `[]`. An empty
   * `lineSet` is an invoice with NO LINES — a real instruction — so a grid that
   * has not loaded must be unable to say anything rather than able to say *none*.
   *
   * ⚠️ **Blocked rows are INCLUDED, unlike draft mode's.** A row that is refused
   * does not get dropped on the floor, it takes the whole conversion with it — no
   * invoice at all rather than an invoice missing a line — so the answer is to
   * refuse to submit, which the blocked count below is for. Withholding it would
   * raise an invoice without a line somebody had added.
   */
  const convertLineSet = useMemo(() => {
    if (!convertMode || dealLines === null) return null;
    const entries: DealConvertLineSetEntry[] = [];
    const rowKeys: string[] = [];
    rows.forEach((row, index) => {
      const resolved = convertShapes.get(row.key);
      if (resolved === undefined) return;
      const entry = convertLineSetEntryOf(
        resolved.shape,
        resolved.seed,
        row.convert?.id ?? null,
        index,
      );
      /*
       * The internal note rides OUTSIDE the shape (see `convertLineNotesWrite`):
       * sent only when it moved, or on an added row that has one. An entry
       * carrying it is no longer a pure carry-over, so `pristine` goes false.
       */
      const notes = convertLineNotesWrite(
        row.draft.notes,
        row.convert === null ? null : row.convert.notes,
      );
      if (notes !== undefined) entry.notes = notes;
      entries.push(entry);
      rowKeys.push(row.key);
    });
    /*
     * ⚠️ **PRISTINE IS READ OFF THE BUILT ENTRIES, plus the one thing they cannot
     * say: that no row was REMOVED.** An entry is a carry-over when it holds
     * nothing but its `dealLineId` and its position — but a grid holding two
     * untouched rows out of the deal's three produces two carry-over entries and
     * is emphatically not pristine, and sending no `lineSet` for it would silently
     * restore the line somebody deleted. Hence the length check, and it is against
     * the DEAL's own array rather than against the row count.
     */
    const pristine =
      entries.length === dealLines.length && entries.every(convertLineSetEntryIsCarryOver);
    return { entries, rowKeys, pristine };
  }, [convertMode, rows, convertShapes, dealLines]);

  const convertBlockedCount = convertMode ? blockedKeys.size : 0;

  /*
   * Refs for the same reason the draft emitters use them: the emission depends on
   * the CONTENT and never on the parent's render identity, so an inline arrow in
   * the modal cannot re-emit on every keystroke in the invoice's header.
   */
  const emitConvertLines = useRef(onConvertLinesChange);
  const emitConvertBlocked = useRef(onConvertBlockedChange);
  useEffect(() => {
    emitConvertLines.current = onConvertLinesChange;
    emitConvertBlocked.current = onConvertBlockedChange;
  });

  /*
   * ⚠️ **A `null` IS EMITTED, and the guard is on the MODE rather than on the
   * value.** `null` means both "not convert mode" AND "the deal's lines have not
   * been read", and a parent left holding the previous value would keep a
   * `lineSet` from a deal it is no longer looking at.
   */
  useEffect(() => {
    if (!convertMode) return;
    emitConvertLines.current?.(convertLineSet);
  }, [convertMode, convertLineSet]);

  useEffect(() => {
    if (!convertMode) return;
    emitConvertBlocked.current?.(convertBlockedCount);
  }, [convertMode, convertBlockedCount]);

  /*
   * ⚠️ The footer total is summed from the SERVER's own per-line `lineTotal`
   * values and is a cross-check against the invoice's stored `grandTotal`,
   * never a replacement for it. The database rounds per line, so re-deriving
   * from prices gives a different answer — and the header's figures are the
   * ones the customer is billed.
   */
  const savedTotal = useMemo(
    () => sumDecimalStrings(rows.map((row) => row.saved?.lineTotal ?? null)),
    [rows],
  );

  /**
   * The draft footer total — the sum of the PREVIEWS, and `null` in saved mode.
   *
   * ⚠️ **This is our arithmetic, not the server's, and the footer says so.**
   * There is no stored figure to sum in draft mode: `lineTotal` is a generated
   * column and no line exists yet. Summing the per-line previews is the same
   * operation `savedTotal` performs on the server's own values — each line
   * rounded first, then added — so it will agree with what the database stores,
   * but it is still a preview and is never labelled as a total.
   *
   * A row with no price contributes nothing rather than zero: its preview is
   * `null`, which is "no figure", not "0.00".
   */
  const draftPreviewTotal = useMemo(
    () =>
      draftMode
        ? sumDecimalStrings(rows.map((row) => previewByKey.get(row.key)?.lineTotal ?? null))
        : null,
    [draftMode, rows, previewByKey],
  );

  const readOnly = !canEdit;
  /**
   * Whether ROWS may be added or removed at all.
   *
   * ⚠️ **TRUE IN CONVERT MODE, AND THE ENDPOINT DECIDES IT RATHER THAN TASTE.**
   * `lineSet` is the complete final list: an entry with no `dealLineId` IS an
   * added line, and a row simply left out of the array is a removed one. Both
   * controls therefore have a request to make.
   *
   * ⚠️ **`convertLocked` still forces it false**, and that half is not cosmetic:
   * once the conversion has landed the invoice exists, a retried Save re-sends
   * the HEADER alone, and an Add row control there would take a line somebody
   * typed and silently drop it.
   */
  const canManageRows = !readOnly && !convertLocked;
  const dirtyCount = dirtyKeys.size;

  /*
   * ── TELLING THE PARENT ────────────────────────────────────────────────────
   *
   * Both effects are inert in draft, convert and quote-convert mode: neither
   * prop exists there, so `undefined?.()` is the whole of it and no branch is
   * needed.
   *
   * ⚠️ **Placed here rather than beside the render, because FOUR early returns
   * sit between this point and the JSX** — loading, a failed quote read, a
   * failed deal read, a failed line read. A hook below any of them would run on
   * some renders and not others, which React refuses; and a grid that stopped
   * reporting the moment it hit a loading state would hand the parent a stale
   * count of unsaved rows and let it close over them.
   */
  const emitUnsaved = useRef(onUnsavedChange);
  useEffect(() => {
    emitUnsaved.current = onUnsavedChange;
  });
  useEffect(() => {
    emitUnsaved.current?.({ dirty: dirtyCount, blocked: blockedKeys.size });
  }, [dirtyCount, blockedKeys.size]);
  /*
   * ⚠️ **REFRESHED ON EVERY RENDER, deliberately, and this is the whole reason
   * the ref exists.** `saveAll` closes over `rows`, `dirtyKeys` and
   * `blockedKeys` as they were at the render that made it. Handing the parent
   * the function itself once would freeze the row set at first mount, so a save
   * pressed from up there would write whatever was on screen when the modal
   * opened — silently, with a 200 per row.
   */
  const latestSaveAll = useRef(saveAll);
  useEffect(() => {
    latestSaveAll.current = saveAll;
  });
  useEffect(() => {
    if (registerSave === undefined) return;
    registerSave(() => latestSaveAll.current());
    /*
     * ⚠️ Cleared on unmount, so a parent that outlives this grid cannot call a
     * save into a component that is gone. The parent must treat `null` as "no
     * grid", never as "nothing to save".
     */
    return () => registerSave(null);
  }, [registerSave]);

  /**
   * This row's derived description — the string the cell shows while
   * `description` is `null`, and the string the backend will compose at push
   * time for the same line.
   *
   * Every input is gathered here and the composition itself is
   * `composeLineDescription`'s. Nothing in this file joins names together.
   *
   * ## 🔴 THE `inclusions: []` DIFFERENCE FROM THE QUOTE GRID IS GONE
   *
   * *SUPERSEDED, and it had a verbatim quotation inside it, which is why it
   * survived: "⚠️ **`inclusions: []`, AND THAT IS THE ONE DELIBERATE DIFFERENCE
   * FROM THE QUOTE GRID** … `xero-quote.payload.ts` composes it;
   * `xero-invoice.payload.ts` does not. So passing them here would put a
   * bracketed promise in the operator's preview that the customer's invoice does
   * not make … **This is therefore NOT a missing feature and must not be
   * 'completed'.**"*
   *
   * **The owner reversed it** (backend `3cd2e1c`): `xero-invoice.payload.ts`
   * now composes the segment by **importing `inclusionsSegment`** from the quote
   * builder rather than holding a copy, so the two document types cannot diverge
   * on it. Convert copies a NULL description, so the same sale reads identically
   * on the quote and the invoice.
   *
   * ⚠️ **The struck comment was correct when written and then described the
   * exact opposite of the backend's behaviour, in bold, forbidding by name the
   * change that had become correct.** It was quoted here from the module's own
   * header, and quoting is what carried it: the header was fixed and this copy
   * was not, because no build, lint or type can see a quotation age. **The
   * instruction it gave — the invoice composer moves FIRST and this grid
   * SECOND — was followed exactly; what nothing scheduled was the second half.**
   *
   * Everything else IS passed, `productCode` included: the project-name
   * substitution is live on both composers, and `assertLineItemCode` makes the
   * item code REQUIRED on the invoice push, so a line reaching Xero at all has
   * one and the substituted head is what the customer will read.
   */
  function descriptionOf(row: Row): string | null {
    const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
    const productName =
      productId === null
        ? null
        : (productNames.get(productId) ?? row.saved?.product.productName ?? null);
    if (productName === null) return null;
    /*
     * ⚠️ **THE RESOLVED SET, WHICH ON A FLAGGED LINE IS NOT `cityIds`.**
     *
     * The backend composes this same sentence from the cities the line actually
     * runs in, and for `allCities` that is whatever its project has — while
     * this draft's `cityIds` is empty, or is a set held aside and never sent.
     * Reading it here would show the operator a sentence with no cities in it
     * while the customer's document names three, which is exactly the silent
     * divergence the shared composer exists to prevent.
     *
     * ⚠️ **The words "all cities" never appear either way**, and no branch
     * below adds them. The description lists WHERE the work happens — "Campus
     * Outreach — Bangalore, Mumbai, Delhi" — so that a document from March
     * still reads as what was agreed in March. That is the owner's decision and
     * it is the backend's behaviour; a client that wrote "All cities" here
     * would disagree with the string pushed to Xero.
     */
    const named = row.draft.allCities
      ? (rowProjects.get(row.key)?.allCitiesToday ?? []).map((city) => city.name)
      : row.draft.cityIds
          .map((id) => cityNames.get(id))
          .filter((name): name is string => name !== undefined);
    const projectId = row.draft.projectId === '' ? null : Number(row.draft.projectId);
    const projectName = projectId === null ? null : (projectNames.get(projectId) ?? null);
    const productCode = productId === null ? null : (productCodes.get(productId) ?? null);
    /*
     * The line's own regions, resolved to names in the DRAFT's order.
     *
     * ⚠️ **Consumed only on a line with NO cities** — `geographySegment` is
     * cities-else-regions, an EITHER, so on a project-backed row this array is
     * built and never printed. It is passed unconditionally anyway because the
     * member is OPTIONAL: omitting it reads as "this line names no regions",
     * which on a projectless line — EMBA, LAE, Virtual Masterclass — showed a
     * bare product name here while the customer's invoice carried the geography.
     * That was live in production and nothing reported it.
     *
     * An id we cannot name is DROPPED, the same rule the cities follow above.
     */
    const namedRegions = row.draft.regionIds
      .map((id) => regionNames.get(id))
      .filter((name): name is string => name !== undefined);
    /*
     * The line's inclusions, in the DRAFT's own order — which is the order that
     * becomes each entry's `sortOrder` on save, so it is the order the customer's
     * invoice prints in. Never re-sorted here; an order-only divergence is the
     * one nobody reports.
     *
     * The quantity box holds a STRING and `''` means "omit the key", which the
     * server stores as 1 — `Number('')` is `0`, which would print `0 campuses`
     * for a line that will carry one. `effectiveInclusionQuantity` is that rule.
     */
    const lineInclusions = row.draft.inclusions
      .map((entry) => {
        const master = inclusionMasters.get(entry.inclusionId);
        return master === undefined
          ? null
          : { ...master, quantity: effectiveInclusionQuantity(entry.quantity) };
      })
      .filter((entry): entry is LineDescriptionInclusion => entry !== null);
    return composeLineDescription({
      productName,
      projectName,
      productCode,
      cityNames: named,
      regionNames: namedRegions,
      inclusions: lineInclusions,
    });
  }

  /*
   * ⚠️ The product picker deliberately does NOT exclude products already on the
   * invoice. The project picker is filtered by the row's own product, so an
   * exclusion deadlocks the legitimate second-project case. The clash is caught
   * across rows instead, with the sentence the server would use.
   */
  function productOptionsFor(row: Row): ComboboxOption[] {
    const list = products.rows.map((product) => ({
      value: String(product.id),
      label: product.productName,
      ...(product.productCode ? { hint: product.productCode } : {}),
    }));
    const saved = row.saved;
    if (saved !== null && !list.some((option) => option.value === String(saved.product.id))) {
      list.unshift({
        value: String(saved.product.id),
        label: saved.product.productName,
        ...(saved.product.productCode ? { hint: saved.product.productCode } : {}),
      });
    }
    /*
     * ⚠️ **THE SAME PIN FOR A SEEDED ROW, which has no `saved` line to pin
     * from.** A planned product retired since the plan was written is absent
     * from the active catalogue, and an unmatched value renders as an EMPTY Item
     * cell over a real `productId` — a line POSTed naming a product nobody was
     * shown. See `seededProducts`. The quote grid carries this branch verbatim.
     *
     * Guarded on `!list.some` like the branch above, so a planned product that
     * is still active is not listed twice; and keyed on the row's CURRENT
     * product, so a row switched to another item stops matching and gets no pin.
     */
    const seeded = seededProducts.get(Number(row.draft.productId));
    if (
      row.draft.productId !== '' &&
      seeded !== undefined &&
      !list.some((option) => option.value === row.draft.productId)
    ) {
      list.unshift({
        value: row.draft.productId,
        label: seeded.productName,
        ...(seeded.productCode ? { hint: seeded.productCode } : {}),
      });
    }
    return list;
  }

  /**
   * Options for one row's region multiselect.
   *
   * ⚠️ **A region the LINE holds that the master no longer offers is pinned
   * back in, under the name the READ gave us.** Reads carry no `is_active`
   * filter, so the line still resolves a retired region perfectly; dropping it
   * from the options would render a bare id AND — far worse — silently remove it
   * from the set the next save writes, because `regionIds` is a full REPLACE.
   * A value nobody touched, lost with a 200.
   *
   * ⚠️ **Only CALLED retired once the master has settled.** An in-flight or
   * failed fetch has empty `records`, where every stored region looks absent and
   * none of them is actually retired — so the hint is withheld rather than
   * asserted.
   */
  function regionOptionsFor(row: Row, resolved: RowProject): MultiComboboxOption[] {
    const settled = !regions.loading && !regions.failed;
    /*
     * ⚠️ **Two sources, and which one is in play is the owner's decision.** A
     * row whose project resolved offers THAT PROJECT'S regions — the line stays
     * inside the geography the project actually covers. A row with no project,
     * or one whose project could not be read, keeps the `/regions` master,
     * because there is no set to narrow against and narrowing on an unknown is
     * how a stored value silently leaves a whole-set REPLACE.
     */
    const narrowed = resolved.projectRegions !== null;
    const source = narrowed
      ? (resolved.projectRegions ?? [])
      : regions.records.map((record) => ({ id: record.id, name: record.name }));

    const list: MultiComboboxOption[] = source.map((region) => ({
      value: String(region.id),
      label: region.name,
    }));

    /*
     * Names, master first and the line's own read LAST so it wins. Reads carry
     * no `is_active` filter, so the name the line was GIVEN is the right one for
     * a region the master has since retired.
     */
    const nameOf = new Map<number, string>();
    for (const record of regions.records) nameOf.set(record.id, record.name);
    for (const region of row.saved?.regions ?? []) nameOf.set(region.id, region.name);

    /*
     * ⚠️ **Everything the ROW HOLDS is pinned back in — draft and saved both.**
     * `regionIds` is a full REPLACE, so an id missing from these options is not
     * merely unlabelled: the next save writes the set without it, and a value
     * nobody touched is gone with a `200`. That was already true of a region the
     * master retired; narrowing to a project makes it true of every region the
     * project does not declare as well, which is a far more reachable state.
     */
    const held = new Set<number>(row.draft.regionIds);
    for (const region of row.saved?.regions ?? []) held.add(region.id);
    for (const id of held) {
      if (list.some((option) => option.value === String(id))) continue;
      /*
       * ⚠️ Only ever CALLED retired once the master has settled — an in-flight
       * or failed fetch has empty `records`, where every region looks absent and
       * none is actually retired. Under a narrowed picker the honest fallback is
       * the fact we do know for certain: the resolved project does not declare
       * it.
       */
      const retired = settled && !nameOf.has(id);
      const hint = retired ? 'No longer offered' : narrowed ? 'Not on this project' : undefined;
      list.push({
        value: String(id),
        /*
         * A bare id rather than dropping the option. An unnameable region is a
         * visible oddity somebody reports; a missing one is a silent deletion on
         * the next save.
         */
        label: nameOf.get(id) ?? `Region #${id}`,
        ...(hint ? { hint } : {}),
      });
    }
    return list;
  }

  function patchRow(key: string, patch: Partial<Draft>) {
    setSummary(null);
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        /*
         * Changing the item invalidates the project (a project sells exactly
         * one product) and therefore the cities read off it.
         *
         * ⚠️ **`allCities` goes with them, and this is the ONE place it
         * does.** The flag is a statement about a PROJECT — "all of the cities
         * that one has" — and this row no longer has one. Moving BETWEEN
         * projects keeps it, because the move does not contradict it (see
         * {@link ProjectControl}); losing the project outright leaves it with
         * nothing to mean, and a ticked box that refuses the row until a
         * project is chosen reads as a fault rather than as a leftover.
         */
        const invalidated =
          patch.productId !== undefined && patch.productId !== row.draft.productId
            ? { projectId: '', cityIds: [], allCities: false }
            : {};
        // Project-first: choosing a project on an item-less row SETS the item,
        // so "this project sells X but this line sells Y" is unreachable.
        const impliedProduct =
          patch.projectId !== undefined &&
          patch.projectId !== '' &&
          row.draft.productId === '' &&
          patch.productId === undefined
            ? (productOfProject(projects.rows, Number(patch.projectId))?.id ?? null)
            : null;
        /*
         * A NEW line picking a product starts at that product's quantity — its
         * step, else 1 — while the box still holds the old product's starting
         * figure (or nothing). A quantity somebody typed is kept, and a stored
         * line's quantity is never moved by a product change.
         */
        const nextProductId =
          impliedProduct !== null ? String(impliedProduct) : (patch.productId ?? row.draft.productId);
        const previousStart =
          row.draft.productId === ''
            ? '1'
            : defaultQuantityFor(productUnits.get(Number(row.draft.productId)));
        const startingQuantity =
          seedsPresets(row) &&
          /* A planned quantity that could not be stated is never filled in for them. */
          row.plannedQuantity === null &&
          patch.quantity === undefined &&
          nextProductId !== '' &&
          nextProductId !== row.draft.productId &&
          (row.draft.quantity.trim() === '' || row.draft.quantity === previousStart)
            ? { quantity: defaultQuantityFor(productUnits.get(Number(nextProductId))) }
            : {};
        return {
          ...row,
          draft: {
            ...row.draft,
            ...patch,
            ...invalidated,
            ...(impliedProduct !== null ? { productId: String(impliedProduct) } : {}),
            ...startingQuantity,
          },
          state: row.state === 'saving' ? row.state : ('idle' as RowState),
          error: null,
          fieldErrors: {},
          refusal: null,
        };
      }),
    );
  }

  /**
   * Switching the Disc box between a percentage and a money amount.
   *
   * ⚠️ **NEITHER DIRECTION CONVERTS ANYTHING, AND NEITHER MAY.** The switch
   * moves `draft.discountAmount` between `null` and `''` and leaves the other
   * box exactly as it was, so flipping back and forth is free and loses
   * nothing. Seeding the amount from what the percentage takes off — which is
   * what this used to do — is the lossy conversion in the other direction, and
   * the second column exists so that neither has to happen.
   *
   * ⚠️ **A switch on its own is not a save.** Both boxes still hold what they
   * held; `discountWriteOf` resolves which one the wire carries, and
   * `updatePayload` emits nothing until a figure actually moves.
   *
   * ⚠️ *SUPERSEDED: "Neither direction touches `draft.discount` … Switching TO
   * an amount seeds the box with the money the CURRENT percentage takes off."*
   * Nothing is seeded, because nothing is converted.
   */
  function setDiscountMode(key: string, toAmount: boolean) {
    patchRow(key, { discountAmount: toAmount ? '' : null });
  }

  /**
   * The internal line note (2026-10-18), saved ON ITS OWN.
   *
   * ⚠️ **A STORED line's note is a `{ notes }`-only PATCH, sent at once and
   * never batched into `saveAll`.** That body is the one write the server
   * accepts on a locked line (Awaiting Payment, Paid, pushed to Xero) and it
   * triggers no push, no repricing and no totals recompute — so it is gated
   * on `invoices.update` alone, not on `canEdit`, and `onLinesChanged` is not
   * called: nothing the parent shows has moved. Only `saved.notes` and
   * `draft.notes` are touched, so any figure edits pending on the row survive.
   *
   * An UNSAVED row (draft mode, Add row, a row added on the quote conversion)
   * keeps the note in its draft, and `createPayload` carries it.
   */
  async function saveLineNote(row: Row, next: string | null): Promise<void> {
    if (row.saved === null) {
      setRows((current) =>
        current.map((r) => (r.key === row.key ? { ...r, draft: { ...r.draft, notes: next } } : r)),
      );
      return;
    }
    if (invoiceId === null) return;
    const lineId = row.saved.id;
    const result = await api.updateInvoiceProduct(invoiceId, lineId, { notes: next });
    const stored = result.data.notes ?? next;
    setRows((current) =>
      current.map((r) =>
        r.saved !== null && r.saved.id === lineId
          ? { ...r, saved: { ...r.saved, notes: stored }, draft: { ...r.draft, notes: stored } }
          : r,
      ),
    );
  }

  function addRow() {
    const key = `new-${newRowSeq}`;
    setNewRowSeq((value) => value + 1);
    setSummary(null);
    setRows((current) => [...current, blankRowOf(key)]);
    setExpanded((current) => new Set(current).add(key));
  }

  function toggleExpanded(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function discardRow(key: string) {
    setRows((current) => current.filter((row) => row.key !== key));
  }

  function revertRow(key: string) {
    /*
     * ⚠️ **In convert mode the stored line is the DEAL's**, and reverting is what
     * takes a row's entry off the wire entirely: the seed is what the change test
     * compares against, so a row rebuilt from it matches and produces nothing but
     * `{dealLineId, sortOrder}`. Rebuilding it from `draftOf` — which takes an
     * `InvoiceProduct` — is not merely a type error, it is the wrong document.
     *
     * ⚠️ **AND ON A COPIED QUOTE ROW IT IS THE QUOTE'S LINE — a third source,
     * which this function had no branch for.** Without it a copied row fell to
     * `draft === null` and Revert became a button that did nothing at all: the
     * control renders (its gate is `sourceLineOf(row) !== null`, which the quote
     * line satisfies), the blocked-rows banner offers Revert as the way out, and
     * pressing it would have left the row exactly as it was. Each of the three
     * seeds must be rebuilt by the SAME function that created it, so that what a
     * revert produces is byte-for-byte what the change test compares against.
     */
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        const draft =
          row.convert !== null
            ? convertRowOf(row.convert).draft
            : row.quoteConvert !== null
              ? quoteConvertRowOf(row.quoteConvert).draft
              : row.saved !== null
                ? draftOf(row.saved)
                : null;
        if (draft === null) return row;
        return { ...row, draft, state: 'idle', error: null, fieldErrors: {}, refusal: null };
      }),
    );
    /*
     * The typed amount needs no clearing of its own any more: it is a `Draft`
     * key, so `draftOf(row.saved)` above puts it back with everything else —
     * including the MODE, since a stored `null` reopens the row on the
     * percentage.
     */
  }

  /**
   * Save every dirty, unblocked row.
   *
   * ⚠️ **N requests, one at a time, with no transaction across them.** There is
   * no bulk endpoint. If the third 409s, the first two are committed — so a
   * partial failure is an ordinary outcome, every row carries its own status
   * and reason, and the run reports "2 of 3 lines saved".
   *
   * ⚠️ **A retry never re-posts a row that already landed.** A successful POST
   * turns its row into a saved row with a real line id, so the next run either
   * skips it or PATCHes it. Re-posting would 409 on the pairs that clash and,
   * worse, quietly create a genuine duplicate for any row whose project differs.
   *
   * ⚠️ **`onLinesChanged` fires ONCE, after the whole run.** That is what makes
   * a batch of rows a single header re-read.
   *
   * ⚠️ **CORRECTED 2026-08-30.** The sentence above used to continue *"and — on
   * a linked invoice — a SINGLE Xero push. Moving it inside the loop would
   * spend one push per row against a budget the whole organisation shares."*
   * The push is the server's now and happens once per WRITE, so this callback
   * decides nothing about it: a run of eight rows is eight pushes however this
   * fires. The budget concern was real and is now the server's. What is handed
   * up here instead is a REPORT — see `onXeroPush`.
   */
  async function saveAll(): Promise<boolean> {
    /*
     * ⚠️ **The boolean is "did everything the run targeted land", and NOTHING
     * ELSE.** It exists for `registerSave`, so a parent can decide whether to
     * close; it is not a summary and it is not what a person reads — the run
     * report below is, row by row.
     *
     * Both early returns answer `true` deliberately. "There was nothing to
     * save" is not a failure, and returning `false` there would leave a caller
     * holding a form open over a grid with nothing wrong with it.
     */
    /*
     * Draft mode never reaches this — the Save control is not rendered — but the
     * guard is here rather than only in the markup, because "no request is
     * issued" is the contract of that mode and a contract enforced only by a
     * hidden button is enforced by nothing.
     */
    if (invoiceId === null) return true;
    const targets = rows.filter((row) => dirtyKeys.has(row.key) && !blockedKeys.has(row.key));
    if (targets.length === 0) return true;
    setSaving(true);
    setSummary(null);
    setRows((current) =>
      current.map((row) =>
        targets.some((target) => target.key === row.key)
          ? { ...row, state: 'saving', error: null, fieldErrors: {}, refusal: null }
          : row,
      ),
    );
    let saved = 0;
    let failed = 0;
    let halted = false;
    /*
     * ⚠️ The FIRST `failed` push report of the run, and nothing else — see
     * {@link InvoiceLineItemsSavedProps.onXeroPush} for why a run of N saves
     * reports one refusal rather than a tally. It stays `undefined` when every
     * report was `pushed` or `skipped`, and `undefined` is what makes the page
     * say nothing.
     *
     * ⚠️ It is deliberately NOT overwritten by a later failure: keeping the
     * first one means the sentence a person reads belongs to the earliest row
     * that hit it, which is the one they will look at.
     */
    let firstFailedPush: XeroPushReport | undefined;
    for (const target of targets) {
      try {
        const offersInclusions = inclusionsFor(target.key).offers;
        const offersProjects = projectsFor(target.key).showProject;
        /*
         * ⚠️ The same gate, for the same class of key: `regionIds` is a full
         * REPLACE whose `[]` CLEARS the set, so a control that is not on screen
         * contributes nothing. See {@link showsRegions}.
         */
        const offersRegions = showsRegions(target);
        const result =
          target.saved === null
            ? await api.createInvoiceProduct(
                invoiceId,
                createPayload(
                  target.draft,
                  undefined,
                  offersInclusions,
                  offersProjects,
                  offersRegions,
                  pricedWriteFor(target),
                ),
              )
            : await api.updateInvoiceProduct(
                invoiceId,
                target.saved.id,
                updatePayload(
                  target.draft,
                  target.saved,
                  offersInclusions,
                  offersProjects,
                  offersRegions,
                  pricedWriteFor(target),
                ),
              );
        saved += 1;
        /*
         * ⚠️ A push report rides on a response that SUCCEEDED, so it is read
         * here and never in the `catch`: an `ApiError` means the LINE was not
         * written at all, which is a different fact and is already rendered on
         * the row itself.
         */
        if (result.xeroPush?.status === 'failed' && firstFailedPush === undefined) {
          firstFailedPush = result.xeroPush;
        }
        setRows((current) =>
          current.map((row) =>
            row.key === target.key ? { ...rowOf(result.data), key: row.key, state: 'saved' } : row,
          ),
        );
      } catch (error: unknown) {
        failed += 1;
        const described = describeFailure(error);
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? {
                  ...row,
                  state: 'failed',
                  error: described.message,
                  fieldErrors: described.fieldErrors,
                  refusal: described.refusal,
                }
              : row,
          ),
        );
        // A 401/403 will refuse every remaining row identically. Stop rather
        // than spend N requests reproducing one sentence.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          halted = true;
          setRows((current) =>
            current.map((row) => (row.state === 'saving' ? { ...row, state: 'idle' } : row)),
          );
          break;
        }
      }
    }
    setSaving(false);
    setSummary({ saved, failed, halted });
    /*
     * ⚠️ **A BLOCKED ROW COUNTS AGAINST THE RUN even though it was never a
     * target.** `targets` skips them, so a run can refuse nothing and still
     * leave work on the grid — and a caller reading `true` there would close the
     * form over a row that was never sent. The parent's own gate should stop
     * this from arising; this makes it unreachable rather than unlikely.
     */
    const everythingLanded = failed === 0 && !halted && blockedKeys.size === 0;
    if (saved > 0) {
      onLinesChanged?.();
      /*
       * ⚠️ **Gated on `saved > 0`, NOT on `everythingLanded`** — a run that
       * wrote three rows and was refused on the fourth has still moved the
       * document, and a caller holding a stale copy of it is exactly who this
       * exists for. See {@link InvoiceLineItemsSavedProps.onRowsSaved}.
       *
       * It rides here rather than at the top of the function because both
       * early returns wrote nothing: "there was nothing to save" must not
       * reach a parent as "the data moved".
       */
      onRowsSaved?.();
      /*
       * ⚠️ Reported only when something actually landed, and gated on the same
       * condition as the refetch on purpose. A run in which every row was
       * refused by the API produced NO push report at all, so there is nothing
       * true to hand up — and passing `undefined` there would CLEAR a standing
       * report about an earlier save that is still the last thing known about
       * Xero.
       */
      onXeroPush?.(firstFailedPush);
    }
    return everythingLanded;
  }

  async function confirmDelete() {
    const target = pendingDelete;
    // Draft rows are discarded locally and never reach here; see `deleteControl`.
    if (invoiceId === null || target === null || target.saved === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      /*
       * ⚠️ **A DELETE PUSHES AND REPORTS NOTHING, and that is the endpoint
       * rather than an omission here.**
       * `DELETE /invoices/{id}/products/{lineId}` answers `204`, and a `204`
       * has no body — the server still pushes the invoice, it simply has
       * nowhere to say so. Widening it to a `200` would change a contract every
       * client already handles.
       *
       * So `onXeroPush` is NOT called here, in either direction: there is no
       * report to hand up, and handing up `undefined` would clear a standing
       * failure on the strength of a push nobody was told the outcome of.
       */
      await api.deleteInvoiceProduct(invoiceId, target.saved.id);
      setRows((current) => current.filter((row) => row.key !== target.key));
      setPendingDelete(null);
      onLinesChanged?.();
      /*
       * ⚠️ **A DELETE STALES A LIST EXACTLY AS A WRITE DOES.** The invoices
       * list's Projects badge is derived live from the line items, so removing
       * the only line that named a project changes it — and a fix that fired
       * only from `saveAll` would leave that half of the bug alive, looking
       * fixed. Inside the `try` and after the `await`, so it reports a delete
       * the server accepted rather than one that was attempted.
       */
      onRowsSaved?.();
    } catch (error: unknown) {
      setDeleteError(describeFailure(error).message);
    } finally {
      setDeleting(false);
    }
  }

  /*
   * ⚠️ **THE THREE STATES STAY APART, in convert mode as well.** `loading` gets
   * skeletons; a failed read gets the error and NO table, because every one of
   * the deal's lines still crosses whether or not we could show them; an empty
   * array is the deal genuinely having no products. Collapsing the middle into
   * "no lines" shows somebody an empty basket for a deal about to bring five
   * products across.
   */
  if (loading || dealLinesLoading || quoteLinesLoading) {
    return (
      <div className="grid min-w-0 gap-2" aria-busy="true">
        <Skeleton className="h-8 w-40" />
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
        <span className="sr-only">Loading line items</span>
      </div>
    );
  }

  if (quoteLinesError !== null) {
    /*
     * ⚠️ **NEVER PHRASED AS "this quote has no line items".** The read failed;
     * the lines are whatever they were, and converting copies every one of them.
     * The sentence says what is unknown and what is unaffected, because the
     * decision in front of the reader — press Convert or not — is not actually
     * blocked by it.
     *
     * ⚠️ **No "Try again", and not an omission:** the read belongs to the
     * parent, so a retry button here would have nothing to call.
     *
     * ⚠️ **The whole grid goes, added rows included.** That costs the ability to
     * add a line while the read is broken, and it is the right trade: a grid
     * showing only the rows somebody typed, with the copied ones missing and no
     * table to hold them, reads as a conversion that will bill just those.
     */
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="The quote’s line items could not be read.">
          <p>{quoteLinesError}</p>
          <p>
            Converting still copies every one of them — they simply cannot be listed or edited
            here.
          </p>
        </ErrorBlock>
      </div>
    );
  }

  if (dealLinesError !== null) {
    /*
     * ⚠️ **No "Try again" here, and that is not an omission.** The read belongs to
     * the parent — this grid issues no request at all in convert mode — so a retry
     * button would be a control with nothing to call. The sentence says the thing
     * that actually matters instead: the lines cross regardless.
     */
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="The products on this deal could not be read.">
          <p>{dealLinesError}</p>
          <p>All of them still come across — they simply cannot be listed or edited here.</p>
        </ErrorBlock>
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="This invoice’s line items could not be loaded.">
          <p>{loadError}</p>
        </ErrorBlock>
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setLoading(true);
              setReloadToken((n) => n + 1);
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Item · Description · Qty · Price · Discount · Tax rate · Amount · actions.
   *
   * Eight since 2026-08-28, when Project and Cities stopped being columns and
   * became a stack inside the Item cell. It drives `colSpan` on the two
   * full-width rows, so it and the `<colgroup>` must move together.
   */
  const columnCount = 8;

  /**
   * The internal note under a row, or `null` for nothing to render.
   *
   * - A COPIED deal or quote row is editable (owner 2026-10-18): the note is
   *   held in the draft and sent as `notes` on its `lineSet` / `lines` entry
   *   only when it differs from the source line's. A source line whose `notes`
   *   is `undefined` is an older API and renders nothing.
   * - A row ADDED on either conversion holds its note in the draft: the deal
   *   path sends it on the new `lineSet` entry, the quote path in `createPayload`.
   * - A STORED line is gated on `invoices.update` only — see {@link saveLineNote}.
   * - Any other unsaved row follows the grid's own edit gate.
   */
  function noteControl(row: Row): ReactNode {
    const source = row.convert ?? row.quoteConvert;
    if (source !== null && source.notes === undefined) return null;
    const value = row.saved !== null ? row.saved.notes : row.draft.notes;
    const disabled =
      row.saved !== null
        ? !canUpdateLineNotes || invoiceId === null || row.state === 'saving'
        : readOnly || convertLocked || saving || row.state === 'saving';
    if (value === undefined) return null;
    if (disabled && (value === null || value.trim() === '')) return null;
    return (
      <LineNoteField value={value} onSave={(next) => saveLineNote(row, next)} disabled={disabled} />
    );
  }

  function rowControls(row: Row, compact: boolean) {
    const problems = problemsByKey.get(row.key) ?? { fieldErrors: {}, error: null, notice: null };
    /*
     * ⚠️ **`convertLocked` belongs HERE and not on the parent's own submit
     * button.** Once the conversion has landed the invoice's lines are already
     * written from whatever `lineSet` was sent, and a retried Save re-sends the
     * header PATCH alone — so a box left live is one somebody types a new price
     * into, presses Save, and watches succeed against the old price on a document
     * that demands money.
     */
    const disabled = readOnly || convertLocked || row.state === 'saving' || saving;
    /*
     * ⚠️ **THE STRUCTURE OF A CONVERT LINE IS A FIELD, NOT A FACT.** `lineSet`
     * carries `productId`, `projectId`, `cityIds`, `regionIds` and `inclusions`,
     * so every one of those pickers has somewhere to put its answer and a lock on
     * them would be a control refusing an edit the request can express.
     *
     * ⚠️ **TWO LOCKS SURVIVE ON THIS PATH AND MUST NOT BE SWEPT UP WITH IT**,
     * because the request genuinely cannot carry either: the TAX cell (a readout —
     * see {@link ConvertedTaxRateCell}) and the **"All cities" FLAG**, which has no
     * key at all and is disabled through `allCitiesLocked` while the city PICKER
     * beside it stays live.
     */
    /**
     * ⚠️ **A COPIED QUOTE ROW'S STRUCTURE IS A FACT, NOT A FIELD — the exact
     * opposite of the deal path two paragraphs up, and the endpoint decides it
     * rather than taste.** `lines` carries five keys and every one of them is a
     * figure: there is no `productId`, no `projectId`, no `cityIds`, no
     * `regionIds` and no `inclusions`. The server copies all of that from the
     * quote inside the conversion's transaction, so a live picker here would
     * take an answer the request has nowhere to put — a 201, an invoice that
     * looks entirely correct, and the edit gone with nothing to say so.
     *
     * **Disabled rather than hidden**, which is this file's standing rule for a
     * control somebody may reasonably expect: a missing project picker does not
     * explain why the same person may retype a price and not repoint a line.
     * Each disabled control carries a `title` saying where the edit belongs —
     * on the quote, before converting.
     *
     * ⚠️ **It applies to COPIED rows only.** A row ADDED on this screen becomes
     * its own `POST /invoices/{id}/products`, which carries every one of those
     * keys, so locking it would refuse an edit the request can express.
     */
    const structureLocked = row.quoteConvert !== null;
    const onChange = (patch: Partial<Draft>) => patchRow(row.key, patch);
    const shared = { row, problems, disabled, compact, onChange };
    /*
     * The identity pickers on a copied row. `disabled` alone is what these
     * controls already take, so nothing new is threaded through them.
     */
    const structural = { ...shared, disabled: disabled || structureLocked };
    const resolved = projectsFor(row.key);
    /* The row's product's unit fields; `undefined` while unread or with no product. */
    const rowUnits =
      row.draft.productId === '' ? undefined : productUnits.get(Number(row.draft.productId));
    return {
      problems,
      disabled,
      /*
       * ⚠️ A RENDER decision only. Both layouts read it to decide whether the
       * project block is in the stack; nothing in it reaches the draft, and the
       * payload builders take the same value as their own explicit argument.
       */
      showProject: resolved.showProject,
      /*
       * ⚠️ Whether the CITY controls belong on this row — see
       * {@link showsCitiesControl}. A strictly narrower gate than `showProject`,
       * which still decides the PROJECT picker and still decides whether cities
       * are written: a project that declares no cities keeps its picker and
       * loses its city block.
       */
      showCities: showsCities(row),
      /*
       * ⚠️ The COMPLEMENT of `showProject`, not a third independent gate — see
       * {@link showsRegionsControl}. Read through `showsRegions` rather than
       * recomputed, so this render and the two payload builders cannot disagree
       * about whether a hidden control may write.
       */
      showRegions: showsRegions(row),
      product: (
        <ProductControl
          {...structural}
          options={productOptionsFor(row)}
          /*
            🔴 **The whole footnote is GONE — owner's stricter pass,
            2026-09-05.** Three strings: "Copied from the quote. Change the item
            on the quote before converting." on a structure-locked convert row,
            "The product list could not be loaded." on a failed read, and
            "Showing the first 100 products — the catalogue holds more." on a
            truncated one.

            ⚠️ **The first was the sharpest loss and is worth naming.** It said
            WHERE the edit belongs, on a row that is inert during a conversion —
            without it a locked picker reads as temporarily unavailable, and the
            reader has no way to learn that the item is changed on the QUOTE.
            `structureLocked` still drives the lock.

            ⚠️ A product missing from the list is now indistinguishable from one
            that does not exist. `products.failed` / `.truncated` still computed.
          */
        />
      ),
      description: (
        <DescriptionControl
          disabled={disabled}
          compact={compact}
          derived={descriptionOf(row)}
          description={row.draft.description}
          /*
           * ⚠️ **`row.saved` only — NOT `row.quoteConvert`.** A copied quote row
           * has never been written as an invoice line, so there is no original
           * invoice wording to return to; offering its quote text as "the
           * original" would name the wrong document.
           */
          saved={row.saved?.description ?? null}
          error={problems.fieldErrors.description}
          onTyped={(value) => onChange({ description: value })}
          onReset={() => onChange({ description: null })}
          onRestore={() => onChange({ description: row.saved?.description ?? null })}
        />
      ),
      project: <ProjectControl {...structural} resolved={resolved} />,
      /*
       * The price tier, under the project. Locked with the identity pickers on a
       * copied quote row, and on the deal-convert path, because neither `lines`
       * nor `lineSet` carries a tier; the server copies the source line's.
       * `null` until the tier master is read.
       */
      tier:
        projectPrices.tiers.length === 0 ? null : (
          <TierControl
            {...structural}
            disabled={structural.disabled || convertMode}
            tiers={projectPrices.tiers}
            offered={tierOptionsFor(
              row.draft.projectId === ''
                ? []
                : (projectPrices.entriesByProject.get(Number(row.draft.projectId)) ?? []),
              projectPrices.tiers,
              row.draft.productId === '' ? null : Number(row.draft.productId),
              currency,
            )}
            defaultTierId={projectPrices.defaultTierId}
          />
        ),
      cities: (
        <CitiesControl
          {...structural}
          resolved={resolved}
          /*
           * ⚠️ The flag is locked on BOTH conversion paths and for the same
           * reason — neither request has a key for it. On the deal path the city
           * PICKER beside it stays live (`lineSet` carries `cityIds`); on the
           * quote path the whole block is inert, because `lines` carries neither.
           */
          allCitiesLocked={convertMode || structureLocked}
        />
      ),
      /*
       * `loading` only while the list is genuinely empty: once the six rows are
       * in, a background refetch must not blank a picker somebody is using.
       * `regions.failed` deliberately does NOT hide the control — a row that
       * holds regions still renders them from `regionOptionsFor`'s pinned
       * entries, and a control that vanishes on a failed read is
       * indistinguishable from a bug.
       */
      regions: (
        <RegionsControl
          {...structural}
          options={regionOptionsFor(row, resolved)}
          loading={regions.loading && regions.records.length === 0}
          /*
           * ⚠️ The SAME `resolved` the project and city controls read, so the
           * three cells cannot disagree about what this line's project declares.
           * It decides both which list the picker offers and whether "All
           * regions" has a bound to mean anything — see {@link RegionsControl}.
           */
          projectRegions={resolved.projectRegions}
        />
      ),
      quantity: (
        <NumberControl
          {...shared}
          field="quantity"
          label="Qty"
          placeholder={defaultQuantityFor(rowUnits)}
          step={rowUnits?.quantityStep ?? null}
        />
      ),
      /*
       * ⚠️ **Built HERE rather than at the two JSX call sites** so it takes the
       * same `disabled` and the same `onChange` as every other control on the
       * row. The table and the sub-`md` card list both render it, and a cell
       * that could be edited in one layout and not the other is the
       * "indistinguishable from a removed feature" failure this file records.
       *
       * ⚠️ **The plain `disabled`, NEVER `structural`'s.** A copied quote row
       * keeps its Price and Discount boxes — `lines` carries both — so the
       * figure those two produce between them has to stay typeable too.
       * Locking it would refuse an edit the request can express, and would
       * refuse it on the one cell somebody reaches for first.
       */
      amount: (
        <AmountCell
          row={row}
          dirty={dirtyKeys.has(row.key)}
          currency={currency}
          convertMode={convertMode}
          preview={previewByKey.get(row.key) ?? null}
          /*
           * ⚠️ **The memoised input, so the box solves backwards from exactly
           * what the preview solves forwards from.** The fallback is the same
           * call the memo itself makes, so the two cannot disagree — it exists
           * only so the prop stays non-nullable rather than growing a branch
           * that silently does nothing.
           */
          input={
            previewInputs.get(row.key) ??
            previewInputOf(
              row,
              inheritedPriceFor(row) ?? null,
              companySalesTax,
              repricedSavedPriceFor(row),
              pricedWriteFor(row)?.discountAmount,
            )
          }
          disabled={disabled}
          onChange={onChange}
        />
      ),
      price: (
        <PriceControl
          {...shared}
          currency={currency}
          catalogPrice={inheritedPriceFor(row)}
          proRated={
            typeof catalogPriceFor(row) === 'string' &&
            inheritedPriceFor(row) !== catalogPriceFor(row)
          }
          projectPriced={
            (row.saved === null
              ? inheritedLinePriceFor(row)
              : repricedSavedLinePriceFor(row)
            )?.source === 'project_price'
          }
          repricedPrice={repricedSavedPriceFor(row)}
          /*
           * ⚠️ **The tri-state Price box loses its third state on BOTH convert
           * paths, for one reason.** Neither `lineSet`, nor `lines` on either
           * route, carries a `unitPrice: null` — so "record no price at all" is
           * a state the request cannot express, and offering it would be a
           * control whose answer is discarded. See {@link convertShapeOf} and
           * `validateConvertLineEdit`, which refuses an emptied box only where
           * the line HAD a price.
           *
           * ⚠️ **Copied rows only on the quote path.** An ADDED row there is
           * written by an ordinary `POST /invoices/{id}/products`, which does
           * carry an explicit null, so it keeps all three states.
           */
          allowNoPrice={!convertMode && !structureLocked}
          unitLabel={unitLabelOf(rowUnits)}
        />
      ),
      /*
       * ⚠️ TWO real columns, mutually exclusive — `draft.discountAmount === null`
       * is percentage mode. The control writes whichever box is on screen and
       * never the other; `discountWriteOf` turns the pair into a payload.
       */
      discount: (
        <DiscountControl {...shared} onModeChange={(toAmount) => setDiscountMode(row.key, toAmount)} />
      ),
      tax: convertMode ? (
        /*
         * ⚠️ **A READOUT, not a disabled dropdown — see
         * {@link ConvertedTaxRateCell}.** Neither request key carries a `taxRate`
         * or an `xeroTaxType`, so a live control would discard a choice silently
         * and a disabled `TaxRateControl` would render a frozen `0%` that nothing
         * measured. The rate comes from the COMPANY, server-side.
         */
        <ConvertedTaxRateCell compact={compact} rate={companySalesTax} />
      ) : row.quoteConvert !== null ? (
        /*
         * ⚠️ **A DIFFERENT readout from the one above, reading a different
         * figure — see {@link QuoteConvertedTaxRateCell}.** A quote line carries
         * its OWN rate and its own Xero code, and the conversion copies both, so
         * the honest answer here is per row rather than one company-wide figure.
         * `lines` has no tax key by an explicit backend ruling.
         *
         * ⚠️ **Only on a COPIED row.** A row ADDED here is written by its own
         * POST, which carries both tax keys, so it falls through to the live
         * control below.
         */
        <QuoteConvertedTaxRateCell compact={compact} rate={row.quoteConvert.taxRate} />
      ) : (
        <TaxRateControl
          {...shared}
          rates={taxRates.rates}
          ratesLoading={taxRates.loading}
          ratesError={taxRates.error?.message ?? null}
          connected={xeroConnected}
          /*
           * ⚠️ **DISPLAY ONLY.** It words the empty option and names the
           * company's default in the popover; it reaches no payload. On a mount
           * that passes none this is `{ known: false }` and the control renders
           * exactly as it always has.
           */
          companySalesTax={companySalesTax}
        />
      ),
    };
  }

  function deleteControl(row: Row) {
    /*
     * ⚠️ **A COPIED QUOTE ROW HAS NO CONTROL AT ALL, and the endpoint decides
     * that rather than a preference.** Convert bills the WHOLE quote: `lines`
     * respells the copied lines and an omission from it means *unchanged*, not
     * *removed*, so there is no request this button could make. Rendering a
     * disabled one — the usual rule in this file — is wrong here too, because it
     * would advertise a capability that does not exist on this endpoint at all
     * rather than one this user lacks.
     *
     * ⚠️ **The opposite of the deal path immediately below, and the difference
     * is `lineSet` versus `lines`.** There, leaving a row out of the array IS
     * how a line is removed. Do not "harmonise" the two.
     *
     * Billing part of a quote is a separate, deliberate action —
     * `POST /quotes/{id}/invoices`, the "Bill line items" control on the quote —
     * and it is not this button.
     */
    if (row.quoteConvert !== null) return null;
    /*
     * ⚠️ **A CONVERT ROW IS DISCARDABLE, AND IT TAKES THE `saved === null` BRANCH
     * BELOW.** `lineSet` is the complete final list, so leaving a row OUT of it is
     * exactly how a line is removed — no request of its own, and nothing to
     * confirm, because discarding it destroys no record on either document.
     *
     * ⚠️ **IT DOES NOT TOUCH THE DEAL, and this is the one place somebody will
     * assume it does.** The deal's basket is untouched by a conversion in every
     * direction; a row discarded here is a line the INVOICE will not have.
     *
     * ⚠️ **Gated by `canDelete` nowhere on this path.** A convert row has never
     * been sent anywhere, so gating it on the admin-tier delete ability would trap
     * a `basic_user` with a line they do not want and no way to take it off the
     * form — the same ruling the draft rows already carry.
     */
    if (row.saved === null) {
      /*
       * ⚠️ **`canManageRows`, not `!readOnly`.** Once a conversion has landed the
       * rows are already on a real invoice and Save re-sends the HEADER alone — so
       * a trash can there would remove a line from a list nothing will send again,
       * and report nothing.
       */
      return canManageRows ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<TrashIcon />}
          aria-label={
            row.convert === null ? 'Discard this row' : 'Leave this line off the invoice'
          }
          onClick={() => discardRow(row.key)}
        />
      ) : null;
    }
    if (!canDelete) {
      // Disabled and explained rather than hidden: a missing control does not
      // explain why the same account may add and edit a line but not remove it.
      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<TrashIcon />}
          disabled
          title={deleteDenialReason}
          aria-label={`Delete this line — ${deleteDenialReason}`}
        />
      );
    }
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label={`Delete the ${row.saved.product.productName} line`}
        onClick={() => {
          setDeleteError(null);
          setPendingDelete(row);
        }}
      />
    );
  }

  function rowActions(row: Row) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded.has(row.key)}
          onClick={() => toggleExpanded(row.key)}
        >
          {expanded.has(row.key) ? 'Hide' : 'Details'}
        </Button>
        {/*
          ⚠️ **Offered on a CONVERTED row too, and it is worth more there.**
          Revert rebuilds the row from the deal line, which is the seed the change
          test compares against — so it is the only way to take a row's edits back
          off the wire once somebody has typed into it. `row.saved !== null` alone
          would withhold it from every converted row, since none of them has a
          saved invoice line and never will.

          ⚠️ Withheld while `convertLocked`, like every other control on the row:
          after the conversion has landed there is nothing left for a revert to
          change and it would read as a way to undo the invoice.
        */}
        {!readOnly && !convertLocked && dirtyKeys.has(row.key) && sourceLineOf(row) !== null && (
          <Button type="button" variant="ghost" size="sm" onClick={() => revertRow(row.key)}>
            Revert
          </Button>
        )}
        {deleteControl(row)}
      </div>
    );
  }

  function detailPanel(row: Row) {
    const { problems, disabled } = rowControls(row, true);
    const dirty = dirtyKeys.has(row.key);
    const preview = previewByKey.get(row.key) ?? null;
    /*
     * ⚠️ **A converted row shows NEITHER, and both halves are deliberate.**
     * `f_deal_products` has ONE generated column — `line_total` — and no
     * `line_subtotal` and no `tax_rate` at all, so there is no stored Subtotal and
     * Tax pair to show; and the preview is the wrong document's answer, for the
     * reason {@link AmountCell} gives at length. The Amount column already carries
     * the one figure that is honest here.
     */
    const figures =
      row.convert !== null
        ? null
        : dirty
          ? preview
          : row.saved === null
            ? null
            : {
                lineSubtotal: row.saved.lineSubtotal,
                lineTax: row.saved.lineTax,
                lineTotal: row.saved.lineTotal,
              };
    const { offers, offerUnread, offeredIds, denialReason, orphaned } = inclusionsFor(row.key);
    return (
      <div className="grid gap-4 border-t border-border bg-surface-sunken px-3 py-3">
        {offers ? (
          <InclusionsEditor
            row={row}
            problems={problems}
            disabled={disabled}
            master={inclusions.rows}
            masterFailed={inclusions.failed}
            offerUnread={offerUnread}
            offeredIds={offeredIds}
            onChange={(patch) => patchRow(row.key, patch)}
          />
        ) : (
          <InclusionsNotOffered
            row={row}
            master={inclusions.rows}
            denialReason={denialReason}
            orphaned={orphaned}
          />
        )}
        {figures !== null && (
          <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-xs">
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Subtotal</dt>
              <dd className="tabular-nums text-fg">{formatDecimalString(figures.lineSubtotal)}</dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Tax</dt>
              <dd className="tabular-nums text-fg">{formatDecimalString(figures.lineTax)}</dd>
            </div>
            <span className="text-2xs text-fg-subtle">{dirty ? 'Preview' : 'As saved'}</span>
          </dl>
        )}
        {figures === null && (
          <p className="text-2xs text-fg-subtle">
            {/*
              ⚠️ "on conversion", never "on save" — nothing on that path is saved
              from here, and a sentence promising a save sends somebody looking
              for a control that does not exist. The `none` branch keeps its own
              sentence because it is a different FACT, not a longer way of saying
              the same one: no price recorded is not a price of zero.
            */}
            {row.draft.priceMode === 'none'
              ? 'No price on this line — not a price of zero.'
              : row.convert !== null || row.quoteConvert !== null
                ? 'Calculated on conversion.'
                : 'Calculated on save.'}
          </p>
        )}
      </div>
    );
  }

  const runReport =
    summary === null ? null : summary.failed === 0 ? (
      <Notice tone="success" role="status">
        {summary.saved} of {summary.saved} {summary.saved === 1 ? 'line' : 'lines'} saved.
      </Notice>
    ) : (
      <ErrorBlock
        title={`${summary.saved} of ${summary.saved + summary.failed} lines saved. Fix the ${
          summary.failed === 1 ? 'row' : 'rows'
        } marked below and save again.`}
      >
        <p>
          Saved lines are already stored.
          {summary.halted ? ' The run stopped early, so the rest were not attempted.' : ''}
        </p>
      </ErrorBlock>
    );

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          {/*
            ⚠️ **Not "copied from the deal", and not "Line items" bare.** Rows can
            be added, removed and repointed at a different product, so "copied"
            would describe only how the list STARTED — and naming it for the deal
            would tell somebody who has just deleted a row that they had changed
            the deal's basket, which is the one thing a conversion never does.
            Still not the invoice's own, either: nothing here is POSTed and
            nothing exists until the invoice is raised.
          */}
          <h3 className="text-sm font-semibold text-fg">
            {convertMode ? 'Line items for the new invoice' : 'Line items'}
          </h3>
          <p className="text-xs text-fg-muted">
            {rows.length === 0
              ? 'None yet.'
              : /*
                 * Never "unsaved" in draft mode — nothing on this grid has
                 * anywhere to be saved to yet, and calling it unsaved would
                 * imply a Save control the person is failing to find.
                 */
                `${rows.length} ${rows.length === 1 ? 'line' : 'lines'}${
                  convertMode
                    ? dirtyCount > 0
                      ? /* ⚠️ "edited or added", never "unsaved". Nothing here is saved
                           from this screen; what has changed is what the conversion
                           will create. Both words are needed because `dirtyKeys` holds
                           two kinds of row — a carry-over somebody changed, and one
                           that was never on the deal at all — and calling a brand new
                           row "edited" sends its author looking for the deal line they
                           supposedly altered. */
                        ` · ${dirtyCount} edited or added`
                      : ''
                    : !draftMode && dirtyCount > 0
                      ? ` · ${dirtyCount} unsaved`
                      : ''
                }`}
          </p>
        </div>
        {canManageRows && (
          <div className="flex shrink-0 items-center gap-2">
            {/* Add row lives below the rows, full width — owner's request, 2026-10-12. */}
            {/*
              ⚠️ No Save control in draft mode, and not a disabled one either. A
              line cannot be written before the invoice has an id, so there is no
              request this button could make — the invoice form's own submit is
              what writes these rows, and a second save-looking control beside it
              would read as a step the person has to take first.

              ⚠️ **And none in EITHER CONVERT mode, for a sharper version of the
              same reason.** `saveAll` issues `POST`/`PATCH
              /invoices/{id}/products`, and there is no invoice: `invoiceId` is
              `null`, so the button would be a control whose action cannot be
              performed. Worse, it reads as a step somebody must take before the
              conversion — and pressing it would look like it had saved the
              lines.

              🔴 **AND NONE WHEN A PARENT HAS TAKEN THE SAVE** (`registerSave`),
              which is the same reason one step out: the form's footer "Save
              changes" runs this grid's own `saveAll` before its PATCH, so a
              button here would be a SECOND control performing the identical
              run. Owner's instruction, 10 September 2026 — one button, and it
              says "Save changes".

              ⚠️ **The label no longer counts rows.** It read `Save ${n} rows`
              while dirty and "Save changes" while clean, so the one control
              renamed itself under the person using it — and on a form it sat
              beside a footer control of the *other* name, reading as two
              different jobs. It is one job on every screen and says so.

              ⚠️ **It IS still rendered on the detail page**, which mounts this
              grid with no `registerSave` and has no save of its own. Removing it
              unconditionally would leave every line edit there with nowhere to
              go — measured before this change, not assumed.
            */}
            {!draftMode && !anyConvertMode && registerSave === undefined && (
              <Button
                type="button"
                variant="primary"
                loading={saving}
                disabled={saving || dirtyCount === 0 || blockedKeys.size > 0}
                onClick={() => {
                  void saveAll();
                }}
              >
                Save changes
              </Button>
            )}
          </div>
        )}
      </div>

      {/*
        ⚠️ **ONE explanatory line above this table, and this is it.** It is the
        only thing on screen that answers "will closing this modal lose what I
        have typed?", and someone entering lines in a New Invoice modal has no
        other way to tell. A person who assumes wrong makes a second invoice.
      */}
      {draftMode && rows.length > 0 && (
        <p className="text-xs text-fg-muted">
          These lines are written when the invoice is created.
        </p>
      )}

      {/*
        ⚠️ **The convert path's one line, and it survives the same test the draft
        one does: it answers a question nothing else on screen can.** Somebody
        looking at boxes over a deal's basket has no way to tell whether they are
        editing the DEAL or the invoice about to be raised from it — and the answer
        decides whether they go back and check the deal afterwards.

        ⚠️ **It does NOT say the row set is fixed.** It once would have been true
        of the older `lines` override, which could only respell a row the deal
        already had; `lineSet` is the complete final list, so Add row and the trash
        can both work here and a sentence claiming otherwise would be a rule
        contradicted by the two controls beside it.
      */}
      {convertMode && rows.length > 0 && (
        <p className="text-xs text-fg-muted">
          The deal keeps its own figures — these become the new invoice’s lines. Add, remove or
          change them here and the conversion writes what you see.
        </p>
      )}

      {/*
        ⚠️ **THE ONE PLACE THE TWO POPULATIONS ARE EXPLAINED, and it replaces a
        second grid rather than labelling one.** Until 2026-08-31 this path
        rendered a read-only table headed "Line items copied from the quote" and,
        beneath it, a separate "Additional line items" grid whose empty state
        read *"No line items yet"* — directly under a table full of line items.
        That is the contradiction the owner reported.

        ⚠️ **It states the NON-ATOMICITY, and that sentence is load-bearing.**
        The conversion runs first and each added line after it, so the invoice
        can exist while one of those was refused. **Never reword this into a
        promise that nothing is saved unless everything is** — there is no
        transaction across the two, and no rollback that would not destroy the
        conversion that worked.

        ⚠️ **It also says what CANNOT be done here**, because two of the three
        are things the grid visibly offers on its other paths: the item and its
        project cannot be repointed, and a copied line cannot be removed. Saying
        only what works would leave somebody hunting for a trash can that is
        deliberately absent.
      */}
      {quoteConvertMode && rows.length > 0 && (
        <p className="text-xs text-fg-muted">
          The quote keeps its own figures — its lines become the invoice’s. Retype a quantity,
          price, discount or wording here and the conversion writes what you see; the item,
          project and cities are copied as they are, and a copied line cannot be removed. Rows you
          add are written after the invoice is created, one at a time, and nothing is undone if
          one is refused.
        </p>
      )}

      {contextNote}
      {runReport}

      {blockedKeys.size > 0 && (
        <ErrorBlock
          title={
            convertMode
              ? /*
                 * ⚠️ **"the invoice cannot be raised", not "would not be written".**
                 * A refused converted row is not dropped on the floor the way a
                 * blocked draft row is — the 422 lands on
                 * `POST /deals/{id}/invoices` itself, so the cost is NO INVOICE AT
                 * ALL rather than an invoice missing a line. Wording it like the
                 * draft banner would understate it by a whole document.
                 */
                `${blockedKeys.size} ${
                  blockedKeys.size === 1 ? 'row needs' : 'rows need'
                } attention — the invoice cannot be raised until ${
                  blockedKeys.size === 1 ? 'it is' : 'they are'
                } fixed.`
              : quoteConvertMode
                ? /*
                   * ⚠️ **"the invoice cannot be raised", like the deal path and
                   * NOT like the draft one.** A blocked row here may be a COPIED
                   * one, whose fault travels inside `lines` — so the 422 lands on
                   * the conversion itself and the cost is no invoice at all,
                   * rather than an invoice missing a line. Wording it as the
                   * draft banner does would understate it by a whole document,
                   * and the banner cannot know which population is at fault
                   * without saying two different things at once.
                   */
                  `${blockedKeys.size} ${
                    blockedKeys.size === 1 ? 'row needs' : 'rows need'
                  } attention — the invoice cannot be raised until ${
                    blockedKeys.size === 1 ? 'it is' : 'they are'
                  } fixed.`
                : draftMode
                  ? `${blockedKeys.size} ${
                      blockedKeys.size === 1 ? 'row is' : 'rows are'
                    } not ready and would not be written with the invoice.`
                  : `${blockedKeys.size} ${
                      blockedKeys.size === 1 ? 'row needs' : 'rows need'
                    } attention before anything can be saved.`
          }
        >
          {convertMode && (
            <p>
              Correct {blockedKeys.size === 1 ? 'it' : 'them'}, remove{' '}
              {blockedKeys.size === 1 ? 'the row' : 'the rows'}, or Revert to the deal’s own
              figures.
            </p>
          )}
          {quoteConvertMode && (
            /*
             * ⚠️ Deliberately does NOT offer "remove the row": a copied row has
             * no trash can, so on the population most likely to be at fault that
             * advice cannot be followed. Revert is offered instead, and it works
             * on both.
             */
            <p>
              Correct {blockedKeys.size === 1 ? 'it' : 'them'}, or Revert to the quote’s own
              figures. A row you added can be discarded.
            </p>
          )}
          {draftMode && (
            <p>
              Fix or discard {blockedKeys.size === 1 ? 'it' : 'them'} before creating the invoice.
            </p>
          )}
        </ErrorBlock>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<LinesIcon />}
          title={
            convertMode
              ? 'This deal has no products'
              : quoteConvertMode
                ? /*
                   * ⚠️ **REACHABLE ONLY ON A QUOTE THAT GENUINELY HAS NO LINES**,
                   * because this whole block is behind `rows.length === 0` and
                   * the copied rows are seeded into the same array. That is the
                   * defect this mode was built to end: the old arrangement put a
                   * SECOND grid under the copied table, and that grid's own empty
                   * state read "No line items yet" directly beneath a table full
                   * of them. One grid means the empty state can only appear when
                   * the grid is actually empty.
                   */
                  'This quote has no line items'
                : draftMode
                  ? 'No line items yet'
                  : 'No line items on this invoice'
          }
          description={
            convertMode
              ? /*
                 * ⚠️ **A deal with no products raises an invoice perfectly well**,
                 * so this is a state rather than a fault — and the tax refusal
                 * cannot fire on it either, because that check early-returns when
                 * there are no lines. Add row IS offered, so the sentence says so
                 * rather than sending somebody back to the deal.
                 */
                'The invoice will be raised with none. Add a row to bill something anyway.'
              : quoteConvertMode
                ? /*
                   * ⚠️ A quote with no lines converts perfectly well — the header
                   * totals come across — so this is a state, not a fault. Add row
                   * is offered, and such a row is written after the invoice
                   * exists rather than by the conversion.
                   */
                  'The invoice will be raised with the quote’s totals. Add a row to bill something anyway.'
                : readOnly
                  ? 'Nothing has been itemised on this invoice yet.'
                  : draftMode
                    ? 'Add a row to bill a product. Lines are written when the invoice is created.'
                    : 'Add a row to bill a product.'
          }
          action={
            canManageRows ? (
              <Button type="button" variant="secondary" onClick={addRow}>
                Add row
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/*
            The wide grid, and the stacked cards below `md`. Two renderings of
            one row set, never two sources of truth: both call `rowControls`,
            and only `compact` differs.
          */}
          <div className="hidden min-w-0 md:block">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[1492px] table-fixed">
                {/*
                  ⚠️ **The `<colgroup>` is the ONE place a column width lives**,
                  and under `table-fixed` these values are authoritative — no
                  `<th>` or `<td>` below carries a `w-`, so nothing can disagree
                  with them. Each body cell instead carries a content `min-w-`
                  of its column MINUS 24px, which is `TD`'s `px-3` twice; a
                  content minimum is what the algorithm may not go under.
                  **Those two lists are the one thing here that can silently
                  drift apart — move them together, always.**

                  Item     285 · Description 377 · Qty     84 · Price 148 ·
                  Discount 240 · Tax rate    142 · Amount 122 · actions 94
                                                                    = 1492

                  Project (165) and Cities (125) were columns until
                  2026-08-28; their 290 went to Item (190 → 285, for the
                  stacked pickers) and to Description (236 → 377, which is what
                  the owner asked for). That move kept the total at 1400.

                  ⚠️ **The total moved to 1492 on 2026-08-31, and ONLY Discount
                  moved (148 → 240, +92).** Its mode switch stopped being a
                  `%`/`EUR` glyph inside the input and became a segmented
                  control reading **Percentage** and **Amount** underneath it,
                  and two words plus a reserved tick do not fit in 124px of
                  content. **The pinning geometry is untouched**: PIN_AMOUNT's
                  `right-[94px]` is the ACTIONS column's width, not the table's
                  total. The quote grid moved by the same 92 in the same commit,
                  so the two still share every width and both still total 1492.

                  ⚠️ **92 IS A MEASUREMENT AND THE FIRST GUESS WAS 56, WHICH
                  WOULD HAVE SHIPPED A CLIPPED CONTROL.** Cut from the type
                  scale the segmented control "should" have been ~170 wide;
                  driven in Chrome at 768 and 1440 it is **193** —
                  `Percentage` 101 + `Amount` 84 + a 2px inter-segment gap +
                  `p-0.5` + a 2px border. The reserved 12px tick on each button
                  is what the arithmetic kept forgetting. **Measure this cell;
                  do not derive it.**

                  Widths are cut at `--text-sm` (13px, tabular-nums) from what
                  each cell must show in its WORST case, never from today's
                  data: `formatDecimalString` renders thousands separators, so
                  Amount and Price each hold `123,456.00` (≈71px) with room to
                  spare.

                  🔴 **CORRECTED 2026-09-12.** *SUPERSEDED: "`formatDecimalString`
                  renders thousands separators AND the currency code, so Amount
                  holds `123,456.00 GBP` (≈96px)".* **It renders no code and
                  never did** — `formatDecimalString` in `src/lib/format.ts:61`
                  takes ONE argument, the value, and every currency code on this
                  grid is concatenated by its call site. So the ~96px this
                  column was said to need was ~71px of figure plus a code that
                  was never in the string.

                  ⚠️ **And the code the Amount cell now shows costs HEIGHT, NOT
                  WIDTH.** It is a STACKED line under the input (see
                  {@link EditableAmount}), on its own row like the `incl. tax`
                  line beside it — so it takes no content width from this
                  column, and **no `<col>`, no cell `min-w-` and not the
                  `min-w-[1492px]` sum moved for it.** A reader re-cutting this
                  column from `123,456.00 GBP` would be sizing for a string this
                  grid does not render.
                  ⚠️ **Discount is the one column NOT cut from its figure** —
                  216px of content is the segmented control, MEASURED at 193,
                  leaving 23px for a font that renders wider than
                  Chrome-on-Windows does. The box above it has the whole 216 to
                  itself now that nothing sits in its trailing slot. The last
                  value is the actions column's width and must equal the
                  `right-[94px]` offset in PIN_AMOUNT.
                */}
                <colgroup>
                  <col className="w-[285px]" />
                  <col className="w-[377px]" />
                  <col className="w-[84px]" />
                  <col className="w-[148px]" />
                  {/* Discount — sized by the SEGMENTED CONTROL, not the figure. */}
                  <col className="w-[240px]" />
                  <col className="w-[142px]" />
                  <col className="w-[122px]" />
                  <col className="w-[94px]" />
                </colgroup>
                <THead>
                  <TR>
                    {/* The Project and Cities controls sit under this one and
                        carry their own labels, because no header names them. */}
                    <TH>Item</TH>
                    <TH>Description</TH>
                    <TH numeric>Qty</TH>
                    <TH numeric>Price</TH>
                    {/* Not "Disc %": the box takes either, and the segmented
                        control in the cell says which it is currently taking. */}
                    <TH numeric>Discount</TH>
                    <TH>Tax rate</TH>
                    {/* ⚠️ The two pinned HEADER cells go through `TH`'s own
                        `frozen` / `pinned` props rather than `PIN_AMOUNT` /
                        `PIN_ACTIONS`: under `frozenHeader` a header cell needs
                        its top AND right offsets and both hairlines in one
                        literal, which only the primitive writes. 94 is the
                        actions column's width, as in `PIN_AMOUNT`. The body
                        cells keep the local literals (z-10, under the header). */}
                    <TH numeric frozen="right" frozenOffset={94}>
                      Amount
                    </TH>
                    <TH pinned>
                      <span className="sr-only">Status and actions</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((row) => {
                    const controls = rowControls(row, true);
                    const dirty = dirtyKeys.has(row.key);
                    const note = noteControl(row);
                    return (
                      <Fragment key={row.key}>
                        <TR className={cx(row.state === 'failed' && 'bg-danger-soft/40')}>
                          {/* The stack: item, then the project it is sold
                              through, then that project's cities. Ordered the
                              way somebody fills it in, and each part is a
                              separate control emitting its own patch — see
                              `ProjectControl` for the one patch that carries
                              two of them. */}
                          <TD className="align-top">
                            <div className="grid min-w-[261px] gap-2">
                              <div>
                                {controls.product}
                                <InclusionSummary
                                  row={row}
                                  master={inclusions.rows}
                                  variant="count"
                                  error={controls.problems.fieldErrors.inclusions}
                                />
                              </div>
                              {controls.showProject && (
                                <>
                                  {controls.project}
                                  {controls.tier}
                                  {/* ⚠️ A SECOND, NARROWER gate — see
                                      `showsCitiesControl`. The project picker
                                      stays on every project-backed row; the
                                      city block goes when the chosen project
                                      declares no cities, which is the owner's
                                      instruction for `Virtual Masterclasses`,
                                      `LAE` and `EMBA`. It never hides cities a
                                      row already holds, and it cannot drop one
                                      from a payload — the writers read
                                      `showProject`, not this. */}
                                  {controls.showCities && controls.cities}
                                </>
                              )}
                              <LineCreditCities line={row.saved} />
                              {/* ⚠️ The other half of the same decision: the
                                  geography a line carries when it has no CITIES
                                  to carry one — either because the product is
                                  not sold through projects at all, or because
                                  its project declares none. Gated by
                                  `showsRegionsControl`, which never touches
                                  state on the way to hiding.

                                  ⚠️ *SUPERSEDED: "the complement of the block
                                  above".* It was, until the owner added the
                                  second case on 2026-08-31; it now renders
                                  BESIDE the project and city blocks on a row
                                  whose project is not city-specific. */}
                              {controls.showRegions && controls.regions}
                            </div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[353px]">{controls.description}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[60px]">{controls.quantity}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[124px]">{controls.price}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[216px]">{controls.discount}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[118px]">{controls.tax}</div>
                          </TD>
                          {/* The pinned pair. The background is chosen by a
                              ternary rather than by two `bg-` utilities on one
                              element: same-property Tailwind utilities resolve
                              by stylesheet order, not by the order written. */}
                          <TD
                            numeric
                            className={cx(
                              'align-top',
                              PIN_AMOUNT,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="min-w-[98px]">{controls.amount}</div>
                          </TD>
                          <TD
                            className={cx(
                              'align-top',
                              PIN_ACTIONS,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="grid min-w-[70px] justify-items-end gap-1">
                              <StatusBadge row={row} dirty={dirty} />
                              <LineCancelledBadge line={row.saved} />
                              {rowActions(row)}
                            </div>
                          </TD>
                        </TR>
                        {/* The internal line note: full width, under its row. */}
                        {note !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              {note}
                            </TD>
                          </TR>
                        )}
                        {/*
                          `text-warning`, not an `ErrorBlock`: nothing here is
                          refused. A danger tone beside a real refusal would make
                          the two indistinguishable. Rendered BEFORE the error
                          block and never instead of it — a row can carry both.
                        */}
                        {controls.problems.notice !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <p className="text-xs break-words text-warning">
                                {controls.problems.notice}
                              </p>
                            </TD>
                          </TR>
                        )}
                        {controls.problems.error !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <ErrorBlock title={controls.problems.error} />
                            </TD>
                          </TR>
                        )}
                        {expanded.has(row.key) && (
                          <TR>
                            <TD colSpan={columnCount} className="p-0">
                              {detailPanel(row)}
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          <ul className="grid gap-3 md:hidden">
            {rows.map((row) => {
              const controls = rowControls(row, false);
              const dirty = dirtyKeys.has(row.key);
              return (
                <li
                  key={row.key}
                  className={cx(
                    'rounded-lg border border-border bg-surface',
                    row.state === 'failed' && 'border-danger-border',
                  )}
                >
                  <div className="grid gap-3 px-3 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge row={row} dirty={dirty} />
                        <LineCancelledBadge line={row.saved} />
                      </div>
                      <div className="text-right">
                        <span className="block text-2xs font-medium text-fg-muted">
                          Amount
                        </span>
                        {controls.amount}
                      </div>
                    </div>
                    {controls.product}
                    <InclusionSummary
                      row={row}
                      master={inclusions.rows}
                      error={controls.problems.fieldErrors.inclusions}
                    />
                    {/* The same stack as the wide grid's Item cell, in the same
                        order and behind the same flag. This is a card, not a
                        grid: `compact` is false, so every control keeps its
                        label and its hint, and `table-fixed` never reaches it. */}
                    {controls.showProject && (
                      <>
                        {controls.project}
                        {controls.tier}
                        {/* Behind the same narrower gate as the wide grid — one
                            rule, both layouts. See `showsCitiesControl`. */}
                        {controls.showCities && controls.cities}
                      </>
                    )}
                    <LineCreditCities line={row.saved} />
                    {/* The same block as the wide grid's Item cell, behind the
                        same predicate — and it can now sit BESIDE the project
                        and city controls above rather than only in place of
                        them. `compact` is false here, so the control keeps its
                        label, the projectless sentence, and the note that says
                        "All regions" is a snapshot rather than a live rule. */}
                    {controls.showRegions && controls.regions}
                    {controls.description}
                    {noteControl(row)}
                    <div className="grid gap-3 sm:grid-cols-2">
                      {controls.quantity}
                      {controls.discount}
                    </div>
                    {controls.price}
                    {controls.tax}
                    {controls.problems.notice !== null && (
                      <p className="text-xs break-words text-warning">
                        {controls.problems.notice}
                      </p>
                    )}
                    {controls.problems.error !== null && (
                      <ErrorBlock title={controls.problems.error} />
                    )}
                    {rowActions(row)}
                  </div>
                  {expanded.has(row.key) && detailPanel(row)}
                </li>
              );
            })}
          </ul>

          {/*
            The label says WHICH figure this is, in three words, and that is the
            load-bearing half — a previewed total reading as a total is the
            number somebody quotes down the phone.

            - "Preview total" — draft mode. Our arithmetic, because no line
              exists yet and `lineTotal` is a generated column.
            - "Total of saved lines" — saved mode with unsaved rows on screen.
            - "Total" — saved mode, nothing outstanding.

            The invoice's own four header totals are the SERVER's and are
            rewritten by it on every line write. This figure has never been them.

            ⚠️ **NO FOOTER TOTAL IN EITHER CONVERT MODE, and it is a decision
            already taken once on this screen.** The invoice's four figures are
            already on the form, in its Totals panel, derived one way; a second sum
            here could disagree with it. There is also nothing honest to sum: the
            deal's stored `lineTotal` values are the DEAL's total, and the
            invoice's own will be recomputed server-side with a tax rate this grid
            has never been told — it is handed a percentage to READ, not to
            multiply by. Do not "finish" this by adding one.

            ⚠️ **On the QUOTE conversion it would be wrong in a second, sharper
            way: the grid holds two populations and only one of them is in those
            totals.** The Totals panel shows the QUOTE's four figures, which the
            conversion copies; a row somebody ADDS here is written afterwards and
            re-derives all four server-side. A footer summing both would sit
            directly beneath a panel showing neither, and the two would differ by
            exactly the rows most likely to be questioned.
          */}
          {canManageRows && (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={addRow}
              disabled={saving}
            >
              Add row
            </Button>
          )}
          {!anyConvertMode && (
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-border pt-2">
              <span className="text-xs text-fg-muted">
                {draftMode ? 'Preview total' : dirtyCount > 0 ? 'Total of saved lines' : 'Total'}
              </span>
              <span className="tabular-nums text-sm font-semibold text-fg">
                {formatDecimalString(draftMode ? draftPreviewTotal : savedTotal)}
                {(draftMode ? draftPreviewTotal : savedTotal) !== null && currency && (
                  <span className="ml-1 text-xs font-normal text-fg-muted">{currency}</span>
                )}
              </span>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => {
          void confirmDelete();
        }}
        title={
          pendingDelete?.saved
            ? `Delete the ${pendingDelete.saved.product.productName} line?`
            : 'Delete this line item?'
        }
        tone="danger"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
        confirmLabel="Delete line"
        description={
          pendingDelete?.saved ? (
            <>
              <p>
                <strong>{pendingDelete.saved.product.productName}</strong>
                {pendingDelete.saved.project ? ` under ${pendingDelete.saved.project.name}` : ''}{' '}
                will be removed from this invoice.
              </p>
              {pendingDelete.saved.cities.length > 0 && (
                <div className="mt-2">
                  {/* ⚠️ Named as what it IS. On a flagged line these are the
                      project's cities RESOLVED at read time rather than a set
                      somebody chose, so the count is true of today and of
                      nothing else — and presenting them as chosen would
                      misdescribe the line at the last moment anyone can check
                      it. The chips look identical either way. */}
                  <p className="text-xs text-fg-muted">
                    {pendingDelete.saved.allCities
                      ? 'It runs in every city its project declares — today'
                      : 'It runs in'}{' '}
                    {pendingDelete.saved.cities.length}{' '}
                    {pendingDelete.saved.cities.length === 1 ? 'city' : 'cities'}:
                  </p>
                  <div className="mt-1">
                    <CityChips cities={pendingDelete.saved.cities} />
                  </div>
                </div>
              )}
              {pendingDelete.saved.inclusions.length > 0 && (
                <p className="mt-2">
                  Its {pendingDelete.saved.inclusions.length}{' '}
                  {pendingDelete.saved.inclusions.length === 1
                    ? 'campaign inclusion goes'
                    : 'campaign inclusions go'}{' '}
                  with it.
                </p>
              )}
              <p className="mt-2">
                The header totals are recalculated. Removing the last line takes them to 0.00.
              </p>
            </>
          ) : null
        }
      />
    </div>
  );
}
